// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Main application entry point
 *
 * Load a dataset via URL query param: ?dataset=LIB_<id> (an old
 * ?dataset=INTERNAL_SOS_768 still resolves through the row's legacy ids)
 * No dataset param = just the default Earth globe
 */

import { MapRenderer } from './services/mapRenderer'
import {
  NO_LAYERS,
  bordersChoice,
  defaultLayers,
  fetchLayerCatalog,
  measureCoverage,
  tintFor,
  capOverlays,
  toggleOverlay,
  resolveLayerImages,
  withBordersChoice,
  type BordersChoice,
  type CatalogLayer,
  type LayerSelection,
} from './services/mapLayers'
import type { LayerReveal, LiveChangeView, MapLayerImages } from './services/earthTileLayer'
import { carriesAlphaStream, isEarthBody } from './services/datasetOverlayOptions'
import { bodyOfDataset, markOffEarth } from './services/offEarthBody'
import { mountLayerPicker, rangesOverlap, type LayerPickerHandle, type RtHidden, type TimeRange } from './ui/mapLayersUI'
import { loadRtRanges, saveRtRanges } from './services/rtRangeCache'
import { mountDataPins, pinEnglishName, type DataPinsHandle, type GlobeTrace, type PinReadability, type PinScrub } from './ui/dataPinsUI'
import { MAX_PINS, PinStore, type ExtremesLocator, type Pin, type PinFrame } from './services/measure/pins'
import { MAX_SERIES_SLOTS, createSeries, recordSample, type MeasurementSeries } from './services/measure/series'
import { createFrameSync } from './services/measure/frameSync'
import { getSharedLumaSampler } from './services/glLumaSampler'
import { findReleaseExtremes, releaseGrid } from './services/measure/releaseExtremes'
import { findChangeExtremes } from './services/measure/changeExtremes'
import { SurfaceMasks, gridOf, httpWaterTiles, type MaskGrid, type SurfaceMode } from './services/measure/surfaceMask'
import { createLocateBudget, trackSegments } from './services/measure/followExtremes'
import { ViewportManager, type ViewLayout } from './services/viewportManager'

// CSS entry point — all component styles imported in dependency order.
// Vite bundles these into a single <link> in the production build.
import './styles/index.css'

import { HLSService, setFollowerPictureCap } from './services/hlsService'
import { catalogueFromSavedCopy, dataService, isStillPicture } from './services/dataService'
import { addressWithoutRoom, showMeetingOver, type MeetingOver } from './ui/meetingOverCard'
import { currentMeetingRoom, isOwnDatasetId, presentingMeeting, setMeetingRoom, setPrivateDatasetInUse } from './services/privateData'
import { onStreamDenied, renderJobFromSearch, setStreamMeeting, setStreamRenderJob, startStreamToken, streamAuthFetch, StreamRefusedError, type StreamDenial } from './services/streamToken'
import { ensureOwnDataset } from './services/ownDataOpen'
import { initShareLinkIntake } from './ui/myDataShareLink'
import { formatDate, videoTimeToDate, SIBLING_MIN_READY_STATE, SIBLING_SEEK_EPS_S, verifySiblingTime, shownFrameTime, isSubDailyPeriod, getSunPosition, inferDisplayInterval, MIN_PLAYBACK_RATE, MAX_PLAYBACK_RATE } from './utils/time'
import { FRAMES_SEEK_COOLDOWN_MS, computePanelSyncCorrection, framePanelDates, isOnFrame, pacingRate, panelDateAt, panelFrameAt, panelLocate, panelMsPerSecond, rangePanelDates, verifyFramesSibling, type PanelDates } from './services/panelDateSync'
// AYNI (multi-globe plan, docs/AYNI_XR_MULTI_GLOBE_PLAN.md): each globe's time rule, and the globes in a link.
import { FOLLOW, applyTimeRule, formatTimeRule, parseLinkedSlots, parseTimeRule, sameRule, writeLinkedSlots, type GlobeTimeRule, type LinkedSlot } from './services/globeSlots'
import { openGlobeTimeMenu, timeRuleLabel } from './ui/globeTimeMenu'
import { logger } from './utils/logger'
import type { AppState, VideoTextureHandle, Dataset, DatasetOverlayOptions } from './types'

// Extracted modules
import { showBrowseUI, hideBrowseUI, collapseBrowseUI, notifyBrowseOpened, refreshBrowseNewSinceBadge, setBrowseTab, syncMyDataTab, setBrowseTarget, setBrowseCompatibility, refreshBrowseCompatibility } from './ui/browseUI'
import { defaultDisplayOf, displayFor, onDisplayPrefsApplied, saveDisplayFor } from './services/displayPrefs'
import { startDisplayPrefsSync } from './services/displayPrefsSync'
import { initDownloadDialogUI } from './ui/downloadDialogUI'
import { setDatasetMenuKhipuHost } from './ui/datasetMenu'
import { convertLegacyPlaylists } from './services/legacyPlaylists'
import { keepsKhipus } from './ui/myLessons'
import { updateMapControlsPosition } from './ui/mapControlsUI'
import { initToolsMenu, closeToolsMenu, openToolsTab, showToolsTab, syncAnalyzeButton, syncSolarButton, syncToolsMenuState, syncToolsMenuLayout, pulseBrowseButton, refreshToolsAccount } from './ui/toolsMenuUI'
import { mountViewPalette, type ViewPaletteHandle } from './ui/viewPaletteUI'
import { initShareButton } from './ui/shareButton'
import { initAutoRotateControl, type AutoRotateHandle } from './ui/autoRotateControl'
import { initPlaybackSpeedControl, type PlaybackSpeedHandle } from './ui/playbackSpeedControl'
import { initGlobeViewControl, type GlobeViewHandle } from './ui/globeViewControl'
import { isOccluded } from './services/maplibreInternals'
import { initAccountUI } from './ui/accountUI'
import { openCreditsPanel } from './ui/creditsPanel'
import { initAnchorButton } from './ui/anchorPanel'
import { initTimeLabelPosition, refreshTimeLabelPosition } from './ui/timeLabelPosition'
import { initGlobePanelOffset } from './ui/globePanelOffset'
import { initLessonToolsColumn } from './ui/lessonToolsColumn'
import { setChatCloser } from './ayni-chat/activeChat'
import { createAyniChat } from './ayni-chat/ayniChat'
import { chatLanguageFor } from './ayni-chat/xrChatScope'
import type { XrChatHost } from './ayni-chat/xrChatExecutor'
import type { XrChatFeatureSource } from './ayni-chat/xrChatFeatures'
import { createChatResearch, explainReading, runChatAnalysis } from './ui/analyzeChat'
import { mountAyniChatPanel, type AyniChatPanelHandle } from './ui/ayniChatPanel'
import { loadViewPreferences, saveViewPreferences, setBordersVisible, type ViewPreferences } from './utils/viewPreferences'
import { renderColorbar, closeDisplayControls } from './ui/colorbarUI'
import { onReferenceLinesChange, referenceLinesOf, referenceLinesVisible, setReferenceLinesVisible, shownReferenceLines } from './services/referenceLines'
import {
  initAnalyzeUI,
  closeAnalyzeUI,
  notifyAnalyzeDatasetChanged,
  notifyAnalyzePlaybackSettled,
  analyzeMethodsState,
  analyzeBundleCsvs,
} from './ui/analyzeUI'
import { hovmollerBundleCsv, hovmollerMethodsState } from './ui/hovmollerUI'
import { thresholdBundleCsv, thresholdMethodsState } from './ui/thresholdUI'
import { regionSeriesBundleCsv } from './ui/regionSeriesUI'
import { openFigureDialog, renderFigureFiles, type FigureChange, type FigurePin, type FigureSource, type FigureTab, type GlobeCapture } from './ui/figureDialog'
import { methodsText, type MethodsEncoding, type MethodsFigure, type MethodsInput } from './services/research/methods'
import { bundleFiles, zipBundle, type BundleCsv } from './services/research/bundle'
import { ayniCsvFilename } from './ui/pinsCsv'
import { trailFile } from './services/research/researchTrail'
import { createPlaybackSettleWatcher } from './services/playbackSettle'
import { createDsaTimelineCache, type DsaTimelineCache } from './services/dsaTimelineCache'
import { availabilityAtFrame, dateAtVideoTimeMs, frameAtVideoTime, lastFrameDateMs, timelineAxis, timelineEndMs, videoTimeForDateMs, videoTimeForFrame, type DsaTimeline } from './services/dsaTimeline'
import { clockFromRange, clockFromTimeline, decideRtOverlaySync, maxRtOverlays, rtBaseRateCeiling, type LinearClock } from './services/rtOverlaySync'
import { formatPlayheadLabel, lastFrameMsOf, type TimelineTrackState } from './services/timelineTrackCanvas'
import { formatFrameIso, formatFrameLabel, frameTimeMs, nearestFrameAtTimeMs } from './services/frameCalendar'
import { createTimelineTrackUI, type TimelineTrackStackState, type TimelineTrackUIHandle } from './ui/timelineTrackUI'
import { attachStackDrag } from './ui/overlayStackDrag'
import { closePaletteDialog, currentPaletteDialog, togglePaletteDialog } from './ui/overlayPaletteDialog'
import { dismissUndoToast, showUndoToast } from './ui/undoToast'
import { mountViewOpacity, type ViewOpacityHandle } from './ui/viewOpacityUI'
import { canRecolour, moveInStack, overlayColours, overlayScale, rampGradient } from './services/overlayDisplay'
import { cropSnapshot, findExtremum, summarize, windowForBounds } from './services/datasetStats'
import { DEFAULT_DISPLAY, buildDisplayLut, isDefaultDisplay, setActiveDisplay, type ColorScaleDisplay } from './services/colorScaleDisplay'
import { MEASUREMENT_COLORS } from './services/measure/colors'
import { RENDER_ENCODING_DATA_LUMA, buildColorScaleLut, releaseOf, type ColorScale } from './types/color-scale'
import { renderClassLegend } from './ui/classLegendUI'
import { withPhaseBars } from './ui/phaseLegendUI'
import { makeLegendMovable, placeLegendAt } from './ui/legendPlacement'
import { mountLegendDock, type LegendDockHandle } from './ui/legendDock'
import { clearPanelLegends } from './ui/legendClearance'
import { initHelpUI, setActiveDataset as setHelpActiveDataset } from './ui/helpUI'
import { showDisclosureBannerIfNeeded } from './ui/disclosureBanner'
import {
  createFetchTransport,
  emit,
  initSession,
  resumeForVrExit,
  setTransport,
  startPerfSampler,
  TELEMETRY_BUILD_ENABLED,
  TELEMETRY_CONSOLE_MODE,
} from './analytics'
import {
  createPlaybackState, startPlaybackLoop, stopPlaybackLoop,
  togglePlayPause, rewind, fastForward, stepFrame, onScrub,
  updatePlayButton, toggleCaptions, resetPlaybackState, initPlaybackPositioning,
  seekToDate,
  cancelLoopHold,
  type PlaybackState,
} from './ui/playbackController'
import {
  loadImageDataset, loadVideoDataset, displayDatasetInfo, renderStreamCredit, setAccuracyFrameClock,
} from './services/datasetLoader'
import { isMobile } from './utils/deviceCapability'
import { cloudStreamUrl } from './services/cloudFrame'
import {
  buildDatasetPath,
  buildNoDatasetPath,
  isDatasetRef,
  parseDatasetPathname,
} from './utils/datasetUrl'
import { getRecent, recordVisit, writeLastSession } from './services/visitMemory'
import { getCatalogMode, setCatalogMode } from './utils/catalogMode'
import { applyEmbedMode } from './utils/embedMode'
import {
  hideCatalogTabs,
  initCatalogTabs,
  setActiveCatalogTab,
  showCatalogTabs,
} from './ui/catalogTabsUI'
import {
  applyPosterDeepLinks,
  parseInitialLayout,
} from './utils/posterDeepLinks'
import { initVrButton } from './ui/vrButton'
import { flyToOnGlobe, getRoomGlobe, isVrActive, loadThree, notifyXr, setFollowedRoomGlobe, setXrRoomFollower, type VrGlobeTapKind, type VrSessionContext, type VrToolsHost } from './services/vrSession'
import { createXrToolsHost, pinName, type XrToolsDeps } from './services/xrToolsHost'
import { focusFor } from './services/datasetCoverage'
import { roomCodeFromSearch, type RoomStatus } from './services/roomClient'
import { activeRoomSync, isExploring, startRoomSync, viewsDiffer, type RoomSyncHost } from './services/roomSync'
import { connectRoom } from './services/roomClient'
import { createOutputSocket, isOutputWindow, newOutputChannel, outputChannelFromSearch, outputWindowUrl, sphereFeedFromSearch } from './services/outputWindow'
import { receiveFeedPins, shareFeedPins, sphereFeedSize, startSphereFeed, type FeedPin } from './services/sphereFeed'
import { roomShade, type RoomColours, type RoomDataPins, type RoomLayerLook, type RoomLayers, type RoomNote, type RoomSlotUpdate, type RoomState, type RoomView } from './services/roomProtocol'
import { adoptedPinsOf, displayOfRoomColours, roomColoursOf, roomDataPinsOf, sameColours } from './services/roomLook'
import { mountRoomChip } from './ui/roomChip'
import { canHostMeetings, rememberedMeeting, takeMeetingKeyParam } from './services/meetingHost'
import { accountKnown, accountState, completeSignIn, currentEntitlements, dismissLicenceNotice, loadAccount, onAccountChange, setLicenceSwitchBlocked, takeReturnTo, takeSignInParams } from './services/account'
import { openPaymentLink, takePaymentLink } from './services/billing'
import { licenceNoticeText, openPlans, openSignIn, openYourPlan, planName, plansFromSearch } from './ui/xrPlansUI'
import { showLicenceBanner } from './ui/licenceBanner'
import { openMeetingPanel, openMeetingSharePanel } from './ui/meetingPanel'
import { meetingSound, mountMeetingMedia, setMeetingSound, tellXrMeetingSound } from './ui/meetingMediaUI'
import { activityGlobeFor, mountMeetingActivities } from './ui/meetingActivitiesUI'
import { gameNameOfConn, gameVrAnswer, gameVrState, gameVrTap, mountMeetingGames } from './ui/meetingGamesUI'
import { takeRoomToken } from './services/roomClient'
import type { VrDatasetTexture } from './services/vrScene'
import { buildReleaseLut, releaseBoundingBox, releaseColorScale, releaseCropRect, releaseTimeRange, resolveDashRelease } from './services/dashRelease'
import type { ReleaseEncoding } from './types/release-encoding'
import type { ColorKey } from './services/colorKey'
import { calibrateRelease, videoHasFrame, withKnownRange } from './services/decoderRange'
import { custodyRows, scaleCodeStep } from './services/valueFidelity'
import type { FrameInstant } from './services/research/compare'
import type { AnalyzeTimeAxis, CompareCandidate } from './ui/analyzeUI'
import { fetchGeoMediaMarkers, pickGeoMediaMarker, type GeoMediaMarker } from './services/geoMedia'
import { isGeoMediaDataset } from './services/geoMediaSwitch'
import { GeoMediaPlayer } from './services/geoMediaPlayer'
import { addGeoMediaLayer, type GeoMediaLayerHandle } from './services/geoMediaLayer'
import { createGeoMediaPanel, type GeoMediaPanelHandle } from './ui/geoMediaPanel'
import type { VrGeoMediaState } from './services/vrGeoMedia'
import { vrOverlayOptionsFor } from './services/vrOverlayOptions'
import {
  createDomChromeHost,
  createFullscreenController,
  createIdleCursor,
  type FullscreenController,
  type IdleCursor,
} from './services/windowChrome'
import { bootstrapI18n } from './i18n/bootstrap'
import { DATA_KINDS, DATA_KIND_LABEL, THEMES, THEME_LABEL, dataKindOf, displayTitle, isEducationOnly, outsidePlan, packageOf, themesOf } from './services/datasetTaxonomy'
import { twinHiddenIds } from './services/libraryTwins'
import { PACKAGE_NAME, dataToolsAllowed, dataToolsLent, effectivePackages, setLentPackages, maxGlobes, maxPins, maxPlanOverlays, pinsExportLocked, researchToolsAllowed, rtOverlayOffered, rtOverlaysAllowed, setLentDataTools, setLentGlobes, setRenderJobAuthorized, setWallGrant, streamLockedBy, streamQuotaReached, streamQuotaToday, teachingPlan, educationStreamsAllowed } from './services/planAccess'
import { fetchLesson, resolveDatasetKey } from './services/lessonService'
import { LIBRARY_PREFIX, ensureLibraryDataset, loadLessonLibrary, type LessonLibraryIndex } from './services/lessonLibrary'
import { activeLessonPlayer, openLessonPlayer, type LessonPlayerHost } from './ui/lessonPlayerUI'
import { activeKhipuPlayer, applyStepToGlobe, openKhipuPlayer, type KhipuPlayerHost, type LiveDataMove } from './ui/khipuPlayerUI'
import { roomNoteVrState, showRoomNote, shownRoomNote } from './ui/roomNoteUI'
import { activeSoloRecording, startSoloRecording } from './ui/soloRecordUI'
import { activeKhipuEditor, openKhipuEditor, type KhipuEditorHost } from './ui/khipuEditorUI'
import { emptyStory, insertStep, stepFromGlobe } from './services/khipuEdit'
import type { SoloScene } from './services/soloRecorder'
import type { VrPinMark } from './services/vrPins'
import { isCanvas, newKhipuId, type Khipu, type KhipuEffects, type KhipuStep } from './services/khipu'
import { liveStopOf, playStopLive, type LiveMotion, type LiveStop } from './ui/liveMotion'
import { compareSplit, type EffectsClock } from './services/studio/effects'
import { createEffectsOverlay, type EffectsOverlay } from './ui/effectsOverlay'
import type { FilmPlan, FilmView } from './services/studio/film'
import { planStep } from './services/khipuPlayback'
import { normalizeKhipu } from './services/khipuFile'
import { createRenderController, isRenderMode, type RenderHost } from './services/studio/renderMode'
import { createCompositor } from './services/studio/compositor'
import { renderCreditLine } from './services/rights'
import { outputSize, parseShot, type Shot } from './services/studio/shot'
import { closeStudio, filmKhipu, isStudioOpen, openStudio, type StudioHost } from './ui/studioUI'
// AYNI (Situation room, docs/AYNI_XR_CONTROL_ROOM_PLAN.md): the operator's panel, wall displays, briefings, own layers.
import { closeSituationPanel, isSituationPanelOpen, openSituationPanel, refreshSituationPanel, type FreshnessRow, type SituationPanelHost } from './ui/situationPanel'
import { isWallDisplay, startWallDisplay } from './ui/wallDisplay'
import { printBriefingPack } from './ui/briefingPack'
import { createOwnLayers } from './services/ownLayers'
import { getSatelliteLayer, type SourceInput } from './services/satellites/satelliteLayer'
import type { DsaPlatform } from './services/satellites/sourcePlatforms'
import { mountSatellitePanel } from './ui/satellitePanelUI'
import { attachSatellitesToMaps, showEvent, type SatelliteMapUiHandle } from './ui/satelliteMapUI'
import { indexDatasetId, platformNote, satelliteDatasets } from './services/satellites/satelliteText'
import { platformName } from './services/satellites/sourcePlatforms'
import type { TimelineBand } from './services/timelineTrackCanvas'
import { refreshBrowseCards, setBrowseBodyScope, setBrowseBodyScopeLabel, setBrowseSatelliteSources } from './ui/browseUI'
import { createSolarSystemMode, setSolarEarthPicture, solarBodyName, type SolarModeHandle } from './ui/solarSystemMode'
import { solarBodyOfDatasetBody } from './services/solarSystem/bodies'
import { EARTH_ASSET_BASE } from './config/endpoints'
import { setSatelliteFacetSource } from './services/datasetFilter'
import { applyKhipuSatellites, captureKhipuSatellites, startSatelliteFollow, stopSatelliteFollow } from './services/satellites/khipuSatellites'
import { applyRoomSatellites, roomSatellitesOf } from './services/satellites/satelliteRoom'
import { createBriefingSession, type BriefingSession } from './services/briefingSession'
import type { BriefingGlobeShown } from './services/briefingRecord'
import { situationAllowed } from './services/situationRoom'
import { streamDates, type StreamDates } from './services/streamDates'
import { loadMeetingLayers, loadSituation, shareMeetingLayers } from './services/situationClient'
import { streamFreshness } from './services/streamFreshness'
import { saveKhipu } from './services/khipuStore'
import { zipKhipu } from './services/lessonService'
import { formatDate as formatLocalDate } from './i18n/format'

/** AYNI Studio: a shot's output size in pixels. */
const outputSizeOf = (shot: Shot): { width: number; height: number } => outputSize(shot.output.aspect, shot.output.resolution)
import { fetchStory, storyFor } from './services/storyService'
import { getLocale, plural, t } from './i18n'
import { formatNumber } from './i18n/format'
import { formatProbeReading, probeDatasetValue, texelUvToLatLon, type ProbeReading } from './services/datasetProbe'
import { initUiScale } from './services/uiScaleService'
import { initShaderSettings } from './services/shaderSettingsService'
import { maybeInitShaderTuner } from './ui/shaderTunerUI'
import { altitudeForZoom, buildViewUrl, parseViewParams, stripViewParams, viewQuery, type ViewState } from './services/research/viewLink'
import { frameLabelNow, precisionOfCadence, setResearchFrameAxis } from './services/research/frameTime'
import { AYNI_XR_DOI, ayniXrSoftware, citationAuthor, citationText, provenanceRows, type CitationInput } from './services/research/citation'
import type { ResearchRecordHandle } from './ui/researchRecordUI'
import type { PinSnapshot } from './services/research/researchRecord'
import { citationIn, openCiteViewDialog } from './ui/citeViewDialog'
import { baselineReferenceAt, changeShown, changeViewState, onChangeView, shownRange, viewPin, viewReading, viewUnits } from './services/research/changeView'
import { groupLabel } from './services/research/climatology'
import { buildChangeLegend } from './ui/changeLegend'
import { looksLikeEnsemble } from './services/research/pinStats'
import { VectorPlayer, inspectionElement, ownClock, vectorPreviewUrl, videoClock } from './services/vector/vectorPlayer'
import { parseDsaVectors } from './services/vector/vectorModel'
import type { VectorFrameLayer } from './services/vector/vectorPick'
import { renderVectorLegend, vectorLegendCanvas, vectorLegendOf, vectorLegendSections, type VectorLegendModel } from './services/vector/vectorLegend'

/** AYNI: "+0.4 °C" for a change above zero; the minus is already written. */
/**
 * AYNI Studio: a stream a data move can draw over the globe as it is (the
 * overlay pass samples a whole-globe picture as published): the whole
 * Earth, centred on the prime meridian, not flipped.
 */
function wholeGlobeStream(d: Dataset): boolean {
  const b = d.boundingBox
  return !!d.realtimeKind && !!(d.dataLink || d.releaseDescriptorLink) && !d.lonOrigin && !d.isFlippedInY
    && (!d.celestialBody || d.celestialBody === 'Earth')
    && !!b && b.n >= 89.9 && b.s <= -89.9 && b.w <= -179.9 && b.e >= 179.9
}

function signed(value: number, text: string): string {
  return changeShown() && value > 0 ? `+${text}` : text
}

/** AYNI: a reading as the globe shows it: the value, or "+0.4 °C vs baseline" (changeView.ts). */
function formatShownReading(reading: ProbeReading): string {
  // AYNI: a % change left out over a near-zero baseline is not missing data.
  // (Left out, it keeps the dataset's units, which the floor is in.)
  if (reading.noData && reading.nearZero) return t('research.change.nearZeroReading', { floor: formatProbeReading({ ...reading, noData: false, nearZero: undefined, quantisationStep: undefined, uncertainty: undefined, value: changeViewState().floor ?? 0 }) })
  const text = formatProbeReading(reading)
  if (!changeShown() || reading.noData) return text
  const view = changeViewState()
  const key = view.other ? (view.mode === 'percent' ? 'research.compare.deltaPercent' : 'research.compare.delta') : 'research.baseline.delta'
  return t(key, { value: signed(reading.value, text) })
}

// Apply the persisted UI-scale to `:root` at module-evaluation
// time — before DOMContentLoaded, before any UI renders, before
// the loading screen's first paint cycle completes. Waiting for
// the DOMContentLoaded handler would let the loading screen
// paint once at `--ui-scale: 1` and then snap to the user's
// preset, producing a visible flash on every reload for visitors
// who pick a non-default size. Module-eval is the earliest hook
// the bundle gives us short of an inline <script> in <head>.
// SSR-safe (the service no-ops when `document` is undefined).
// See docs/WEB_CATALOG_FEATURES_PLAN.md §7.1.
initUiScale()

// Apply persisted shader settings (§7.2) — currently the specular-
// strength preset. Contrast / saturation / bump are dev-tuned and
// ship via SHADER_DEFAULTS. The earth-tile-layer + photoreal Earth
// read getShaderSettings() per-frame, so this just primes the
// snapshot.
initShaderSettings()

// Mount the §7.2 shader-tuner panel — no-op unless the URL carries
// `?tune=shader`. Dev surface for picking the shipped SHADER_DEFAULTS
// values; never reaches end-users via the Tools menu.
maybeInitShaderTuner()

// --- App constants ---
// AYNI: the cloud stream the globes take their clouds from (cloudFrame.ts).
const CLOUD_TEXTURE_URL = cloudStreamUrl() ?? ''
const EARTH_TEXTURE_WEIGHT = 0.8
const CLOUD_TEXTURE_WEIGHT = 0.2
const LOADING_BASE_PROGRESS = 20
const LOADING_TEXTURE_RANGE = 70
const LOADING_HIDE_DELAY_MS = 300

/**
 * How long to let a requested redraw land before a panel that is showing
 * the wrong frame is reported as such.
 *
 * `needsUpdate` schedules a repaint, so the upload takes a frame or two.
 * Generous next to that, and short enough to be imperceptible against
 * the settle delay that precedes it.
 */
const SIBLING_REPAIR_CONFIRM_MS = 120

/**
 * Root application class that boots the WebGL globe, loads datasets,
 * and orchestrates all UI subsystems (browse panel, chat, playback controls).
 *
 * Lifecycle: constructed once on DOMContentLoaded, then {@link initialize}
 * fetches the dataset catalog and either displays the default Earth or
 * loads a dataset specified by the `?dataset=` URL parameter.
 */
/**
 * Per-panel dataset + video state. Length is kept in sync with the
 * viewport count by onLayoutChange callbacks from ViewportManager.
 * Slot 0 is always the first panel; the primary index (which slot
 * drives the playback transport UI) lives inside ViewportManager.
 */
interface PanelState {
  dataset: Dataset | null
  hlsService: HLSService | null
  videoTexture: VideoTextureHandle | null
  /** Decoded image element for image datasets — passed to VR to avoid re-fetching. */
  image: HTMLImageElement | null
  /** Wall-clock `Date.now()` at which the current dataset finished
   * loading into this panel. Used to compute `layer_unloaded.dwell_ms`.
   * Null when the panel is empty (default Earth). */
  loadedAt: number | null
}

/**
 * The date the globe is showing, as its label reads, or null when the label
 * is hidden or empty. For the Analyze panel's "measured at" caption.
 */
function readTimeLabel(): string | null {
  const labelEl = document.getElementById('time-label')
  if (!labelEl || labelEl.classList.contains('hidden')) return null
  const text = document.getElementById('time-display')?.textContent?.trim()
  return text && text !== '--' ? text : null
}

function createPanelState(): PanelState {
  return {
    dataset: null,
    hlsService: null,
    videoTexture: null,
    image: null,
    loadedAt: null,
  }
}

/** AYNI: one real-time overlay stream playing over the dataset (main.ts `rtOverlays`). */
interface RtOverlayPlaying {
  id: string
  hls: HLSService
  video: HTMLVideoElement
  /** A value-encoded release's palette and map rectangle (earthTileLayer.MapOverlayLayer). */
  lut?: Uint8Array
  crop?: { u0: number; v0: number; us: number; vs: number }
  /** AYNI: its class map (IMERG's rain/snow; releasePhase.ts), when it has one. */
  phase?: { classMap: Uint8Array; crop: { u0: number; v0: number; us: number; vs: number }; rows: number }
  /** AYNI: its value encoding, so Analyze can compare it with the dataset (research tier, "Compare"). */
  encoding?: ReleaseEncoding
  /** AYNI: a picture stream's colour key, from its .dsa (colorKey.ts). */
  colorKey?: ColorKey
  /** The overlay's .dsa: its dates, for keeping it on the base's date. */
  timelineUrl?: string
  /** False while the base's date is outside the overlay's dates. */
  visible: boolean
  /** AYNI: hidden with the eye on its date track; it keeps playing in step. */
  userHidden?: boolean
  /** AYNI (owner, 2026-10-12): its opacity, from its transparency slider; 1 (opaque) each time it starts. */
  opacity?: number
  /** The overlay's dates, once its .dsa has arrived (its yellow date track). */
  range?: TimeRange
}

/** AYNI (owner, 2026-10-12): what the dataset's ✕ took off, for Undo to put back. */
interface LayerUndoSnapshot {
  datasetId: string
  /** The date shown, from its .dsa; else the video's time. */
  atMs: number | null
  videoTime: number | null
  playing: boolean
  display: ColorScaleDisplay
  opacity: number
  hidden: boolean
  /** The real-time overlays, lowest first. */
  overlays: Array<{ id: string; opacity: number; hidden: boolean }>
}

/** AYNI (multi-globe plan, phase 2): a globe's own layer stack while it is not the main one. */
interface OtherStack {
  selection: LayerSelection
  images: Awaited<ReturnType<typeof resolveLayerImages>> | null
  rt: RtOverlayPlaying[]
  rtWanted: string[]
}

class InteractiveSphere {
  private appState: AppState = {
    datasets: [],
    currentDataset: null,
    isLoading: false,
    error: null,
    timeLabel: '--',
    isPlaying: false,
    currentFrame: 0,
    totalFrames: 0
  }

  private readonly isMobile = isMobile()

  private viewports: ViewportManager = new ViewportManager()

  /** Per-panel dataset state (one entry per active viewport). */
  private panelStates: PanelState[] = []

  /** Listener attached to the primary video for sibling sync. */
  private primaryVideoSyncListeners: Array<{ event: string; handler: EventListener }> = []
  private primaryVideoSyncTarget: HTMLVideoElement | null = null
  /**
   * True while sibling sync is wired to a primary video. Gates the
   * per-frame drift correction driven off the playback rAF loop — see
   * `correctSiblingDrift()`.
   */
  private primaryVideoSyncActive = false
  /**
   * Per-slot record of whether a sibling was frozen out-of-range on the
   * previous drift-correction frame. Lets `correctSiblingDrift` resume a
   * sibling with a single `play()` on the out→in transition instead of
   * re-issuing `play()` (and allocating/catching a Promise) every frame
   * when autoplay is blocked or a stream is briefly unplayable.
   */
  private siblingOutOfRange: boolean[] = []
  /**
   * The primary's true instant behind the shared time label, or `null`
   * when the label is asserting nothing. Stashed by
   * `updateVideoTimeLabel` as it formats, so `verifySiblingTimes` checks
   * panels against the position actually on screen rather than
   * re-deriving one that could differ. Unsnapped on purpose — see
   * `verifySiblingTime`.
   */
  private assertedLabelDate: Date | null = null
  /** Per-slot record of which siblings currently carry a time notice,
   *  so clearing them while playing costs one array scan and no DOM. */
  private siblingTimeNoticed: boolean[] = []
  /**
   * Per-slot abort handle for an armed `seeked`-driven texture upload.
   *
   * Non-null means one is already armed, so a second seek in the same
   * frame does not stack another listener. Aborting both removes the
   * listener and clears that state in one step, which matters because
   * the two must never disagree: a slot left marked armed after its
   * video is gone can never arm again, and would silently lose the
   * upload-on-seek fix for the rest of the session.
   */
  private siblingSeekUploads: Array<AbortController | null> = []
  /** AYNI: when each stream sibling was last seeked by the drift correction (`FRAMES_SEEK_COOLDOWN_MS`). */
  private siblingLastSeekAt: number[] = []
  /** Pending re-check after a repair attempt — see `verifySiblingTimes`. */
  private siblingRepairTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Fullscreen for this window. Held so the Tools menu can read the
   * state and `dispose()` can detach the F11 handler.
   */
  private fullscreen: FullscreenController | null = null
  private idleCursor: IdleCursor | null = null

  /**
   * Convenience getter returning the primary viewport's renderer.
   * Most call sites operate on a single "the renderer" for the
   * primary panel; per-slot operations go through `this.viewports`
   * and `this.panelStates` directly.
   */
  private get renderer(): MapRenderer | null {
    return this.viewports.getPrimary()
  }

  /** Primary panel's HLS service, if any. */
  private get hlsService(): HLSService | null {
    return this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService ?? null
  }

  /** Primary panel's video texture, if any. */
  private get videoTexture(): VideoTextureHandle | null {
    return this.panelStates[this.viewports.getPrimaryIndex()]?.videoTexture ?? null
  }

  private playback: PlaybackState = createPlaybackState()

  /**
   * Parsed `.dsa` time axes for real-time and forecast streams, keyed by
   * URL. The catalog resolves each row's annotation into
   * `Dataset.timelineLink`; this fetches it once and remembers both
   * outcomes, so the per-frame poll in VR cannot put a request on the wire.
   * See `services/dsaTimelineCache.ts`.
   */
  // AYNI: through streamAuthFetch, so an own dataset's .dsa carries its token (privateData.ts) and a stream's the stream token (streamToken.ts).
  private dsaTimelines: DsaTimelineCache = createDsaTimelineCache({ fetchImpl: streamAuthFetch })

  // --- AYNI: DSA 1.8 vector layers, services/vector/ ---
  /** Each globe's vector layers, by slot; each player's loop calls syncVectorLayers first on every frame. */
  private vectorPlayers: VectorPlayer[] = []
  /** The `.dsa` whose vector layers each globe should draw now (null: none). */
  private vectorWanted: Array<string | null> = []
  /** Developer switch `?vectorPreview=<.dsa url>`: a DSA's vector layers on the bare main globe. */
  private readonly vectorPreview = vectorPreviewUrl()
  /** Developer switch `?vectorDsa=<.dsa url>`: that DSA's vector layers over every globe's dataset, in place of its own. */
  private readonly vectorOverride = vectorPreviewUrl(undefined, undefined, 'vectorDsa')
  /** Each globe's vector layers still loading (a Studio render waits for them). */
  private vectorLoads: Array<Promise<void>> = []
  /** What a tap on the immersive globe last inspected (its panel beside the globe), or null. */
  private vectorXrInspection: { key: string; text: string } | null = null

  // --- AYNI: layer stack (services/mapLayers.ts) ---
  /** The basemap + overlays chosen for the loaded dataset. */
  private mapLayerSelection: LayerSelection = NO_LAYERS
  /**
   * The viewer's own word on borders, from the picker. The next dataset
   * undoes only the automatic switch-on (mapLayers.withBordersChoice).
   */
  private viewerBorders: BordersChoice | null = null
  /** Their decoded images; the same object until the stack changes, which the VR poll relies on. */
  private mapLayerImages: MapLayerImages | null = null
  /** The slot the stack belongs to (the one its dataset loaded into). */
  private mapLayerSlot = 0
  /** Bumped by every apply / clear, so a slow one never lands over a newer one. */
  private mapLayerGen = 0
  private layerPicker: LayerPickerHandle | null = null
  /**
   * AYNI: the real-time overlay streams playing over the dataset, first
   * chosen first (lowest on the globe). Phase 1 of
   * docs/archive/ayni/REALTIME_OVERLAY_PLAN.md allowed one; the owner asked
   * for two (2026-10-04), so a computer plays two and a phone, tablet or
   * Quest, whose decoders cannot take three videos, still one
   * (`maxRtOverlays`). Each has its own player and video element; kept
   * across base-dataset changes, stopped on None or home.
   */
  private rtOverlays: RtOverlayPlaying[] = []
  /** The last dataset's default layers being put up (a Studio render waits for them, so every frame has them). */
  private defaultLayersPending: Promise<void> = Promise.resolve()
  /** AYNI (multi-globe plan): the default layers being worked out for each globe's last load. */
  private defaultLayersBySlot = new Map<number, Promise<void>>()
  /** AYNI Studio: the next stop's dataset drawn over the globe while a data move (crossfade, wipe) runs. */
  private dataMix: { id: string; rt: RtOverlayPlaying; reveal: LayerReveal } | null = null
  /** Counts the mix layer's starts and stops: a start that finishes after a newer call gives its stream up. */
  private dataMixGen = 0
  /** AYNI Studio: a split compare on the live globe (the other date, and whether its layer is up yet). */
  private liveCompareState: { atMs: number; ready: boolean } | null = null
  /** AYNI Studio: whether the render's split compare is on the globe now (its line and dates are painted only then). */
  private renderCompareShown = false
  /** AYNI: the overlays asked for, in order, including any still loading: what the picker shows. */
  private rtWanted: string[] = []
  /** AYNI: how many real-time overlays this device may play at once. */
  private readonly rtCap = typeof navigator === 'undefined' ? 1 : maxRtOverlays(navigator.userAgent, navigator.platform, navigator.maxTouchPoints)
  /** AYNI: which overlays the picker was last told are hidden, so it is told only on a change. */
  private rtHiddenKey = ''
  private rtOverlayGen = 0
  /**
   * AYNI: each real-time overlay candidate's dates, by dataset id, for the
   * Real time list (null: it gives none). Fetched when the list is opened.
   */
  private rtRanges = new Map<string, TimeRange | null>()
  /**
   * AYNI (owner, 2026-10-20): those kept on this device from an earlier
   * visit (rtRangeCache.ts) are in rtRanges from the start, so the list
   * opens filtered; the ones old enough are fetched again, quietly.
   */
  private rtRangesStale = new Set<string>()
  /** When each was fetched this visit, to keep with it. */
  private rtRangesFetchedAt = new Map<string, number>()
  /** AYNI (multi-globe plan): datasets Browse asked the dates of (its "shares dates with globe 1" marks), fetched with the overlays'. */
  private rangeWanted = new Set<string>()
  private rtRangesFetching = false
  /**
   * AYNI: the yellow date tracks, one per overlay that may play: each
   * overlay's dates on the dataset's axis, stacked above the dataset's own
   * track as the overlays are stacked above it on the globe.
   */
  private overlayTrackUIs: TimelineTrackUIHandle[] = []
  /**
   * AYNI (owner, 2026-10-09): each overlay's colours (palette and limits,
   * set from its palette button), by dataset id, for the session: an
   * overlay taken off and put back comes back in the colours it had.
   */
  private rtDisplays = new Map<string, ColorScaleDisplay>()
  /**
   * AYNI (owner, 2026-10-12): the dataset's opacity (its transparency
   * slider) and its eye. Each new dataset starts opaque and shown; a
   * meeting's lead sends both, and Khipu steps record them.
   */
  private baseOpacity = 1
  private baseHidden = false
  /** AYNI: told when an opacity or an eye changes (the headset's and the phone's panels, a meeting's lead). */
  private layerViewListeners = new Set<() => void>()

  /** AYNI: tell the listeners an opacity or an eye changed. */
  private notifyLayerViewListeners(): void {
    for (const fn of this.layerViewListeners) {
      try { fn() } catch (err) { logger.warn('[App] layer view listener failed:', err) }
    }
  }
  /** AYNI: the ramp each overlay's palette button shows, rebuilt only when its colours change. */
  private rtSwatches = new Map<string, { display: ColorScaleDisplay; css: string }>()
  /**
   * AYNI: the geo-media dataset on the globe (radio stations, wildlife
   * cams): its markers, the player of the one picked, the dots on the
   * browser globe and the panel. Null for every other dataset.
   */
  private geoMedia: {
    markers: readonly GeoMediaMarker[]
    player: GeoMediaPlayer
    layer: GeoMediaLayerHandle | null
    panel: GeoMediaPanelHandle
    /** What the immersive globe draws (vrGeoMedia), rebuilt on every change so the session sees it. */
    vrState: VrGeoMediaState
    pictureVersion: number
  } | null = null
  /**
   * The overlay follows the base's date (rtOverlaySync), checked on a
   * timer rather than the playback rAF loop, which WebXR stops while an
   * immersive session runs.
   */
  private rtSyncTimer: ReturnType<typeof setInterval> | null = null
  /** The base's rate when no overlay paces it, and the last rate the sync set on it. */
  private rtBaseUserRate = 1
  private rtBaseRateSet: number | null = null
  /** AYNI (2026-10-20): the fastest the main globe's live layers let its dataset play (rtBaseRateCeiling); Infinity: no limit. The speed bar greys the steps above it. */
  private rtSpeedCeiling = Infinity
  /**
   * AYNI (multi-globe plan, phase 2): the layer stacks of the globes that
   * are not the main one, by globe. The main globe's stack lives in
   * `mapLayerSelection` / `resolvedLayerImages` / `rtOverlays` / `rtWanted`,
   * which Tools → Layers edits; making another globe the main one swaps the
   * two (`swapLayerStacks`). Each globe's real-time streams follow its own
   * date (`tickSiblingRtOverlays`).
   */
  private otherStacks = new Map<number, OtherStack>()
  /** Bumped per globe by every apply there, so a slow one never lands over a newer one. */
  private otherStackGen = new Map<number, number>()

  /**
   * The 2D transport's date track. Mounted into `#playback-controls` at
   * boot, hidden until the primary dataset declares a time axis, and fed
   * from the same snapshot the headset draws — one axis, two surfaces.
   */
  private timelineTrackUI: TimelineTrackUIHandle | null = null
  private loadingHideTimer: ReturnType<typeof setTimeout> | null = null
  private loadGeneration = 0 // guards against concurrent dataset loads (the primary globe's)
  /**
   * AYNI (multi-globe plan): the other globes' load generations, one each,
   * so a load into globe 2 does not cancel a load into globe 1 in flight
   * (a meeting's follower loads several at once).
   */
  private siblingLoadGen: number[] = []

  private loadGenFor(toPrimary: boolean, slot: number): number {
    return toPrimary ? this.loadGeneration : (this.siblingLoadGen[slot] ?? 0)
  }

  /** Start a new load into a globe, superseding one in flight there. */
  private bumpLoadGen(slot: number): number {
    if (slot === this.viewports.getPrimaryIndex()) return ++this.loadGeneration
    this.siblingLoadGen[slot] = (this.siblingLoadGen[slot] ?? 0) + 1
    return this.siblingLoadGen[slot]
  }
  /** AYNI: the AYNI chatbot (AYNI 3D's engine, `src/ayni-chat/`), the site's chat assistant. */
  private ayniChat: AyniChatPanelHandle | null = null

  /** Persisted view preferences: info panel + legend visibility. */
  private viewPrefs: ViewPreferences = loadViewPreferences()

  /**
   * The data-encoded viewing transform, shared by every panel.
   *
   * Session-scoped rather than persisted: a palette or threshold is
   * chosen against the field in front of you, and silently re-applying
   * last week's threshold to a different dataset would hide data with
   * no visible cause. It does survive dataset and layout changes within
   * a session, which is the span over which a viewer is comparing.
   * AYNI: since 2026-10-09 colours belong to a dataset and are kept: a
   * dataset opens with what the viewer saved for it, else its display
   * group's standard, else its own colours (`syncDisplayToDataset`,
   * `displayPrefs.ts`). The threshold is not kept.
   */
  private colorScaleDisplay: ColorScaleDisplay = DEFAULT_DISPLAY
  /** AYNI: what the open Khipu shows a meeting's room (`roomSync` note), and its pins on this globe. */
  private khipuRoomNote: RoomNote | null = null
  private khipuMarkers: Array<{ remove(): void }> = []
  /** AYNI (Khipu 1.1): the playing step's live motion, and the canvas its effects are painted on. */
  private stepMotion: LiveMotion | null = null
  private effectsOverlay: EffectsOverlay | null = null
  private khipuPinMarks: VrPinMark[] = []
  /** AYNI: the spin button, which a Khipu step can set. */
  private autoRotate: AutoRotateHandle | null = null
  /** AYNI (2026-10-20): the playback speed bar in the top corner. */
  private speedControl: PlaybackSpeedHandle | null = null
  private globeView: GlobeViewHandle | null = null
  /** AYNI (owner, 2026-10-19): every globe is shown as the flat 2:1 map (Reset and 2D/3D, globeViewControl.ts). */
  private flatMap = false
  /** The switch to or from the flat map under way (resolved when none is). */
  private flatSwitch: Promise<void> = Promise.resolve()
  /** Overlay titles by id, for a recorded Khipu's dataset list. */
  private layerTitles = new Map<string, string>()
  /** AYNI: the dataset the display was chosen for (`syncDisplayToDataset`). */
  private displayDatasetId: string | null = null
  /** AYNI: the display is a Khipu's, not the viewer's: not saved, dropped with the dataset. */
  private displayTemporary = false
  /** AYNI: the display each panel draws with, by slot. */
  private panelDisplays: ColorScaleDisplay[] = []
  /**
   * AYNI (multi-globe plan, phase 1): how each globe's date follows the
   * primary's (globeSlots.ts), by slot. The primary's own entry is kept
   * but idle: it is the clock. Resized with the layout.
   */
  private panelTimeRules: GlobeTimeRule[] = []
  /** AYNI (multi-globe plan, phase 0): the globe Browse loads into next, when a globe's chip opened it; null: the primary. */
  private browseTarget: number | null = null
  /**
   * AYNI (multi-globe plan, phase 1): the pins read on the other globes —
   * each slot's series per pin, on the primary's dates (the graph's x-axis,
   * owner's decision 1), and each slot's latest reading per pin (the
   * value beside the pin on that globe).
   */
  private globeSeries = new Map<number, Map<string, MeasurementSeries>>()
  private globeReadings = new Map<number, Map<string, ProbeReading | null>>()
  /** AYNI: each other globe's playhead when its date label was last written, so a still globe costs nothing. */
  private siblingDateAt: number[] = []
  /** AYNI: the View tab's palette, invert and limits (viewPaletteUI). */
  private viewPalette: ViewPaletteHandle | null = null
  /** AYNI (owner, 2026-10-12): Tools → View's transparency and eye for the dataset. */
  private viewOpacity: ViewOpacityHandle | null = null
  /** AYNI: the scale each panel's legend was last drawn from (`refreshLegendsIfStale`). */
  private legendScales: (ColorScale | null)[] = []
  /** AYNI: which panels show a floating legend, so the info panel need not repeat it. */
  private legendFloating: boolean[] = []
  /** AYNI: the globes whose legend is parked in Tools → View (legendDock.ts). */
  private parkedLegendSlots: number[] = []
  private legendDock: LegendDockHandle | null = null
  /** AYNI: the Data tab's pins, for the tab's Clear. */
  private dataPinStore: PinStore | null = null
  /** AYNI: the Data tab's pins panel, for the methods paragraph (research tier, §Next S5). */
  private dataPinsUi: DataPinsHandle | null = null
  /** AYNI: the research record (research tier, slice 4). */
  private researchRecord: ResearchRecordHandle | null = null
  /** AYNI (the AR refresh): the pins as the headset and a phone's overlay use them (initDataPins). */
  private xrPins: XrToolsDeps['pins'] | null = null
  /** AYNI: drop a pin at a place tapped on the immersive globe in pin mode. */
  private xrPinPick: ((lat: number, lon: number) => void) | null = null
  /** AYNI: the immersive session's context, as handed to the Enter AR button (wireVrButton). */
  private vrContext: VrSessionContext | null = null
  /** AYNI: the layer catalogue once fetched, for the headset's Layers page. */
  private layerCatalog: readonly CatalogLayer[] | null = null
  /** AYNI: what the AYNI chatbot needs of the pins beyond the store (set in initDataPins). */
  private dataPinsForChat: { readability: () => PinReadability; markExtremes: () => number } | null = null
  /** AYNI: the pins' clock (the frame's time in its window, dates or not, and seeking), shared with the Hovmöller diagram (set in initDataPins). */
  private pinClock: { frame: () => PinFrame | null; dated: () => boolean; seek: (timeMs: number) => void } | null = null

  /**
   * Which slot's dataset the info panel currently displays.
   *
   * - `null` → follow the primary. The info panel re-renders whenever
   *   the primary changes.
   * - A slot index → "pinned" to that slot. The user explicitly picked
   *   a non-primary dataset from the picker dropdown. Resets to null
   *   whenever the primary changes (via `onViewportPrimaryChange`) so
   *   a fresh promotion doesn't silently keep pointing at an old slot.
   */
  private infoDisplayOverride: number | null = null

  /** True once the info-selector change handler has been wired. */
  private infoSelectorWired = false

  /**
   * Boot the application: create the WebGL renderer, fetch the dataset
   * catalog, and either load a URL-specified dataset or show the default
   * Earth with the browse panel.
   */
  async initialize(): Promise<void> {
    // AYNI — own data: a private dataset's token is renewed while a globe shows it (privateData.ts).
    setPrivateDatasetInUse((id) => this.panelStates.some((panel) => panel?.dataset?.id === id))
    try {
      this.setLoading(true)
      this.setLoadingStatus('Starting up\u2026', 5)

      // Catalog mode (`?catalog=true`) inverts the default surface:
      // the dataset browser becomes primary and the globe stays
      // hidden until selection. Apply the body class before any UI
      // renders so the CSS surface decision (full-width browse vs.
      // overlay browse) is made on the first paint, not after a
      // flash of the globe-first layout. See
      // `docs/WEB_CATALOG_FEATURES_PLAN.md` \u00a73.
      const catalogModeActive = getCatalogMode()
      if (catalogModeActive) document.body.classList.add('catalog-mode')

      // Embed mode (`?embed=1`) strips app chrome for iframe hosting
      // (WordPress blocks, kiosk, the poster). Apply before any UI
      // renders — same first-paint reasoning as catalog mode — so the
      // chrome never flashes in. Composes with `?dataset=`/
      // `?catalog=true`. See `docs/EMBED_URL_GRAMMAR.md`.
      const embedModeActive = applyEmbedMode()

      if (!this.checkWebGLSupport()) return

      const container = document.getElementById('container')
      if (!container) throw new Error('Container element not found')
      const mapGrid = document.getElementById('map-grid')
      if (!mapGrid) throw new Error('Map grid element not found')

      this.setLoadingStatus('Creating renderer\u2026', 15)
      const initialLayout = this.getInitialLayoutFromUrl()
      // AYNI (multi-globe plan): the link's other globes, read now, before
      // a layout change or a dataset load rewrites the address.
      this.linkedAtBoot = parseLinkedSlots(window.location.search)
      this.viewports.init(mapGrid, initialLayout, {
        onLayoutChange: (newCount, oldCount) => this.onViewportLayoutChange(newCount, oldCount),
        onPrimaryChange: (newIdx, oldIdx) => this.onViewportPrimaryChange(newIdx, oldIdx),
        getLayerIdForSlot: (slot) => this.panelStates[slot]?.dataset?.id ?? null,
        // AYNI: the plan's globes (Free and Plus: 1), on top of the device's budget.
        panelLimit: () => maxGlobes(),
        // AYNI (multi-globe plan): a globe's chips — Browse into it, and its time rule.
        onDatasetChip: (slot) => this.openBrowseFor(slot),
        onTimeChip: (slot, anchor) => openGlobeTimeMenu(anchor, {
          globe: slot + 1,
          current: this.panelTimeRules[slot] ?? FOLLOW,
          onChoose: (rule) => this.setPanelTimeRule(slot, rule),
        }),
      })
      // Parallel per-panel dataset state, one entry per viewport.
      this.panelStates = Array.from({ length: this.viewports.getPanelCount() }, createPanelState)
      this.panelTimeRules = this.panelStates.map(() => FOLLOW)
      // AYNI: the account arrives after the first layout; once it does (and
      // whenever it changes), a layout wider than the plan allows shrinks.
      // Until then the plan is Free's one globe, so a deep link's wider
      // layout (`?layout=4`) is asked for again, once, when the account is
      // known: the plan and the device still cap it.
      let deepLinkLayout: typeof initialLayout | null = initialLayout === '1' ? null : initialLayout
      onAccountChange((state) => {
        if (state.status === 'unknown') return
        if (deepLinkLayout && this.viewports.getLayout() === '1') this.viewports.setLayout(deepLinkLayout, 'plan')
        deepLinkLayout = null
        if (this.viewports.getPanelCount() > maxGlobes()) this.viewports.setLayout(this.viewports.getLayout(), 'plan')
        // AYNI (multi-globe plan): the link's other globes, once the plan has allowed the layout.
        if (this.viewports.getPanelCount() > 1 && this.appState.datasets.length) void this.applyLinkedSlots()
      })
      // AYNI (several licences on one account, owner 2026-10-10,
      // docs/AYNI_XR_MULTIPLE_LICENCES_PLAN.md): when the licence in use
      // changes, everything on screen the new plan allows stays (datasets,
      // view, zoom, tools) and what it does not cover closes
      // (`keepWhatThePlanAllows`; globes past its count close above). Not
      // while presenting a meeting (decision M4). A change the account did
      // not choose is said once (M6).
      setLicenceSwitchBlocked(() => presentingMeeting() !== null)
      let inUseRef: string | null | undefined
      let noticeSeen: string | null = null
      onAccountChange((state) => {
        if (state.status === 'unknown') return
        const ref = state.status === 'signedIn' ? state.licence?.ref ?? null : null
        const changed = inUseRef !== undefined && inUseRef !== ref && state.status === 'signedIn'
        inUseRef = ref
        const notice = state.status === 'signedIn' && state.notice && state.notice.at !== noticeSeen ? state.notice : null
        if (notice) noticeSeen = notice.at
        // AYNI (owner, 2026-11-03): the Real time list follows the plan (`rtOverlayOffered`).
        this.refreshRtOverlayPicker()
        if (changed || notice) void this.afterLicenceChange(changed, notice ? licenceNoticeText(notice) : null)
      })
      const primary = this.viewports.getPrimary()
      if (!primary) throw new Error('Viewport manager failed to create a primary renderer')
      // Window chrome (fullscreen). Built before the Tools menu because
      // the menu reads the fullscreen state while it builds its markup.
      this.initWindowChrome()
      // AYNI: a host key in the address (?meetingKey=) is stored and taken
      // out of the address bar before the menu decides what to offer.
      const withoutMeetingKey = takeMeetingKeyParam(window.location.href)
      if (withoutMeetingKey) window.history.replaceState(window.history.state, '', withoutMeetingKey)
      initToolsMenu(this.viewports, {
        onSetLayout: (layout) => this.viewports.setLayout(layout),
        // AYNI: the Browse button closes the panel it opened, as the
        // Tools button does. Through the panel's own close button, so
        // both ways out are the same one (hide, announce).
        onOpenBrowse: () => {
          const close = document.getElementById('browse-close')
          if (document.body.classList.contains('browse-open') && close) close.click()
          else this.openBrowsePanel()
        },
        // AYNI (owner, 2026-11-04): Solar System mode, beside Browse (solarSystemMode.ts).
        onOpenSolar: isOutputWindow() || isWallDisplay() ? undefined : () => void this.solarMode().toggle(),
        onOpenCredits: (trigger) => openCreditsPanel(this.viewports, trigger),
        // AYNI (multi-globe plan, phase 4): the globes alone in a second window, for a projector.
        onOpenOutput: isOutputWindow() ? undefined : () => this.openOutputWindow(),
        onOpenSphereOutput: isOutputWindow() ? undefined : () => this.openOutputWindow('2048'),
        // AYNI: offered while this browser can host, by the owner's host key
        // or a licence whose plan hosts (meetingHost.ts); asked on each
        // open, since a licence can be activated or removed mid-session.
        onOpenMeeting: (trigger) => openMeetingPanel(trigger),
        meetingAvailable: canHostMeetings,
        // AYNI (Situation room): the operator's panel, on the Situation Room plans.
        onOpenSituation: isOutputWindow() || isWallDisplay() ? undefined : (trigger) => this.openSituation(trigger),
        situationAvailable: () => accountState().status === 'signedIn' && situationAllowed(currentEntitlements()),
        // AYNI: the panel's foot says what this account's plan is.
        planLine: () => {
          const meetingOpener = { onStartMeeting: (trigger: HTMLElement) => openMeetingPanel(trigger) }
          const account = accountState()
          if (account.status !== 'signedIn') {
            return { text: t('tools.plan.signedOut'), action: t('tools.plan.signIn'), onClick: (trigger) => openSignIn(trigger) }
          }
          const licence = account.licence
          if (licence?.status === 'active' && licence.plan) {
            return { text: t('tools.plan.named', { plan: planName(licence.plan) }), action: t('tools.plan.yourPlan'), onClick: (trigger) => openYourPlan(trigger, meetingOpener) }
          }
          return { text: t('tools.plan.free'), action: t('tools.plan.seePlans'), onClick: (trigger) => openPlans(trigger, undefined, meetingOpener) }
        },
        onToggleDatasetInfo: (visible) => this.setDatasetInfoVisible(visible),
        // AYNI: off parks the legends in Tools → View, on brings them back (legendDock.ts).
        onToggleLegend: (visible) => {
          if (!this.legendDock) this.setLegendVisible(visible)
          else if (visible) this.legendDock.unpark(document.getElementById('tools-menu-legend'))
          else this.legendDock.park()
        },
        announce: (msg) => this.announce(msg),
        onClearPins: () => this.dataPinStore?.clear(),
        measureNote: () => {
          if (!this.appState.currentDataset) return t('tools.measure.noDataset')
          return this.viewports.getPrimary()?.probeKind() ? null : t('tools.measure.picture')
        },
        fullscreen: this.fullscreen ?? undefined,
      })
      // AYNI: Start a meeting and the plan line follow the account as it loads or changes.
      onAccountChange(() => refreshToolsAccount())
      this.initDataPins()
      // AYNI: the View tab's colours. They act on the primary globe's
      // dataset, as the colorbar did, and fan out to every panel.
      const paletteHost = document.getElementById('tools-view-palette')
      if (paletteHost) {
        this.viewPalette = mountViewPalette(paletteHost, {
          scale: () => (this.appState.currentDataset ? this.viewports.getPrimary()?.displayScale() ?? null : null),
          display: () => this.colorScaleDisplay,
          setDisplay: (next) => this.applyColorScaleDisplay(next),
          defaultDisplay: () => defaultDisplayOf(this.appState.currentDataset),
          group: () => this.appState.currentDataset?.displayGroup ?? null,
          // AYNI (owner, 2026-10-14): the reference lines' switch (referenceLines.ts).
          references: {
            has: () => referenceLinesOf(this.appState.currentDataset?.id).length > 0,
            on: referenceLinesVisible,
            set: setReferenceLinesVisible,
            lines: () => shownReferenceLines(this.appState.currentDataset?.id),
          },
          announce: (msg) => this.announce(msg),
        })
        // AYNI: the switch redraws the legends and the View tab, wherever it was flipped.
        onReferenceLinesChange(() => {
          this.refreshPanelLegends()
          this.viewPalette?.refresh(true)
        })
      }
      // AYNI (owner, 2026-10-12): the dataset's transparency and eye, also for a dataset playing alone.
      const opacityHost = document.getElementById('tools-view-opacity')
      if (opacityHost) {
        this.viewOpacity = mountViewOpacity(opacityHost, {
          hasDataset: () => !!this.appState.currentDataset,
          opacity: () => this.baseOpacity,
          hidden: () => this.baseHidden,
          setOpacity: (opacity) => this.setBaseOpacity(opacity),
          setHidden: (hidden) => this.setBaseHidden(hidden),
        })
        this.layerViewListeners.add(() => this.viewOpacity?.refresh())
      }
      // AYNI (owner, 2026-10-11): a closed legend is parked at the foot of Tools → View.
      const legendDockHost = document.getElementById('tools-view-legend')
      if (legendDockHost) {
        this.legendDock = mountLegendDock(legendDockHost, {
          parked: () => !this.viewPrefs.legendVisible,
          setParked: (parked) => this.setLegendVisible(!parked),
          parkedLegends: () => this.parkedLegends(),
          hasLegend: () => this.legendFloating.some(Boolean),
          floatingLegends: () => {
            const out: Array<{ slot: number; el: HTMLElement }> = []
            for (let slot = 0; slot < this.panelStates.length; slot++) {
              const el = this.viewports.panelLegendElement(slot)
              if (el) out.push({ slot, el })
            }
            return out
          },
          placeAt: (slot, rect) => {
            const panel = document.querySelector<HTMLElement>(`[data-viewport-index="${slot}"]`)
            if (panel) placeLegendAt(slot, panel, rect)
          },
          showView: () => openToolsTab('view'),
          // The same width at which the panel closes on an outside click (toolsMenuUI.ts).
          stepAside: () => {
            if (window.innerWidth <= 768) closeToolsMenu()
          },
          immersive: () => isVrActive(),
          announce: (msg) => this.announce(msg),
        })
      }
      // AYNI: borders are an overlay now (Tools → Layers), with no switch
      // for the old reference lines, so a "borders on" saved by that
      // switch would stay on in VR with no way to turn it off.
      setBordersVisible(false)
      // AYNI: the Layers section of the Tools menu. A choice there applies to
      // the loaded dataset's slot; a word on borders stays.
      this.layerPicker = mountLayerPicker(
        selection => this.chooseMapLayers(selection),
        id => this.pickRtOverlay(id),
        () => this.fetchRtRanges(),
      )
      void fetchLayerCatalog().then(layers => this.layerPicker?.update(layers, this.mapLayerSelection))
      // AYNI (satellites plan): the Satellites card under the layer cards.
      const satHost = document.getElementById('tools-menu-satellites')
      if (satHost) {
        const sats = this.satellites
        mountSatellitePanel(satHost, sats, { lang: () => getLocale(), onEvent: (e) => showEvent(sats, e), inXr: () => isVrActive(), onLoadDataset: (id) => void this.selectDatasetFromBrowse(id) })
      }
      // AYNI (owner, 2026-10-23): the satellites the data came from (DSA 1.10 platforms) pulse on the layer.
      this.satellites.setSourceProvider(() => this.satelliteSourcesNow())
      // AYNI: the account (services/account.ts). An emailed sign-in link
      // (?signin=…) is finished here, a Google sign-in's outcome is shown,
      // and an address with ?plans (or ?plans=<segment>) opens the plans.
      const planOptions = { onStartMeeting: (trigger: HTMLElement) => openMeetingPanel(trigger) }
      const signInAsked = takeSignInParams()
      // AYNI (security fix plan 2026-10, S-02): the stream token follows the
      // account; the host's refusals say why (streamToken.ts).
      // A Studio render asks for its token with the job's own (no account in the worker's browser).
      setStreamRenderJob(renderJobFromSearch(window.location.search))
      startStreamToken()
      onStreamDenied((why) => this.showStreamDenied(why))
      void initAccountUI().then(async () => {
        // A deep link kept by the welcome page reopens once signed in.
        const returnTo = (): boolean => {
          const back = takeReturnTo()
          if (back) window.location.replace(back)
          return back !== null
        }
        if (signInAsked.token) {
          const done = await completeSignIn(signInAsked.token)
          if (done.ok) { if (!returnTo()) openYourPlan(null, planOptions, t('account.signIn.done')) }
          else openSignIn(null, done.error)
        } else if (signInAsked.google) {
          if (!returnTo()) openYourPlan(null, planOptions, t('account.signIn.done'))
        } else if (signInAsked.error) {
          openSignIn(null, signInAsked.error)
        }
      })
      // AYNI (phase 4): a Paddle payment link (?_ptxn=txn_…, e.g. the email to
      // update a card after a failed renewal) opens that payment's checkout;
      // no account is needed, and the gate serves the app for it (_gate.ts).
      const paymentAsked = takePaymentLink()
      if (paymentAsked) void openPaymentLink(paymentAsked, getLocale(), { onCompleted: () => void loadAccount() })
      // AYNI own data (phase 4): a share link (#ayni-share=…) is taken out of the address and accepted once signed in.
      initShareLinkIntake({
        announce: (msg) => this.announce(msg),
        openMyData: () => {
          this.openBrowsePanel()
          setBrowseTab('mydata', true)
        },
        openSignIn: () => openSignIn(null),
      })
      const plansAsked = plansFromSearch()
      if (plansAsked) openPlans(null, plansAsked === 'default' ? undefined : plansAsked, planOptions)
      // Catalog ↔ sphere tab control — only becomes visible when
      // `?catalog=true` is in the URL (see the show/hide calls in
      // the boot branches further below and in
      // `wireCatalogModePopstate` for history navigation). The
      // control is mounted unconditionally so the popstate handler
      // can flip its visibility without re-wiring listeners.
      initCatalogTabs({
        onSelectCatalog: () => this.enterCatalogTab(),
        onSelectSphere: () => this.enterSphereTab(),
      })
      this.wireCatalogModePopstate()
      // Apply persisted view prefs to the toolbar button state now
      // that the toolbar exists.
      syncToolsMenuState({
        datasetInfo: this.viewPrefs.infoPanelVisible,
        legend: this.viewPrefs.legendVisible,
      })
      // The zip-download dialog (§8.2), opened from 📦 on a Browse card
      // and from the info panel.
      initDownloadDialogUI({ announce: (msg) => this.announce(msg) })
      initHelpUI()
      // AYNI: the info panel's "Cite this view" button (datasetLoader.ts
      // draws it with each dataset, so the click is answered here once).
      document.addEventListener('click', (e) => {
        const button = (e.target as Element | null)?.closest?.('.info-cite-btn')
        if (button instanceof HTMLElement) void this.openCiteView(button)
      })
      // Statistics over the frame on screen (§A3). Reads through the
      // primary panel, which is the one the info panel, playback and
      // the value readout all already follow — analysing a globe the
      // user is not driving would be a different feature.
      // AYNI: the research tools name frames as the date track does (research/frameTime.ts).
      setResearchFrameAxis(() => this.timelineSnapshot()?.axis ?? null)
      initAnalyzeUI({
        frame: () => this.viewports.getPrimary()?.analysisFrame() ?? null,
        visibleBounds: () => this.viewports.getPrimary()?.visibleBounds() ?? null,
        display: () => this.colorScaleDisplay,
        datasetTitle: () => this.appState.currentDataset?.title ?? null,
        datasetId: () => this.appState.currentDataset?.id ?? null,
        // AYNI: provenance in every export, and "Cite this view" (research tier, slice 1).
        provenance: () => this.viewProvenance({ frame: true, exportedAt: true }),
        citeView: (trigger) => void this.openCiteView(trigger),
        // AYNI: and for the Line over time, which spans the window rather than one frame.
        spanProvenance: () => this.viewProvenance({ frame: false, exportedAt: true }),
        // AYNI: a figure of the view and the methods paragraph (research tier, §Next S4/S5).
        figure: (trigger, tab) => void this.openFigure(trigger, tab),
        // AYNI: everything in one zip with a manifest of SHA-256 (research tier, §Next S8).
        bundle: () => this.downloadBundle(),
        // AYNI: a held frame's view, for sharing its definition in Lab space.
        viewQuery: () => viewQuery(this.currentViewState()),
        // AYNI: the change from a held baseline on the globe (research tier, slice 2).
        frameKey: () => this.viewports.getPrimary()?.currentFrameId() ?? null,
        showChange: (layer) => {
          if (layer === null && this.changeLayer === null) return
          this.changeLayer = layer
          this.composeMapLayers()
        },
        // AYNI: the same change every frame, on the graphics card.
        liveChange: (view) => this.setLiveChange(view),
        liveGlobe: () => this.viewports.getPrimary(),
        // AYNI: a stream's frames, for a period baseline (research tier, S2).
        timeAxis: () => this.analyzeTimeAxis(),
        probeValue: (lat, lon) => {
          const r = this.viewports.getPrimary()?.probeValueAt(lat, lon) ?? null
          return r && !r.noData ? r.value : null
        },
        // AYNI: Compare (research tier): the real-time overlays that carry values.
        compareCandidates: () => this.compareCandidates(),
        frameInstant: () => this.baseFrameInstant(),
        compareActive: (id) => {
          if (id === this.compareId) return
          this.compareId = id
          this.compareSeries.clear()
          this.composeMapLayers()
          this.renderer?.getMap()?.triggerRepaint()
        },
        compareSeriesAt: (pinId) => this.compareSeries.get(pinId) ?? null,
        // AYNI: the Line over time (Hovmöller, research tier slice 3): the
        // pins' clock, the line's codes on the primary globe, every frame.
        hovmoller: () => {
          const primary = this.viewports.getPrimary()
          const clock = this.pinClock
          if (!primary || !clock) return null
          return {
            frame: () => {
              const ds = this.appState.currentDataset
              const video = ds && dataService.isVideoDataset(ds) ? this.hlsService?.video : null
              // A seek in flight still shows the frame it left.
              if (video?.seeking) return null
              const f = clock.frame()
              if (!f) return null
              return { ...f, frames: this.timelineSnapshot()?.frameCount ?? null, dated: clock.dated() }
            },
            codesAlong: (points) => primary.lineCodesAt(points),
            seek: (ms) => clock.seek(ms),
            video: () => {
              const ds = this.appState.currentDataset
              return ds && dataService.isVideoDataset(ds) ? this.hlsService?.video ?? null : null
            },
          }
        },
        // Primary panel only, matching the probe readout: `probeValueAt`
        // is a MapRenderer method rather than part of the GlobeRenderer
        // interface, so a transect on a secondary panel would have
        // nothing to sample. Named as a constraint in
        // `docs/DATA_ANALYSIS_PLAN.md` rather than worked around here.
        transect: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            begin: (onChange, path) => primary.beginTransect(onChange, path),
            // AYNI: the chatbot's hovmoller.summary sets the Line by its ends.
            set: (ends, onChange) => primary.setTransect(ends, onChange),
            progress: () => primary.transectProgress(),
            clear: () => primary.clearTransect(),
          }
        },
        regionOutline: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            show: (bounds) => primary.showRegionOutline(bounds),
            showShape: (rings) => primary.showRegionShape(rings),
            clear: () => primary.clearRegionOutline(),
          }
        },
        contours: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            show: (levels) => primary.showContours(levels),
            clear: () => primary.clearContours(),
          }
        },
        // AYNI: the Data tab's Box, on the primary globe like the transect.
        boxPicker: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            begin: (onChange, onFirstCorner) => primary.beginBox(onChange, onFirstCorner),
            progress: () => primary.boxProgress(),
            clear: () => primary.clearBox(),
          }
        },
        // The same label Orbit's tool results are stamped with, so the
        // panel and an Orbit answer can never name different frames for
        // the same measurement.
        frameTime: () => readTimeLabel(),
        // Identity, not display. `readTimeLabel` reads the *label*,
        // which is snapped to the display interval and hidden outright
        // for a dataset without start/end times — useless for telling
        // whether the frame moved. This is the video playhead, the same
        // value the luma sampler keys its snapshot cache on.
        frameId: () => this.viewports.getPrimary()?.currentFrameId() ?? null,
      })
      // AYNI (owner, 2026-11-03): playlists retired for Khipus. The dataset
      // menu adds to a new or an existing Khipu; a new one on a plan that
      // keeps none opens in the Khipu editor.
      setDatasetMenuKhipuHost({
        openInEditor: (k, trigger) => this.editKhipu(k, [], 0, trigger, true),
        announce: (msg) => this.announce(msg),
      })
      // AYNI: colours per dataset follow the account (displayPrefsSync.ts).
      startDisplayPrefsSync()
      onDisplayPrefsApplied(() => this.reloadDisplayPrefs())
      // First-session privacy disclosure. No-ops on every launch
      // after the user dismisses it. Skipped inside an embed — the
      // host page owns consent, and a privacy banner popping up in a
      // third-party iframe is both wrong and intrusive (§J of
      // `docs/WORDPRESS_INTEGRATION_PLAN.md`).
      if (!embedModeActive) showDisclosureBannerIfNeeded()
      // Telemetry transport — skipped entirely when the compile-time
      // flag is off (telemetry-free builds) and when console mode
      // is on (dev convenience: events log locally, no POSTs).
      if (TELEMETRY_BUILD_ENABLED && !TELEMETRY_CONSOLE_MODE) {
        setTransport(createFetchTransport())
      }
      logger.info('[App] Using MapLibre renderer (layout: %s)', initialLayout)

      // Wire up lat/lng display. AYNI (multi-globe): every globe reports
      // where the cursor is on it, not only the first one.
      this.bindLatLngReadout()

      // Fetch datasets, then load from URL if specified
      this.setLoadingStatus('Loading datasets\u2026', 30)
      await this.loadDatasets()
      // AYNI: the playlists people had become Khipus, once (they need the catalogue's titles).
      this.convertOldPlaylists()

      // Initialize digital docent chat (available on all views)
      this.initChat()

      // Enter VR button — feature-gated internally; hides itself on
      // non-WebXR browsers and warm-loads Three.js in the background
      // on devices that can enter VR.
      void this.wireVrButton()

      // AYNI Studio: render mode (?render=1) — one shot, frame by frame, for the GPU worker.
      if (isRenderMode()) {
        await this.startRenderMode()
        return
      }

      // AYNI (DSA 1.8): `?vectorPreview` draws a .dsa's vector layers with no dataset loaded.
      if (this.vectorPreview) this.syncVectorLayers()

      // AYNI (Situation room, CR5): the room's own layers are drawn on every globe.
      this.ownLayers.attach(() => this.viewports.getAll().map((r) => r.getMap()))
      // AYNI (satellites plan): satellites on every globe, from the layer's own clock.
      this.satelliteMaps = attachSatellitesToMaps(this.satellites, () => this.viewports.getAll(), {
        lang: () => getLocale(),
        primary: () => this.viewports.getPrimary() ?? null,
        // Each globe of a layout answers for its own dataset's body (owner, 2026-10-23).
        bodyOf: (renderer) => bodyOfDataset(this.panelStates[this.viewports.getAll().indexOf(renderer as MapRenderer)]?.dataset),
        // Owner, 2026-10-24: the card lists the datasets the satellite feeds; Load opens one as Browse would.
        onLoadDataset: (id) => void this.selectDatasetFromBrowse(id),
        datasetLocked: (id) => {
          const d = dataService.getDatasetById(id)
          return !!d && outsidePlan(d, effectivePackages(), educationStreamsAllowed()) !== null
        },
        onLockedDataset: (_id, trigger) => openPlans(trigger, 'public'),
      })
      this.syncSatelliteBody()
      this.satellites.start()
      // Owner, 2026-10-24: Browse names the satellites each dataset is built from (sources.json), layer on or off.
      setBrowseSatelliteSources((id) => {
        const lang = getLocale()
        const indexId = indexDatasetId(id)
        if (!indexId) return []
        return this.satellites.satellitesOfDataset(indexId).map((p) => ({
          norad: p.norad,
          name: typeof p.name === 'string' ? p.name : (p.name[lang] ?? p.name.en ?? Object.values(p.name)[0] ?? String(p.norad)),
          tracked: p.tracked,
          note: platformNote(this.satellites, p.norad),
        }))
      })
      // …and Browse's Satellite facet reads the same names (datasetFilter.ts).
      setSatelliteFacetSource((id) => {
        const indexId = indexDatasetId(id)
        if (!indexId) return []
        const lang = getLocale()
        return this.satellites.satellitesOfDataset(indexId).map((p) => (typeof p.name === 'string' ? p.name : (p.name[lang] ?? p.name.en ?? Object.values(p.name)[0] ?? String(p.norad))))
      })
      void this.satellites.loadSourcesIndex().then((index) => {
        if (index) refreshBrowseCards()
      })
      // AYNI (Situation room): a member's own layers are on the globes from the start, not only once the panel opened.
      if (!isWallDisplay()) {
        void accountKnown(10_000).then(() => {
          if (accountState().status !== 'signedIn' || !situationAllowed(currentEntitlements())) return
          void loadSituation().then((res) => {
            if (res.ok && !this.meetingLayersCode) this.ownLayers.setList(res.body.layers, { kind: 'member' })
          })
        })
        this.ownLayers.onChange(() => this.shareShownLayers())
      }
      // AYNI (Situation room, CR1): a wall display plays its room's board, or follows the briefing put on the walls.
      if (isWallDisplay()) {
        void this.startWall()
        if (!roomCodeFromSearch()) return
      }

      const datasetId = this.getDatasetIdFromUrl()
      if (datasetId) {
        // Canonicalize whatever form the visitor arrived on \u2014 an old
        // `?dataset=<ULID>` link, or `/dataset/<legacy_id>` \u2014 to
        // `/dataset/<slug>`, so the address bar reads as something
        // worth pasting into a message and anything copied from here
        // spreads the human-friendly form.
        // AYNI: a view link's frame and camera (viewLink.ts), read before
        // anything rewrites the address.
        const viewLink = parseViewParams(window.location.search)
        this.writeDatasetUrl(datasetId, 'replace')
        this.setLoadingStatus('Loading dataset\u2026', 50)
        await this.loadDataset(datasetId, 'url')
        this.setLoading(false)
        this.runUrlLoadUiSync()
        // Also for a link whose values were refused, so they leave the address bar.
        if (viewLink || stripViewParams(window.location.search) !== window.location.search) {
          void this.applyViewLink(viewLink ?? { atMs: null, camera: null, layers: null })
        }
        // In multi-viewport mode, pre-render the browse panel in its
        // collapsed state so users can slide it open to load datasets
        // into the remaining panels, and pulse the Browse button so
        // they notice where to click. In single-view mode we don't —
        // the URL-specified dataset is the only thing the user wanted.
        //
        // Catalog mode (`?catalog=true&dataset=<id>`) gets the same
        // treatment as multi-viewport: render the browse panel
        // collapsed so the tab control's "back to catalog" affordance
        // has something to expand. Plan §3.1 case 2.
        if (this.viewports.getPanelCount() > 1 || catalogModeActive) {
          showBrowseUI(this.appState.datasets, this.browseCallbacks())
          collapseBrowseUI()
          if (!catalogModeActive) pulseBrowseButton()
          // AYNI (multi-globe plan): the link's other globes (`g2`, `g2t`…).
          if (this.viewports.getPanelCount() > 1) void this.applyLinkedSlots()
        }
        if (catalogModeActive) {
          showCatalogTabs()
          setActiveCatalogTab('sphere')
        }
      } else if (catalogModeActive) {
        // Catalog-mode no-dataset boot: the browse panel is the
        // primary surface and the globe stays hidden until the
        // visitor picks a dataset. Skip the Earth-textures load
        // since nothing is going to show them; the regular
        // dataset-load path runs them on demand when the visitor
        // exits catalog mode by selecting something.
        //
        // `catalog-empty` is the CSS hook for "globe hidden,
        // browse full-surface"; it stays set until the first
        // dataset loads (cleared in `selectDatasetFromBrowse`).
        // `catalog-mode` is the sticky session marker \u2014 set once
        // when the visitor entered via `?catalog=true` and never
        // removed within the session so the catalog\u2194sphere tab
        // control stays visible even after they flip to Sphere.
        document.body.classList.add('catalog-empty')
        this.setLoading(false)
        showBrowseUI(this.appState.datasets, this.browseCallbacks())
        // No collapse, no hide, no pulse \u2014 the browse panel is
        // the visible surface, not an overlay competing with the
        // globe for attention.
        showCatalogTabs()
        setActiveCatalogTab('catalog')
      } else {
        this.setLoadingStatus('Loading Earth textures\u2026', 20)
        const cloudUrl = CLOUD_TEXTURE_URL

        let earthFraction = 0
        let cloudFraction = 0
        const updateProgress = () => {
          const combined = earthFraction * EARTH_TEXTURE_WEIGHT + cloudFraction * CLOUD_TEXTURE_WEIGHT
          this.setLoadingStatus('Loading Earth textures\u2026', LOADING_BASE_PROGRESS + Math.round(combined * LOADING_TEXTURE_RANGE))
        }
        // Earth materials are a visual enhancement (day/night, clouds,
        // specular) — if loading them times out or fails (common in
        // multi-viewport mode on mobile when 4 WebGL contexts compete
        // for bandwidth), the base tiles still render fine. Don't let
        // the failure abort the init flow and block the browse panel.
        try {
          await Promise.all([
            primary.loadDefaultEarthMaterials((f: number) => { earthFraction = f; updateProgress() }),
            primary.loadCloudOverlay(cloudUrl, (f: number) => { cloudFraction = f; updateProgress() })
          ])
          // Only if the globe is still bare. `enableSunLighting` clears
          // the dataset texture and the probe source by design — that
          // is how unloading returns to the plain Earth — so firing it
          // here after a dataset has already been loaded wipes that
          // dataset off the globe and silently kills its value readout.
          //
          // The window is real whenever these two textures are slow or
          // unreachable: they are large external assets, and the
          // catalog can be ready long before them. The dataset is the
          // thing the visitor asked for; the day/night look is not.
          if (!this.panelStates.some(p => p?.dataset)) {
            const sun = getSunPosition(new Date())
            primary.enableSunLighting(sun.lat, sun.lng)
          }
        } catch (err) {
          logger.warn('[App] Earth material loading failed — continuing without day/night overlay:', err)
        }

        this.setLoading(false)
        // Render the browse panel (populates category filters and
        // dataset cards) then decide whether to leave it visible.
        // - Multi-viewport mode: the visitor explicitly chose
        //   multiple globes (via ?layout= or the layout picker).
        //   Don't cover one of them with the browse panel by
        //   default — collapse it and pulse the Browse button so
        //   they notice where to click. Mirrors the same decision
        //   in the dataset-from-URL branch above.
        // - Mobile single-view: panel is full-width and would
        //   hide the globe entirely; start hidden and pulse.
        // - Desktop single-view: keep the existing side-panel UX
        //   so the visitor sees the catalog by default.
        showBrowseUI(this.appState.datasets, this.browseCallbacks())
        if (this.viewports.getPanelCount() > 1) {
          collapseBrowseUI()
        } else if (window.matchMedia('(max-width: 768px)').matches) {
          hideBrowseUI()
        }
        pulseBrowseButton()
      }
    } catch (error) {
      this.setLoading(false)
      this.setError(error instanceof Error ? error.message : 'Unknown error')
    }

    // Apply the view deep-link query params (?terrain=, ?labels=,
    // ?borders=, ?rotate=, ?orbit=). These run
    // after the catalog is loaded and the Tools menu is wired so the
    // dispatch can route through canonical APIs and button click
    // handlers without any race risk. ?layout= and ?dataset= are
    // already handled by the existing initial-load path above.
    await applyPosterDeepLinks({
      openChatWithQuery: (query) => this.openChatWithQuery(query),
    })
  }

  /**
   * Post-load UI sync that every URL-driven dataset load needs to
   * run on top of `loadDataset()`: surface the floating Orbit chat
   * trigger so the visitor can re-open chat after closing it,
   * refresh the canvas's accessible description so screen readers
   * stop announcing the stale Earth title, announce the dataset
   * change to the chat panel so its system prompt picks up the
   * new context, and tell the help overlay which dataset is now
   * active.
   *
   * Browse-panel and chat-driven loads (`selectDatasetFromBrowse`,
   * `selectDatasetFromChat`) call equivalent code inline; URL
   * paths share this helper — boot `?dataset=` and the
   * `applyPosterDeepLinks` poster pipeline — so the sequencing stays
   * consistent across every URL-driven entry point.
   */
  private runUrlLoadUiSync(): void {
    this.chatShowTrigger()
    const dataset = this.appState.currentDataset
    if (dataset) {
      this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
      setHelpActiveDataset(dataset.id)
    }
    // Catalog-mode integration — a URL-driven load just resolved
    // (a dataset or a native deep-link), which means
    // the globe is now the active surface. Drop the
    // `catalog-empty` flag so the CSS rule that hides `#map-grid`
    // releases, and (if we're still in catalog mode) flip the
    // tab control to Sphere active. Mirrors the inline sync that
    // `selectDatasetFromBrowse` does for browse-driven loads.
    // Centralising the sync here covers every URL-driven entry point
    // through a single hook.
    document.body.classList.remove('catalog-empty')
    if (getCatalogMode()) {
      showCatalogTabs()
      setActiveCatalogTab('sphere')
    }
  }

  /** Extract the dataset to boot with from the current URL — the
   *  canonical `/dataset/<slug>` path form, or the `?dataset=` query
   *  param older links carry. Either may name the dataset by slug,
   *  ULID, or legacy id; all three resolve through
   *  `getDatasetById`. A well-formed reference that isn't in the
   *  catalog is returned as-is, so the loader surfaces a "not found"
   *  naming what the visitor actually asked for.
   *
   *  Both forms are validated against the same alphabet. The path
   *  form gets that from `parseDatasetPathname`; applying it to the
   *  query form too means a malformed `?dataset=` value (markup,
   *  spaces) is ignored rather than laundered into a
   *  `/dataset/<percent-encoded-junk>` path by the canonicalize step
   *  — a URL that wouldn't parse back on reload. */
  private getDatasetIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('dataset') ?? parseDatasetPathname(window.location.pathname)
    if (!raw || !isDatasetRef(raw)) return null
    return dataService.getDatasetById(raw)?.id ?? raw
  }

  /**
   * Write the canonical `/dataset/<slug>` URL for a dataset into the
   * address bar. A reference that isn't in the loaded catalog is
   * written through as-is (`/dataset/<ref>`) rather than resolved —
   * the resulting URL still boots to the same "not found", and it
   * keeps naming what the visitor actually asked for.
   */
  private writeDatasetUrl(id: string, mode: 'push' | 'replace'): void {
    const search = window.location.search
    const next = buildDatasetPath(dataService.getDatasetById(id) ?? { id }, search)
    if (next === `${window.location.pathname}${search}`) return
    if (mode === 'push') {
      window.history.pushState({}, '', next)
    } else {
      window.history.replaceState({}, '', next)
    }
  }

  /** Reset the address bar to the no-dataset form, keeping whatever
   *  mode the visitor arrived in (`?catalog=true`, `?embed=1`). */
  private clearDatasetUrl(mode: 'push' | 'replace'): void {
    const next = buildNoDatasetPath(window.location.pathname, window.location.search)
    if (mode === 'push') {
      window.history.pushState({}, '', next)
    } else {
      window.history.replaceState({}, '', next)
    }
  }

  /**
   * Read the initial viewport layout from the URL. Prefers
   * `?layout=` (the public form used by the companion poster's
   * deep-link buttons); falls back to the legacy dev `?setview=`
   * for backwards compatibility. Both forms accept the same set
   * of values: `1`, `2`, `2h`, `2v`, `4`. The shorthand `2`
   * resolves to `2h`. Unknown / missing values default to
   * single-view (`1`).
   */
  private getInitialLayoutFromUrl(): ViewLayout {
    return parseInitialLayout(window.location.search)
  }

  /** Fetch the dataset catalog from the data service and store in app state. */
  private async loadDatasets(): Promise<void> {
    const datasets = await dataService.fetchDatasets()
    this.appState.datasets = datasets
    // AYNI (security fix plan 2026-10, S-02): the stream host's index could
    // not be read and the bundled copy stands in — said, never silent, so a
    // stale catalogue is never mistaken for today's.
    const saved = catalogueFromSavedCopy()
    if (saved) {
      logger.warn(`[App] The stream catalogue could not be loaded (${saved.status ?? 'unreachable'}); showing the saved copy`)
      this.setError(t('catalog.savedCopy'))
    }
    this.seedRtRanges()
    // §9.2 — once the catalog is in hand, light the "new since your
    // last visit" badge on the Browse trigger if anything was added
    // after the previous session ended. No-op on a first-ever visit
    // (no stored lastSession) — fail-closed in countNewSince.
    refreshBrowseNewSinceBadge(datasets)
  }

  /** Load a dataset by ID onto the globe, tearing down any previous video stream first. Uses a generation counter to safely ignore superseded loads. */
  private async loadDataset(
    datasetId: string,
    trigger: import('./types').LoadTrigger = 'default',
    /**
     * AYNI (multi-globe plan, phase 0): the globe to load into; the
     * primary when absent. Another globe keeps the primary's transport,
     * clock, info card and address untouched: it only gets the dataset.
     */
    slot?: number,
  ): Promise<void> {
    // AYNI: in a meeting the presenter chooses the dataset (a driver, its own globe's).
    if (this.refusedByMeeting(slot)) return
    const targetSlot = slot ?? this.viewports.getPrimaryIndex()
    const toPrimary = targetSlot === this.viewports.getPrimaryIndex()
    if (!this.panelStates[targetSlot]) return
    // AYNI: a stream outside the plan opens the plans instead (planAccess.ts).
    // Not for a load the room makes: watching what the lead shows is free.
    // Read before any await: roomActing is true only during the room's call.
    const fromRoom = this.roomActing
    // AYNI: a lesson library row (LIB_<id>) is registered on first use: a
    // link, a lesson step, or a room following a teacher's lesson.
    if (datasetId.startsWith(LIBRARY_PREFIX) && !dataService.getDatasetById(datasetId)) await ensureLibraryDataset(datasetId)
    // AYNI — own data: a private dataset (My data, a link, a reload) opens
    // with a token session first; it never comes from the catalogue.
    if (isOwnDatasetId(datasetId)) {
      const own = await ensureOwnDataset(datasetId)
      if (!own.ok) {
        this.announce(t(own.error === 'not_ready' ? 'myData.open.notReady' : fromRoom && currentMeetingRoom() ? 'myData.meeting.notShown' : 'myData.open.failed'))
        return
      }
      // Decision O6: a presenter bringing a private dataset into a meeting is told it is not shown to the others until they choose to.
      if (!fromRoom && presentingMeeting()) this.announce(t('myData.meeting.presenterHint'))
    }
    const requested = dataService.getDatasetById(datasetId)
    if (!fromRoom && requested) {
      const pkg = await streamLockedBy(requested)
      if (pkg) {
        // AYNI (owner, 2026-10-20): a stream only Education plays says so, not a package the plan may already have.
        const educationOnly = isEducationOnly(requested)
        const notice = educationOnly
          ? t('plans.locked.educationStream', { title: displayTitle(requested) })
          : t('plans.locked.stream', { title: displayTitle(requested), package: t(PACKAGE_NAME[pkg]) })
        this.announce(notice)
        // AYNI: the plans dialog is not drawn over a camera or in a headset; say it there.
        if (isVrActive()) {
          notifyXr(educationOnly
            ? t('vr.notice.lockedEducationStream', { title: displayTitle(requested) })
            : t('vr.notice.lockedStream', { title: displayTitle(requested), package: t(PACKAGE_NAME[pkg]) }))
        }
        // A meeting's participant (a class exploring) is told, not offered plans.
        if (!currentMeetingRoom() || presentingMeeting()) openPlans(null, 'education', { notice, onStartMeeting: (trigger: HTMLElement) => openMeetingPanel(trigger) })
        return
      }
      // AYNI (owner, 2026-10-06): past the plan's daily streaming, a new
      // stream waits for tomorrow; the one playing has finished loading.
      if (streamQuotaReached(requested)) {
        this.refuseOverDailyStreaming()
        return
      }
    }
    const gen = this.loadGenFor(toPrimary, targetSlot)
    const loadStartWall = Date.now()
    logger.debug('[App] loadDataset start:', datasetId, toPrimary ? '' : `into globe ${targetSlot + 1}`)
    if (toPrimary) {
      stopPlaybackLoop(this.playback)
      this.appState.isPlaying = false
      resetPlaybackState(this.playback)
    }

    // Emit layer_unloaded for whatever was in the panel before
    // the new dataset supersedes it. "replaced" covers both user-driven
    // browses and orbit-driven loads; `goHome` has its own reason.
    this.emitLayerUnloadedForSlot(targetSlot, 'replaced')

    // Tear down the previous video *before* starting the new load so the old
    // HLS stream stops downloading and the old MediaSource is released. Two
    // concurrent HLS.js instances fight over bandwidth and can exhaust the
    // browser's MediaSource / SourceBuffer limits, stalling the new load.
    this.cleanupPanelVideo(targetSlot)
    if (toPrimary) this.stopGeoMedia()

    const targetRenderer = this.viewports.getRendererAt(targetSlot)
    targetRenderer?.removeCloudOverlay()
    targetRenderer?.removeNightLights()
    targetRenderer?.disableSunLighting()

    try {
      await this.displayDataset(datasetId, gen, trigger, loadStartWall, fromRoom, targetSlot)
      if (gen !== this.loadGenFor(toPrimary, targetSlot)) {
        logger.debug('[App] loadDataset superseded:', datasetId)
        this.cleanupPanelVideo(targetSlot)
        return
      }
      if (!toPrimary) {
        // AYNI (multi-globe plan): the other globe is up; its chip, the
        // address and the sync with the primary's date follow.
        this.refreshPanelHeads()
        this.writeSlotsUrl()
        this.globeSeries.delete(targetSlot)
        this.globeReadings.delete(targetSlot)
        logger.debug('[App] loadDataset complete into globe:', targetSlot + 1, datasetId)
        return
      }
      this.showSpeedBar()
      // AYNI: a dataset with data over only part of the Earth opens on its
      // region (owner, 2026-10-04). Not for a load the room makes (the
      // follower's camera is the lead's), nor for one whose Khipu opens it
      // (it sets its own view), and only when it went to the primary
      // globe, whose camera the others follow.
      const loaded = this.appState.currentDataset
      if (!fromRoom && loaded && loaded.id === datasetId && !loaded.khipu) {
        this.focusOnDatasetRegion(loaded)
      }
      logger.debug('[App] loadDataset complete:', datasetId)
    } catch (error) {
      // displayDataset clears its own loading overlay via a
      // try/finally scoped to the slot it pinned at entry, so we
      // don't need to touch it here — doing so risks clearing the
      // wrong panel if the user promoted a different slot mid-load.
      if (gen !== this.loadGenFor(toPrimary, targetSlot)) {
        logger.debug('[App] loadDataset superseded (error ignored):', datasetId)
        this.cleanupPanelVideo(targetSlot)
        return
      }
      logger.debug('[App] loadDataset failed:', datasetId, error)
      // Clean up any partially-created resources from the failed load
      this.cleanupPanelVideo(targetSlot)
      // AYNI (S-02): the host refused the stream; the notice (sign in, or the plans) is already up.
      if (error instanceof StreamRefusedError && toPrimary) return
      if (!toPrimary) {
        // AYNI: the other globe goes back to empty, and says so on its chip.
        const panel = this.panelStates[targetSlot]
        if (panel) panel.dataset = null
        this.syncSatelliteBody()
        this.refreshPanelHeads()
        this.writeSlotsUrl()
        this.announce(t('khipu.step.globes.failed', { n: targetSlot + 1, title: dataService.getDatasetById(datasetId)?.title ?? datasetId }))
        return
      }
      this.setError(error instanceof Error ? error.message : 'Failed to load dataset')
      // AYNI (Quest review B2): the banner is out of sight in a headset.
      if (isVrActive()) notifyXr(t('vr.notice.loadFailed', { title: dataService.getDatasetById(datasetId)?.title ?? datasetId }))
    }
  }

  /**
   * AYNI — the layers a dataset starts with: borders on top of whatever
   * hides most of the Earth (mapLayers.defaultLayers); under it is always
   * the globe's own Earth. Coverage is read from the first
   * decoded frame, so a video waits for one — briefly.
   */
  private async applyDefaultMapLayers(dataset: Dataset, slot: number): Promise<void> {
    // AYNI (multi-globe plan, phase 2): another globe gets its own stack; the main globe's stays as it is.
    if (slot !== this.viewports.getPrimaryIndex()) return this.applyOtherDefaultLayers(dataset, slot)
    const gen = ++this.mapLayerGen
    const layers = await fetchLayerCatalog()
    if (gen !== this.mapLayerGen) return
    if (layers.length === 0) {
      // No catalog layers (or none reachable): the real-time overlay
      // choices do not depend on them.
      this.refreshRtOverlayPicker()
      return
    }
    const transparent =
      carriesAlphaStream(dataset) || dataset.renderEncoding === RENDER_ENCODING_DATA_LUMA || !!dataset.releaseEncoding
    const coverage = transparent ? await this.measureDatasetCoverage(dataset, slot) : null
    if (gen !== this.mapLayerGen) return
    const streamed = dataset.format === 'application/dash+xml'
    // The Sun (and any other body) starts bare: not even a viewer's
    // standing "borders on" from an Earth dataset carries over to it.
    const autoLayersOff = !!dataset.autoLayersOff || !isEarthBody(dataset.celestialBody)
    // AYNI (owner, 2026-10-06): a stream whose descriptor lists the shared
    // country borders starts with them on as an overlay, transparent or not
    // (first the classified maps, then every stream that declares them).
    const declaredBorders = streamed && !autoLayersOff && !!dataset.timelineLink
      && !!(await this.dsaTimelines.loadMetadata(dataset.timelineLink).catch(() => null))?.declaresBorders
    if (gen !== this.mapLayerGen) return
    const regional = !!dataset.releaseEncoding && releaseBoundingBox(dataset.releaseEncoding) !== null
    const defaults = defaultLayers(layers, { streamed, transparent, coverage, autoLayersOff, declaredBorders, regional })
    await this.applyMapLayers(autoLayersOff ? defaults : withBordersChoice(defaults, this.viewerBorders, layers), slot, gen)
  }

  // --- AYNI: the multi-globe plan, phase 2 (a layer stack per globe) ---

  /** The default layers for a dataset on another globe, as the main globe would get them, on that globe's own stack. */
  private async applyOtherDefaultLayers(dataset: Dataset, slot: number): Promise<void> {
    const gen = this.bumpOtherGen(slot)
    const layers = await fetchLayerCatalog()
    if (gen !== this.otherStackGen.get(slot) || !layers.length) return
    const transparent = carriesAlphaStream(dataset) || dataset.renderEncoding === RENDER_ENCODING_DATA_LUMA || !!dataset.releaseEncoding
    const coverage = transparent ? await this.measureDatasetCoverage(dataset, slot) : null
    const streamed = dataset.format === 'application/dash+xml'
    const autoLayersOff = !!dataset.autoLayersOff || !isEarthBody(dataset.celestialBody)
    const declaredBorders = streamed && !autoLayersOff && !!dataset.timelineLink
      && !!(await this.dsaTimelines.loadMetadata(dataset.timelineLink).catch(() => null))?.declaresBorders
    if (gen !== this.otherStackGen.get(slot)) return
    const regional = !!dataset.releaseEncoding && releaseBoundingBox(dataset.releaseEncoding) !== null
    const defaults = defaultLayers(layers, { streamed, transparent, coverage, autoLayersOff, declaredBorders, regional })
    await this.applyOtherLayers(slot, autoLayersOff ? defaults : withBordersChoice(defaults, this.viewerBorders, layers), gen)
  }

  private bumpOtherGen(slot: number): number {
    const gen = (this.otherStackGen.get(slot) ?? 0) + 1
    this.otherStackGen.set(slot, gen)
    return gen
  }

  /** A globe's stack, made empty the first time it is asked for. */
  private otherStack(slot: number): OtherStack {
    let stack = this.otherStacks.get(slot)
    if (!stack) {
      stack = { selection: NO_LAYERS, images: null, rt: [], rtWanted: [] }
      this.otherStacks.set(slot, stack)
    }
    return stack
  }

  /** Put a selection on a globe that is not the main one. Lands on the main globe's stack instead if it became the main one meanwhile. */
  private async applyOtherLayers(slot: number, selection: LayerSelection, gen = this.bumpOtherGen(slot)): Promise<void> {
    if (slot === this.viewports.getPrimaryIndex()) return this.applyMapLayers(selection, slot)
    this.otherStack(slot).selection = selection
    const layers = await fetchLayerCatalog()
    const images = await resolveLayerImages(layers, selection)
    if (gen !== this.otherStackGen.get(slot) || slot === this.viewports.getPrimaryIndex()) return
    const stack = this.otherStacks.get(slot)
    if (!stack) return
    stack.images = images
    this.composeOtherStack(slot)
  }

  /** Draw a globe's own stack (not the main globe's): its real-time streams over its dataset, then its overlays and borders. */
  private composeOtherStack(slot: number): void {
    const stack = this.otherStacks.get(slot)
    const renderer = this.viewports.getRendererAt(slot)
    if (!(renderer instanceof MapRenderer)) return
    if (!stack) {
      renderer.setMapLayers(null)
      this.slotLayerStacks.delete(slot)
      return
    }
    const images = stack.images ?? { overlays: [] }
    const rt = stack.rt.map((r) => ({ image: r.video as HTMLVideoElement, tint: 'source' as const, lut: r.lut, crop: r.crop, phase: r.phase, colorKey: r.colorKey, visible: () => r.visible && !r.userHidden, userOpacity: () => r.opacity ?? 1 }))
    const own = { overlays: [...rt, ...images.overlays] }
    this.slotLayerStacks.set(slot, own)
    renderer.setMapLayers(own.overlays.length ? own : null)
  }

  /** A globe's stack goes (its dataset was taken off, or the globe was): its streams stop, its layers come off. */
  private disposeOtherStack(slot: number): void {
    const stack = this.otherStacks.get(slot)
    this.bumpOtherGen(slot)
    if (!stack) return
    for (const rt of stack.rt) this.destroyRtOverlay(rt)
    this.otherStacks.delete(slot)
    if (slot < this.viewports.getPanelCount()) this.composeOtherStack(slot)
    this.ensureRtTimer()
  }

  /** How many more real-time streams this device may play: the decoders are shared by every globe's streams. */
  private rtBudget(): number {
    let others = 0
    for (const stack of this.otherStacks.values()) others += stack.rt.length
    // AYNI (Plus, owner 2026-10-06): the plan's own limit, where it is lower than the device's.
    return Math.max(0, this.rtPlanCap - others)
  }

  /** The overlay sync's timer runs while any globe has a real-time stream. */
  private ensureRtTimer(): void {
    let any = this.rtOverlays.length > 0
    for (const stack of this.otherStacks.values()) if (stack.rt.length) any = true
    if (any && this.rtSyncTimer === null) {
      this.rtBaseRateSet = null
      this.rtSyncTimer = setInterval(() => {
        this.tickRtOverlay()
        this.tickSiblingRtOverlays()
      }, 100)
    } else if (!any && this.rtSyncTimer !== null) {
      clearInterval(this.rtSyncTimer)
      this.rtSyncTimer = null
    }
  }

  /**
   * Another globe's real-time streams on that globe's date (rtOverlaySync).
   * Its dataset is paced by the main globe's sync, so its streams follow it
   * without slowing it: one that cannot keep up is seeked instead.
   */
  private tickSiblingRtOverlays(): void {
    for (const [slot, stack] of this.otherStacks) {
      if (!stack.rt.length) continue
      const panel = this.panelStates[slot]
      const ds = panel?.dataset ?? null
      const base = ds && dataService.isVideoDataset(ds) ? panel?.hlsService?.video ?? null : null
      const baseClock = base && ds ? this.baseClock(ds, base) : null
      let repaint = false
      for (const rt of stack.rt) {
        if (rt.timelineUrl) this.dsaTimelines.prefetch(rt.timelineUrl)
        const overlayTimeline = rt.timelineUrl ? this.dsaTimelines.get(rt.timelineUrl) : null
        const overlayClock = overlayTimeline ? clockFromTimeline(overlayTimeline) : null
        if (overlayClock) rt.range = { startMs: overlayClock.startMs, endMs: overlayClock.endMs }
        const v = rt.video
        const d = decideRtOverlaySync({
          base: base && ds ? { currentTime: base.currentTime, paused: base.paused, clock: baseClock } : null,
          overlay: { currentTime: v.currentTime, paused: v.paused, seeking: v.seeking, readyState: v.readyState, clock: overlayClock },
          baseUserRate: base?.playbackRate ?? 1,
        })
        v.loop = d.mode === 'untimed'
        if (Math.abs(v.playbackRate - d.overlayRate) > 1e-3) v.playbackRate = d.overlayRate
        if (d.seekTo !== null) v.currentTime = d.seekTo
        if (d.playing && v.paused) void v.play().catch(() => {})
        else if (!d.playing && !v.paused) v.pause()
        if (rt.visible !== d.visible) {
          rt.visible = d.visible
          repaint = true
        }
      }
      if (repaint) this.viewports.getRendererAt(slot)?.getMap()?.triggerRepaint()
    }
  }

  /** Play these real-time streams on a globe that is not the main one (a room's lead says so), in this order, and no others. */
  private async setOtherRtOverlays(slot: number, ids: readonly string[]): Promise<void> {
    if (slot === this.viewports.getPrimaryIndex()) return this.setRtOverlays(ids, { fromRoom: true })
    const stack = this.otherStack(slot)
    const room = this.rtBudget() + stack.rt.length
    const wanted = [...new Set(ids)].slice(0, room)
    stack.rtWanted = wanted
    const gen = this.bumpOtherGen(slot)
    for (const rt of [...stack.rt]) if (!wanted.includes(rt.id)) {
      stack.rt.splice(stack.rt.indexOf(rt), 1)
      this.destroyRtOverlay(rt)
    }
    const started = await Promise.all(wanted.filter((id) => !stack.rt.some((rt) => rt.id === id)).map((id) => this.startRtOverlay(id)))
    if (gen !== this.otherStackGen.get(slot) || this.otherStacks.get(slot) !== stack) {
      for (const rt of started) if (rt) this.destroyRtOverlay(rt)
      return
    }
    for (const rt of started) if (rt) stack.rt.push(rt)
    stack.rt.sort((a, b) => wanted.indexOf(a.id) - wanted.indexOf(b.id))
    stack.rtWanted = wanted.filter((id) => stack.rt.some((rt) => rt.id === id))
    this.ensureRtTimer()
    this.composeOtherStack(slot)
  }

  /**
   * Another globe became the main one: the old main globe's stack is set
   * aside as its own, and the new main globe's is what Tools → Layers, the
   * date tracks and the overlay sync now work on. Nothing restarts: the
   * streams keep playing, each on its globe.
   */
  private swapLayerStacks(oldIndex: number, newIndex: number): void {
    if (oldIndex === newIndex) return
    closePaletteDialog()
    // The old main globe's dataset goes back to its own speed if the overlays were pacing it.
    const oldBase = this.panelStates[oldIndex]?.hlsService?.video
    if (oldBase && this.rtBaseRateSet !== null && Math.abs(oldBase.playbackRate - this.rtBaseRateSet) < 1e-3) oldBase.playbackRate = this.rtBaseUserRate
    this.rtBaseRateSet = null
    const outgoing: OtherStack = { selection: this.mapLayerSelection, images: this.resolvedLayerImages, rt: this.rtOverlays, rtWanted: this.rtWanted }
    const incoming = this.otherStacks.get(newIndex) ?? { selection: NO_LAYERS, images: null, rt: [], rtWanted: [] }
    this.otherStacks.delete(newIndex)
    // The old main globe's pending applies land nowhere. The new main globe's
    // own (its default layers still being worked out) are kept: they see it
    // is the main one now and land on its stack there (`applyOtherLayers`).
    this.mapLayerGen++
    this.rtOverlayGen++
    this.bumpOtherGen(oldIndex)
    if (oldIndex < this.viewports.getPanelCount() && this.panelStates[oldIndex]?.dataset) {
      this.otherStacks.set(oldIndex, outgoing)
    } else {
      for (const rt of outgoing.rt) this.destroyRtOverlay(rt)
    }
    this.mapLayerSelection = incoming.selection
    this.resolvedLayerImages = incoming.images
    this.rtOverlays = incoming.rt
    this.rtWanted = incoming.rtWanted
    this.mapLayerSlot = newIndex
    this.rtBaseUserRate = this.panelStates[newIndex]?.hlsService?.video?.playbackRate ?? 1
    this.rtSpeedCeiling = Infinity
    this.rtHiddenKey = ''
    this.layerPicker?.setRtOverlayStatus([])
    this.composeMapLayers(newIndex)
    if (oldIndex < this.viewports.getPanelCount()) this.composeOtherStack(oldIndex)
    if (this.layerCatalog) this.layerPicker?.update(this.layerCatalog, this.mapLayerSelection)
    this.ensureRtTimer()
    this.refreshRtOverlayPicker()
    this.updateTimelineTrack()
  }

  /** A globe's own layers as the room carries them; null with none. */
  private roomLayersOf(slot: number): RoomLayers | null {
    const stack = this.otherStacks.get(slot)
    if (!stack) return null
    const rt = stack.rt.map((r) => r.id)
    return {
      // No basemap since 2026-10-12; the field stays for older clients.
      basemapId: null,
      overlays: stack.selection.overlays.map((o) => ({ id: o.id, tint: o.tint })),
      rt: rt[0] ?? null,
      ...(rt[1] ? { rt2: rt[1] } : {}),
      ...(rt[2] ? { rt3: rt[2] } : {}),
      ...(rt[3] ? { rt4: rt[3] } : {}),
    }
  }

  /** What the room's lead last asked each other globe to show, so a stack is not reapplied on every state. */
  private otherRoomLayersAsked = new Map<number, { key: string; at: number }>()

  /** A follower: put the lead's layers and streams on one of the other globes; a no-op once they are there. */
  private applyOtherRoomLayers(slot: number, layers: RoomLayers | null): void {
    const want = layers ?? { basemapId: null, overlays: [], rt: null }
    const rtWant = [want.rt, want.rt2, want.rt3, want.rt4].filter((id): id is string => typeof id === 'string')
    const key = JSON.stringify([want.overlays.map((o) => [o.id, o.tint]), rtWant])
    const stack = this.otherStacks.get(slot)
    const have = JSON.stringify([(stack?.selection.overlays ?? []).map((o) => [o.id, o.tint]), stack?.rtWanted ?? []])
    if (key === have) return
    const asked = this.otherRoomLayersAsked.get(slot)
    const now = Date.now()
    if (asked && asked.key === key && now - asked.at < 6000) return
    this.otherRoomLayersAsked.set(slot, { key, at: now })
    const sameLayers = JSON.stringify(want.overlays.map((o) => [o.id, o.tint])) === JSON.stringify((stack?.selection.overlays ?? []).map((o) => [o.id, o.tint]))
    if (!sameLayers) void this.applyOtherLayers(slot, { overlays: want.overlays })
    if (JSON.stringify(rtWant) !== JSON.stringify(stack?.rtWanted ?? [])) void this.setOtherRtOverlays(slot, rtWant)
  }

  /** Fraction of the slot's first frame that hides the Earth, or null if it cannot be read. */
  private async measureDatasetCoverage(dataset: Dataset, slot: number): Promise<number | null> {
    const panel = this.panelStates[slot]
    const video = panel?.hlsService?.getVideo() ?? null
    const source: HTMLImageElement | HTMLVideoElement | null = panel?.image ?? video
    if (!source) return null
    if (source instanceof HTMLVideoElement && source.readyState < 2) {
      await new Promise<void>(resolve => {
        const done = () => { clearTimeout(timer); resolve() }
        const timer = setTimeout(done, 8000)
        source.addEventListener('loadeddata', done, { once: true })
      })
      if (source.readyState < 2) return null
    }
    // A value-encoded frame counts what its palette actually draws.
    const release = dataset.releaseEncoding
    if (release) {
      return measureCoverage(source, { kind: 'palette', lut: buildReleaseLut(release), crop: release.dataRegion })
    }
    const scale = dataset.renderEncoding === RENDER_ENCODING_DATA_LUMA ? dataset.colorScale : undefined
    if (scale) return measureCoverage(source, { kind: 'palette', lut: buildColorScaleLut(scale) })
    // AYNI: a colour-keyed picture covers the Earth where it is not the key (colorKey.ts).
    return measureCoverage(source, dataset.colorKey ? { kind: 'alpha', colorKey: dataset.colorKey } : { kind: 'alpha' })
  }

  /** AYNI: the globe's input handlers that were on when a meeting took the globe, to put back. */
  private lockedHandlers: { enable(): unknown; disable(): unknown }[] | null = null

  /**
   * AYNI: true while this device is a meeting's audience or moderator and
   * a presenter leads (roomSync's setLocked). Every way of changing what
   * is on the sphere checks it — dataset loads, going home, playback, the
   * keyboard, the headset's HUD — so a control the CSS has not hidden
   * still does nothing.
   */
  private audienceLocked = false
  /** Set while the room itself is changing the dataset, which the lock must let through. */
  private roomActing = false
  /** AYNI: while the presenter lets the audience explore, the globe is theirs (roomSync's setExploring). */
  private roomExploring = false
  /** AYNI: while an explore activity gives this device the whole sphere (setRoomExploring). */
  private roomExploreFree = false

  /**
   * AYNI (owner, 2026-10-13): a meeting lends its host's stream packages to
   * a participant while the sphere is in their hands — exploring on their
   * own, holding the presenter's globe (not while the presenter has taken
   * over), or driving one of the globes — and takes them back after.
   */
  private syncLentPackages(): void {
    const s = this.lastRoomStatus
    const lent = s?.connected && s.meeting && s.seat !== 'presenter' ? s.grants?.packages ?? null : null
    const inHands = !!s && (this.roomExploreFree || (!!s.spotlight?.me && !s.spotlight.override) || s.driving !== null)
    setLentPackages(lent && inHands ? lent : null)
  }
  /** AYNI: whether this device follows a room's lead (the lead drives the camera). */
  private roomFollowing = false
  /** AYNI: the lock a meeting asks for (roomSync's setLocked). */
  private roomMeetingLock = false
  /**
   * AYNI: a guest (no account) in an open room only watches, as a meeting's
   * audience does: the room never lets a guest lead (workers/rooms), and the
   * controls are locked here (item 1.3: using AYNI XR needs an account).
   */
  private roomGuestLock = false

  /** AYNI: lock for a meeting's audience or for a guest in an open room; an explore activity lifts a meeting's lock. */
  private applyRoomLock(): void {
    this.setAudienceLocked((this.roomMeetingLock && !this.roomExploreFree) || this.roomGuestLock || this.outputLock)
  }

  /** AYNI (multi-globe plan, phase 4): this page is an output window: locked for good. */
  private outputLock = false
  /** AYNI (multi-globe plan, phase 5): this output window's sphere feed, when it is one. */
  private sphereFeed: { stop(): void; frames(): number } | null = null
  /** AYNI (phase 4): the channel this page's output window follows, once one is open. */
  private outputChannel: string | null = null

  /**
   * Open the output window: the globes alone, following this page. In a
   * meeting it joins the meeting (the room relays); otherwise this page
   * leads it over a BroadcastChannel, as a room's lead would.
   */
  /**
   * AYNI (multi-globe plan, phase 5, Science On a Sphere route A): this
   * output window shows the main globe as one 2:1 equirectangular picture
   * over the page (`sphereFeed.ts`), which a sphere's computer captures
   * and plays; the globes under it keep following the other window.
   */
  private startSphereOutput(param: '2048' | '4096'): void {
    document.body.classList.add('output-sphere')
    const canvas = document.createElement('canvas')
    canvas.id = 'sphere-feed'
    canvas.setAttribute('role', 'img')
    canvas.setAttribute('aria-label', t('output.sphere.aria'))
    document.body.appendChild(canvas)
    const within = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
      Promise.race([p, new Promise<T>((resolve) => window.setTimeout(() => resolve(fallback), ms))])
    // The pins come from the window with the controls (the room sync does not carry them); not in a meeting's output.
    let pins: FeedPin[] = []
    const channel = outputChannelFromSearch()
    if (channel && channel !== 'room' && typeof BroadcastChannel !== 'undefined') {
      receiveFeedPins((name) => new BroadcastChannel(name), channel, (next) => { pins = next })
    }
    this.sphereFeed = startSphereFeed(canvas, {
      unwrap: (w, h) => within(this.viewports.getPrimary()?.unwrapFrame(w, h) ?? Promise.resolve(null), 3000, null),
      pins: () => pins,
    }, sphereFeedSize(param))
  }

  private openOutputWindow(sphere: '2048' | '4096' | null = null): 'opened' | 'blocked' {
    const room = activeRoomSync()?.status()
    const inRoom = room?.connected ? room.code : null
    const channel = this.outputChannel ?? newOutputChannel()
    const url = outputWindowUrl(window.location.href, channel, inRoom, sphere)
    // AYNI (phase 5): a sphere feed is a window of its own beside the globes' one, 2:1.
    const win = window.open(url, `ayni-${sphere ? 'sphere' : 'output'}-${channel}`, sphere ? 'popup=yes,width=1280,height=640' : 'popup=yes,width=1280,height=720')
    if (!win) return 'blocked'
    if (!inRoom && this.outputChannel !== channel) {
      this.outputChannel = channel
      startRoomSync(channel, this.roomSyncHost(), {
        connect: (code, opts) => connectRoom(code, { ...opts, createSocket: () => createOutputSocket('main', code) as unknown as WebSocket }),
      })
      // AYNI (phase 5): the Data tab's pins, for a sphere feed (the room sync does not carry them).
      if (typeof BroadcastChannel !== 'undefined') {
        shareFeedPins((name) => new BroadcastChannel(name), channel, () => this.dataPinStore?.list() ?? [], (fn) => this.dataPinStore?.subscribe(fn) ?? (() => {}))
      }
    }
    return 'opened'
  }

  /**
   * AYNI: bring playback to `want` (roomSync's setPlaying): the app's
   * playing flag and the video element both, each only if it is not there
   * already. The app's own toggle is used where the two agree, so its loop
   * rest and stall recovery still apply; where they disagree, the side
   * that is wrong is corrected directly, since a toggle would flip the
   * side that was right.
   */
  private setRoomPlaying(want: boolean): void {
    const hls = this.hlsService
    const video = hls?.video
    if (!hls || !video) return
    // On its last frame between loops the app is playing and the element paused.
    const resting = video.paused && video.duration > 0 && video.currentTime >= video.duration - 0.5
    if (want) {
      if (this.appState.isPlaying) {
        if (video.paused && !resting) {
          hls.play()?.catch(() => { /* refused until the page is touched; asked again next tick */ })
        }
        return
      }
      if (!video.paused) {
        this.appState.isPlaying = true
        updatePlayButton(false)
        return
      }
      togglePlayPause(hls, this.appState, (m) => this.announce(m), this.playback)
      return
    }
    if (!this.appState.isPlaying) {
      if (!video.paused) hls.pause()
      return
    }
    togglePlayPause(hls, this.appState, (m) => this.announce(m), this.playback)
  }

  /** AYNI: run a change the room asked for, past the audience lock. */
  private asRoom(change: () => void): void {
    this.roomActing = true
    try {
      change()
    } finally {
      this.roomActing = false
    }
  }

  /** AYNI: whether a viewer-initiated change is refused (see audienceLocked). */
  private refusedByMeeting(slot?: number): boolean {
    // AYNI (multi-globe plan, phase 3): the globe this device drives is its own.
    if (slot !== undefined && slot === this.roomDriving) return false
    return this.audienceLocked && !this.roomActing
  }

  /**
   * AYNI (Quest review A2, 2026-11-03): whether this viewer's own pins are
   * refused in a headset or phone session. A meeting that lends the data
   * tools (a class) keeps each participant's own pins beside the
   * presenter's, as the browser's Data tab always has; elsewhere the lead's
   * pins are the only ones, so the presenter lock holds.
   */
  private pinsRefusedByMeeting(): boolean {
    return this.refusedByMeeting() && !dataToolsLent()
  }

  /** AYNI (multi-globe plan, phase 3): the globe this device drives in a meeting (roomSync), or null. */
  private roomDriving: number | null = null
  /** AYNI: what the room last asked each globe to show, and when, so a slow load is not asked for ten times a second. */
  private roomSlotAsked = new Map<number, { id: string; at: number }>()

  /**
   * AYNI: take the controls out of this device's hands, or give them back
   * (roomSync, for a meeting's audience and moderator): the globe's own
   * handlers, the panels that change the dataset (browse, Tools, Orbit,
   * the transport) and the body class the stylesheet hides them by.
   */
  private setAudienceLocked(locked: boolean): void {
    const was = this.audienceLocked
    this.audienceLocked = locked
    document.body.classList.toggle('meeting-audience', locked)
    // Only as the lock comes on: the room asks again on every roster change,
    // and a class's Tools panel (the data tools it lends) must stay open.
    if (locked && !was) {
      hideBrowseUI()
      closeToolsMenu()
      this.chatClose()
    }
    this.setGlobeLocked(locked && !this.roomExploring, this.roomDriving)
  }

  /**
   * AYNI: the presenter lets the audience explore, or brings them back
   * (roomSync). An explore activity (`free`) lifts the meeting's lock
   * altogether (owner, 2026-10-12: full control of the sphere and the Tools
   * panel until the time ends or the presenter calls them back); the lock
   * closes Browse and Tools again when it returns. A game's point round
   * frees the globe's handlers only.
   */
  private setRoomExploring(exploring: boolean, free = false): void {
    this.roomExploring = exploring
    const wasFree = this.roomExploreFree
    this.roomExploreFree = exploring && free
    this.syncLentPackages()
    if (wasFree !== this.roomExploreFree) this.applyRoomLock()
    else if (this.audienceLocked) this.setGlobeLocked(!exploring, this.roomDriving)
  }


  /**
   * AYNI: take the browser globe out of this device's hands, or give it
   * back. Only the handlers that were on are turned off, and only those
   * are turned back on.
   */
  private setGlobeLocked(locked: boolean, except: number | null = null): void {
    // AYNI (multi-globe plan): every globe is locked, but the one this
    // device drives; a change of that globe unlocks and locks again.
    if (locked && this.lockedHandlers !== null && this.lockedExcept !== except) this.setGlobeLocked(false)
    if (locked === (this.lockedHandlers !== null)) return
    if (!locked) {
      for (const handler of this.lockedHandlers ?? []) handler.enable()
      this.lockedHandlers = null
      this.lockedExcept = null
      return
    }
    const handlers: { enable(): unknown; disable(): unknown; isEnabled(): boolean }[] = []
    for (let slot = 0; slot < this.viewports.getPanelCount(); slot++) {
      if (slot === except) continue
      const map = this.viewports.getRendererAt(slot)?.getMap()
      if (!map) continue
      handlers.push(map.dragPan, map.scrollZoom, map.touchZoomRotate, map.dragRotate, map.doubleClickZoom, map.keyboard, map.touchPitch, map.boxZoom)
    }
    if (!handlers.length) return
    this.lockedHandlers = handlers.filter((handler) => handler.isEnabled())
    this.lockedExcept = except
    for (const handler of this.lockedHandlers) handler.disable()
  }

  /** AYNI (multi-globe plan): the globe left unlocked by `setGlobeLocked`. */
  private lockedExcept: number | null = null

  /** AYNI: the layer stack a shared session's lead last asked for, and when. */
  private roomLayersAsked: { key: string; at: number; tries: number } | null = null

  /**
   * AYNI: take the lead's layer stack (roomSync). A no-op when it is
   * already on, and not asked for twice within a few seconds, because
   * both the images and the real-time overlay's stream take time to
   * arrive and this is called on every beat until they have.
   */
  private applyRoomLayers(layers: RoomLayers): void {
    // AYNI (owner, 2026-10-12): the lead's opacities and eyes, now and on overlays still starting.
    this.applyRoomLook(layers.look ?? null)
    // A basemap the lead names (an older client) is ignored: the Earth is the only one.
    const keyOf = (overlays: ReadonlyArray<{ id: string; tint: string }>, rt: readonly string[]) =>
      JSON.stringify([overlays.map((o) => [o.id, o.tint]), rt])
    // AYNI: up to four real-time overlays (rt … rt4), as many as this device plays.
    const rtWant = [layers.rt, layers.rt2, layers.rt3, layers.rt4].filter((id): id is string => typeof id === 'string').slice(0, this.rtCap)
    const want = keyOf(layers.overlays, rtWant)
    const selection = this.mapLayerSelection
    if (want === keyOf(selection.overlays, this.rtWanted)) {
      this.roomLayersAsked = null
      return
    }
    const now = Date.now()
    const asked = this.roomLayersAsked?.key === want ? this.roomLayersAsked : null
    // Twice, then no more until the lead asks for something else: a
    // stack this device cannot produce (an overlay stream that will not
    // start) must not restart a player every few seconds for good.
    if (asked && (asked.tries >= 2 || now - asked.at < 6000)) return
    this.roomLayersAsked = { key: want, at: now, tries: (asked?.tries ?? 0) + 1 }
    if (keyOf(layers.overlays, []) !== keyOf(selection.overlays, [])) {
      void this.applyMapLayers({ overlays: layers.overlays }, this.mapLayerSlot)
    }
    if (JSON.stringify(rtWant) !== JSON.stringify(this.rtWanted)) void this.setRtOverlays(rtWant, { fromRoom: true })
  }

  /** AYNI (owner, 2026-10-12): the lead's opacities and eyes as last received; overlays that start later take theirs from it. */
  private roomLook: RoomLayerLook | null = null

  /** AYNI: this device's opacities and eyes, for the room (`RoomLayers.look`); nothing while all are opaque and shown. */
  private roomLookOf(): { look?: RoomLayerLook } {
    const base = roomShade(this.baseOpacity, this.baseHidden)
    const rt = this.rtOverlays.flatMap((o) => {
      const shade = roomShade(o.opacity ?? 1, !!o.userHidden)
      return shade ? [{ id: o.id, ...shade }] : []
    })
    if (!base && !rt.length) return {}
    return { look: { ...(base ? { base } : {}), ...(rt.length ? { rt } : {}) } }
  }

  /** AYNI: follow the lead's opacities and eyes (null: all opaque and shown). */
  private applyRoomLook(look: RoomLayerLook | null): void {
    this.roomLook = look
    this.setBaseOpacity(look?.base?.opacity ?? 1, { fromRoom: true })
    this.setBaseHidden(!!look?.base?.hidden, { fromRoom: true })
    for (const rt of this.rtOverlays) {
      const shade = look?.rt?.find((r) => r.id === rt.id)
      this.setRtOverlayOpacity(rt.id, shade?.opacity ?? 1, { fromRoom: true })
      this.setRtOverlayHidden(rt.id, !!shade?.hidden, { fromRoom: true })
    }
  }

  /**
   * AYNI (owner, 2026-10-22): the lead's colours on this follower, by
   * dataset id (`applyRoomColours`); null while not following. Never saved
   * as this viewer's own.
   */
  private roomDisplays: Map<string, ColorScaleDisplay> | null = null
  private roomColoursKey = ''
  /** AYNI: the main dataset is drawn in the lead's colours, and these overlays are: what gets this viewer's own back. */
  private roomBaseColoured = false
  private roomRtColoured = new Set<string>()

  /** AYNI (owner, 2026-10-22): the colours this device draws each dataset in, for the room: the main globe's first, then the overlays', then the other globes'. */
  private roomColoursNow(): RoomColours[] {
    const out: RoomColours[] = []
    const add = (id: string | null | undefined, display: ColorScaleDisplay): void => {
      if (id && !out.some((c) => c.id === id)) out.push(roomColoursOf(id, display))
    }
    add(this.appState.currentDataset?.id, this.colorScaleDisplay)
    for (const rt of this.rtOverlays) if (canRecolour(rt.encoding)) add(rt.id, this.rtDisplayOf(rt.id))
    this.panelStates.forEach((p, i) => add(p.dataset?.id, this.panelDisplays[i] ?? DEFAULT_DISPLAY))
    return out
  }

  /**
   * AYNI (owner, 2026-10-22): follow the lead's colours — the main
   * dataset's, the overlays', the other globes' — drawn without being saved
   * (a Khipu's colours are drawn the same way). Asked on every tick and
   * cheap once they match, so a follower that changes one is put back.
   * Null: this viewer's own colours back.
   */
  private applyRoomColours(list: RoomColours[] | null): void {
    if (list === null) {
      if (!this.roomDisplays) return
      this.roomDisplays = null
      this.roomColoursKey = ''
      const dataset = this.appState.currentDataset
      if (this.roomBaseColoured && dataset) {
        this.displayTemporary = false
        this.colorScaleDisplay = displayFor(dataset)
        setActiveDisplay(this.colorScaleDisplay)
      }
      this.roomBaseColoured = false
      for (const id of this.roomRtColoured) {
        this.rtDisplays.delete(id)
        this.setRtOverlayDisplay(id, this.rtDisplayOf(id), { save: false })
      }
      this.roomRtColoured.clear()
      this.refreshPanelLegends()
      this.viewPalette?.refresh()
      this.viewOpacity?.refresh()
      return
    }
    const key = JSON.stringify(list)
    const changed = key !== this.roomColoursKey || !this.roomDisplays
    if (changed) {
      this.roomColoursKey = key
      this.roomDisplays = new Map(list.map((c) => [c.id, displayOfRoomColours(c)]))
    }
    const displays = this.roomDisplays!
    const dataset = this.appState.currentDataset
    const base = dataset ? displays.get(dataset.id) : undefined
    if (base && !sameColours(base, this.colorScaleDisplay)) {
      this.roomBaseColoured = true
      // Redraws every globe's colours too (refreshPanelLegends).
      this.applyColorScaleDisplay(base, { save: false })
    } else if (changed) {
      // The other globes' colours.
      this.refreshPanelLegends()
    }
    for (const rt of this.rtOverlays) {
      const want = displays.get(rt.id)
      if (!want || !canRecolour(rt.encoding) || sameColours(want, this.rtDisplayOf(rt.id))) continue
      this.roomRtColoured.add(rt.id)
      this.setRtOverlayDisplay(rt.id, want, { save: false })
    }
  }

  /** AYNI (owner, 2026-10-22): this viewer's own pins while it shows a room lead's (`applyRoomPins`); null while not following. */
  private roomOwnPins: Pin[] | null = null
  /** AYNI: the lead's graph at its last change (null: not heard yet), and whether this device took its own out for it. */
  private roomGraph: boolean | null = null
  private roomGraphOut = false

  /** AYNI (owner, 2026-10-22): this device's pins and whether its pin graph is on screen with any, for the room. */
  private roomDataPinsNow(): RoomDataPins | undefined {
    const store = this.dataPinStore
    if (!store) return undefined
    const pins = store.list()
    return roomDataPinsOf(pins, { graph: pins.length > 0 && !!this.dataPinsUi?.graphShown(), track: store.trackShown, surface: store.surface })
  }

  /**
   * AYNI (owner, 2026-10-22): show the lead's pins, put back on every tick
   * if this follower changed them (a no-op once they match); where the data
   * tools are lent, beside the participant's own. The graph comes
   * out when the lead's comes on screen, and goes when it goes, on the
   * lead's change only: a viewer may put it away meanwhile (a phone needs
   * the globe). Null: this viewer's own pins back.
   */
  private applyRoomPins(data: RoomDataPins | null): void {
    const store = this.dataPinStore
    if (!store) return
    if (data === null) {
      // This viewer's own pins back: those put aside on joining, or those kept beside the lead's.
      if (this.roomOwnPins) store.restore(this.roomOwnPins)
      else store.dropShared()
      this.roomOwnPins = null
      if (this.roomGraphOut) this.dataPinsUi?.showGraph(false)
      this.roomGraph = null
      this.roomGraphOut = false
      return
    }
    // A meeting that lends the data tools (a class, owner 2026-10-05) keeps each
    // participant's own pins beside the presenter's (owner, 2026-10-22: the
    // presenter's pins and graph must reach every participant). Elsewhere the
    // lead's pins are the only ones, and this viewer's own are put aside until it leaves.
    const keepOwn = dataToolsLent()
    if (!keepOwn && !this.roomOwnPins) this.roomOwnPins = store.snapshot().filter((p) => !p.shared)
    // Cheap and a no-op once they match, so asked on every tick: a change of the follower's is put back.
    store.adopt(adoptedPinsOf(data), { keepOwn })
    store.setTrackShown(!!data.track)
    store.setSurface(data.surface ?? 'all')
    // An output window shows the globes alone.
    const graph = !!data.graph
    if (graph === this.roomGraph || this.outputLock || !this.dataPinsUi) return
    this.roomGraph = graph
    if (graph && !this.dataPinsUi.graphShown()) {
      this.dataPinsUi.showGraph(true)
      this.roomGraphOut = this.dataPinsUi.graphShown()
    } else if (!graph && this.roomGraphOut) {
      this.dataPinsUi.showGraph(false)
      this.roomGraphOut = false
    }
  }

  /** AYNI: the change from a held baseline, drawn just above the dataset (research tier; analyzeUI). */
  private changeLayer: { canvas: HTMLCanvasElement; frameKey: string | null } | null = null
  /** AYNI: the real-time overlay being compared with the dataset (Analyze → Compare), or null. */
  private compareId: string | null = null
  /** AYNI: that overlay's values at each pin, on the pin's own time slots (pin id → series, in its own units). */
  private compareSeries = new Map<string, MeasurementSeries>()
  /** AYNI: the overlay images last resolved, so the stack can be recomposed without fetching. */
  private resolvedLayerImages: Awaited<ReturnType<typeof resolveLayerImages>> | null = null

  /** Put a selection on the slot's globe (and the VR globe, which polls mapLayerImages). */
  private async applyMapLayers(selection: LayerSelection, slot: number, gen = ++this.mapLayerGen): Promise<void> {
    this.mapLayerSelection = selection
    this.mapLayerSlot = slot
    const layers = await fetchLayerCatalog()
    this.layerCatalog = layers
    this.layerPicker?.update(layers, selection)
    const images = await resolveLayerImages(layers, selection)
    if (gen !== this.mapLayerGen) return
    this.resolvedLayerImages = images
    this.composeMapLayers(slot)
  }

  /**
   * The stack the globe draws. The real-time overlays sit right above the
   * dataset (the first chosen lowest), then AYNI's change-from-baseline map
   * (shown only on the frame it was computed for), then borders and labels,
   * so those stay readable over moving data.
   */
  private composeMapLayers(slot = this.mapLayerSlot): void {
    const images = this.resolvedLayerImages ?? { overlays: [] }
    const change = this.changeLayer
    // AYNI: the change map belongs to the primary globe, the one Analyze reads.
    const primary = this.viewports.getPrimaryIndex()
    const changeEntry = change
      ? [{ image: change.canvas, tint: 'source' as const, visible: () => (this.viewports.getPrimary()?.currentFrameId() ?? null) === change.frameKey }]
      : []
    const rt = this.rtOverlays.map((rt) => ({ image: rt.video as HTMLVideoElement, tint: 'source' as const, lut: rt.lut, crop: rt.crop, phase: rt.phase, colorKey: rt.colorKey, visible: () => rt.visible && !rt.userHidden && rt.id !== this.compareId, userOpacity: () => rt.opacity ?? 1 }))
    const mix = this.dataMix && slot === primary
      ? [{ image: this.dataMix.rt.video as HTMLVideoElement, tint: 'source' as const, lut: this.dataMix.rt.lut, crop: this.dataMix.rt.crop, phase: this.dataMix.rt.phase, colorKey: this.dataMix.rt.colorKey, reveal: () => this.dataMix?.reveal ?? { opacity: 0, wipe: null } }]
      : []
    const overlays = [...mix, ...rt, ...(slot === primary ? changeEntry : []), ...images.overlays]
    this.refreshRtOverlayPicker()
    this.mapLayerImages = overlays.length ? { overlays } : null
    this.slotLayerStacks.set(slot, { overlays: [...rt, ...images.overlays] })
    const renderer = this.viewports.getRendererAt(slot)
    if (renderer instanceof MapRenderer) renderer.setMapLayers(this.mapLayerImages)
    // With 2 or 4 globes the stack may sit on another globe (the last one
    // loaded); the primary keeps the stack it was given, plus the change map.
    if (slot !== primary) {
      const target = this.viewports.getRendererAt(primary)
      const base = this.slotLayerStacks.get(primary) ?? null
      const own = [...changeEntry, ...(base?.overlays ?? [])]
      if (target instanceof MapRenderer && (change || this.changeOnOtherSlot)) {
        target.setMapLayers(own.length ? { overlays: own } : null)
      }
      this.changeOnOtherSlot = !!change
    }
    // A globe that held the change picture and is no longer the main one gets its own stack back.
    const had = this.changeSlot
    this.changeSlot = change ? primary : null
    if (had !== null && had !== primary && had !== slot && had < this.viewports.getPanelCount()) {
      const demoted = this.viewports.getRendererAt(had)
      const own = this.slotLayerStacks.get(had) ?? null
      if (demoted instanceof MapRenderer) demoted.setMapLayers(own?.overlays.length ? own : null)
    }
  }

  /** AYNI: the globe whose stack carries the change picture (composeMapLayers); null when none does. */
  private changeSlot: number | null = null

  /**
   * AYNI: the change drawn live (research tier) goes on the main globe and
   * is taken off the globe it was put on, which after another globe became
   * the main one is not the main globe any more (2026-10-06: with two
   * globes, switching left the demoted globe drawing the change under its
   * own legend).
   */
  private setLiveChange(view: LiveChangeView | null): boolean {
    const primary = this.viewports.getPrimary()
    const had = this.liveChangeOn
    this.liveChangeOn = null
    if (had && had !== primary && this.rendererOnScreen(had)) had.setChangeView(null)
    if (!primary) return view === null
    const ok = primary.setChangeView(view)
    if (view && ok) this.liveChangeOn = primary
    return ok
  }

  /** AYNI: the globe the live change is on (setLiveChange); null when none is. */
  private liveChangeOn: MapRenderer | null = null

  /** AYNI: true while a renderer is one of the globes on screen (a smaller layout removes some). */
  private rendererOnScreen(renderer: MapRenderer): boolean {
    for (let i = 0; i < this.viewports.getPanelCount(); i++) if (this.viewports.getRendererAt(i) === renderer) return true
    return false
  }

  /** AYNI: the stack each globe was last given, without the change map (composeMapLayers). */
  private slotLayerStacks = new Map<number, MapLayerImages>()
  /** AYNI: a change map was put on a primary globe that does not hold the layer stack, so taking it off must restore that globe. */
  private changeOnOtherSlot = false

  /**
   * AYNI: the picker's choice. A stream that is playing is taken off; one
   * that is not is added, and when the list is full the one added first
   * makes way (with one allowed, that is simply a replacement). None (null)
   * takes them all off.
   */
  /** AYNI: today's streaming is used up: say so, and when it starts again (planAccess.streamQuotaReached). */
  private refuseOverDailyStreaming(): void {
    const { capMB, resetsAt } = streamQuotaToday()
    const cap = capMB === null ? '' : capMB >= 1000 ? `${formatNumber(capMB / 1000, { maximumFractionDigits: 1 })} GB` : `${formatNumber(capMB, { maximumFractionDigits: 0 })} MB`
    const time = new Date(resetsAt).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' })
    const notice = t('plans.locked.streamDaily', { cap, time })
    this.announce(notice)
    if (isVrActive()) notifyXr(notice)
    openPlans(null, 'public', { notice })
  }

  /**
   * AYNI: the most overlays the viewer may choose: the device's cap, or
   * fewer when the plan says so (Plus 1, owner, 2026-10-06). A room's own
   * loads keep the device's cap: watching what someone shows is free.
   */
  private get rtPlanCap(): number {
    const plan = maxPlanOverlays()
    return plan === null ? this.rtCap : Math.max(1, Math.min(this.rtCap, plan))
  }

  private pickRtOverlay(id: string | null): void {
    const now = this.rtWanted
    const adding = id !== null && !now.includes(id) ? dataService.getDatasetById(id) : undefined
    if (adding && streamQuotaReached(adding)) {
      this.refuseOverDailyStreaming()
      return
    }
    const next = id === null ? [] : now.includes(id) ? now.filter((x) => x !== id) : [...now, id].slice(-Math.max(1, this.rtBudget()))
    void this.setRtOverlays(next)
  }

  /**
   * AYNI: play these real-time overlay streams over the dataset, in this
   * order (the first lowest), and no others. Ones already playing carry on;
   * a stream that fails to start is dropped rather than left half-started.
   */
  private async setRtOverlays(ids: readonly string[], opts: { fromRoom?: boolean } = {}): Promise<void> {
    // AYNI (owner, 2026-11-03): a stream only Education plays is refused on
    // any other plan, whoever asks (a Khipu step, a lesson, Undo, the
    // chatbot), as Browse refuses it; a room's lead's choice is shown as any
    // load the room makes. Checked only when such a stream is asked for, so
    // every other call starts at once, as before.
    let asked = ids
    if (!opts.fromRoom && ids.some((id) => { const d = dataService.getDatasetById(id); return !!d && isEducationOnly(d) })) {
      const checkGen = ++this.rtOverlayGen
      const { allowed, refused } = await rtOverlaysAllowed(ids, (id) => dataService.getDatasetById(id))
      // Asked for something else meanwhile; that call decides.
      if (checkGen !== this.rtOverlayGen) return
      if (refused.length > 0) {
        const title = refused.map((d) => displayTitle(d)).join(', ')
        this.announce(t('tools.layers.rt.educationOnly', { title }))
        if (isVrActive()) notifyXr(t('vr.notice.lockedEducationStream', { title }))
      }
      asked = allowed
    }
    // AYNI (multi-globe plan, phase 2): the decoders are shared with the other globes' streams.
    const wanted = [...new Set(asked)].slice(0, this.rtBudget())
    this.rtWanted = wanted
    const gen = ++this.rtOverlayGen
    for (const rt of [...this.rtOverlays]) if (!wanted.includes(rt.id)) this.stopRtOverlay(rt)
    this.refreshRtOverlayPicker()
    const starting = wanted.filter((id) => !this.rtOverlays.some((rt) => rt.id === id))
    const started = await Promise.all(starting.map((id) => this.startRtOverlay(id, { tell: true })))
    if (gen !== this.rtOverlayGen) {
      // Asked for something else meanwhile; that call starts what it wants.
      for (const rt of started) if (rt) this.destroyRtOverlay(rt)
      return
    }
    for (const rt of started) {
      if (!rt) continue
      // AYNI (owner, 2026-10-12): a meeting's lead may have shaded it already.
      const shade = this.roomLook?.rt?.find((r) => r.id === rt.id)
      if (shade && this.roomFollowing) {
        rt.opacity = shade.opacity ?? 1
        rt.userHidden = !!shade.hidden
      }
      this.rtOverlays.push(rt)
    }
    // The order asked for, as moved since (moveRtOverlay) while streams started.
    const order = this.rtWanted
    this.rtOverlays.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    // A stream that would not start is no longer asked for.
    this.rtWanted = order.filter((id) => this.rtOverlays.some((rt) => rt.id === id))
    this.ensureRtTimer()
    this.tickRtOverlay()
    this.updateTimelineTrack()
    await this.applyMapLayers(this.mapLayerSelection, this.mapLayerSlot)
  }

  /** AYNI: the real-time overlays that carry values, as Analyze's Compare reads them. */
  private compareCandidates(): CompareCandidate[] {
    const sampler = getSharedLumaSampler()
    if (!sampler) return []
    return [...this.rtOverlays.filter((rt) => rt.encoding).map((rt): CompareCandidate => {
      const enc = rt.encoding!
      const ds = dataService.getDatasetById(rt.id)
      const scale = releaseColorScale(enc)
      const box = releaseBoundingBox(enc) ?? undefined
      const probeOptions = { colorScale: scale, cropRect: rt.crop, releaseEncoding: enc, ...(box ? { boundingBox: box } : {}) }
      return {
        id: rt.id,
        title: ds ? displayTitle(ds) : rt.id,
        units: enc.units ?? undefined,
        classified: enc.kind === 'luma8-classified',
        visible: () => rt.visible,
        frame: () => {
          const shot = sampler.snapshot(rt.video)
          if (!shot) return null
          const map = rt.crop ? cropSnapshot(shot, rt.crop) : shot
          return map ? { snapshot: map, scale, options: box ? { boundingBox: box } : {} } : null
        },
        valueAt: (lat: number, lon: number) => {
          const r = probeDatasetValue(lat, lon, rt.video, (src, uv) => sampler.sample(src, uv), probeOptions)
          return r && !r.noData ? r.value : null
        },
        instant: () => {
          const tl = rt.timelineUrl ? this.dsaTimelines.get(rt.timelineUrl) : null
          return tl ? { ms: dateAtVideoTimeMs(tl, rt.video.currentTime), cadenceMs: tl.cadenceMs } : null
        },
        kind: 'overlay',
        live: () => ({ source: rt.video, crop: rt.crop ?? null }),
        provenance: () => {
          if (!ds) return null
          const tl = rt.timelineUrl ? this.dsaTimelines.get(rt.timelineUrl) : null
          const atMs = tl ? dateAtVideoTimeMs(tl, rt.video.currentTime) : null
          return provenanceRows(this.citationFor(ds, { ...this.currentViewState(), atMs, atPrecision: tl ? timelineAxis(tl).precision : null, layers: null }))
        },
      }
    }), ...this.globeCompareCandidates()]
  }

  /** AYNI: the base frame's date and its dataset's time step, from its .dsa; null without one. */
  private baseFrameInstant(): FrameInstant | null {
    return this.slotFrameInstant(this.viewports.getPrimaryIndex())
  }

  /** AYNI: the date of the frame a globe shows and its dataset's time step, from its .dsa; null without one. */
  private slotFrameInstant(slot: number): FrameInstant | null {
    const panel = this.panelStates[slot]
    const dataset = panel?.dataset ?? null
    const video = dataset && dataService.isVideoDataset(dataset) ? panel?.hlsService?.video ?? null : null
    const tl = dataset?.timelineLink ? this.dsaTimelines.get(dataset.timelineLink) : null
    if (!tl) return null
    return { ms: dateAtVideoTimeMs(tl, video?.currentTime ?? 0), cadenceMs: tl.cadenceMs }
  }

  /**
   * AYNI: the other globes (2 or 4 on screen) whose dataset carries values,
   * as Compare's B (research tier, slice 2): globe A is the primary, the
   * one Analyze reads. Each is read through its own renderer.
   */
  private globeCompareCandidates(): CompareCandidate[] {
    const primary = this.viewports.getPrimaryIndex()
    const out: CompareCandidate[] = []
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      if (slot === primary) continue
      const renderer = this.viewports.getRendererAt(slot)
      const ds = this.panelStates[slot]?.dataset ?? null
      if (!(renderer instanceof MapRenderer) || !ds) continue
      const scale = renderer.displayScale()
      if (!scale) continue
      // Its credit for the CSV, fetched once (cached after).
      if (ds.timelineLink) void this.dsaTimelines.loadMetadata(ds.timelineLink).catch(() => null)
      const kind = (scale as { release?: { kind?: string } }).release?.kind
      out.push({
        id: `globe:${slot}`,
        kind: 'globe',
        globe: slot + 1,
        title: displayTitle(ds),
        units: scale.units,
        classified: kind === 'luma8-classified',
        visible: () => true,
        frame: () => renderer.analysisFrame(),
        valueAt: (lat, lon) => {
          const r = renderer.probeValueAt(lat, lon)
          return r && !r.noData ? r.value : null
        },
        instant: () => this.slotFrameInstant(slot),
        // Streams without catalogue dates are not moved by the globes' date
        // sync, so Compare can put this globe on A's date by its own axis.
        seekTo: async (ms) => {
          const video = this.panelStates[slot]?.hlsService?.video ?? null
          const tl = ds.timelineLink ? this.dsaTimelines.get(ds.timelineLink) : null
          if (!video || !tl) return false
          const target = videoTimeForDateMs(tl, ms)
          if (!video.paused) video.pause()
          if (Math.abs(video.currentTime - target) > 1e-3) {
            const seeked = new Promise<void>((resolve) => video.addEventListener('seeked', () => resolve(), { once: true }))
            video.currentTime = target
            await Promise.race([seeked, new Promise<void>((resolve) => setTimeout(resolve, 5000))])
          }
          this.uploadSiblingFrameOnSeek(slot, video)
          renderer.getMap()?.triggerRepaint()
          return true
        },
        live: () => renderer.liveSource(),
        provenance: () => {
          const at = this.slotFrameInstant(slot)
          return provenanceRows(this.citationFor(ds, { ...this.currentViewState(), atMs: at?.ms ?? null, atPrecision: at ? precisionOfCadence(at.cadenceMs) : null, layers: null }))
        },
      })
    }
    return out
  }

  /**
   * AYNI: load one real-time overlay stream, not yet played (tickRtOverlay plays it on the base's date); null if it fails.
   * A Studio data move may draw any whole-globe stream this way, transparent or not (`anyWholeGlobe`).
   */
  private async startRtOverlay(id: string, opts: { anyWholeGlobe?: boolean; tell?: boolean } = {}): Promise<RtOverlayPlaying | null> {
    const dataset = dataService.getDatasetById(id)
    if (!dataset || !(dataset.rtOverlayCandidate || (opts.anyWholeGlobe && wholeGlobeStream(dataset)))) return null
    const hls = new HLSService()
    const video = hls.createVideo()
    // Looping is for an overlay with no dates to follow (tickRtOverlay);
    // dash.js may end a static presentation rather than honour `loop`.
    video.addEventListener('ended', () => {
      if (video.loop) { video.currentTime = 0; void video.play().catch(() => {}) }
    })
    // A frame found by a seek while everything is paused still has to reach the globe.
    video.addEventListener('seeked', () => this.renderer?.getMap()?.triggerRepaint())
    let encoded: Pick<RtOverlayPlaying, 'lut' | 'crop' | 'phase' | 'encoding'> | null = null
    let colorKey: ColorKey | null = null
    let timelineUrl = dataset.timelineLink
    try {
      // A release is followed to its current MPD, and a value-encoded one
      // (Global Cloud Cover) to its palette, like a base dataset at load.
      // A plain picture release (GeoColor) plays as a picture stream does.
      let mpdUrl = dataset.dataLink
      if (dataset.releaseDescriptorLink) {
        // AYNI (2026-11-04): with the stream token, as the base dataset's
        // load reads it; plain fetch is refused since the gate enforces.
        const release = await resolveDashRelease(dataset.releaseDescriptorLink, streamAuthFetch)
        mpdUrl = release.mpdUrl
        timelineUrl = release.dsaUrl ?? undefined
        if (release.encoding) encoded = this.rtEncoded(id, withKnownRange(release.encoding))
      }
      if (timelineUrl) this.dsaTimelines.prefetch(timelineUrl)
      // AYNI: a picture stream that keys out a colour (colorKey.ts) says so in its .dsa.
      if (!encoded && timelineUrl) {
        const key = (await this.dsaTimelines.loadMetadata(timelineUrl).catch(() => null))?.colorKey
        if (key) colorKey = key
      }
      // AYNI (Quest review A11): a picture overlay may be sent smaller on a phone or headset; values never.
      await hls.loadDash(mpdUrl, video, this.isMobile, !encoded && !dataset.colorScale)
      // AYNI: the codes as this browser's decoder delivers them, measured on
      // the first frame's calibration strip (decoderRange.ts).
      if (encoded?.encoding && await videoHasFrame(video, 4000)) {
        const measured = calibrateRelease(video, encoded.encoding)
        if (measured !== encoded.encoding) encoded = this.rtEncoded(id, measured)
      }
    } catch (err) {
      logger.warn('[App] real-time overlay failed to start:', err)
      hls.destroy()
      video.remove()
      // AYNI (2026-11-04): said, not dropped in silence (in a headset too).
      // A refusal has its own notice already (sign in, or the plans).
      if (opts.tell && !(err instanceof StreamRefusedError)) {
        const message = t('tools.layers.rt.startFailed', { title: displayTitle(dataset) })
        this.announce(message)
        if (isVrActive()) notifyXr(message)
      }
      return null
    }
    logger.info('[App] real-time overlay loaded:', dataset.id)
    return { id: dataset.id, hls, video, ...(encoded ?? {}), ...(colorKey ? { colorKey } : {}), ...(timelineUrl ? { timelineUrl } : {}), visible: true }
  }

  /**
   * AYNI: a real-time overlay's palette and map rectangle for an encoding.
   * With a class map (IMERG's rain/snow), one palette row per class; in the
   * colours chosen for it (this session, else saved for the dataset, else
   * its display group's standard: `rtDisplayOf`), so a temperature overlay
   * matches a temperature base map.
   */
  private rtEncoded(id: string, encoding: ReleaseEncoding): Pick<RtOverlayPlaying, 'lut' | 'crop' | 'phase' | 'encoding'> {
    const colours = overlayColours(encoding, this.rtDisplayOf(id))
    return {
      encoding,
      lut: colours.lut,
      crop: releaseCropRect(encoding),
      ...(colours.phase ? { phase: colours.phase } : {}),
    }
  }

  /**
   * AYNI Studio: a data move between two datasets (crossfade, wipe): the
   * incoming one drawn over the globe at its date with this reveal, or
   * none. Resolves once its frame can be drawn. False when it cannot be an
   * overlay (not a whole-globe stream): the move then changes at its end.
   */
  private async setDataMixLayer(next: { datasetId: string; atMs: number | null; reveal: LayerReveal } | null): Promise<boolean> {
    if (!next) {
      this.dataMixGen++
      if (this.dataMix) {
        this.destroyRtOverlay(this.dataMix.rt)
        this.dataMix = null
        this.composeMapLayers()
      }
      return true
    }
    const fresh = this.dataMix?.id !== next.datasetId
    if (fresh) {
      const gen = ++this.dataMixGen
      if (this.dataMix) this.destroyRtOverlay(this.dataMix.rt)
      this.dataMix = null
      // AYNI (owner, 2026-11-03): a stream only Education plays is not drawn over the globe on any other plan.
      if ((await rtOverlaysAllowed([next.datasetId], (id) => dataService.getDatasetById(id))).refused.length > 0) return false
      if (gen !== this.dataMixGen) return false
      const rt = await this.startRtOverlay(next.datasetId, { anyWholeGlobe: true })
      if (!rt) return false
      if (gen !== this.dataMixGen) {
        this.destroyRtOverlay(rt)
        return false
      }
      this.dataMix = { id: next.datasetId, rt, reveal: next.reveal }
      this.composeMapLayers()
    }
    const mix = this.dataMix!
    mix.reveal = next.reveal
    const video = mix.rt.video
    if (!video.paused) video.pause()
    const timeline = next.atMs !== null && mix.rt.timelineUrl ? await this.dsaTimelines.load(mix.rt.timelineUrl) : null
    const target = timeline && next.atMs !== null ? videoTimeForDateMs(timeline, next.atMs) : null
    // Its frame decoded at the date: a frame drawn before then would be blank (or another date's).
    const settled = () => video.readyState >= 2 && !video.seeking && (target === null || Math.abs(video.currentTime - target) <= 1e-3)
    // `seeked` can come before the frame is there to upload: a new stream, or a seek, waits for it to be presented.
    const presented = fresh || !settled()
      ? new Promise<void>((resolve) => {
        if ('requestVideoFrameCallback' in video) video.requestVideoFrameCallback(() => resolve())
        setTimeout(resolve, 1500)
      })
      : null
    if (!settled()) {
      if (target !== null && Math.abs(video.currentTime - target) > 1e-3) video.currentTime = target
      const until = Date.now() + 8000
      while (!settled() && Date.now() < until) {
        await new Promise<void>((resolve) => {
          const done = () => resolve()
          video.addEventListener('seeked', done, { once: true })
          video.addEventListener('loadeddata', done, { once: true })
          setTimeout(done, 100)
        })
        // A seek asked before the stream could take it is asked again.
        if (target !== null && video.readyState >= 1 && !video.seeking && Math.abs(video.currentTime - target) > 1e-3) video.currentTime = target
      }
    }
    if (presented) await presented
    this.viewports.getPrimary()?.getMap()?.triggerRepaint()
    return true
  }

  /**
   * AYNI Studio (live: the Khipu player, the Studio's preview): change to
   * dataset `id` with a data move while the camera arrives — the new one
   * drawn over the globe (crossfade, wipe) until `load` has put it up, or
   * the old one faded to the Earth and the new one in (through the
   * basemap). Reduced motion, a stream that cannot be drawn so, or a
   * headset only load. Stops once `current` is false.
   */
  private async liveDataMove(id: string, move: LiveDataMove, load: () => Promise<boolean>, current: () => boolean = () => true): Promise<boolean> {
    const renderer = this.viewports.getPrimary()
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    if (!renderer || reduce || isVrActive() || move.seconds <= 0) return load()
    /** Call `frame` with 0 → 1 over `seconds`, one animation frame at a time; false if stopped part way. */
    const run = (seconds: number, frame: (p: number) => void) => new Promise<boolean>((resolve) => {
      const start = performance.now()
      const tick = (now: number) => {
        if (!current()) return resolve(false)
        const p = Math.min(1, (now - start) / (seconds * 1000))
        frame(p)
        if (p < 1) requestAnimationFrame(tick)
        else resolve(true)
      }
      requestAnimationFrame(tick)
    })
    try {
      if (move.data === 'basemap') {
        if (!(await run(move.seconds / 2, (p) => renderer.setDatasetOpacity(1 - p)))) return false
        const ok = await load()
        if (!current()) return ok
        await run(move.seconds / 2, (p) => renderer.setDatasetOpacity(p))
        return ok
      }
      const reveal = (p: number) => this.dataMixReveal(move.data as 'crossfade' | 'wipe', move.wipe, p, move.view)
      if (!(await this.setDataMixLayer({ datasetId: id, atMs: move.atMs, reveal: reveal(0) })) || !current()) return current() ? load() : false
      const done = await run(move.seconds, (p) => {
        const r = reveal(p)
        if (this.dataMix) this.dataMix.reveal = r
        renderer.setDatasetOpacity(move.data === 'crossfade' ? Math.min(1, (1 - p) / 0.3) : 1)
        renderer.setDatasetWipe(r.wipe)
        renderer.getMap()?.triggerRepaint()
      })
      if (!done) return false
      const ok = await load()
      // The new dataset's own first frame on the globe before the layer standing in for it goes.
      const until = Date.now() + 3000
      while (ok && (this.hlsService?.video?.readyState ?? 4) < 2 && Date.now() < until) await new Promise((r) => setTimeout(r, 50))
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
      return ok
    } finally {
      await this.setDataMixLayer(null)
      renderer.setDatasetOpacity(1)
      renderer.setDatasetWipe(null)
    }
  }

  /** AYNI Studio: how a data move shows the incoming dataset at `progress` (0 → 1). */
  private dataMixReveal(move: 'crossfade' | 'wipe', wipe: 'meridian' | 'terminator', progress: number, at: { lat: number; lon: number }): LayerReveal {
    if (move === 'crossfade') return { opacity: progress, wipe: null }
    // A meridian sweeps west to east through the stop; the day–night edge grows from beyond the western limb.
    return wipe === 'terminator'
      ? { opacity: 1, wipe: { kind: 'circle', progress, lat: at.lat, lon: at.lon - 100 } }
      : { opacity: 1, wipe: { kind: 'meridian', progress, lat: at.lat, lon: at.lon } }
  }

  private destroyRtOverlay(rt: RtOverlayPlaying): void {
    rt.hls.destroy()
    rt.video.remove()
  }

  /** Take one overlay off; with none left, stop the sync and hand the base back its own speed. */
  private stopRtOverlay(rt: RtOverlayPlaying): void {
    const at = this.rtOverlays.indexOf(rt)
    if (at < 0) return
    this.rtOverlays.splice(at, 1)
    // Its palette button's dialog, or one whose button now shows another overlay.
    closePaletteDialog()
    this.destroyRtOverlay(rt)
    if (!this.rtOverlays.length) {
      // AYNI (phase 2): the timer stays while another globe still has streams.
      this.ensureRtTimer()
      // Hand the base back its own speed if the sync was pacing it.
      const base = this.hlsService?.video
      if (base && this.rtBaseRateSet !== null && Math.abs(base.playbackRate - this.rtBaseRateSet) < 1e-3) {
        base.playbackRate = this.rtBaseUserRate
      }
      this.rtBaseRateSet = null
      this.rtSpeedCeiling = Infinity
    }
    this.rtHiddenKey = ''
    this.layerPicker?.setRtOverlayStatus([])
    this.updateTimelineTrack()
  }

  /** Take every overlay off at once (home, no dataset). */
  private stopAllRtOverlays(): void {
    this.rtWanted = []
    for (const rt of [...this.rtOverlays]) this.stopRtOverlay(rt)
  }

  /** The loaded dataset's date axis, when it has one: its .dsa, else its declared start and end. */
  private baseClock(dataset: Dataset, video: HTMLVideoElement): LinearClock | null {
    const url = dataset.timelineLink
    if (url) {
      this.dsaTimelines.prefetch(url)
      const timeline = this.dsaTimelines.get(url)
      return timeline ? clockFromTimeline(timeline) : null
    }
    if (!dataset.startTime || !dataset.endTime) return null
    return clockFromRange(Date.parse(dataset.startTime), Date.parse(dataset.endTime), video.duration)
  }

  /**
   * One step of keeping the real-time overlays on the base's date
   * (rtOverlaySync). Each is synced on its own; the base goes at the pace
   * of the slowest, so neither overlay falls behind it.
   */
  private tickRtOverlay(): void {
    if (!this.rtOverlays.length) return
    const dataset = this.appState.currentDataset
    const base = dataset && dataService.isVideoDataset(dataset) ? this.hlsService?.video ?? null : null
    // A rate the sync did not set came from someone else (a Khipu, a new
    // dataset): that is the base's own speed from now on.
    if (base && (this.rtBaseRateSet === null || Math.abs(base.playbackRate - this.rtBaseRateSet) > 1e-3)) {
      this.rtBaseUserRate = base.playbackRate
    }
    const baseClock = base && dataset ? this.baseClock(dataset, base) : null
    let baseRate: number | null = null
    let repaint = false
    let ranges = false
    const hidden: RtHidden[] = []
    // Two passes. First what each overlay would have the base run at; the
    // base runs at the slowest of those. Then each overlay is paced against
    // that rate, the one the base really gets: paced against its own wish,
    // an overlay that wanted the base faster ran ahead of it, was seeked
    // back, and ran ahead again (owner, 2026-10-04: "syncing in steps").
    const inputs = this.rtOverlays.map((rt) => {
      if (rt.timelineUrl) this.dsaTimelines.prefetch(rt.timelineUrl)
      const overlayTimeline = rt.timelineUrl ? this.dsaTimelines.get(rt.timelineUrl) : null
      const overlayClock = overlayTimeline ? clockFromTimeline(overlayTimeline) : null
      if (overlayClock && (rt.range?.startMs !== overlayClock.startMs || rt.range?.endMs !== overlayClock.endMs)) {
        rt.range = { startMs: overlayClock.startMs, endMs: overlayClock.endMs }
        ranges = true
      }
      const v = rt.video
      const input = {
        base: base && dataset ? { currentTime: base.currentTime, paused: base.paused, clock: baseClock } : null,
        overlay: { currentTime: v.currentTime, paused: v.paused, seeking: v.seeking, readyState: v.readyState, clock: overlayClock },
        baseUserRate: this.rtBaseUserRate,
      }
      return { rt, overlayClock, input, wish: decideRtOverlaySync(input) }
    })
    const baseRateLimit = Math.min(...inputs.map((i) => i.wish.baseRate))
    // AYNI (2026-10-20): what the speed bar may offer — the slowest overlay on its dates sets it.
    this.rtSpeedCeiling = Math.min(Infinity, ...inputs
      .filter((i) => i.wish.mode === 'synced')
      .map((i) => rtBaseRateCeiling(baseClock, i.overlayClock)))
    for (const { rt, overlayClock, input } of inputs) {
      const v = rt.video
      const d = decideRtOverlaySync({ ...input, baseRateLimit })
      v.loop = d.mode === 'untimed'
      if (Math.abs(v.playbackRate - d.overlayRate) > 1e-3) v.playbackRate = d.overlayRate
      if (d.seekTo !== null) v.currentTime = d.seekTo
      if (d.playing && v.paused) void v.play().catch(() => {})
      else if (!d.playing && !v.paused) v.pause()
      baseRate = baseRate === null ? d.baseRate : Math.min(baseRate, d.baseRate)
      if (rt.visible !== d.visible) {
        rt.visible = d.visible
        repaint = true
      }
      if (d.mode === 'out-of-range' && overlayClock) {
        const ds = dataService.getDatasetById(rt.id)
        hidden.push({ title: ds ? displayTitle(ds) : rt.id, startMs: overlayClock.startMs, endMs: overlayClock.endMs })
      }
    }
    if (base && baseRate !== null) {
      if (Math.abs(base.playbackRate - baseRate) > 1e-3) base.playbackRate = baseRate
      this.rtBaseRateSet = baseRate
    }
    if (repaint) this.renderer?.getMap()?.triggerRepaint()
    if (ranges) this.updateTimelineTrack()
    const hiddenKey = hidden.map((h) => `${h.title}:${h.startMs}:${h.endMs}`).join('|')
    if (hiddenKey !== this.rtHiddenKey) {
      this.rtHiddenKey = hiddenKey
      this.layerPicker?.setRtOverlayStatus(hidden)
    }
  }

  /**
   * The picker's overlay choices: global transparent streams other than the
   * dataset itself. AYNI: each with its topic (Browse's first theme) and its
   * dates once fetched, and the dataset's own dates, so the list can leave
   * out the streams it shares none with.
   */
  private refreshRtOverlayPicker(): void {
    const dataset = this.appState.currentDataset
    const current = dataset?.id
    const choices = this.rtOverlayCandidates()
      .filter(d => d.id !== current)
      .map(d => ({
        id: d.id,
        title: displayTitle(d),
        thumb: d.thumbnailLink,
        pkg: packageOf(d),
        ...(d.realtimeKind ? { kind: d.realtimeKind } : {}),
        ...this.rtTopicOf(d),
        ...this.rtRangeEntry(d.id),
      }))
    this.layerPicker?.setRtBase(this.baseRange(), dataset ? displayTitle(dataset) : '')
    this.layerPicker?.updateRtOverlay(choices, this.rtWanted, Math.max(1, this.rtBudget()), this.rtPlanCap < this.rtCap)
    // A lesson's real-time variables show whether each plays.
    activeLessonPlayer()?.refresh()
  }

  /** AYNI: a Real time stream's topic and where it sorts, for the browser's card and the headset's page alike. */
  private rtTopicOf(d: Dataset): { topic?: string; topicRank?: number } {
    // The most specific topic wins: a fire or smoke stream is also tagged atmosphere.
    const themes = themesOf(d)
    const theme = themes.includes('fire-aq') ? 'fire-aq' : themes[0]
    return theme ? { topic: t(THEME_LABEL[theme]), topicRank: THEMES.indexOf(theme) } : {}
  }

  /** AYNI: a stream's dates as the Real time lists take them: absent while they come, null when it has none. */
  private rtRangeEntry(id: string): { range?: TimeRange | null } {
    return this.rtRanges.has(id) ? { range: this.rtRanges.get(id) ?? null } : {}
  }

  private rtOverlayCandidates(): Dataset[] {
    // AYNI (owner, 2026-11-03): a stream only Education plays is not offered on any other plan (`rtOverlayOffered`).
    return this.appState.datasets.filter(d => d.rtOverlayCandidate && rtOverlayOffered(d))
  }

  /** AYNI: the loaded dataset's dates: its .dsa's axis, else its catalogue start and end; null when it has none. */
  private baseRange(): TimeRange | null {
    const snap = this.timelineSnapshot()
    if (snap) return { startMs: snap.startMs, endMs: snap.endMs }
    const ds = this.appState.currentDataset
    const start = ds?.startTime ? Date.parse(ds.startTime) : NaN
    const end = ds?.endTime ? Date.parse(ds.endTime) : NaN
    return Number.isFinite(start) && Number.isFinite(end) && end > start ? { startMs: start, endMs: end } : null
  }

  /**
   * AYNI: fetch the dates of every overlay candidate not yet known (the
   * Real time list was opened). A release's come from its release.json
   * (`releaseTimeRange`, cached), a direct stream's from its .dsa. Six at a
   * time; the list is redrawn as they arrive.
   */
  private fetchRtRanges(): void {
    // The dataset's own dates may have arrived since the list was drawn.
    this.refreshRtOverlayPicker()
    if (this.rtRangesFetching) return
    // AYNI (multi-globe plan): with the datasets Browse wants marked.
    const wanted = [...this.rangeWanted].map((id) => dataService.getDatasetById(id)).filter((d): d is Dataset => !!d)
    this.rangeWanted.clear()
    const todo = [...this.rtOverlayCandidates(), ...wanted]
      .filter((d, i, all) => (!this.rtRanges.has(d.id) || this.rtRangesStale.has(d.id)) && all.findIndex((o) => o.id === d.id) === i)
    if (!todo.length) return
    this.rtRangesFetching = true
    let redraw = 0
    const queueRedraw = () => {
      if (redraw) return
      redraw = requestAnimationFrame(() => {
        redraw = 0
        this.refreshRtOverlayPicker()
        refreshBrowseCompatibility()
      })
    }
    const one = async (d: Dataset): Promise<void> => {
      const range = await this.rtRangeOf(d).catch(() => null)
      // A kept range being refreshed stays when the fetch gives nothing (offline, a blip).
      if (range || !this.rtRangesStale.has(d.id)) this.rtRanges.set(d.id, range)
      if (range) this.rtRangesFetchedAt.set(d.id, Date.now())
      this.rtRangesStale.delete(d.id)
      queueRedraw()
    }
    const queue = [...todo]
    const worker = async (): Promise<void> => {
      for (let d = queue.shift(); d; d = queue.shift()) await one(d)
    }
    void Promise.all(Array.from({ length: 6 }, worker)).finally(() => {
      this.rtRangesFetching = false
      saveRtRanges(this.appState.datasets, this.rtRanges, this.rtRangesFetchedAt)
      queueRedraw()
      // Browse may have asked for more while these were fetched.
      if (this.rangeWanted.size) this.fetchRtRanges()
    })
  }

  /** AYNI (owner, 2026-10-20): the dates kept on this device from an earlier visit, for the datasets in the catalogue. */
  private seedRtRanges(): void {
    const { ranges, stale } = loadRtRanges(this.appState.datasets)
    for (const [id, range] of ranges) {
      if (this.rtRanges.has(id)) continue
      this.rtRanges.set(id, range)
      if (stale.has(id)) this.rtRangesStale.add(id)
    }
  }

  /** AYNI: an overlay candidate's dates, end exclusive: its release's, else its .dsa's; null when it has none. */
  private async rtRangeOf(d: Dataset): Promise<TimeRange | null> {
    if (d.releaseDescriptorLink) return releaseTimeRange(d.releaseDescriptorLink, streamAuthFetch)
    if (!d.timelineLink) return null
    const timeline = await this.dsaTimelines.load(d.timelineLink).catch(() => null)
    return timeline ? { startMs: timeline.startMs, endMs: timelineEndMs(timeline) } : null
  }

  /**
   * AYNI (2026-10-09): put the dataset on a date these real-time streams
   * show on. When the date on the globe is outside the dates the dataset and
   * every one of them share, go to the latest such date (a stream outside its
   * own dates is hidden, so a lesson's variable would not show). Asked by a
   * press on a lesson's variable, never by itself. The date gone to (ISO),
   * 'none' when they share no date, null when the date could stay.
   */
  private async showRtDates(ids: readonly string[]): Promise<string | 'none' | null> {
    const base = this.baseRange()
    const now = this.timelineSnapshot()?.currentMs
    if (!base || typeof now !== 'number') return null
    let start = base.startMs
    let end = base.endMs
    for (const id of ids) {
      const d = dataService.getDatasetById(id)
      const r = d ? (this.rtRanges.get(id) ?? (await this.rtRangeOf(d).catch(() => null))) : null
      if (d && !this.rtRanges.has(id)) this.rtRanges.set(id, r)
      if (!r) continue
      start = Math.max(start, r.startMs)
      end = Math.min(end, r.endMs)
    }
    if (end <= start) return 'none'
    if (now >= start && now < end) return null
    const iso = new Date(end - 1).toISOString()
    return this.seekToIsoDate(iso).success ? iso : null
  }

  /** No dataset, no layers: the default Earth has its own base and effects. */
  private clearMapLayers(): void {
    this.rtOverlayGen++
    this.stopAllRtOverlays()
    // AYNI (phase 2): every globe's own stack goes too.
    for (const slot of [...this.otherStacks.keys()]) this.disposeOtherStack(slot)
    this.mapLayerGen++
    this.mapLayerSelection = NO_LAYERS
    this.mapLayerImages = null
    for (const r of this.viewports.getAll()) {
      if (r instanceof MapRenderer) r.setMapLayers(null)
    }
    void fetchLayerCatalog().then(layers => this.layerPicker?.update(layers, NO_LAYERS))
    this.refreshRtOverlayPicker()
  }

  /**
   * AYNI: put a geo-media dataset on the globe: the Earth as it is lit
   * right now, a dot per station or cam, and the panel that plays the
   * one picked. Nothing plays until the visitor picks a dot.
   */
  private async startGeoMedia(
    dataset: Dataset,
    renderer: MapRenderer,
    gen: number,
  ): Promise<void> {
    const markers = await fetchGeoMediaMarkers(dataset.dataLink)
    if (gen !== this.loadGeneration) return
    if (!markers.length) throw new Error(`${dataset.title} has nothing to play right now`)
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    await renderer.loadDefaultEarthMaterials()
    if (gen !== this.loadGeneration) return
    // Day and night as they are now: a cam on the dark side shows the dark.
    const sun = getSunPosition(new Date())
    renderer.enableSunLighting(sun.lat, sun.lng)

    const player = new GeoMediaPlayer({ onChange: () => this.refreshGeoMedia() })
    const map = renderer.getMap()
    const layer = map ? addGeoMediaLayer(map, markers, marker => this.pickGeoMedia(marker)) : null
    const panel = createGeoMediaPanel({
      markers,
      video: player.video,
      image: player.image,
      onPauseResume: () => {
        const { marker, phase } = player.playback
        if (!marker) return
        if (phase === 'unavailable') player.play(marker)
        else if (phase === 'paused') player.resume()
        else player.pause()
      },
      onToggleMute: () => {
        player.setMuted(!player.muted)
        this.refreshGeoMedia()
      },
      onStop: () => player.stop(),
    })
    // Each new still reaches the headset's panel through a version bump.
    player.image.addEventListener('load', () => {
      const geo = this.geoMedia
      if (geo?.player === player) {
        geo.pictureVersion += 1
        this.refreshGeoMedia()
      }
    })
    this.geoMedia = { markers, player, layer, panel, vrState: this.geoMediaVrState(markers, player, 0), pictureVersion: 0 }
    this.refreshGeoMedia()
  }

  /**
   * How close a tap on the immersive globe must land to a marker, in
   * degrees of arc: about 3.5 cm on the half-metre globe, a finger's width.
   */
  private static readonly VR_PICK_REACH_DEG = 4
  /** AYNI (DSA 1.8): how far from a vector feature a tap on the immersive globe still picks it, degrees of arc. */
  private static readonly VR_VECTOR_REACH_DEG = 1.5

  private geoMediaVrState(markers: readonly GeoMediaMarker[], player: GeoMediaPlayer, pictureVersion: number): VrGeoMediaState {
    return {
      markers,
      playback: player.playback,
      isUnavailable: marker => !marker.online || player.isUnavailable(marker.id),
      video: player.video,
      image: player.image,
      pictureVersion,
    }
  }

  /**
   * A pick of a place on either globe: the marker there plays, the one
   * already playing stops when it is picked again, and markers that
   * share a place are taken in turn.
   */
  private pickGeoMedia(marker: GeoMediaMarker): void {
    const geo = this.geoMedia
    if (!geo) return
    const { marker: active, phase } = geo.player.playback
    if (active?.id === marker.id && phase !== 'unavailable') geo.player.stop()
    else geo.player.play(marker)
  }

  /**
   * A press on the immersive globe. AYNI (the AR refresh): A / X in a
   * headset always asks for a pin ('pin'); a phone's tap drops one in pin
   * mode, the owner's one exception to a phone's touch being spin and tilt
   * only (2026-10-02) ('touch'); a headset's trigger only turns the globe
   * (owner, 2026-10-04) and picks a geo-media marker ('select').
   */
  private onVrGlobeTap(lat: number, lon: number, kind: VrGlobeTapKind = 'touch'): void {
    // AYNI (meeting games plan): a place round is answered on the globe: a
    // phone's tap, a headset's trigger or A / X (Quest review A1, 2026-11-03:
    // the headset's presses never reached the round).
    if (gameVrTap(lat, lon)) return
    if (kind === 'pin' || (kind === 'touch' && this.xrPins?.picking())) {
      this.dropXrPin(lat, lon)
      return
    }
    // AYNI (DSA 1.8): a vector feature under the tap opens its panel beside the globe.
    if (this.pickVectorInXr(lat, lon)) return
    const geo = this.geoMedia
    if (!geo) return
    const active = geo.player.playback.marker?.id ?? null
    const marker = pickGeoMediaMarker(geo.markers, lat, lon, InteractiveSphere.VR_PICK_REACH_DEG, active)
    if (marker) this.pickGeoMedia(marker)
  }

  /** AYNI: a pin asked for in a session, or the reason there is none. */
  private dropXrPin(lat: number, lon: number): void {
    const pins = this.xrPins
    if (!pins || !this.xrPinPick) return
    if (this.pinsRefusedByMeeting()) {
      notifyXr(t('vr.notice.presenter'))
      return
    }
    const r = pins.readability()
    if (r !== 'release' && r !== 'encoded') {
      notifyXr(t(r === 'plan' ? 'vr.data.plan' : r === 'picture' ? 'vr.data.picture' : 'vr.data.none'))
      return
    }
    if (pins.store.full) {
      notifyXr(pins.store.limit < MAX_PINS ? t('vr.data.fullPlan', { count: pins.store.limit }) : t('vr.data.full'))
      return
    }
    this.xrPinPick(lat, lon)
  }

  /** Bring the dots and the panel in line with what the geo-media player is doing. */
  private refreshGeoMedia(): void {
    const geo = this.geoMedia
    if (!geo) return
    const playback = geo.player.playback
    const on = playback.marker && playback.phase !== 'unavailable' ? playback.marker.id : null
    geo.layer?.setStatus(on, marker => !marker.online || geo.player.isUnavailable(marker.id))
    geo.panel.update(playback, geo.player.muted)
    geo.vrState = this.geoMediaVrState(geo.markers, geo.player, geo.pictureVersion)
    updateMapControlsPosition()
  }

  private stopGeoMedia(): void {
    const geo = this.geoMedia
    if (!geo) return
    this.geoMedia = null
    geo.player.dispose()
    geo.layer?.dispose()
    geo.panel.dispose()
    updateMapControlsPosition()
  }

  /**
   * AYNI — follow a release row's `latest.json` to the release current
   * right now, and point the row at its MPD, its `.dsa` (date track and
   * the docent) and its value encoding (palette, crop and decoding, on
   * the browser globe and the immersive one). Resolved at
   * every load, not once, because the publisher replaces releases as
   * data arrives.
   */
  private async resolveRelease(dataset: Dataset): Promise<void> {
    // AYNI: an own dataset's pointer and descriptor need its token (privateData.ts); others are unchanged.
    const release = await resolveDashRelease(dataset.releaseDescriptorLink!, streamAuthFetch)
    dataset.dataLink = release.mpdUrl
    dataset.timelineLink = release.dsaUrl ?? undefined
    // AYNI: in the space this browser's decoder was last measured in (decoderRange.ts); the load measures again.
    dataset.releaseEncoding = release.encoding ? withKnownRange(release.encoding) : undefined
  }

  /**
   * AYNI — a picture stream whose .dsa says `alphaMode: "color_key"`
   * draws that colour transparent on every globe (colorKey.ts). Read at
   * each load from the cached .dsa; a descriptor that cannot be read
   * leaves the stream drawn as before.
   */
  private async resolveColorKey(dataset: Dataset): Promise<void> {
    if ((dataset.format !== 'application/dash+xml' && !isStillPicture(dataset)) || !dataset.timelineLink) return
    const key = (await this.dsaTimelines.loadMetadata(dataset.timelineLink).catch(() => null))?.colorKey ?? null
    if (key) dataset.colorKey = key
    else if (dataset.colorKey) delete dataset.colorKey
  }

  /**
   * AYNI (owner, 2026-10-14) — every body but the Earth gets the Sun's
   * treatment. The index names no body yet, so a stream whose `.dsa`
   * does (a `body:io` keyword, or a `celestialBody` field;
   * offEarthBody.ts) is marked here, before any globe draws it: no
   * automatic layers, no ground or Earth tiles under it (celestialBody),
   * and no place on Browse's Map view. Read from the cached .dsa; one
   * that cannot be read leaves the row as the index made it. The Sun's
   * rows already carry their fields from the index, and so does a row the
   * index (catalog 1.5 `celestialBody`) names a body for. A still picture
   * (catalog 1.5) is read the same way.
   */
  private async resolveCelestialBody(dataset: Dataset): Promise<void> {
    if ((dataset.format !== 'application/dash+xml' && !isStillPicture(dataset)) || !dataset.timelineLink) return
    const body = (await this.dsaTimelines.loadMetadata(dataset.timelineLink).catch(() => null))?.celestialBody
    if (!body) return
    markOffEarth(dataset, body)
  }

  /** Resolve, render, and apply a dataset (image or video) to the sphere. */
  private async displayDataset(
    datasetId: string,
    gen: number,
    trigger: import('./types').LoadTrigger = 'default',
    loadStartWall: number = Date.now(),
    /** AYNI: the room put it up (a follower): the lead's globe decides the rest. */
    fromRoom = false,
    /** AYNI (multi-globe plan): the globe to load into; the primary when absent. */
    slot?: number,
  ): Promise<void> {
    const dataset = dataService.getDatasetById(datasetId)
    if (!dataset) throw new Error(`Dataset not found: ${datasetId}`)
    const toPrimary = slot === undefined || slot === this.viewports.getPrimaryIndex()
    // AYNI: a release row is resolved to its current MPD, .dsa and
    // encoding at every load.
    if (dataset.releaseDescriptorLink) await this.resolveRelease(dataset)
    // AYNI: a picture stream's colour key, from its .dsa, before the globe
    // draws it (colorKey.ts); a plain picture release is one too.
    if (!dataset.releaseEncoding) await this.resolveColorKey(dataset)
    // AYNI (owner, 2026-10-14): a stream whose .dsa names another body starts bare, as the Sun does.
    await this.resolveCelestialBody(dataset)

    // §9.2 — count an open as a visit for the Continue-exploring row.
    // viewSeconds still accrues separately from info-panel reading time
    // (datasetLoader).
    recordVisit(dataset.id)

    // Pin the target slot + its renderer once, at the very start. Both
    // must stay fixed for the lifetime of this call — the awaits below
    // span image decode / HLS manifest fetches, during which the user
    // can promote a different panel to primary. Reading `this.renderer`
    // (a live getter over the primary) after an await would then apply
    // the new dataset's texture to a different panel than the one we
    // recorded in `panelStates`, leaving the two in disagreement and
    // letting a single load bleed across globes.
    const targetSlot = slot ?? this.viewports.getPrimaryIndex()
    const targetRenderer = this.viewports.getRendererAt(targetSlot)
    if (!targetRenderer) throw new Error('Renderer not initialized')

    // AYNI (multi-globe plan): another globe's dataset is its own; the app's
    // current dataset (the transport, the info card, the address) is the primary's.
    if (toPrimary) this.appState.currentDataset = dataset
    if (this.panelStates[targetSlot]) {
      this.panelStates[targetSlot].dataset = dataset
    }
    // AYNI (owner, 2026-10-23): satellites only around the Earth.
    this.syncSatelliteBody()
    // Register the dataset's attribution as a phantom MapLibre
    // source so Tools → Credits picks it up. See creditsPanel.ts
    // for the design. No-op on non-MapRenderer surfaces.
    if (targetRenderer instanceof MapRenderer) {
      targetRenderer.setDatasetCredits(dataset)
    }

    logger.info('[App] Loading dataset:', {
      id: dataset.id,
      title: dataset.title,
      format: dataset.format,
      slot: targetSlot,
      hasTimeData: !!(dataset.startTime && dataset.endTime)
    })

    // Loading a new dataset into the primary resets any picker
    // override — the user probably wants to see their fresh load.
    if (toPrimary) this.infoDisplayOverride = null
    this.renderInfoPanel()
    this.refreshPanelLegends()

    const loaderCallbacks = {
      showPlaybackControls: (show: boolean) => this.showPlaybackControls(show),
      showTimeLabel: (show: boolean) => this.showTimeLabel(show),
    }
    // AYNI: a load into another globe leaves the singular playback UI alone (datasetLoader `isPrimary`).
    const loaderOptions = { isPrimary: toPrimary }

    // Show a per-panel loading overlay so the user sees "Loading…"
    // instead of the confusing Blue Marble intermediate state. Wrap
    // the whole async body in try/finally so the overlay always
    // clears on the slot we pinned — even if the load throws and
    // even if the user promotes a different panel mid-flight.
    this.viewports.setPanelLoading(targetSlot, true, `Loading ${dataset.title}\u2026`)

    try {
      if (dataset.format === 'khipu/story' || isGeoMediaDataset(dataset)) {
        // AYNI: a story plays its Khipu, and geo-media goes with the primary globe; neither fits another globe.
        if (!toPrimary) throw new Error(`Only the primary globe can show ${dataset.format}`)
      }
      if (dataset.format === 'khipu/story') {
        // AYNI: a story row plays its Khipu (`/api/stories`).
        await this.playDatasetKhipu(dataset, gen, true)
        return
      } else if (isGeoMediaDataset(dataset)) {
        if (this.panelStates[targetSlot]) this.panelStates[targetSlot].image = null
        await this.startGeoMedia(dataset, targetRenderer, gen)
        if (gen !== this.loadGenFor(toPrimary, targetSlot)) return
      } else if (dataService.isImageDataset(dataset)) {
        const img = await loadImageDataset(dataset, targetRenderer, this.appState, this.isMobile, loaderCallbacks, loaderOptions)
        if (gen !== this.loadGenFor(toPrimary, targetSlot)) return
        if (this.panelStates[targetSlot]) this.panelStates[targetSlot].image = img
        this.emitLayerLoaded(dataset, targetSlot, trigger, 'image', Date.now() - loadStartWall)
        // No playback loop runs for a picture: put up its .dsa's vector layers (a catalog 1.5
        // still picture), or take down the last dataset's, now.
        this.syncVectorLayers()
      } else if (dataService.isVideoDataset(dataset)) {
        // Clear any previously-cached image element for this slot —
        // if the user just switched from an image dataset to a video
        // dataset, the old decoded image is no longer referenced and
        // should be released so its backing bytes can be GC'd.
        if (this.panelStates[targetSlot]) this.panelStates[targetSlot].image = null
        const result = await loadVideoDataset(
          dataset, targetRenderer, this.appState, this.isMobile, this.playback, loaderCallbacks, loaderOptions
        )
        // If a newer load started while we were awaiting, discard these results.
        // Don't dispose videoTexture here — setVideoTexture already placed it on
        // the sphere material, so the next load's setVideoTexture will replace it.
        if (gen !== this.loadGenFor(toPrimary, targetSlot)) {
          result.hlsService.destroy()
          return
        }
        this.storePanelVideoResult(targetSlot, result)
        // The primary's sync takes the new globe in either way: as the clock, or as a follower.
        this.attachPrimaryVideoSync()
        if (toPrimary) this.doStartPlaybackLoop()
        this.emitLayerLoaded(dataset, targetSlot, trigger, 'hls', Date.now() - loadStartWall)
      } else {
        throw new Error(`Unsupported format: ${dataset.format}`)
      }
    } finally {
      // Guard against a superseded load hiding the newer load's
      // overlay: only clear if we're still the most recent load.
      if (gen === this.loadGenFor(toPrimary, targetSlot)) {
        this.viewports.setPanelLoading(targetSlot, false)
      }
    }

    this.defaultLayersPending = this.applyDefaultMapLayers(dataset, targetSlot)
    // AYNI (multi-globe plan): each globe's own, for a render that waits on all of them.
    this.defaultLayersBySlot.set(targetSlot, this.defaultLayersPending)

    // AYNI: a NOAA dataset with a Khipu (`/api/stories`) opens as it
    // says (its camera, date, speed and borders), or plays its story. Not
    // while a Khipu plays (the Khipu decides what the globe does), nor for
    // a room's follower (the lead's globe does). The dataset is already
    // up; the Khipu is optional. Nor while a lesson plays: its step chose
    // this dataset, and the story would close the lesson (2026-10-30).
    if (toPrimary && gen === this.loadGenFor(toPrimary, targetSlot) && !activeKhipuPlayer()?.isOpen() && !activeLessonPlayer()?.isOpen() && !fromRoom) {
      await this.playDatasetKhipu(dataset, gen, false)
    }
  }

  /**
   * AYNI: the story Khipu a catalogue dataset has (`storyService`). An
   * `opening` puts its one step on the globe quietly; a `story` (or a
   * story row of its own, `storyRow`) opens in the Khipu player. Behind the
   * plan's `khipus` switch.
   */
  private async playDatasetKhipu(dataset: Dataset, gen: number, storyRow: boolean): Promise<void> {
    if (!currentEntitlements().khipus) return
    const entry = await storyFor(dataset.id, fetch, dataset.legacyIds)
    if (!entry || gen !== this.loadGeneration) {
      if (storyRow && !entry) this.announce(t('khipu.story.unavailable', { title: dataset.title }))
      return
    }
    const k = await fetchStory(entry.slug)
    if (!k || gen !== this.loadGeneration || activeKhipuPlayer()?.isOpen()) return
    if (entry.role === 'opening' && !storyRow) {
      // Quietly: no pins, which nothing would take away again.
      await applyStepToGlobe(this.khipuPlayerHost(), planStep(k, k.path.steps[0], getLocale()), () => gen === this.loadGeneration, { pins: false, lang: getLocale(), keepFlat: true }, k)
      return
    }
    activeLessonPlayer()?.close()
    openKhipuPlayer(k, this.khipuPlayerHost())
  }

  /**
   * Tells the Analyze panel when the globe has stopped on a frame.
   *
   * Reads the transport rather than subscribing to it, because the
   * playback loop below already runs every frame and a second
   * requestAnimationFrame on a page whose performance story is a WebGL
   * globe would be a poor trade for a signal that only matters when
   * nothing is moving. `playbackSettle` owns the decision about what
   * counts as settled; this only supplies the sample.
   *
   * A missing video is reported as `paused` with no playhead — an image
   * dataset has nothing to settle on, and the detector treats that as
   * "forget where you were" rather than as a still frame.
   */
  private readonly playbackSettle = createPlaybackSettleWatcher(
    () => {
      const video = this.hlsService?.getVideo() ?? null
      return video
        ? { playhead: video.currentTime, paused: video.paused, seeking: video.seeking }
        : { playhead: null, paused: true }
    },
    () => {
      notifyAnalyzePlaybackSettled()
      // The transport has stopped on a frame, so every sibling's
      // position is finally stable enough to be worth reading back.
      this.verifySiblingTimes()
    },
  )

  /** Start the requestAnimationFrame playback loop that syncs the scrubber, time label, and auto-loop. */
  private doStartPlaybackLoop(): void {
    startPlaybackLoop(
      this.playback,
      this.hlsService,
      this.videoTexture,
      this.appState,
      (time) => this.updateVideoTimeLabel(time),
      () => this.renderer?.getMap()?.triggerRepaint(),
      // Per-frame sibling drift correction. The primary video is the
      // master clock; siblings are kept in temporal lockstep here
      // rather than free-running on a once-set playbackRate (which
      // integrates duration-imprecision error into visible drift).
      () => {
        this.correctSiblingDrift()
        // Any time notice describes a settled frame; once the playhead
        // is moving again it describes nothing. correctSiblingDrift has
        // already returned above unless the primary is playing.
        if (this.primaryVideoSyncActive) this.clearSiblingTimeNoticesWhileMoving()
        // Cheap on every frame — a comparison and a subtraction — and it
        // has to be sampled at frame rate to know when the playhead
        // stopped. The loop already treats a throw here as something to
        // log and survive, and the watcher swallows its own.
        this.playbackSettle.tick(performance.now())
        this.syncVectorLayers()
      },
    )
  }

  /**
   * AYNI (DSA 1.8): keeps every globe's vector layers on its dataset's
   * `.dsa` (the main globe on `?vectorPreview`, every globe on `?vectorDsa`), and
   * the video a vector layer supersedes hidden on whatever renderer holds
   * that slot now. Cheap: a string comparison per globe per frame, from the
   * playback loop, the players' own loops and the headset's.
   */
  private syncVectorLayers(): void {
    const count = this.panelStates.length
    const primary = this.viewports.getPrimaryIndex()
    for (let slot = 0; slot < Math.max(count, this.vectorPlayers.length); slot++) {
      let url = slot < count ? (this.panelStates[slot]?.dataset?.timelineLink ?? null) : null
      if (slot === primary && this.vectorPreview) url = this.vectorPreview
      else if (url && this.vectorOverride) url = this.vectorOverride
      if (url !== (this.vectorWanted[slot] ?? null)) {
        this.vectorWanted[slot] = url
        this.vectorPlayers[slot]?.clear()
        if (slot === primary) this.vectorXrInspection = null
        this.vectorLoads[slot] = url ? this.showVectorLayers(slot, url) : Promise.resolve()
        // The legend follows (a vector legend replaces or joins the dataset's).
        queueMicrotask(() => this.refreshPanelLegends())
      }
      const player = this.vectorPlayers[slot]
      this.viewports.getRendererAt(slot)?.setDatasetSuperseded(!!player?.layers.length && player.state.hidesVideo)
    }
  }

  /** The vector player of a globe, made on first use. */
  private vectorPlayerAt(slot: number): VectorPlayer {
    let player = this.vectorPlayers[slot]
    if (!player) {
      player = new VectorPlayer(() => this.viewports.getRendererAt(slot)?.getMap() ?? null, {
        onFrame: () => this.syncVectorLayers(),
        canInspect: () => !this.viewports.getRendererAt(slot)?.isPickingPlaces() && !this.dataPinsUi?.isPicking(),
        lang: () => getLocale(),
        renderInspection: (inspection) => inspectionElement(inspection, t('vector.inspect.link')),
      })
      // A Studio render moves the clock itself, frame by frame (§6).
      if (isRenderMode()) player.setManual(true)
      this.vectorPlayers[slot] = player
    }
    return player
  }

  private async showVectorLayers(slot: number, url: string): Promise<void> {
    const preview = url === this.vectorPreview
    try {
      // A developer switch's .dsa is read as is; a dataset's comes through the timeline cache.
      const vectors = preview || url === this.vectorOverride
        ? parseDsaVectors(await (await streamAuthFetch(url)).json())
        : await this.dsaTimelines.loadVectors(url)
      if (!vectors || this.vectorWanted[slot] !== url) return
      await this.vectorPlayerAt(slot).show(
        url, vectors,
        // A still picture (catalog 1.5) has no video: its timed layers play on their own clock (DSA 1.8 §6).
        (clock) => (preview || isStillPicture(this.panelStates[slot]?.dataset)
          ? ownClock(clock)
          : videoClock(() => this.panelStates[slot]?.hlsService?.getVideo() ?? null, clock)),
        streamAuthFetch,
      )
      if (this.vectorWanted[slot] !== url) return
      this.syncVectorLayers()
      this.refreshPanelLegends()
    } catch (err) {
      logger.warn('[vector] could not show the vector layers of', url, err)
    }
  }

  /**
   * A globe's vector legends (§8), and whether they replace the dataset's
   * own: they do when a drawn vector layer supersedes the dataset's video
   * or its legend picture.
   */
  private vectorLegendAt(slot: number): { models: VectorLegendModel[]; replaces: boolean } {
    const player = this.vectorPlayers[slot]
    if (!player?.layers.length) return { models: [], replaces: false }
    const lang = getLocale()
    const models = player.layers.map((l) => vectorLegendOf(l, lang)).filter((m): m is VectorLegendModel => m !== null)
    return { models, replaces: player.state.hidesVideo || player.state.hidesLegend }
  }

  /**
   * AYNI (DSA 1.8): each globe's vector layers as the last frame drew them,
   * by slot, for the headset's globes. Ticks the players, because the
   * window's animation frames stop in an immersive session.
   */
  private vectorFramesForXr(): Array<{ version: number; layers: readonly VectorFrameLayer[] } | null> {
    this.syncVectorLayers()
    const now = performance.now()
    return this.panelStates.map((_, slot) => {
      const player = this.vectorPlayers[slot]
      if (!player?.layers.length) return null
      player.tick(now)
      return { version: player.version, layers: player.frameLayers }
    })
  }

  /** AYNI (DSA 1.8): the main globe's vector layers as the chatbot reads them (`features.query`); null without any. */
  private vectorFeaturesForChat(): XrChatFeatureSource | null {
    this.syncVectorLayers()
    const player = this.vectorPlayers[this.viewports.getPrimaryIndex()]
    if (!player?.layers.length) return null
    const nowS = player.dataTime()
    return {
      layers: player.layers,
      clock: player.dataClock,
      nowS,
      shown: (layer) => player.featuresAt(layer, nowS),
      playing: this.appState.isPlaying,
    }
  }

  /** AYNI (DSA 1.8): a tap on the immersive globe on a vector feature opens its panel; elsewhere it closes it. */
  private pickVectorInXr(lat: number, lon: number): boolean {
    const player = this.vectorPlayers[this.viewports.getPrimaryIndex()]
    if (!player?.layers.length) return false
    const hit = player.pickAt(lat, lon, InteractiveSphere.VR_VECTOR_REACH_DEG)
    if (!hit) {
      const had = this.vectorXrInspection !== null
      this.vectorXrInspection = null
      return had
    }
    const inspection = player.inspect(hit)
    const text = [inspection.title, ...inspection.rows.map((r) => `${r.label}: ${r.value}`)].join('\n')
    this.vectorXrInspection = { key: `${hit.layer.order}:${hit.feature.index}`, text }
    return true
  }

  /** AYNI: the bare Earth, as a Khipu step with no dataset asks: like goHome, without opening Browse. */
  private async clearToEarth(): Promise<void> {
    await this.unloadAllPanels()
    this.clearMapLayers()
    this.appState.currentDataset = null
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    const infoPanel = document.getElementById('info-panel')
    if (infoPanel) {
      infoPanel.classList.add('hidden')
      infoPanel.classList.remove('expanded')
    }

    if (this.renderer) {
      await this.renderer.loadDefaultEarthMaterials()
      const sun = getSunPosition(new Date())
      this.renderer.enableSunLighting(sun.lat, sun.lng)
    }
  }

  /**
   * The primary dataset's declared time axis, or null when it declares
   * none. One implementation for both surfaces: the VR session asks for it
   * per XR frame and the 2D track per animation frame, and both get the
   * same answer from the same cache.
   *
   * `prefetch` is idempotent and fire-and-forget, so calling this from a
   * render loop costs a map lookup. A dataset with no annotation — every
   * catalog row — and one whose annotation has not arrived answer null
   * alike, and both surfaces simply draw no track.
   */
  private timelineSnapshot(): TimelineTrackState | null {
    const url = this.appState.currentDataset?.timelineLink
    if (!url) return null
    this.dsaTimelines.prefetch(url)
    const timeline = this.dsaTimelines.get(url)
    if (!timeline) return null
    const video = this.hlsService?.video
    return {
      startMs: timeline.startMs,
      // The exclusive end of the axis, derived from the frames rather
      // than from `timeRange.end`: the declared end is either exclusive
      // or the last frame's stamp depending on the file, and a track
      // wants one meaning. AYNI: through the frame calendar, so a monthly
      // axis ends a calendar month after its last frame.
      endMs: timelineEndMs(timeline),
      currentMs: video
        ? dateAtVideoTimeMs(timeline, video.currentTime)
        : timeline.startMs,
      frameCount: timeline.frameCount,
      cadenceMs: timeline.cadenceMs,
      axis: timelineAxis(timeline),
      availabilitySpans: timeline.availability.spans,
      bands: this.platformBands(url),
      // The drag state belongs to whoever owns the pointer; the surfaces
      // add it themselves.
      scrubbing: false,
    }
  }

  /**
   * AYNI — put the credit a stream's publisher asks for (its .dsa's
   * `attribution`) into the info panel once the descriptor has arrived.
   * The panel is drawn first, so this fills the slot it leaves.
   */
  /**
   * AYNI (owner, 2026-10-23): where the data on the primary globe came
   * from, for the Satellites layer (`sourcePlatforms.ts`): the primary
   * dataset's platforms (DSA 1.10; GeoColor carries them live since
   * 2026-10-24) and those of the real-time overlays showing over it,
   * with the frame's instant so each one's window is read.
   * From the cached descriptors only, so it costs nothing per call.
   */
  /**
   * AYNI (satellites ↔ catalog plan, step 2): the dataset's satellites as
   * bands on its date track, one per platform entry with a window (an entry
   * without one covers the whole axis and is left off: nothing to show).
   * A whole-year window that overlaps another's is marked approximate
   * (VPS, 2026-10-24: GOES generations' windows are whole years and overlap).
   */
  private platformBands(url: string | undefined): TimelineBand[] {
    const meta = url ? this.dsaTimelines.getMetadata(url) : null
    const sats = (meta?.platforms ?? []).filter((p) => p.kind === 'satellite' && (p.fromMs !== null || p.toMs !== null))
    if (!sats.length) return []
    const lang = getLocale()
    const wholeYear = (ms: number | null): boolean => ms === null || (new Date(ms).getUTCMonth() === 0 && new Date(ms).getUTCDate() === 1 && ms % 86_400_000 === 0)
    const overlaps = (a: DsaPlatform, b: DsaPlatform): boolean =>
      (a.fromMs ?? -Infinity) < (b.toMs ?? Infinity) && (b.fromMs ?? -Infinity) < (a.toMs ?? Infinity)
    return sats.map((p) => ({
      startMs: p.fromMs,
      endMs: p.toMs,
      label: platformName(p, lang),
      approximate: wholeYear(p.fromMs) && wholeYear(p.toMs) && sats.some((q) => q !== p && overlaps(p, q)),
    }))
  }

  private satelliteSourcesNow(): SourceInput {
    const platforms: DsaPlatform[] = []
    const seen = new Set<string>()
    const add = (id: string, url: string | undefined): void => {
      const meta = url ? this.dsaTimelines.getMetadata(url) : null
      for (const p of meta?.platforms ?? []) {
        const key = p.kind === 'satellite' ? `sat:${p.norad}` : `${id}:${p.id}`
        if (seen.has(key)) continue
        seen.add(key)
        platforms.push(p)
      }
    }
    const ds = this.appState.currentDataset
    if (ds) add(ds.id, ds.timelineLink)
    for (const rt of this.rtOverlays) if (!rt.userHidden) add(rt.id, rt.timelineUrl)
    return { platforms, atMs: this.currentFrameMs() }
  }

  private async showStreamCredit(dataset: Dataset): Promise<void> {
    const url = dataset.timelineLink
    if (!url) return
    const meta = await this.dsaTimelines.loadMetadata(url)
    if (meta?.attribution) renderStreamCredit(dataset.id, meta.attribution)
  }

  // ---------------------------------------------------------------------
  // AYNI — cite and reopen a view (docs/AYNI_XR_RESEARCH_TIER_PLAN.md, slice 1)

  /**
   * The instant of the frame on the primary globe: from the stream's
   * `.dsa` axis, else the catalogue row's dates; null for a dataset
   * without dates or an image.
   */
  private currentFrameMs(): number | null {
    const ds = this.appState.currentDataset
    const video = ds && dataService.isVideoDataset(ds) ? this.hlsService?.video : null
    if (!ds || !video) return null
    const timeline = this.timelineSnapshot()
    // The frame's own stamp on the stream's calendar (`dateAtVideoTimeMs`), not the playhead between frames.
    if (timeline?.currentMs != null) return timeline.currentMs
    const duration = this.hlsService?.duration ?? video.duration
    if (!ds.startTime || !ds.endTime || !(duration > 0)) return null
    const start = Date.parse(ds.startTime)
    const end = Date.parse(ds.endTime)
    if (!(end > start)) return null
    return videoTimeToDate(video.currentTime, duration, new Date(start), new Date(end)).getTime()
  }

  /**
   * AYNI (several licences on one account, decision M1, 2026-10-10): after
   * the licence in use changes, keep what the new plan allows and close a
   * stream outside its packages on any globe. A locked stream on the main
   * globe goes home, as a refused load would never have opened it (the
   * camera stays). Everything else (globes past its count, the data and
   * research tools, lessons, Lab space, My data, the chatbot) follows the
   * account by itself. Following a room closes nothing: watching what
   * someone shows is free. `notice`: why the licence changed by itself
   * (M6), said first.
   */
  private async afterLicenceChange(changed: boolean, notice: string | null): Promise<void> {
    const closed: string[] = []
    if (changed && !this.roomFollowing) {
      const primary = this.viewports.getPrimaryIndex()
      let primaryLocked = false
      for (let slot = 0; slot < this.panelStates.length; slot++) {
        const ds = this.panelStates[slot]?.dataset
        if (!ds || !(await streamLockedBy(ds))) continue
        closed.push(displayTitle(ds))
        if (slot === primary) {
          primaryLocked = true
          continue
        }
        this.emitLayerUnloadedForSlot(slot, 'replaced')
        await this.unloadPanelDataset(slot)
      }
      // AYNI (owner, 2026-11-03): a real-time overlay only Education plays stops too, on every globe.
      const datasetOf = (id: string) => dataService.getDatasetById(id)
      const mainRt = await rtOverlaysAllowed(this.rtWanted, datasetOf)
      if (mainRt.refused.length > 0) {
        closed.push(...mainRt.refused.map((d) => displayTitle(d)))
        await this.setRtOverlays(mainRt.allowed)
      }
      for (const [slot, stack] of [...this.otherStacks]) {
        const otherRt = await rtOverlaysAllowed(stack.rtWanted, datasetOf)
        if (otherRt.refused.length === 0) continue
        closed.push(...otherRt.refused.map((d) => displayTitle(d)).filter((title) => !closed.includes(title)))
        await this.setOtherRtOverlays(slot, otherRt.allowed)
      }
      if (primaryLocked) await this.goHome()
      else if (closed.length > 0) this.refreshPanelLegends()
      this.syncStudioButton()
    }
    const licence = accountState().licence
    const plan = licence?.plan ? planName(licence.plan) : t('xr.mine.aLicence')
    const said = [
      notice,
      changed && closed.length > 0 ? t('account.switch.closed', { plan, list: closed.join(', ') }) : null,
    ].filter((line): line is string => line !== null)
    if (changed) this.announce(said.length > 0 ? said.join(' ') : t('account.switch.done', { plan }))
    if (said.length === 0) return
    if (isVrActive()) notifyXr(said.join(' '))
    showLicenceBanner({
      text: said.join(' '),
      onPlan: (trigger) => openYourPlan(trigger, { onStartMeeting: (t2: HTMLElement) => openMeetingPanel(t2) }),
      onDismiss: notice ? () => void dismissLicenceNotice() : undefined,
    })
  }

  /**
   * AYNI Studio: the bar's Studio button, on the plans with render credits
   * (Media Desk, Newsroom) once a dataset is up; pressed while the panel is
   * open. Bound once, on the first tick that finds it.
   */
  private syncStudioButton(): void {
    const btn = document.getElementById('tools-bar-studio') as HTMLButtonElement | null
    if (!btn) return
    if (!btn.dataset.bound) {
      btn.dataset.bound = '1'
      btn.addEventListener('click', () => {
        if (isStudioOpen()) closeStudio()
        else {
          openStudio(this.studioHost(), btn)
          this.announce(t('tools.announce.studioOpened'))
        }
        this.syncStudioButton()
      })
    }
    const show = currentEntitlements().credits > 0 && !!this.appState.currentDataset && !document.body.classList.contains('render-mode')
    if (btn.hidden === show) btn.hidden = !show
    const open = String(isStudioOpen())
    if (btn.getAttribute('aria-pressed') !== open) btn.setAttribute('aria-pressed', open)
  }

  /** AYNI Studio: what the Studio panel reads of the primary globe and drives. */
  private studioHost(): StudioHost {
    const map = () => this.viewports.getPrimary()?.getMap() ?? null
    return {
      datasetId: () => this.appState.currentDataset?.id ?? null,
      datasetTitle: () => this.appState.currentDataset?.title ?? '',
      view: () => {
        const m = map()
        if (!m) return null
        const c = m.getCenter()
        const round = (v: number, d: number) => Math.round(v * 10 ** d) / 10 ** d
        return { lat: round(c.lat, 4), lon: round(c.lng, 4), zoom: round(Math.min(9.99, m.getZoom()), 2), bearing: round(((m.getBearing() % 360) + 360) % 360, 1), pitch: round(Math.min(85, m.getPitch()), 1) }
      },
      dateMs: () => this.currentFrameMs(),
      playing: () => this.appState.isPlaying,
      dateRange: () => {
        const snap = this.timelineSnapshot()
        return snap && snap.endMs > snap.startMs ? { startMs: snap.startMs, endMs: snap.endMs } : null
      },
      setView: (v) => map()?.jumpTo({ center: [v.lon, v.lat], zoom: v.zoom, bearing: v.bearing, pitch: v.pitch }),
      seekToMs: (ms) => this.seekTimelineDate(ms),
      formatDate: (ms) => frameLabelNow(ms) ?? new Date(ms).toISOString().slice(0, 10),
      announce: (message) => this.announce(message),
      scene: () => this.globeScene(),
      titleOf: (id) => dataService.getDatasetById(id)?.title ?? null,
      layerTitleOf: (id) => this.layerTitles.get(id) ?? null,
      showStep: (k, index) => this.showKhipuStep(k, index),
      previewFilm: (k, film, onEnd) => this.previewFilm(k, film, onEnd),
      language: () => getLocale(),
    }
  }

  /** AYNI Studio: what live motion moves and paints on the primary globe. */
  private liveMotionHost(): Parameters<typeof playStopLive>[0] {
    const map = () => this.viewports.getPrimary()?.getMap() ?? null
    this.effectsOverlay ??= createEffectsOverlay({
      container: () => map()?.getContainer() ?? null,
      project: (lat, lon) => {
        const m = map()
        if (!m) return null
        // On the far side of the globe a place is hidden, whatever its projected pixel (a flat map has none).
        const c = m.getCenter()
        const rad = Math.PI / 180
        const cos = Math.sin(lat * rad) * Math.sin(c.lat * rad) + Math.cos(lat * rad) * Math.cos(c.lat * rad) * Math.cos((lon - c.lng) * rad)
        if (cos < 0.05 && !this.flatMap) return null
        const pt = m.project([lon, lat])
        return { x: pt.x, y: pt.y }
      },
      readValue: (lat, lon) => {
        const reading = this.viewports.getPrimary()?.probeValueAt(lat, lon)
        return reading && !reading.noData ? formatProbeReading(reading) : null
      },
      language: () => getLocale(),
      compareLabels: (effects) => {
        const at = effects.compare?.at ? Date.parse(effects.compare.at) : NaN
        if (!this.liveCompareState?.ready || !Number.isFinite(at)) return null
        return { here: readTimeLabel() ?? '', there: frameLabelNow(at) ?? new Date(at).toISOString().slice(0, 10) }
      },
    })
    const overlay = this.effectsOverlay
    return {
      view: () => {
        const m = map()
        if (!m) return null
        const c = m.getCenter()
        return { lat: c.lat, lon: c.lng, zoom: m.getZoom(), bearing: ((m.getBearing() % 360) + 360) % 360, pitch: m.getPitch() }
      },
      jumpTo: (v: FilmView) => map()?.jumpTo({ center: [v.lon, v.lat], zoom: v.zoom, bearing: v.bearing, pitch: v.pitch }),
      reducedMotion: () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
      paint: (effects, clock) => {
        this.liveCompare(effects.compare ?? null, clock)
        overlay.paint(effects, clock)
      },
      clear: () => {
        overlay.clear()
        this.liveCompare(null)
      },
    }
  }

  /**
   * AYNI Studio (live): a split compare while a step holds — the same
   * dataset at the other date drawn right of a line that moves across the
   * picture. The layer comes up once (a moment after the hold starts),
   * then only its line moves; null takes it off.
   */
  private liveCompare(compare: KhipuEffects['compare'] | null, clock?: EffectsClock): void {
    const renderer = this.viewports.getPrimary()
    const atMs = compare?.at ? Date.parse(compare.at) : NaN
    const id = this.appState.currentDataset?.id
    if (!compare || !clock || !Number.isFinite(atMs) || !id || isVrActive()) {
      if (this.liveCompareState) {
        this.liveCompareState = null
        void this.setDataMixLayer(null)
        renderer?.setDatasetWipe(null)
      }
      return
    }
    const wipe = { kind: 'split' as const, progress: compareSplit(compare, clock), lat: 0, lon: 0 }
    if (this.liveCompareState?.atMs !== atMs) {
      const state = { atMs, ready: false }
      this.liveCompareState = state
      void this.setDataMixLayer({ datasetId: id, atMs, reveal: { opacity: 1, wipe } }).then((ok) => {
        if (this.liveCompareState === state) state.ready = ok
      })
      return
    }
    if (!this.liveCompareState.ready) return
    if (this.dataMix) this.dataMix.reveal = { opacity: 1, wipe }
    renderer?.setDatasetWipe(wipe)
  }

  /** AYNI (Khipu 1.1): a step's arrival, hold and effects, live; in the headset the camera simply goes there. */
  private playStepMotion(stop: LiveStop): void {
    this.stopStepMotion()
    if (isVrActive()) {
      this.lessonPlayerHost().setView({ lat: stop.view.lat, lon: stop.view.lon, zoom: stop.view.zoom, bearing: stop.view.bearing, pitch: stop.view.pitch })
      return
    }
    // The spin and the step's own move would fight: a step with motion holds the globe still.
    this.autoRotate?.set(false)
    this.stepMotion = playStopLive(this.liveMotionHost(), stop)
  }

  private stopStepMotion(): void {
    stopSatelliteFollow()
    this.stepMotion?.cancel()
    this.stepMotion = null
    this.effectsOverlay?.clear()
    // A data move or a split compare under way stops with it (its own loop then ends): no layer or fade is left behind.
    this.liveCompareState = null
    if (this.dataMix || this.dataMixGen) void this.setDataMixLayer(null)
    const renderer = this.viewports.getPrimary()
    renderer?.setDatasetOpacity(1)
    renderer?.setDatasetWipe(null)
  }

  /**
   * AYNI Studio: the stops played live on the globe (the panel's Preview):
   * each stop's dataset, layers and date put up (the clock waits while a
   * dataset loads), then its arrival, hold and effects, as the video will
   * have them.
   */
  private previewFilm(k: Khipu, film: FilmPlan, onEnd: () => void): { stop(): void } {
    let stopped = false
    const run = async () => {
      for (const stop of film.stops) {
        if (stopped) return
        const index = k.path.steps.findIndex((st) => st.id === stop.stepId)
        if (index < 0) continue
        const before = stop.index > 0 ? film.stops[stop.index - 1] : null
        const data = stop.transition.data
        const step = k.path.steps[index]
        // AYNI (2026-10-19): the stop's flat map or globe, as the video shows it.
        await this.setFlatMap(step.view?.flat === true)
        if (stopped) return
        const resolved = before && step.dataset && before.dataset !== stop.dataset && (data === 'crossfade' || data === 'wipe' || data === 'basemap')
          ? await resolveDatasetKey(k, step.dataset, getLocale())
          : null
        if (stopped) return
        if (resolved && 'datasetId' in resolved && (data === 'crossfade' || data === 'wipe' || data === 'basemap')) {
          // A data move: the camera arrives while the dataset changes, as in the video.
          this.stopStepMotion()
          this.stepMotion = playStopLive(this.liveMotionHost(), liveStopOf(stop))
          await this.liveDataMove(resolved.datasetId, { data, wipe: stop.transition.wipe, seconds: stop.transition.seconds, atMs: stop.atMs, view: stop.view },
            () => this.showKhipuStep(k, index, { camera: false }).then(() => true), () => !stopped)
          if (stopped) return
          await this.stepMotion?.done
          continue
        }
        if (data === 'timeRun' && before && before.atMs !== null && stop.atMs !== null) {
          // A time run (a recorded play): the dates run by while the camera arrives, as in the video.
          this.stopStepMotion()
          this.stepMotion = playStopLive(this.liveMotionHost(), liveStopOf(stop))
          await this.liveTimeRun(before.atMs, stop.atMs, stop.transition.seconds, () => !stopped)
          if (stopped) return
          await this.showKhipuStep(k, index, { camera: false })
          if (stopped) return
          await this.stepMotion?.done
          continue
        }
        await this.showKhipuStep(k, index, { camera: false })
        if (stopped) return
        this.stopStepMotion()
        this.stepMotion = playStopLive(this.liveMotionHost(), liveStopOf(stop))
        await this.stepMotion.done
      }
      if (!stopped) { this.stopStepMotion(); onEnd() }
    }
    void run()
    return {
      stop: () => {
        stopped = true
        this.stopStepMotion()
      },
    }
  }

  /** AYNI Studio (live): the dates run from one to the other in `seconds`, as a time run does in the video. */
  private liveTimeRun(fromMs: number, toMs: number, seconds: number, alive: () => boolean): Promise<void> {
    const start = performance.now()
    let lastSeek = -Infinity
    return new Promise<void>((resolve) => {
      const tick = () => {
        if (!alive()) return resolve()
        const now = performance.now()
        const p = seconds > 0 ? Math.min(1, (now - start) / (seconds * 1000)) : 1
        // A seek about every 80 ms: the decoder keeps up, the dates still run smoothly.
        if (p >= 1 || now - lastSeek >= 80) {
          lastSeek = now
          this.seekTimelineDate(fromMs + (toMs - fromMs) * p)
        }
        if (p >= 1) return resolve()
        requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    })
  }

  /** The view on the primary globe: its frame and camera. */
  private currentViewState(): ViewState {
    // The primary globe's camera, turned and tilted as the viewer left it.
    const map = this.viewports.getPrimary()?.getMap()
    const centre = map?.getCenter()
    return {
      atMs: this.currentFrameMs(),
      // A stream's frames are years, months, days…: the link and the citation say no more (frameCalendar.ts).
      atPrecision: this.timelineSnapshot()?.axis?.precision ?? null,
      camera: map && centre
        ? { lat: centre.lat, lon: centre.lng, zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch(), ...(this.flatMap ? { flat: true } : {}) }
        : null,
      // The layers around the data, as a room shares them (owner, 2026-10-05).
      layers: {
        basemapId: null,
        overlays: this.mapLayerSelection.overlays.map((o) => ({ id: o.id, tint: o.tint })),
        rt: this.rtOverlays.map((rt) => rt.id),
      },
    }
  }

  /**
   * Everything a citation of the view says, from the dataset row and
   * its `.dsa` as far as it has arrived (`showStreamCredit` fetches it
   * when the dataset loads); null with no dataset.
   */
  private citationForView(): CitationInput | null {
    const ds = this.appState.currentDataset
    if (!ds) return null
    return this.citationFor(ds, this.currentViewState())
  }

  /** AYNI: a citation of one dataset in a view (Compare's B: another globe or an overlay at its own frame). */
  private citationFor(ds: Dataset, state: ViewState): CitationInput {
    const meta = ds.timelineLink ? this.dsaTimelines.getMetadata(ds.timelineLink) : null
    return {
      datasetId: ds.id,
      slug: ds.slug ?? null,
      title: displayTitle(ds),
      author: citationAuthor(ds.organization, meta?.creator),
      published: meta?.modified ?? meta?.created ?? ds.enriched?.dateAdded ?? null,
      credit: meta?.attribution ?? null,
      frameMs: state.atMs,
      framePrecision: state.atPrecision ?? null,
      viewUrl: buildViewUrl(window.location.origin, ds, state),
      accessed: new Date(),
      software: ayniXrSoftware(),
      softwareDoi: AYNI_XR_DOI,
      // AYNI: the release's custody and error in every export (value error audit, 2026-10-19).
      custody: custodyRows(ds.releaseEncoding),
    }
  }

  /**
   * The provenance rows a CSV carries. The Analyze exports read one
   * frame (`frame`) and have no export time of their own (`exportedAt`);
   * a pins export spans the whole window and writes its own
   * `exported_at`, so it takes neither. Its link still reopens the frame
   * the export was made on.
   */
  private viewProvenance(opts: { frame: boolean; exportedAt: boolean }): ReturnType<typeof provenanceRows> | null {
    const input = this.citationForView()
    if (!input) return null
    return provenanceRows(opts.frame ? input : { ...input, frameMs: null }, opts.exportedAt ? { exportedAt: new Date() } : {})
  }

  /** Open "Cite this view", once the `.dsa` credit has had its chance to arrive. */
  private async openCiteView(trigger: HTMLElement | null): Promise<void> {
    const url = this.appState.currentDataset?.timelineLink
    if (url) await this.dsaTimelines.loadMetadata(url)
    const input = this.citationForView()
    if (!input) {
      this.announce(t('cite.noDataset'))
      return
    }
    openCiteViewDialog(input, { trigger, announce: (m) => this.announce(m) })
  }

  /**
   * AYNI: the figure and methods dialog (research tier, §Next S4/S5), once
   * the `.dsa` credit has had its chance to arrive. Playback pauses, so the
   * figure, its caption and the methods describe one frame.
   */
  private async openFigure(trigger: HTMLElement | null, tab: FigureTab): Promise<void> {
    const url = this.appState.currentDataset?.timelineLink
    if (url) await this.dsaTimelines.loadMetadata(url)
    if (!this.appState.currentDataset) {
      this.announce(t('cite.noDataset'))
      return
    }
    openFigureDialog(this.figureSource(), { tab, trigger, announce: (m) => this.announce(m) })
  }

  /** AYNI: what a figure is of: the main globe's dataset, frame, change, pins and methods. */
  private figureSource(): FigureSource {
    const primary = () => this.viewports.getPrimary()
    return {
      datasetTitle: () => (this.appState.currentDataset ? displayTitle(this.appState.currentDataset) : null),
      units: () => primary()?.displayScale()?.units ?? null,
      citation: () => this.citationForView(),
      frame: () => {
        const f = primary()?.analysisFrame()
        // A copy: the sampler reuses its buffer for the next frame.
        return f ? { snapshot: { ...f.snapshot, data: new Uint8Array(f.snapshot.data) }, scale: f.scale, options: f.options, display: this.colorScaleDisplay } : null
      },
      change: () => this.figureChange(),
      pins: () => this.figurePins(),
      captureGlobe: (w, h) => this.captureGlobeForFigure(w, h),
      methods: (figure) => this.methodsInput(figure),
      pause: () => {
        if (!this.appState.isPlaying) return false
        togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
        return true
      },
    }
  }

  /**
   * AYNI: the reproducibility bundle (research tier, §Next S8): the citation
   * in three forms, the view link, the methods paragraph, every CSV the
   * session can produce now, the figure as the figure dialog last set it up
   * (or its defaults), and the research record while one is recording, in
   * one zip with a README and a manifest of SHA-256 (`research/bundle.ts`).
   * Playback pauses first, so every part describes one frame. The zip's own
   * SHA-256 goes to the research record like any exported file.
   */
  private async downloadBundle(): Promise<{ filename: string; files: number; sha256: string } | null> {
    const ds = this.appState.currentDataset
    if (!ds) return null
    if (ds.timelineLink) await this.dsaTimelines.loadMetadata(ds.timelineLink)
    const src = this.figureSource()
    src.pause?.()
    // A paused frame settles before it is read (the figure and the CSVs describe it).
    await new Promise((resolve) => window.setTimeout(resolve, 150))
    const citation = this.citationForView()
    if (!citation) return null
    const csvs: BundleCsv[] = [...analyzeBundleCsvs(), ...(this.dataPinsUi?.bundleCsvs() ?? [])]
    const hov = hovmollerBundleCsv()
    if (hov) csvs.push({ kind: 'hovmoller', text: hov })
    const threshold = thresholdBundleCsv()
    if (threshold) csvs.push({ kind: 'threshold', text: threshold })
    const regionThroughTime = regionSeriesBundleCsv()
    if (regionThroughTime) csvs.push({ kind: 'regionSeries', text: regionThroughTime })
    const figure = await renderFigureFiles(src).catch(() => null)
    const methodsInput = this.methodsInput(figure?.figure ?? null)
    const record = (await this.researchRecord?.snapshot().catch(() => null)) ?? null
    const createdAt = new Date()
    const files = await bundleFiles({
      citation,
      methods: methodsInput ? methodsText(methodsInput) : null,
      csvs,
      figure: figure
        ? {
            png: new Uint8Array(await figure.png.arrayBuffer()),
            svg: figure.svg,
            note: `${figure.settings.kind === 'globe' ? 'The globe as shown' : 'The whole frame on the Equal Earth projection'}, ${figure.settings.size === 'single' ? 'one column (85 mm)' : 'two columns (180 mm)'}, titled "${figure.settings.title}"${figure.settings.pins ? `, with ${figure.settings.pins} pin${figure.settings.pins === 1 ? '' : 's'}` : ''}.`, // i18n-exempt: bundle README, English like the citation
          }
        : null,
      record,
      createdAt,
    })
    const name = ayniCsvFilename('bundle', displayTitle(ds), createdAt).replace(/\.csv$/, '.zip')
    const { blob, filename, sha256 } = await zipBundle(files, name, createdAt)
    if (typeof URL.createObjectURL === 'function') {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
    }
    // The research record, when one is recording, keeps the zip's name and SHA-256.
    trailFile({ name: filename, body: blob, kind: 'zip' })
    return { filename, files: files.length, sha256 }
  }

  /** AYNI: the change the primary globe shows now, for the figure; null for the dataset's values. */
  private figureChange(): FigureChange | null {
    const s = changeViewState()
    const range = shownRange(s)
    if (!changeShown(s) || s.limit === null || !range || !s.mode) return null
    if (s.other) {
      // The picture layer when the difference is drawn paused; painted on demand when the graphics card draws it live.
      const canvas = this.changeLayer?.canvas ?? s.other.picture?.() ?? null
      return canvas ? { kind: 'compare', canvas, limit: range.max, min: range.min, colours: s.colours, otherTitle: s.other.title ?? s.other.label, mode: s.mode === 'percent' ? 'percent' : 'difference' } : null
    }
    if (!s.field) return null
    // AYNI S2: a period held: the change against the climatology of the frame's group.
    const period = s.field.period
    if (period) {
      const group = period.groupNow()
      if (group === null) return null
      return { kind: 'period', mode: s.mode, limit: range.max, min: range.min, colours: s.colours, floor: s.floor, clim: period.clim, group, span: period.label, groupLabel: groupLabel(period.clim.calendar, group) }
    }
    return {
      kind: 'baseline',
      mode: s.mode,
      limit: range.max,
      min: range.min,
      colours: s.colours,
      floor: s.floor,
      base: { data: s.field.data, width: s.field.width, height: s.field.height, scale: s.field.scale },
      frameLabel: s.field.frameLabel,
    }
  }

  /** AYNI: the pins for the figure, named in English as the figure is. */
  private figurePins(): FigurePin[] {
    return (this.dataPinStore?.list() ?? []).map((pin, i) => ({ lat: pin.lat, lon: pin.lon, color: pin.color, label: pinEnglishName(pin, i) }))
  }

  /**
   * AYNI: the primary globe as rendered, cut to the figure's map (`width` ×
   * `height`, centred on the view) and rendered at that size when the
   * graphics card allows: the map's pixel ratio is raised for one frame and
   * put back. MapLibre caps its canvas (4096 px a side by default), so on a
   * small screen the render can be smaller than the figure's map; the
   * capture says what it was, and the figure and methods state it.
   */
  private async captureGlobeForFigure(width: number, height: number): Promise<GlobeCapture | null> {
    const map = this.viewports.getPrimary()?.getMap()
    if (!map) return null
    const canvas = map.getCanvas()
    const cssW = canvas.clientWidth
    const cssH = canvas.clientHeight
    if (!(cssW > 0 && cssH > 0 && width > 0 && height > 0)) return null
    const aspect = width / height
    let c = map.project(map.getCenter())
    let halfW = Math.min(c.x, cssW - c.x)
    let halfH = Math.min(c.y, cssH - c.y)
    if (!(halfW > 20 && halfH > 20)) {
      c = { x: cssW / 2, y: cssH / 2 } as typeof c
      halfW = cssW / 2
      halfH = cssH / 2
    }
    halfW = Math.min(halfW, halfH * aspect)
    halfH = halfW / aspect
    const crop = { x: c.x - halfW, y: c.y - halfH, w: 2 * halfW, h: 2 * halfH }
    const before = map.getPixelRatio()
    const wanted = Math.min(8, width / crop.w)
    try {
      if (wanted > before * 1.01) map.setPixelRatio(wanted)
      await new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, 4000)
        map.once('render', () => {
          window.clearTimeout(timer)
          resolve()
        })
        map.triggerRepaint()
      })
      const ratio = canvas.width / cssW
      const out = document.createElement('canvas')
      out.width = width
      out.height = height
      const g = out.getContext('2d')
      if (!g) return null
      g.imageSmoothingEnabled = true
      g.imageSmoothingQuality = 'high'
      g.drawImage(canvas, crop.x * ratio, crop.y * ratio, crop.w * ratio, crop.h * ratio, 0, 0, width, height)
      const source = { width: Math.round(crop.w * ratio), height: Math.round(crop.h * ratio) }
      // MapLibre's own test for the far side of the globe (internal, so guarded).
      const LngLatOf = map.getCenter().constructor as new (lng: number, lat: number) => unknown
      return {
        canvas: out,
        source,
        project: (lat, lon) => {
          try {
            if (isOccluded(map, new LngLatOf(lon, lat))) return null
          } catch {
            // Without the test, a pin is placed wherever it projects.
          }
          const p = map.project([lon, lat])
          const x = (p.x - crop.x) / crop.w
          const y = (p.y - crop.y) / crop.h
          return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null
        },
      }
    } catch {
      return null
    } finally {
      if (map.getPixelRatio() !== before) map.setPixelRatio(before)
    }
  }

  /**
   * AYNI: what the methods paragraph may say (research tier, §Next S5):
   * the dataset as read now, and each analysis only while the app holds
   * its result (`research/methods.ts`).
   */
  private methodsInput(figure: MethodsFigure | null): MethodsInput | null {
    const ds = this.appState.currentDataset
    const cite = this.citationForView()
    if (!ds || !cite) return null
    const meta = ds.timelineLink ? this.dsaTimelines.getMetadata(ds.timelineLink) : null
    const frame = this.viewports.getPrimary()?.analysisFrame() ?? null
    const scale = frame?.scale ?? null
    const release = scale ? releaseOf(scale) : undefined
    let encoding: MethodsEncoding | null = null
    if (scale) {
      if (release?.kind === 'luma8-classified') encoding = { kind: 'classified', classes: release.classes.length }
      else if (release?.kind === 'luma8-log') encoding = { kind: 'log', min: scale.vmin, max: scale.vmax }
      else encoding = { kind: 'linear', min: scale.vmin, max: scale.vmax, step: scaleCodeStep(scale) ?? Math.abs(scale.vmax - scale.vmin) / 255 }
    }
    const box = frame?.options.boundingBox
    const regional = box && !(box.n >= 90 && box.s <= -90 && box.w <= -180 && box.e >= 180) ? { n: box.n, s: box.s, w: box.w, e: box.e } : null
    const clock = this.pinClock?.dated() ? this.pinClock.frame() : null
    const frames = this.timelineSnapshot()?.frameCount ?? null
    const completeness = meta?.completeness
    const analyze = analyzeMethodsState()
    const pins = this.dataPinsUi?.methodsState() ?? null
    const view = changeViewState()
    return {
      dataset: {
        id: ds.id,
        title: displayTitle(ds),
        author: cite.author,
        revised: meta?.modified ?? meta?.created ?? null,
        productType: meta?.productType ?? null,
        units: scale?.units ?? null,
        encoding,
        grid: frame ? { width: frame.snapshot.width, height: frame.snapshot.height } : null,
        bounds: regional,
        completeness: completeness
          ? { expected: completeness.expectedFrames, real: completeness.realFrames, filled: completeness.filledFrames, fillPolicy: completeness.fillPolicy }
          : null,
      },
      credit: meta?.attribution?.text ?? null,
      sources: (meta?.attribution?.sources ?? []).map((src) => ({ label: src.label, licence: src.licenseName ?? src.licenseUrl })),
      frameMs: cite.frameMs,
      framePrecision: cite.framePrecision ?? null,
      window: clock && clock.startMs !== null && clock.endMs !== null ? { firstMs: clock.startMs, lastMs: clock.endMs, frames } : null,
      statistics: analyze?.statistics ?? null,
      baseline: analyze?.baseline ?? null,
      change: analyze?.change ?? null,
      line: analyze?.line ?? null,
      hovmoller: analyze?.line ? hovmollerMethodsState() : null,
      pins: pins && pins.pins.length
        ? { pins: pins.pins, frames, values: changeShown(view) ? (view.other ? 'compare' : view.mode === 'percent' ? 'percent' : view.mode === 'sigma' ? 'sigma' : 'difference') : 'values' }
        : null,
      trend: pins?.trend ?? null,
      emergence: pins?.emergence ?? null,
      correlation: pins?.correlation ?? null,
      compare: analyze?.compare ?? null,
      threshold: thresholdMethodsState(),
      figure,
      software: cite.software,
      softwareDoi: cite.softwareDoi ?? null,
      viewUrl: cite.viewUrl,
      citation: citationText(cite),
      accessed: cite.accessed,
    }
  }

  /**
   * Apply a view link's `at` and `view` (viewLink.ts) once the boot
   * dataset is on the globe, then drop them from the address bar so the
   * next dataset does not inherit them. The frame waits for the player
   * and the `.dsa` axis, for up to 15 s; a link to a frame outside the
   * dataset's window says why it was not shown.
   */
  /**
   * AYNI Studio — render mode (`services/studio/renderMode.ts`). Reads the
   * shot (a Khipu with `x-ayni-shot`) from `?shot=<url>`, puts its first
   * step on the globe the way the Khipu player does, stops the spin, and
   * exposes `window.aynRender` for the worker. Anything unusable leaves a
   * rejected `aynRender`, so the worker stops instead of rendering nothing.
   *
   * A version 2 shot (stops) puts each stop's dataset, layers and colours on
   * the globe as the video reaches it (`applyStop`), and the credit and
   * legend follow the dataset on screen; every credit shown goes to the end
   * card. A stop whose dataset has no credit stops the render.
   */
  private async startRenderMode(): Promise<void> {
    document.body.classList.add('render-mode')
    const expose = (value: unknown) => { (window as unknown as { aynRender: unknown }).aynRender = value }
    const fail = (message: string): void => {
      const error = Promise.reject(new Error(message))
      error.catch(() => {})
      expose({ ready: error, renderFrame: () => Promise.reject(new Error(message)) })
      this.setLoading(false)
    }
    const shotUrl = new URLSearchParams(window.location.search).get('shot')
    if (!shotUrl) return fail('render mode needs ?shot=<url of a shot Khipu>')
    let raw: unknown
    try {
      const response = await fetch(shotUrl, { credentials: 'same-origin' })
      if (!response.ok) return fail(`the shot could not be read (${response.status})`)
      raw = await response.json()
    } catch {
      return fail('the shot could not be read')
    }
    // A paid order: the shot came from this site's jobs API under a job token
    // (the server checked it); the dev server may render local test shots.
    const shotPath = new URL(shotUrl, window.location.href)
    const fromJobs = shotPath.origin === window.location.origin && /^\/api\/studio\/jobs\/[a-f0-9]{16}\/shot$/.test(shotPath.pathname)
    setRenderJobAuthorized(fromJobs || import.meta.env.DEV)
    const shot = parseShot(raw)
    const khipu = normalizeKhipu(raw)
    const firstStepId = shot?.film?.stops[0]?.stepId ?? khipu?.path.steps[0]?.id
    const first = khipu?.path.steps.find((st) => st.id === firstStepId)
    if (!shot || !khipu || !first?.dataset) return fail('not a shot Khipu with a dataset in its first step')
    const primary = this.viewports.getPrimary()
    if (!primary) return fail('no globe to render')
    // AYNI (multi-globe plan, phase 4: a split render): a stop may show up to four globes.
    const main = (): MapRenderer => this.viewports.getPrimary() ?? primary
    /** Every globe on screen, the main one first. */
    const renderers = (): MapRenderer[] => {
      const all = [main()]
      for (let i = 0; i < this.viewports.getPanelCount(); i++) {
        const r = this.viewports.getRendererAt(i)
        if (r && !all.includes(r)) all.push(r)
      }
      return all
    }
    // What `prepare` was asked (null: not yet), so a globe a later stop adds is prepared the same way.
    let transparentShot: boolean | null = null
    const prepared = new WeakSet<MapRenderer>()
    const prepareMap = async (r: MapRenderer, transparent: boolean): Promise<void> => {
      const m = r.getMap()
      if (!m || prepared.has(r)) return
      prepared.add(r)
      for (const layer of m.getStyle().layers) if (layer.type === 'raster') m.setPaintProperty(layer.id, 'raster-fade-duration', 0)
      await r.loadDefaultEarthMaterials().catch(() => {})
      if (transparent) {
        // The stars (the earth layer's skybox) and the atmosphere go.
        if (m.getLayer('earth-skybox-layer')) m.setLayoutProperty('earth-skybox-layer', 'visibility', 'none')
        m.setSky({ 'atmosphere-blend': 0 })
        return
      }
      // The stars load when the browser is idle; a render waits for them (20 s at most: they are not data).
      const until = Date.now() + 20_000
      while (!r.isSkyboxReady() && Date.now() < until) await new Promise((res) => setTimeout(res, 100))
    }
    // What is on screen: the credit and legend of the dataset now shown, and every credit shown so far (the end card).
    let onScreen: { credits: string[]; legend: CanvasImageSource | null } = { credits: [], legend: null }
    const shown: string[] = []
    const legends = new Map<string, HTMLImageElement | null>()
    let timelineUrl: string | undefined
    const loadLegend = (url: string) => new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = url
    })
    /** A step's dataset, layers and colours on the globe; never its camera or date (the render moves those). */
    const putStep = async (step: KhipuStep): Promise<void> => {
      if (!step.dataset) throw new Error(`stop ${step.id} has no dataset`)
      // AYNI (2026-10-19): the stop's flat map or globe (between the two the video cuts).
      await this.setFlatMap(step.view?.flat === true)
      const resolved = await resolveDatasetKey(khipu, step.dataset, getLocale())
      if (!('datasetId' in resolved)) throw new Error(`the shot's dataset is not available (${resolved.kind})`)
      if (this.appState.currentDataset?.id !== resolved.datasetId) {
        await this.loadDataset(resolved.datasetId, 'url')
        if (this.appState.currentDataset?.id !== resolved.datasetId) throw new Error(`the dataset ${resolved.datasetId} did not load`)
        // Its default layers (borders) first: arriving later, they would change the video part way, or undo the stop's own.
        await this.defaultLayersPending.catch(() => {})
      }
      const plan = planStep(khipu, step, getLocale())
      // The Khipu player's own path for the rest: layers (borders are an overlay), palette; a first or latest date.
      // A stop taken on one globe shows one; the step's globes resolve through the Khipu (multi-globe plan).
      if (!plan.globes && this.viewports.getPanelCount() > 1) await this.setGlobesFromStep('1', [])
      await applyStepToGlobe(this.khipuPlayerHost(), {
        ...plan, view: null, region: null, autorotate: { on: false },
        time: plan.time?.relative ? { relative: plan.time.relative } : null,
      }, () => true, { pins: false, lang: getLocale() }, khipu)
      // Every globe's default layers (borders on a stream) before the picture, as for the main one.
      await Promise.all([...this.defaultLayersBySlot.values()].map((pending) => pending.catch(() => {})))
      if (plan.globes && this.viewports.getLayout() !== plan.globes.layout) {
        throw new Error(`stop ${step.id} needs ${plan.globes.layout} globes; this machine shows ${this.viewports.getLayout()}`)
      }
      if (plan.globes) {
        for (let i = 0; i < this.viewports.getPanelCount(); i++) {
          const wanted = plan.globes.others[i - (i > this.viewports.getPrimaryIndex() ? 1 : 0)]
          if (i !== this.viewports.getPrimaryIndex() && wanted?.datasetKey && !this.panelStates[i]?.dataset) {
            throw new Error(`globe ${i + 1} of stop ${step.id} did not load`)
          }
        }
      }
      this.autoRotate?.set(false)
      if (this.appState.isPlaying) togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
      const dataset = this.appState.currentDataset!
      timelineUrl = dataset.timelineLink
      const meta = timelineUrl ? await this.dsaTimelines.loadMetadata(timelineUrl) : null
      // The data credit is burned into every frame (rights audit §13 Q10); a stop without one stops the render.
      const credit = renderCreditLine(dataset.rights ?? meta?.rights, meta?.attribution?.text)
      if (!credit) throw new Error(`${dataset.id} has no data credit to show`)
      if (!legends.has(dataset.id)) legends.set(dataset.id, dataset.legendLink ? await loadLegend(dataset.legendLink) : null)
      // Every other globe's credit too: a split shows each dataset, so each is credited.
      const credits = [credit]
      for (let i = 0; i < this.viewports.getPanelCount(); i++) {
        const other = this.panelStates[i]?.dataset
        if (i === this.viewports.getPrimaryIndex() || !other) continue
        const otherMeta = other.timelineLink ? await this.dsaTimelines.loadMetadata(other.timelineLink) : null
        const line = renderCreditLine(other.rights ?? otherMeta?.rights, otherMeta?.attribution?.text)
        if (!line) throw new Error(`${other.id} has no data credit to show`)
        if (!credits.includes(line)) credits.push(line)
      }
      // DSA 1.8 (§8): a vector layer that supersedes the video or its legend picture brings its own legend.
      this.syncVectorLayers()
      const primarySlot = this.viewports.getPrimaryIndex()
      await this.vectorLoads[primarySlot]
      const vector = this.vectorLegendAt(primarySlot)
      const vectorLegend = vector.replaces && vector.models[0] ? vectorLegendCanvas(vector.models[0]) : null
      onScreen = { credits, legend: vectorLegend ?? legends.get(dataset.id) ?? null }
      for (const line of credits) if (!shown.includes(line)) shown.push(line)
      // A globe this stop added is prepared as the first ones were.
      if (transparentShot !== null) for (const r of renderers()) await prepareMap(r, transparentShot)
    }
    /** The globes' area on the page, for a split. */
    const gridBox = (): DOMRect | null => document.getElementById('map-grid')?.getBoundingClientRect() ?? null
    // A split: every globe's canvas drawn where it sits on the page, into one picture.
    const composite = document.createElement('canvas')
    const pictureOf = (): HTMLCanvasElement | null => {
      const all = renderers()
      const first = main().getCanvas()
      if (all.length <= 1) return first
      const grid = gridBox()
      if (!grid?.width || !first) return first
      const scale = first.width / (first.getBoundingClientRect().width || first.width)
      const w = Math.round(grid.width * scale)
      const h = Math.round(grid.height * scale)
      if (composite.width !== w || composite.height !== h) Object.assign(composite, { width: w, height: h })
      const ctx = composite.getContext('2d')
      if (!ctx) return first
      ctx.clearRect(0, 0, w, h)
      for (const r of all) {
        const c = r.getCanvas()
        if (!c) continue
        const b = c.getBoundingClientRect()
        ctx.drawImage(c, (b.left - grid.left) * scale, (b.top - grid.top) * scale, b.width * scale, b.height * scale)
      }
      return composite
    }
    try {
      await putStep(first)
    } catch (error) {
      return fail(error instanceof Error ? error.message : String(error))
    }
    this.setLoading(false)
    const host: RenderHost = {
      // Every globe takes the shot's camera (a step keeps one camera for all).
      jumpTo: (v) => { for (const r of renderers()) r.getMap()?.jumpTo({ center: [v.lon, v.lat], zoom: v.zoom, bearing: v.bearing, pitch: v.pitch }) },
      video: () => this.hlsService?.video ?? null,
      videoTimeForDateMs: (ms) => {
        const timeline = timelineUrl ? this.dsaTimelines.get(timelineUrl) : null
        return timeline ? videoTimeForDateMs(timeline, ms) : null
      },
      applyStop: async (stop) => {
        const step = khipu.path.steps.find((st) => st.id === stop.stepId)
        if (step) await putStep(step)
      },
      setDataMix: async (data) => {
        const renderer = this.viewports.getPrimary()
        const incoming = data.incoming && shot.film ? shot.film.stops[data.incoming.stop] : null
        const step = incoming ? khipu.path.steps.find((st) => st.id === incoming.stepId) : null
        if (!data.incoming || !incoming || !step?.dataset || (data.move !== 'crossfade' && data.move !== 'wipe')) {
          await this.setDataMixLayer(null)
          renderer?.setDatasetOpacity(data.opacity)
          renderer?.setDatasetWipe(null)
          return
        }
        const resolved = await resolveDatasetKey(khipu, step.dataset, getLocale())
        if (!('datasetId' in resolved)) return
        const p = data.incoming.mix
        const reveal = this.dataMixReveal(data.move, incoming.transition.wipe, p, incoming.view)
        if (!(await this.setDataMixLayer({ datasetId: resolved.datasetId, atMs: data.incoming.atMs, reveal }))) return
        // The outgoing data goes where the incoming arrives (a transparent incoming dataset never shows it through):
        // a crossfade fades it over its last 30 % (sooner, the Earth would show through both), a wipe clears it behind the same edge.
        renderer?.setDatasetOpacity(data.move === 'crossfade' ? Math.min(1, (1 - p) / 0.3) : 1)
        renderer?.setDatasetWipe(reveal.wipe)
      },
      setCompare: async (compare) => {
        const renderer = this.viewports.getPrimary()
        const id = this.appState.currentDataset?.id
        if (!compare || !id) {
          this.renderCompareShown = false
          await this.setDataMixLayer(null)
          renderer?.setDatasetWipe(null)
          return
        }
        const wipe = { kind: 'split' as const, progress: compare.split, lat: 0, lon: 0 }
        // A dataset that cannot be drawn so has no split, and so no line or dates either.
        this.renderCompareShown = await this.setDataMixLayer({ datasetId: id, atMs: compare.atMs, reveal: { opacity: 1, wipe } })
        renderer?.setDatasetWipe(this.renderCompareShown ? wipe : null)
      },
      uploadedFrameTime: () => main().getUploadedFrameTime(),
      reuploadFrame: () => { for (const r of renderers()) r.requestVideoUpdate() },
      tilesLoaded: () => renderers().every((r) => r.getMap()?.areTilesLoaded() ?? true),
      freshRender: async () => { await Promise.all(renderers().map((r) => r.triggerFreshRender())) },
      canvas: pictureOf,
      globesReady: () => this.viewports.getPanelCount() <= 1 || this.otherGlobesOnDate(),
      // DSA 1.8: vector layers on the render's own clock (§6), drawn before the frame is read.
      vectorStep: async (seconds, at) => {
        this.syncVectorLayers()
        // A stop's dataset may still be reading its rows: wait for them, as for its video.
        await Promise.race([Promise.all(this.vectorLoads), new Promise((r) => setTimeout(r, 15_000))])
        const players = this.vectorPlayers.filter((p) => p?.layers.length)
        if (!players.length) return
        for (const p of players) p.renderStep(seconds, at)
        const deadline = Date.now() + 5000
        while (!renderers().every((r) => r.getMap()?.areTilesLoaded() ?? true) && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 16))
        }
        await Promise.all(renderers().map((r) => r.triggerFreshRender()))
      },
      webglRenderer: () => {
        const gl = document.createElement('canvas').getContext('webgl2')
        const info = gl?.getExtension('WEBGL_debug_renderer_info')
        return gl ? String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)) : 'no webgl2'
      },
      prepare: async ({ transparent }) => {
        transparentShot = transparent
        for (const r of renderers()) await prepareMap(r, transparent)
      },
    }
    // The brand kit the shot carries: its logo decoded before the first frame.
    const logo = shot.brand?.logo ? await new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = shot.brand!.logo!
    }) : null
    const compositor = createCompositor({
      shot,
      ...outputSizeOf(shot),
      credits: onScreen.credits,
      legend: onScreen.legend,
      // Version 2: the credit and legend follow the stop on screen; the end card lists all of them.
      ...(shot.film ? { current: () => onScreen, allCredits: () => shown, compareShown: () => this.renderCompareShown } : {}),
      language: getLocale(),
      labels: { endCardTitle: t('studio.endCard.title'), madeWith: t('studio.endCard.madeWith') },
      makeCanvas: (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h }),
      brand: shot.brand ? { logo, lowerThird: shot.brand.lowerThird, accent: shot.brand.accent, font: '' } : null,
      formatDate: (ms) => frameLabelNow(ms) ?? new Date(ms).toISOString().slice(0, 10),
      // A split: each globe labelled with its own date (and its dataset when they differ).
      globeLabels: () => {
        const count = this.viewports.getPanelCount()
        const grid = count > 1 ? gridBox() : null
        if (!grid?.width) return []
        const k = outputSizeOf(shot).width / grid.width
        const ids = new Set(this.panelStates.slice(0, count).map((st) => st?.dataset?.id ?? ''))
        const labels: Array<{ x: number; y: number; w: number; h: number; text: string }> = []
        for (let i = 0; i < count; i++) {
          const box = this.viewports.getRendererAt(i)?.getMap()?.getContainer().getBoundingClientRect()
          if (!box) continue
          const date = this.slotDateText(i) ?? ''
          const title = ids.size > 1 ? this.panelStates[i]?.dataset?.title ?? '' : ''
          labels.push({
            x: (box.left - grid.left) * k, y: (box.top - grid.top) * k, w: box.width * k, h: box.height * k,
            text: [date, title].filter(Boolean).join(' · '),
          })
        }
        return labels
      },
      project: (lat, lon) => {
        const map = main().getMap()
        if (!map) return null
        // On the far side of the globe a place is hidden, whatever its projected pixel (a flat map has none).
        const c = map.getCenter()
        const rad = Math.PI / 180
        const cos = Math.sin(lat * rad) * Math.sin(c.lat * rad) + Math.cos(lat * rad) * Math.cos(c.lat * rad) * Math.cos((lon - c.lng) * rad)
        if (cos < 0.05 && !this.flatMap) return null
        // Layout pixels to the picture's: the worker lays a 4K page out as 1080 at pixel ratio 2.
        const pt = map.project([lon, lat])
        // In a split the main globe is one box of the picture.
        const grid = this.viewports.getPanelCount() > 1 ? gridBox() : null
        if (grid?.width) {
          const box = map.getContainer().getBoundingClientRect()
          const g = outputSizeOf(shot).width / grid.width
          return { x: (box.left - grid.left + pt.x) * g, y: (box.top - grid.top + pt.y) * g }
        }
        const k = outputSizeOf(shot).width / (map.getContainer().clientWidth || outputSizeOf(shot).width)
        return { x: pt.x * k, y: pt.y * k }
      },
      readValue: (lat, lon) => {
        const reading = main().probeValueAt(lat, lon)
        return reading && !reading.noData ? formatProbeReading(reading) : null
      },
    })
    expose(createRenderController(host, shot, compositor))
  }

  private async applyViewLink(view: ViewState): Promise<void> {
    const stripped = stripViewParams(window.location.search)
    if (stripped !== window.location.search) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${stripped}${window.location.hash}`)
    }
    if (view.camera) {
      // AYNI (2026-10-19): the flat map or the globe the link was taken on, before the camera moves.
      await this.setFlatMap(view.camera.flat === true)
      const map = this.viewports.getPrimary()?.getMap()
      const { lat, lon, zoom, bearing = 0, pitch = 0 } = view.camera
      if (map) {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
        map.flyTo({ center: [lon, lat], zoom, bearing, pitch, duration: reduced ? 0 : 2500 })
      } else {
        void this.renderer?.flyTo(lat, lon, altitudeForZoom(zoom))
      }
    }
    // The layer stack, through the room's path: it waits for the catalog and
    // the overlay streams, and leaves anything this device cannot play.
    if (view.layers) {
      const [rt = null, rt2, rt3, rt4] = view.layers.rt
      this.applyRoomLayers({
        basemapId: view.layers.basemapId,
        overlays: view.layers.overlays.map((o) => ({ id: o.id, tint: o.tint })),
        rt,
        ...(rt2 ? { rt2 } : {}),
        ...(rt3 ? { rt3 } : {}),
        ...(rt4 ? { rt4 } : {}),
      })
    }
    if (view.atMs === null) return
    const atMs = view.atMs
    // A stream's axis is its .dsa (the same request as the timeline);
    // wait for it rather than seek by the catalogue's dates.
    const url = this.appState.currentDataset?.timelineLink
    if (url) await this.dsaTimelines.loadMetadata(url)
    const ready = (): boolean => {
      const video = this.hlsService?.video
      return !!video && video.duration > 0
    }
    const deadline = Date.now() + 15_000
    while (!ready() && Date.now() < deadline) await new Promise((r) => window.setTimeout(r, 250))
    if (!ready()) return
    if (this.appState.isPlaying) togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
    if (this.timelineSnapshot()) {
      this.seekTimelineDate(atMs)
      return
    }
    const outcome = seekToDate(new Date(atMs).toISOString(), this.hlsService, this.appState, this.playback)
    if (!outcome.success) this.announce(outcome.message)
  }

  /**
   * AYNI: seek to an ISO date, for a lesson's kept moment, a Khipu step's date
   * and the chatbot's host. A stream with a `.dsa` axis
   * (which has no catalogue start and end) seeks on its frame calendar,
   * so the date lands on the frame whose label names it and pauses there
   * as the catalogue seek does; anything else takes the catalogue seek.
   */
  private seekToIsoDate(isoDate: string): { success: boolean; message: string } {
    const url = this.appState.currentDataset?.timelineLink
    const timeline = url ? this.dsaTimelines.get(url) : null
    const video = this.hlsService?.video
    if (!timeline || !video || !this.hlsService) return seekToDate(isoDate, this.hlsService, this.appState, this.playback)
    if (!video.duration) return { success: false, message: t('playback.error.videoNotReady') }
    const ms = Date.parse(isoDate)
    if (!Number.isFinite(ms)) return { success: false, message: t('playback.error.invalidDate') }
    const endMs = timelineEndMs(timeline)
    // A step of slack before the first stamp: a stamp a few days into its
    // month still owns that month's first days.
    if (ms < timeline.startMs - timeline.cadenceMs || ms >= endMs) {
      return {
        success: false,
        message: t('playback.error.dateOutsideRange', {
          date: isoDate,
          start: new Date(timeline.startMs).toISOString().slice(0, 10),
          end: new Date(lastFrameDateMs(timeline)).toISOString().slice(0, 10),
        }),
      }
    }
    video.currentTime = videoTimeForDateMs(timeline, ms)
    this.playback.scrubbing = true
    if (!video.paused || cancelLoopHold(this.playback)) {
      this.hlsService.pause()
      this.appState.isPlaying = false
      updatePlayButton(true)
    }
    return { success: true, message: t('playback.seekingTo', { date: isoDate }) }
  }

  /** Seek the primary playback to an instant on that axis. */
  /**
   * AYNI: the stream's frames as a period baseline reads them (research
   * tier, S2; `analyzeUI.AnalyzeTimeAxis`): its .dsa axis, the frame on the
   * globe, every frame presented as playback, a seek or a step passes it
   * (the video's frame callback, which names the exact frame), and a way to
   * show one frame, paused. Null without a stream axis.
   */
  private analyzeTimeAxis(): AnalyzeTimeAxis | null {
    const url = this.appState.currentDataset?.timelineLink
    const timeline = url ? this.dsaTimelines.get(url) : null
    if (!timeline || timeline.frameCount < 2 || !this.hlsService?.video) return null
    const current = (): { video: HTMLVideoElement; timeline: DsaTimeline } | null => {
      const u = this.appState.currentDataset?.timelineLink
      const tl = u ? this.dsaTimelines.get(u) : null
      const video = this.hlsService?.video ?? null
      return tl && video ? { video, timeline: tl } : null
    }
    return {
      startMs: timeline.startMs,
      frameCount: timeline.frameCount,
      cadenceMs: timeline.cadenceMs,
      currentFrame: () => {
        const c = current()
        return c ? frameAtVideoTime(c.timeline, c.video.currentTime) : null
      },
      frameAtVideoTime: (seconds) => frameAtVideoTime(timeline, seconds),
      // A frame's own presentation time: half a frame in, so it never rounds to the one before.
      frameAtMediaTime: (seconds) => frameAtVideoTime(timeline, seconds + 0.5 / timeline.videoFrameRate),
      // Filled, interpolated, estimated or missing frames are not measurements.
      usable: (frame) => {
        const kind = availabilityAtFrame(timeline, frame)
        return kind === 'real' || kind === 'unknown'
      },
      showFrame: (frame) => new Promise<boolean>((resolve) => {
        const c = current()
        if (!c) return resolve(false)
        this.setRoomPlaying(false)
        const { video, timeline: tl } = c
        let done = false
        const finish = (ok: boolean): void => {
          if (done) return
          done = true
          window.clearTimeout(timer)
          video.removeEventListener('seeked', onSeeked)
          resolve(ok)
        }
        const timer = window.setTimeout(() => finish(frameAtVideoTime(tl, video.currentTime) === frame), 5000)
        const onSeeked = (): void => {
          // Seeked is not yet drawn: wait for the frame itself.
          const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }
          if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(() => finish(true))
          else window.requestAnimationFrame(() => finish(true))
        }
        video.addEventListener('seeked', onSeeked)
        video.currentTime = videoTimeForFrame(tl, frame)
      }),
      onFrame: (fn) => {
        let stopped = false
        let attached: HTMLVideoElement | null = null
        let lastPaused = -1
        type FrameVideo = HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number }
        const attach = (): void => {
          const c = current()
          const v = (c?.video ?? null) as FrameVideo | null
          attached = v
          if (!v?.requestVideoFrameCallback) return
          v.requestVideoFrameCallback((_now, meta) => {
            if (stopped || attached !== v) return
            const tl = current()?.timeline
            // Half a frame in, so a frame's own timestamp never rounds to the one before.
            if (tl) fn(frameAtVideoTime(tl, meta.mediaTime + 0.5 / tl.videoFrameRate))
            attach()
          })
        }
        attach()
        // A new video element (another dataset and back) is picked up; without
        // the frame callback, only a paused frame is read (its time is exact).
        const watch = window.setInterval(() => {
          const c = current()
          if (!c) return
          if (c.video !== attached) attach()
          const v = c.video as FrameVideo
          if (!v.requestVideoFrameCallback && v.paused) {
            const f = frameAtVideoTime(c.timeline, v.currentTime)
            if (f !== lastPaused) fn(f)
            lastPaused = f
          }
        }, 250)
        return () => {
          stopped = true
          window.clearInterval(watch)
        }
      },
    }
  }

  private seekTimelineDate(epochMs: number): void {
    const url = this.appState.currentDataset?.timelineLink
    const timeline = url ? this.dsaTimelines.get(url) : null
    const video = this.hlsService?.video
    if (!timeline || !video) return
    // Mid-frame: the decoder lands inside the frame that represents the
    // instant instead of on the boundary between two of them.
    video.currentTime = videoTimeForDateMs(timeline, epochMs)
    // A seek during the rest on the last frame plays on from the seek.
    if (cancelLoopHold(this.playback) && this.appState.isPlaying) void video.play().catch(() => {})
  }

  /**
   * Mount the 2D date track into the transport panel, above the scrubber.
   * Called once at boot; the strip hides itself until a dataset declares an
   * axis, so a catalog-only session never sees it.
   *
   * Before the scrubber rather than appended: the scrubber is the fine
   * control for the same axis, and the strip is its context.
   */
  private mountTimelineTrack(): void {
    if (this.timelineTrackUI) return
    const controls = document.getElementById('playback-controls')
    const scrubber = document.getElementById('scrubber')
    if (!controls || !scrubber) return
    this.timelineTrackUI = createTimelineTrackUI({
      onSeek: (epochMs) => this.seekTimelineDate(epochMs),
      // AYNI (owner, 2026-10-12): the dataset's eye, and a cross that closes it and its overlays (with Undo).
      onHiddenChange: (hidden) => this.setBaseHidden(hidden),
      onClose: () => void this.closeBaseLayer(),
      // AYNI: with overlays on, a lock (nothing goes below the dataset) and the dataset's palette button.
      stack: { kind: 'base', onPalette: (button) => this.openBasePalette(button) },
    })
    controls.insertBefore(this.timelineTrackUI.element, scrubber)
    // AYNI: each real-time overlay's dates, in yellow, on the same axis,
    // stacked above the dataset's track as the overlays are stacked above
    // it on the globe (the first chosen just above it). A drag along one
    // moves the dataset, which the overlays follow.
    let above: HTMLElement = this.timelineTrackUI.element
    for (let i = 0; i < this.rtCap; i++) {
      const track = createTimelineTrackUI({
        onSeek: (epochMs) => this.seekTimelineDate(epochMs),
        height: 46,
        ariaLabel: t('timeline.overlay.aria', { title: '' }),
        // The eye and the cross act on whichever overlay this track shows now.
        onHiddenChange: (hidden) => {
          const rt = this.rtOverlays[i]
          if (rt) this.setRtOverlayHidden(rt.id, hidden)
        },
        onClose: () => {
          const rt = this.rtOverlays[i]
          if (rt) this.pickRtOverlay(rt.id)
        },
        // AYNI (owner, 2026-10-09): the grip and the palette button, left of the strip.
        stack: { kind: 'overlay', onPalette: (button) => this.openRtPalette(i, button) },
      })
      track.element.dataset.accent = 'overlay'
      controls.insertBefore(track.element, above)
      above = track.element
      this.overlayTrackUIs.push(track)
      if (track.grip) {
        attachStackDrag(track.grip, {
          rows: () => this.overlayRowsTopFirst().map((r) => r.track.element),
          row: () => track.element,
          onDrop: (from, to) => {
            const rows = this.overlayRowsTopFirst()
            const moving = rows[from]?.id
            const onto = rows[to]?.id
            if (!moving || !onto) return
            const hadFocus = document.activeElement === track.grip
            this.moveRtOverlay(moving, this.rtOverlays.findIndex((rt) => rt.id === onto))
            // The keyboard's focus follows the overlay to its new track.
            if (hadFocus) this.overlayTrackUIs[this.rtOverlays.findIndex((rt) => rt.id === moving)]?.grip?.focus()
          },
        })
      }
    }
  }

  /**
   * AYNI: the yellow tracks on screen, top first, with the overlay each
   * shows (the top one is drawn on top). An overlay without dates has no
   * track, so this may hold fewer rows than overlays are playing.
   */
  private overlayRowsTopFirst(): Array<{ id: string; track: TimelineTrackUIHandle }> {
    const rows: Array<{ id: string; track: TimelineTrackUIHandle }> = []
    for (let i = this.overlayTrackUIs.length - 1; i >= 0; i--) {
      const track = this.overlayTrackUIs[i]!
      const rt = this.rtOverlays[i]
      if (rt && track.isVisible()) rows.push({ id: rt.id, track })
    }
    return rows
  }

  /**
   * AYNI (owner, 2026-10-09): move a playing overlay to `to` in the stack
   * (0 is the lowest, right over the dataset; the last is on top). The
   * streams keep playing; only the order they are drawn in changes, on the
   * browser globes and in the headset, and a meeting's lead sends it to
   * the room (`rt` … `rt4` are in this order).
   */
  private moveRtOverlay(id: string, to: number): void {
    if (this.refusedByMeeting()) return
    const now = this.rtOverlays.map((rt) => rt.id)
    const order = moveInStack(now, id, to)
    if (order.every((x, k) => x === now[k])) return
    closePaletteDialog()
    this.rtOverlays.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    // Streams still starting keep their place above the playing ones.
    this.rtWanted = [...order, ...this.rtWanted.filter((x) => !order.includes(x))]
    this.composeMapLayers()
    this.renderer?.getMap()?.triggerRepaint()
    this.updateTimelineTrack()
    const at = this.rtOverlays.findIndex((rt) => rt.id === id)
    const dataset = dataService.getDatasetById(id)
    if (dataset) this.announce(t('timeline.overlay.moved', { title: displayTitle(dataset), n: this.rtOverlays.length - at, count: this.rtOverlays.length }))
  }

  /**
   * AYNI: the colours an overlay is drawn in: as changed this session, else
   * its dataset's (what the viewer saved for it, else its display group's
   * standard, else the release's own: `displayFor`).
   */
  private rtDisplayOf(id: string): ColorScaleDisplay {
    const dataset = dataService.getDatasetById(id)
    return this.rtDisplays.get(id) ?? (dataset ? displayFor(dataset) : DEFAULT_DISPLAY)
  }

  /**
   * AYNI (owner, 2026-10-09): draw an overlay in this palette and these
   * limits. A new colour table, so every globe uploads it (they cache by
   * the array), and the session remembers it for that overlay. AYNI (owner,
   * 2026-10-22): a room's lead sends it (`getColours`), and a follower draws
   * the lead's with `save: false`, not keeping them as its own.
   */
  private setRtOverlayDisplay(id: string, display: ColorScaleDisplay, opts: { save?: boolean } = {}): void {
    const rt = this.rtOverlays.find((r) => r.id === id)
    if (!rt || !canRecolour(rt.encoding)) return
    this.rtDisplays.set(id, display)
    // Kept for the dataset, as Tools → View keeps the base dataset's (owner,
    // 2026-10-09: changes to a dataset's palette and limits are remembered).
    const dataset = dataService.getDatasetById(id)
    if (dataset && opts.save !== false) saveDisplayFor(id, display, defaultDisplayOf(dataset))
    const colours = overlayColours(rt.encoding, display)
    rt.lut = colours.lut
    if (colours.phase) rt.phase = colours.phase
    else delete rt.phase
    this.composeMapLayers()
    this.renderer?.getMap()?.triggerRepaint()
    this.updateTimelineTrack()
  }

  /**
   * AYNI: an overlay's palette button: its palette and limits dialog, and
   * its transparency (owner, 2026-10-12; an overlay whose colours cannot
   * change opens it for the transparency alone).
   */
  private openRtPalette(slot: number, button: HTMLButtonElement): void {
    const rt = this.rtOverlays[slot]
    if (!rt) return
    const scale = overlayScale(rt.encoding)
    const dataset = dataService.getDatasetById(rt.id)
    const id = rt.id
    const title = dataset ? displayTitle(dataset) : id
    togglePaletteDialog({
      anchor: button,
      beside: document.getElementById('playback-controls'),
      title,
      accent: 'overlay',
      scale,
      fixedReason: t(rt.encoding?.kind === 'luma8-classified' ? 'palette.dialog.classes' : 'palette.dialog.picture', { title }),
      display: this.rtDisplayOf(id),
      onChange: (next) => this.setRtOverlayDisplay(id, next),
      opacity: rt.opacity ?? 1,
      onOpacityChange: (opacity) => this.setRtOverlayOpacity(id, opacity),
    })
  }

  /** AYNI: the dataset's palette button: the same dialog, in step with Tools → View, and its transparency. */
  private openBasePalette(button: HTMLButtonElement): void {
    const dataset = this.appState.currentDataset
    if (!dataset) return
    const scale = this.basePaletteScale()
    const raw = this.viewports.getPrimary()?.displayScale() ?? null
    const title = displayTitle(dataset)
    togglePaletteDialog({
      anchor: button,
      beside: document.getElementById('playback-controls'),
      title,
      accent: 'base',
      scale,
      fixedReason: t(raw && releaseOf(raw)?.kind === 'luma8-classified' ? 'palette.dialog.classes' : 'palette.dialog.picture', { title }),
      display: this.colorScaleDisplay,
      onChange: (next) => this.applyColorScaleDisplay(next),
      opacity: this.baseOpacity,
      onOpacityChange: (opacity) => this.setBaseOpacity(opacity),
    })
  }

  /** AYNI (owner, 2026-10-12): the opacity the primary globe draws its dataset at (0 while its eye hides it); other globes draw theirs opaque. */
  private applyBaseOpacity(): void {
    const primary = this.viewports.getPrimaryIndex()
    const own = this.baseHidden ? 0 : this.baseOpacity
    for (let slot = 0; slot < this.viewports.getPanelCount(); slot++) {
      const renderer = this.viewports.getRendererAt(slot)
      if (renderer instanceof MapRenderer) renderer.setDatasetUserOpacity(slot === primary ? own : 1)
      // AYNI (DSA 1.8): and the dataset's vector layers.
      this.vectorPlayers[slot]?.setUserOpacity(slot === primary ? own : 1)
    }
  }

  /**
   * AYNI: a dataset just loaded starts opaque and shown (syncDisplayToDataset),
   * which otherwise happens when its frame is up and would undo an opacity
   * set right after the load (Undo, a Khipu step, a meeting's lead).
   */
  private settleNewDataset(): void {
    if ((this.appState.currentDataset?.id ?? null) !== this.displayDatasetId) this.syncDisplayToDataset()
  }

  /** AYNI (owner, 2026-10-12): the dataset's transparency slider (1 opaque … 0 transparent). */
  private setBaseOpacity(opacity: number, opts: { fromRoom?: boolean } = {}): void {
    if (!opts.fromRoom && this.refusedByMeeting()) return
    this.settleNewDataset()
    const next = Math.min(1, Math.max(0, opacity))
    if (next === this.baseOpacity) return
    this.baseOpacity = next
    this.applyBaseOpacity()
    this.layerViewChanged()
  }

  /** AYNI (owner, 2026-10-12): the dataset's eye: hide it on the globe (it keeps playing; the overlays stay) or show it again. */
  private setBaseHidden(hidden: boolean, opts: { fromRoom?: boolean } = {}): void {
    if (!opts.fromRoom && this.refusedByMeeting()) return
    this.settleNewDataset()
    if (hidden === this.baseHidden) return
    this.baseHidden = hidden
    this.applyBaseOpacity()
    this.updateTimelineTrack()
    this.layerViewChanged()
    const dataset = this.appState.currentDataset
    if (dataset && !opts.fromRoom) this.announce(t(hidden ? 'timeline.base.hidden' : 'timeline.base.shown', { title: displayTitle(dataset) }))
  }

  /** AYNI (owner, 2026-10-12): an overlay's transparency slider. */
  private setRtOverlayOpacity(id: string, opacity: number, opts: { fromRoom?: boolean } = {}): void {
    if (!opts.fromRoom && this.refusedByMeeting()) return
    const rt = this.rtOverlays.find((r) => r.id === id)
    if (!rt) return
    const next = Math.min(1, Math.max(0, opacity))
    if (next === (rt.opacity ?? 1)) return
    rt.opacity = next
    this.renderer?.getMap()?.triggerRepaint()
    this.layerViewChanged()
  }

  /**
   * AYNI (owner, 2026-10-12): an opacity or an eye changed. The dialog
   * open for that layer shows it, the headset's and the phone's panels
   * redraw, and a meeting's lead sends it to the room.
   */
  private layerViewChanged(): void {
    const dialog = currentPaletteDialog()
    if (dialog) {
      const slot = this.overlayTrackUIs.findIndex((tr) => tr.paletteButton === dialog.anchor)
      if (dialog.anchor === this.timelineTrackUI?.paletteButton) dialog.setOpacity(this.baseOpacity)
      else if (slot >= 0) dialog.setOpacity(this.rtOverlays[slot]?.opacity ?? 1)
    }
    this.renderer?.getMap()?.triggerRepaint()
    this.notifyLayerViewListeners()
  }

  /** AYNI: the dataset's ✕ (owner, 2026-10-12) closes it and every overlay, back to the plain Earth, with Undo for a few seconds. */
  private async closeBaseLayer(): Promise<void> {
    if (this.refusedByMeeting()) return
    const dataset = this.appState.currentDataset
    if (!dataset) return
    const snapshot = this.layerUndoSnapshot()
    await this.goHome({ browse: false })
    const message = t(snapshot.overlays.length ? 'timeline.base.closedWithOverlays' : 'timeline.base.closed', { title: displayTitle(dataset) })
    // A phone in AR draws only its overlay, so Undo goes there; a headset draws no page at all and hears it on its dock.
    const arOverlay = document.getElementById('xr-dom-overlay')
    // AYNI (Quest review E10, 2026-11-03): the phone's controls are on, not
    // only while placing (that class was all this checked, so a phone got
    // no Undo).
    const phoneAr = !!arOverlay && (arOverlay.classList.contains('xr-phone-on') || arOverlay.classList.contains('xr-dom-overlay-active'))
    if (isVrActive() && !phoneAr) {
      notifyXr(message)
      return
    }
    showUndoToast({ message, onUndo: () => void this.restoreLayerUndo(snapshot), ...(phoneAr && arOverlay ? { container: arOverlay } : {}) })
  }

  /** AYNI: what the dataset's ✕ takes away, to put back with Undo. */
  private layerUndoSnapshot(): LayerUndoSnapshot {
    const dataset = this.appState.currentDataset!
    const tl = dataset.timelineLink ? this.dsaTimelines.get(dataset.timelineLink) : null
    const video = this.hlsService?.video
    return {
      datasetId: dataset.id,
      atMs: tl && video ? dateAtVideoTimeMs(tl, video.currentTime) : null,
      videoTime: video?.currentTime ?? null,
      playing: this.appState.isPlaying,
      display: this.colorScaleDisplay,
      opacity: this.baseOpacity,
      hidden: this.baseHidden,
      overlays: this.rtOverlays.map((rt) => ({ id: rt.id, opacity: rt.opacity ?? 1, hidden: !!rt.userHidden })),
    }
  }

  /** AYNI: Undo after the dataset's ✕: the dataset again at its date, its colours, its overlays in their order, and every opacity and eye. */
  private async restoreLayerUndo(snap: LayerUndoSnapshot): Promise<void> {
    if (this.refusedByMeeting()) return
    const ok = await this.loadDatasetAndConfirm(snap.datasetId, 'browse')
    if (!ok || this.appState.currentDataset?.id !== snap.datasetId) return
    this.settleNewDataset()
    this.applyColorScaleDisplay(snap.display, { save: false })
    this.baseOpacity = snap.opacity
    this.baseHidden = snap.hidden
    this.applyBaseOpacity()
    if (snap.atMs !== null) this.seekTimelineDate(snap.atMs)
    else if (snap.videoTime !== null && this.hlsService?.video) this.hlsService.video.currentTime = snap.videoTime
    this.setRoomPlaying(snap.playing)
    await this.setRtOverlays(snap.overlays.map((o) => o.id))
    for (const o of snap.overlays) {
      const rt = this.rtOverlays.find((r) => r.id === o.id)
      if (!rt) continue
      rt.opacity = o.opacity
      rt.userHidden = o.hidden
    }
    this.composeMapLayers()
    this.updateTimelineTrack()
    this.layerViewChanged()
  }

  /** AYNI: the dataset's scale when its colours can change (values, not classes), else null. */
  private basePaletteScale(): ColorScale | null {
    const scale = this.appState.currentDataset ? this.viewports.getPrimary()?.displayScale() ?? null : null
    return scale && releaseOf(scale)?.kind !== 'luma8-classified' ? scale : null
  }

  /** AYNI: what a track's control shows for a layer: its ramp, or why its colours cannot change. */
  private stackStateFor(title: string, scale: ColorScale | null, display: ColorScaleDisplay, key: string, classified: boolean, gripDisabled: boolean): TimelineTrackStackState {
    if (!scale) return { swatch: null, reason: t(classified ? 'palette.dialog.classes' : 'palette.dialog.picture', { title }), gripDisabled }
    let cached = this.rtSwatches.get(key)
    if (!cached || cached.display !== display) {
      cached = { display, css: rampGradient(scale, display) }
      this.rtSwatches.set(key, cached)
    }
    return { swatch: cached.css, gripDisabled }
  }

  /** AYNI: hide or show a playing overlay on the globe (its eye: the date track's, the headset's, the phone's). */
  private setRtOverlayHidden(id: string, hidden: boolean, opts: { fromRoom?: boolean } = {}): void {
    // AYNI (owner, 2026-10-12): the eye is the lead's in a meeting, and the room's to everyone.
    if (!opts.fromRoom && this.refusedByMeeting()) return
    const rt = this.rtOverlays.find((r) => r.id === id)
    if (!rt) return
    if (!!rt.userHidden === hidden) return
    rt.userHidden = hidden
    this.renderer?.getMap()?.triggerRepaint()
    this.updateTimelineTrack()
    this.layerViewChanged()
  }

  /**
   * Push the axis into the 2D date track, or hide it when there is none.
   *
   * AYNI (owner, 2026-10-04): with real-time overlays playing, a yellow
   * track per overlay, above the dataset's (as on the globe), shows that
   * overlay's dates on the dataset's axis (hatched where the overlay has
   * none), each track captioned with its dataset. The
   * browser's slider is hidden while the green track shows: two bars for
   * one dataset's position said the same thing twice.
   */
  private updateTimelineTrack(): void {
    const snap = this.timelineSnapshot()
    this.timelineTrackUI?.setState(snap)
    this.overlayTrackUIs.forEach((track, i) => {
      const rt = this.rtOverlays[i]
      const overlay = snap && rt?.range ? dataService.getDatasetById(rt.id) : undefined
      track.setState(snap && rt?.range && overlay ? { ...snap, availabilitySpans: [], accent: 'overlay', coverage: rt.range } : null)
      const title = overlay ? displayTitle(overlay) : null
      track.setCaption(title)
      track.setHidden(!!rt?.userHidden)
      // AYNI: the grip and palette button, while the track shows an overlay.
      track.setStack(rt && title
        ? this.stackStateFor(title, overlayScale(rt.encoding), this.rtDisplayOf(rt.id), rt.id, rt.encoding?.kind === 'luma8-classified', this.rtOverlays.length < 2)
        : null)
      const label = title ? t('timeline.overlay.aria', { title }) : null
      if (label && track.element.getAttribute('aria-label') !== label) track.element.setAttribute('aria-label', label)
    })
    const dataset = this.appState.currentDataset
    // AYNI (owner, 2026-10-13): the dataset's track is named, with its eye and ✕, its lock and its
    // palette button (colours and transparency), alone as much as under overlays.
    this.timelineTrackUI?.setCaption(dataset ? displayTitle(dataset) : null)
    this.timelineTrackUI?.setHidden(this.baseHidden)
    if (this.timelineTrackUI) {
      if (dataset) {
        const scale = this.basePaletteScale()
        const raw = this.viewports.getPrimary()?.displayScale() ?? null
        const classified = !!raw && releaseOf(raw)?.kind === 'luma8-classified'
        this.timelineTrackUI.setStack(this.stackStateFor(displayTitle(dataset), scale, this.colorScaleDisplay, ' base', classified, false))
      } else {
        this.timelineTrackUI.setStack(null)
      }
    }
    const scrubber = document.getElementById('scrubber')
    const scrubberDisplay = snap ? 'none' : ''
    if (scrubber && scrubber.style.display !== scrubberDisplay) scrubber.style.display = scrubberDisplay
  }

  /** Map the current video playback time to a real-world date and update the time label. */
  private updateVideoTimeLabel(videoTime: number): void {
    // The track reads the dataset and the playhead itself, so it is fed
    // before the label's own early returns — a dataset without a catalog
    // start/end still has an axis.
    this.updateTimelineTrack()
    const dataset = this.appState.currentDataset
    if (!dataset) {
      this.assertedLabelDate = null
      return
    }

    if (dataset.startTime && dataset.endTime) {
      const start = new Date(dataset.startTime)
      const end = new Date(dataset.endTime)
      const videoDuration = this.hlsService?.duration ?? 1
      const snapMs = this.playback.displayInterval?.intervalMs
      const currentDate = videoTimeToDate(videoTime, videoDuration, start, end, snapMs)
      // The same playhead without the snap. `verifySiblingTimes` compares
      // true instants across panels; snapping first would make a
      // sub-frame difference at a bucket edge look like a full step.
      const trueDate = videoTimeToDate(videoTime, videoDuration, start, end)
      const showTime = dataset.period
        ? isSubDailyPeriod(dataset.period)
        : (this.playback.displayInterval?.showTime ?? false)
      this.appState.timeLabel = formatDate(currentDate, showTime)
      this.assertedLabelDate = trueDate
      this.showTimeLabel(true)
    } else {
      // No catalog time range. A real-time or forecast stream still knows
      // its axis — its `.dsa` declares one — so the label fills in where it
      // used to hide. It borrows the track's formatter, which prints UTC:
      // the axis is declared in UTC and two surfaces disagreeing about the
      // same instant by a timezone offset is worse than either choice.
      const snapshot = this.timelineSnapshot()
      if (snapshot) {
        this.appState.timeLabel = formatPlayheadLabel(
          snapshot.currentMs,
          snapshot.cadenceMs,
        )
        this.assertedLabelDate = new Date(snapshot.currentMs)
        this.showTimeLabel(true)
      } else {
        this.assertedLabelDate = null
        this.showTimeLabel(false)
      }
    }
  }

  /** Show or hide the time label overlay and update its text from app state. */
  private showTimeLabel(show: boolean): void {
    const timeLabel = document.getElementById('time-label')
    const timeDisplay = document.getElementById('time-display')
    if (timeLabel && timeDisplay) {
      if (show) {
        timeDisplay.textContent = this.appState.timeLabel
        timeLabel.classList.remove('hidden')
        const scrubber = document.getElementById('scrubber')
        if (scrubber) scrubber.setAttribute('aria-valuetext', this.appState.timeLabel)
      } else {
        timeLabel.classList.add('hidden')
      }
    }
  }

  // --- UI helpers ---

  /** Toggle visibility of the playback transport controls. */
  private showPlaybackControls(show: boolean): void {
    const controls = document.getElementById('playback-controls')
    if (controls) {
      controls.classList.toggle('hidden', !show)
    }
    updateMapControlsPosition()
  }

  /**
   * Toggle dataset info panel visibility. Persists the choice and
   * updates the DOM immediately. When the info panel is the only
   * place showing the legend (single-view with legend prefs on), the
   * legend moves to a floating element in the primary panel.
   */
  private setDatasetInfoVisible(visible: boolean): void {
    this.viewPrefs.infoPanelVisible = visible
    saveViewPreferences(this.viewPrefs)
    this.applyDatasetInfoVisibility()
    this.refreshPanelLegends()
  }

  /** Toggle legend visibility. Persists and refreshes per-panel legends. */
  private setLegendVisible(visible: boolean): void {
    this.viewPrefs.legendVisible = visible
    saveViewPreferences(this.viewPrefs)
    // AYNI: parked by its ✕ or brought back from Tools → View, the switch in Tools → More follows.
    syncToolsMenuState({ legend: visible })
    // Also hides/shows the in-info-panel legend thumbnail
    // (`syncInfoLegendThumb`).
    this.refreshPanelLegends()
  }

  /** Apply the current infoPanelVisible preference to the DOM. */
  private applyDatasetInfoVisibility(): void {
    const panel = document.getElementById('info-panel')
    if (!panel) return
    const hasDataset = !!this.appState.currentDataset
    // When no dataset is loaded, the info panel is hidden regardless —
    // the preference only matters when there's content to show.
    if (!hasDataset) {
      panel.classList.add('hidden')
      return
    }
    panel.classList.toggle('hidden', !this.viewPrefs.infoPanelVisible)
  }

  /**
   * Which slot's dataset the info panel is currently displaying —
   * the override if set, otherwise the primary. Returns -1 if no
   * panel has a loaded dataset.
   */
  private getInfoDisplayIndex(): number {
    if (
      this.infoDisplayOverride !== null &&
      this.panelStates[this.infoDisplayOverride]?.dataset
    ) {
      return this.infoDisplayOverride
    }
    const primaryIdx = this.viewports.getPrimaryIndex()
    if (this.panelStates[primaryIdx]?.dataset) return primaryIdx
    // Fall back to the first slot with a loaded dataset so the info
    // panel isn't blank when the primary happens to be empty.
    for (let i = 0; i < this.panelStates.length; i++) {
      if (this.panelStates[i]?.dataset) return i
    }
    return -1
  }

  /**
   * Render the info panel for whichever slot `getInfoDisplayIndex`
   * points at, repopulate the picker dropdown with all loaded
   * datasets, and show/hide the picker based on how many are loaded.
   *
   * Call after: dataset load/unload, primary change, layout change,
   * info-panel-visibility toggle. Idempotent — safe to call
   * repeatedly.
   */
  private renderInfoPanel(): void {
    const idx = this.getInfoDisplayIndex()
    const dataset = idx >= 0 ? this.panelStates[idx]?.dataset ?? null : null

    if (!dataset) {
      // Nothing to show — hide the whole panel.
      document.getElementById('info-panel')?.classList.add('hidden')
      return
    }

    // Render the currently-selected dataset into the info panel body.
    displayDatasetInfo(
      dataset,
      this.appState.datasets,
      (id) => this.loadDataset(id, 'browse'),
    )
    this.syncInfoLegendThumb()
    void this.showStreamCredit(dataset)

    // Repopulate the picker with every loaded dataset (in panel order)
    // and wire the change handler once.
    const picker = document.getElementById('info-panel-picker')
    const select = document.getElementById('info-selector') as HTMLSelectElement | null
    if (!picker || !select) return

    const entries: Array<{ slot: number; label: string }> = []
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const d = this.panelStates[slot]?.dataset
      if (!d) continue
      const label = this.panelStates.length > 1
        ? `Panel ${slot + 1}: ${d.title}`
        : d.title
      entries.push({ slot, label })
    }

    // Only show the picker when there's a choice to make.
    if (entries.length > 1) {
      picker.classList.remove('hidden')
      // Rebuild options only if the set changed — a naive innerHTML
      // rewrite would lose focus if the user is mid-interaction.
      const wantSignature = entries.map(e => `${e.slot}:${e.label}`).join('|')
      if (select.dataset.signature !== wantSignature) {
        select.innerHTML = ''
        for (const e of entries) {
          const opt = document.createElement('option')
          opt.value = String(e.slot)
          opt.textContent = e.label
          select.appendChild(opt)
        }
        select.dataset.signature = wantSignature
      }
      select.value = String(idx)
    } else {
      picker.classList.add('hidden')
    }

    if (!this.infoSelectorWired) {
      this.infoSelectorWired = true
      select.addEventListener('click', (ev) => { ev.stopPropagation() })
      select.addEventListener('change', () => {
        const newSlot = parseInt(select.value, 10)
        if (Number.isFinite(newSlot)) {
          this.infoDisplayOverride = newSlot
          this.renderInfoPanel()
        }
      })
    }

    // Now that the body has content, apply the user's visibility
    // preference — the panel may still be hidden if they toggled it
    // off in Tools.
    this.applyDatasetInfoVisibility()
  }

  /**
   * Rebuild the floating per-panel legends from the current
   * panelStates array + view prefs. Called after any change that
   * affects whether legends should be visible or which dataset each
   * panel is showing:
   *
   * - Legend toggle flipped
   * - Info panel toggle flipped
   * - A dataset loaded/unloaded in any panel
   * - Layout changed (panels added/removed)
   * - Primary changed
   * - A panel's scale arrived or changed (`refreshLegendsIfStale`)
   */
  private refreshPanelLegends(): void {
    this.syncDisplayToDataset()
    // AYNI (multi-globe plan): called wherever a globe's dataset changes, so the chips follow here too.
    this.refreshPanelHeads()
    const legendOn = this.viewPrefs.legendVisible
    let anyColorbar = false
    this.legendScales = []
    this.legendFloating = []
    this.parkedLegendSlots = []

    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const panel = this.panelStates[slot]
      const dataset = panel?.dataset ?? null
      const legendLink = dataset?.legendLink ?? null

      // Decide whether this panel gets a floating legend:
      // - Off entirely if the Legend toggle is off
      // - Off if the panel has no dataset, and neither a scale nor a legendLink
      // - On otherwise
      // AYNI: also on for the primary in single view with the info panel
      // showing. The legend used to move into the info panel there, which
      // starts collapsed, so most datasets showed none (owner, 2026-10-04).
      // A data-encoded row carries its exact palette, range and units,
      // so the rendered colorbar supersedes the uploaded legend image —
      // which for these datasets describes at best the same thing and
      // at worst a previous encode.
      // AYNI: the same goes for a release, always, so the legend is the
      // bar Tools → View shows (owner, 2026-10-04). A classified release
      // gets its class list instead (classLegendUI).
      const scale = dataset ? this.legendScaleAt(slot) : null
      this.legendScales[slot] = scale

      // A release waits for its scale rather than flashing its picture
      // first; until then (or if it never decodes) the info panel keeps it.
      const picture = dataset?.releaseEncoding ? null : legendLink
      // AYNI (owner, 2026-10-11): the Legend switch no longer hides a legend; off, it is parked in Tools → View (legendDock.ts).
      // AYNI (DSA 1.8): a vector layer's own legend counts too.
      const hasLegend = !!dataset && (!!picture || !!scale || this.vectorLegendAt(slot).models.length > 0)
      const showFloating = legendOn && hasLegend
      this.legendFloating[slot] = showFloating && !!dataset
      if (hasLegend && !legendOn) {
        this.viewports.setPanelLegend(slot, null)
        this.viewports.setPanelColorbar(slot, null)
        this.parkedLegendSlots.push(slot)
        // Still something on screen carrying a palette.
        if (scale) anyColorbar = true
        continue
      }

      // AYNI: while the primary globe shows a change, its legend is the change's.
      const drawn = showFloating ? this.drawnLegend(slot, scale, { onOpen: () => openToolsTab('view') }) : null
      if (drawn) {
        this.viewports.setPanelLegend(slot, null)
        this.viewports.setPanelColorbar(slot, drawn)
        anyColorbar = true
        continue
      }
      this.viewports.setPanelColorbar(slot, null)

      if (showFloating && picture && dataset) {
        this.viewports.setPanelLegend(slot, picture, {
          title: dataset.title,
          onClick: () => this.openLegendModal(picture, dataset.title),
        })
      } else {
        this.viewports.setPanelLegend(slot, null)
      }
    }

    // Nothing on screen carries a palette any more — the dataset was
    // unloaded or swapped for a picture — so an open controls popover
    // is adjusting the colours of nothing, and an open Analyze panel is
    // showing statistics for a frame that is no longer displayed.
    if (!anyColorbar) {
      closeDisplayControls()
      closeAnalyzeUI()
    } else {
      // Still something to colour, but possibly a different dataset —
      // the Analyze panel's numbers belong to the one it opened on.
      notifyAnalyzeDatasetChanged(this.appState.currentDataset?.id ?? null)
    }
    // AYNI: each globe's legend can be moved and resized, and keeps its place (legendPlacement.ts);
    // its ✕, or a drag onto Tools, parks it in Tools → View (legendDock.ts).
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const legend = this.viewports.panelLegendElement(slot)
      if (legend) {
        makeLegendMovable(legend, slot, {
          onSettled: refreshTimeLabelPosition,
          onClose: () => this.parkLegends(),
          dropTargets: () => this.legendDock?.dropTargets() ?? [],
          onDropHover: (target) => this.legendDock?.hover(target),
          onDrop: (target) => this.legendDock?.drop(target),
        })
      }
    }
    this.legendDock?.refresh()
    // AYNI: with two or four globes, a legend is lifted off the information card, the chatbot's button and the transport.
    clearPanelLegends()
    this.syncInfoLegendThumb()
    refreshTimeLabelPosition()
  }

  /**
   * AYNI: a globe's drawn legend — a change's, a classified release's
   * classes, or the colour bar — fresh, for the globe or for Tools → View
   * (legendDock.ts). Null for a picture, which the panel shows as its image.
   */
  private drawnLegend(slot: number, scale: ColorScale | null, opts: { onOpen?: () => void } = {}): HTMLElement | null {
    const dataset = this.panelStates[slot]?.dataset
    if (!dataset) return null
    // AYNI: while the primary globe shows a change, its legend is the change's.
    const change = changeViewState()
    const range = shownRange(change)
    if (slot === this.viewports.getPrimaryIndex() && changeShown(change) && range) {
      const legend = buildChangeLegend({ mode: change.mode!, limit: range.max, min: range.min, colours: change.colours, units: scale?.units, frameLabel: change.field?.frameLabel, title: change.other?.legendTitle })
      // The colour bar's own box (.panel-colorbar): its corner, its lift above the transport, its step aside, and
      // legendPlacement's moves. Without it the legend fell into the panel's flow, under the corner buttons.
      legend.classList.add('panel-colorbar', 'change-legend-floating')
      return legend
    }
    // AYNI (DSA 1.8, §8): a vector layer that supersedes the video or its legend picture brings its own legend.
    const vector = this.vectorLegendAt(slot)
    // So does one over a dataset with no legend of its own to join (a still picture, DSA 1.9).
    const ownLegend = !!scale || (!dataset.releaseEncoding && !!dataset.legendLink)
    if ((vector.replaces || !ownLegend) && vector.models.length) {
      return renderVectorLegend(vector.models, t('vector.legend.aria', { title: dataset.title }))
    }
    if (!scale) return null
    const panelDisplay = this.panelDisplays[slot] ?? this.colorScaleDisplay
    const drawn = renderClassLegend({ scale, title: dataset.title }) ?? withPhaseBars(renderColorbar({
      scale,
      display: panelDisplay,
      title: dataset.title,
      // AYNI (owner, 2026-10-14): its reference lines, marked on the bar.
      references: shownReferenceLines(dataset.id),
      // AYNI: the colours are set in Tools → View.
      onOpen: opts.onOpen,
    }), scale, panelDisplay)
    // Otherwise its keys go under the dataset's bar (the sea-ice edge over the ice).
    const column = vector.models.length ? drawn.querySelector('.panel-colorbar-scale') : null
    if (column) {
      drawn.classList.add('vector-legend-joined')
      for (const m of vector.models) column.append(...vectorLegendSections(m, true))
    }
    return drawn
  }

  /** AYNI: the legends parked in Tools → View, drawn afresh (legendDock.ts). */
  private parkedLegends(): Array<{ slot: number; el: HTMLElement }> {
    const out: Array<{ slot: number; el: HTMLElement }> = []
    for (const slot of this.parkedLegendSlots) {
      const drawn = this.drawnLegend(slot, this.legendScales[slot] ?? null)
      if (drawn) {
        out.push({ slot, el: drawn })
        continue
      }
      const link = this.panelStates[slot]?.dataset?.legendLink
      if (!link) continue
      const box = document.createElement('div')
      box.className = 'panel-legend'
      const img = document.createElement('img')
      img.src = link
      img.alt = t('legend.dock.picture')
      box.appendChild(img)
      out.push({ slot, el: box })
    }
    return out
  }

  /** AYNI: park every globe's legend in Tools → View, each flying there (its ✕). */
  private parkLegends(): void {
    if (this.legendDock) this.legendDock.park()
    else this.setLegendVisible(false)
  }

  /**
   * AYNI: the scale a panel's legend is drawn from — the one Tools → View
   * colours (`displayScale`), so the legend and the View tab always show
   * the same bar. Null for a picture, and for a release until its frame
   * is up, which `refreshLegendsIfStale` waits out.
   */
  private legendScaleAt(slot: number): ColorScale | null {
    const dataset = this.panelStates[slot]?.dataset
    if (!dataset) return null
    if (dataset.renderEncoding === RENDER_ENCODING_DATA_LUMA) return dataset.colorScale ?? null
    if (!dataset.releaseEncoding) return null
    return this.viewports.getRendererAt(slot)?.displayScale() ?? null
  }

  /**
   * AYNI: redraw the legends when a panel's scale has changed since they
   * were drawn. The legends are drawn when a load starts, before the
   * globe has the frame a release's scale comes from, so without this a
   * release had no legend until something else redrew them. Cheap when
   * nothing changed; run on the View tab's timer.
   */
  private refreshLegendsIfStale(): void {
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      if (this.legendScaleAt(slot) !== (this.legendScales[slot] ?? null)) {
        this.refreshPanelLegends()
        return
      }
    }
  }

  /**
   * AYNI: the info panel's legend picture shows only when the Legend
   * switch is on and the dataset it describes has no legend on its globe.
   * Two legends would be one too many, and for a dataset with values the
   * picture, made for the published colours, would disagree with the
   * drawn one after a change in Tools → View.
   */
  private syncInfoLegendThumb(): void {
    const thumb = document.querySelector<HTMLElement>('.info-legend-thumb')
    if (!thumb) return
    const idx = this.getInfoDisplayIndex()
    const floating = idx >= 0 && !!this.legendFloating[idx]
    // AYNI: the picture shows the published colours; a dataset drawn in
    // other ones (a display group's, or the viewer's) would contradict it.
    const recoloured = idx >= 0 && !isDefaultDisplay(this.panelDisplays[idx] ?? DEFAULT_DISPLAY)
    thumb.style.display = this.viewPrefs.legendVisible && !floating && !recoloured ? '' : 'none'
  }

  /**
   * Apply a palette / limits / threshold transform, and fan it out to all
   * panels. AYNI: set from Tools → View (viewPaletteUI), which replaced
   * the colorbar's own popover.
   *
   * The transform goes to every globe rather than the one that was
   * tapped: in a 2- or 4-globe layout the panels exist to be compared,
   * and comparing two fields through two different palettes is worse
   * than not comparing them.
   */
  private applyColorScaleDisplay(next: ColorScaleDisplay, opts: { save?: boolean } = {}): void {
    this.colorScaleDisplay = next
    setActiveDisplay(next)
    // AYNI: a viewer's change is kept for the dataset (displayPrefs.ts) and,
    // signed in, follows the account. A Khipu's colours are its own and are
    // not (`save: false`); they last until the dataset changes.
    const dataset = this.appState.currentDataset
    this.displayTemporary = opts.save === false
    if (dataset && opts.save !== false) saveDisplayFor(dataset.id, next, defaultDisplayOf(dataset))
    // Rebuild the floating bars so they track the globe. Cheap:
    // this is DOM, and the LUT upload has already happened.
    this.refreshPanelLegends()
    this.viewPalette?.refresh()
    this.viewOpacity?.refresh()
  }

  /**
   * AYNI: colours belong to a dataset (owner, 2026-10-09). When the
   * primary dataset changes it opens with, highest first, what the viewer
   * saved for it, its display group's standard, or its own published
   * colours (`displayFor`); nothing carries over from the last one. Then
   * every panel gets its own dataset's colours: the primary the live
   * display, a panel showing the same dataset the same, any other its own.
   */
  private syncDisplayToDataset(): void {
    const dataset = this.appState.currentDataset ?? null
    const id = dataset?.id ?? null
    if (id !== this.displayDatasetId) {
      this.displayDatasetId = id
      this.displayTemporary = false
      this.colorScaleDisplay = dataset ? displayFor(dataset) : DEFAULT_DISPLAY
      setActiveDisplay(this.colorScaleDisplay)
      // AYNI (owner, 2026-10-12): opacity is not remembered; a new dataset starts opaque and shown.
      this.baseOpacity = 1
      this.baseHidden = false
    }
    this.applyBaseOpacity()
    const primary = this.viewports.getPrimaryIndex()
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const shown = this.panelStates[slot]?.dataset ?? null
      // AYNI (owner, 2026-10-22): a room's follower draws the other globes in the lead's colours.
      const display = slot === primary || (shown && shown.id === id)
        ? this.colorScaleDisplay
        : shown ? this.roomDisplays?.get(shown.id) ?? displayFor(shown) : DEFAULT_DISPLAY
      this.panelDisplays[slot] = display
      this.viewports.setPanelColorScaleDisplay(slot, display)
    }
  }

  /** AYNI: the account's colours arrived (displayPrefsSync): redraw with them, unless a Khipu's are showing. */
  private reloadDisplayPrefs(): void {
    const dataset = this.appState.currentDataset
    if (dataset && !this.displayTemporary) {
      this.colorScaleDisplay = displayFor(dataset)
      setActiveDisplay(this.colorScaleDisplay)
    }
    this.refreshPanelLegends()
    this.viewPalette?.refresh()
    this.viewOpacity?.refresh()
  }

  /** Open the full-size legend modal for a dataset. Mirrors the
   *  legend click handler inside datasetLoader's info panel so the
   *  floating legends and the info-panel thumbnail use the same UI. */
  private openLegendModal(src: string, title: string): void {
    let overlay = document.getElementById('legend-modal-overlay')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = 'legend-modal-overlay'
      overlay.className = 'legend-modal-overlay'
      overlay.setAttribute('role', 'dialog')
      overlay.setAttribute('aria-modal', 'true')
      overlay.innerHTML = `<img class="legend-modal-img" alt="Legend"><button class="legend-modal-close" aria-label="Close legend">&times;</button>`
      const closeModal = () => overlay!.classList.add('hidden')
      overlay.querySelector('.legend-modal-close')!.addEventListener('click', (e) => {
        e.stopPropagation()
        closeModal()
      })
      overlay.addEventListener('click', closeModal)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay!.classList.contains('hidden')) closeModal()
      })
      document.body.appendChild(overlay)
    }
    const img = overlay.querySelector('img') as HTMLImageElement
    img.src = src
    img.alt = `${title} legend`
    overlay.setAttribute('aria-label', `${title} legend`)
    overlay.classList.remove('hidden')
  }

  private dismissBrowseAfterLoad(): void {
    // Multi-viewport mode keeps the browse panel around so the
    // visitor can load datasets into the remaining panels.
    // Catalog mode (`?catalog=true`) keeps it around for the same
    // reason — the catalog↔sphere tab control treats the
    // collapsed browse panel as the "back to catalog" affordance.
    // Single-viewport sphere mode hides the panel outright.
    if (this.viewports.getPanelCount() > 1 || getCatalogMode()) {
      collapseBrowseUI()
    } else {
      hideBrowseUI()
    }
  }

  /**
   * Open (or re-open) the dataset browse panel. Called by the
   * Browse button in the map-controls toolbar. Handles all three
   * states the panel can be in: hidden (initial), collapsed (after
   * a previous multi-view load), or already visible (no-op).
   */
  /**
   * AYNI: what a shared session may do with this page (roomSync host): a
   * room's lead reads it, a follower is steered through it. Shared with
   * an output window (multi-globe plan, phase 4), which follows this page
   * over a BroadcastChannel as a room follower would.
   */
  private roomSyncHost(): RoomSyncHost {
    let followedNoteKey = 'null'
    return {
      getDatasetId: () => this.appState.currentDataset?.id ?? null,
      loadDataset: (id) => {
        // AYNI: the room starts before the catalogue has loaded; a dataset
        // it does not know yet is asked for again with the lead's next
        // message, rather than shown as "Dataset not found".
        if (!dataService.getDatasetById(id)) {
          // AYNI: a teacher's lesson can show a library dataset this browser has not registered yet.
          if (id.startsWith(LIBRARY_PREFIX)) {
            void ensureLibraryDataset(id).catch(() => false)
            return false
          }
          // Still loading the catalogue: ask again. Loaded and still
          // unknown: load it anyway, which says "Dataset not found".
          if (this.appState.datasets.length === 0) return false
        }
        this.asRoom(() => { void this.loadDataset(id) })
        return true
      },
      clearDataset: () => this.asRoom(() => { void this.goHome() }),
      getVideo: () => this.hlsService?.video ?? null,
      isPlaying: () => this.appState.isPlaying,
      setPlaying: (want) => this.setRoomPlaying(want),
      getGlobe: getRoomGlobe,
      setFollowedGlobe: setFollowedRoomGlobe,
      getView: () => {
        const map = this.viewports.getPrimary()?.getMap()
        if (!map) return null
        const center = map.getCenter()
        return {
          lat: center.lat,
          lon: center.lng,
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          // AYNI (2026-10-19): the flat map; absent, the globe.
          ...(this.flatMap ? { flat: true as const } : {}),
        }
      },
      setView: (view) => {
        const map = this.viewports.getPrimary()?.getMap()
        if (!map) return
        // AYNI (2026-10-19): the lead's flat map or globe first; the view once switched (the switch sets its own camera).
        if (!!view.flat !== this.flatMap) {
          void this.setFlatMap(!!view.flat).then(() => this.easeRoomView(this.viewports.getPrimaryIndex(), view))
          return
        }
        // MapLibre does not wrap the camera's longitude, so the lead's
        // is taken the short way round from wherever this globe is.
        const here = map.getCenter().lng
        const lng = here + (((((view.lon - here + 180) % 360) + 360) % 360) - 180)
        // A short linear ease: at ten messages a second it reads as
        // the lead's own motion rather than a string of jumps.
        map.easeTo({
          center: [lng, view.lat],
          zoom: view.zoom,
          bearing: view.bearing,
          pitch: view.pitch,
          // Longer than the 100 ms between the lead's messages, so each
          // ease is still running when the next one takes over from it
          // and the globe never comes to rest mid-gesture. (150 ms let
          // it stop between messages: still frames in every drag.)
          duration: 260,
          easing: (t) => t,
        })
      },
      getLayers: () => ({
        // No basemap since 2026-10-12; the field stays for older clients.
        basemapId: null,
        overlays: this.mapLayerSelection.overlays.map((o) => ({ id: o.id, tint: o.tint })),
        rt: this.rtOverlays[0]?.id ?? null,
        // AYNI: the second overlay; a client that knows only one ignores it.
        ...(this.rtOverlays[1] ? { rt2: this.rtOverlays[1].id } : {}),
        ...(this.rtOverlays[2] ? { rt3: this.rtOverlays[2].id } : {}),
        ...(this.rtOverlays[3] ? { rt4: this.rtOverlays[3].id } : {}),
        // AYNI (owner, 2026-10-12): the opacities and eyes, when any is not opaque and shown.
        ...this.roomLookOf(),
      }),
      setLayers: (layers) => this.applyRoomLayers(layers),
      setLocked: (locked, driving) => {
        this.roomMeetingLock = locked
        // AYNI (multi-globe plan, phase 3): the globe this device drives stays in its hands.
        const drove = this.roomDriving
        this.roomDriving = driving ?? null
        this.applyRoomLock()
        if (drove !== this.roomDriving) {
          if (drove !== null && this.browseTarget === drove) this.setBrowseTarget(null)
          this.refreshPanelHeads()
        }
      },
      // AYNI (multi-globe plan, phase 3): this page's globes, for the room.
      getSlots: () => {
        if (this.viewports.getPanelCount() <= 1) return null
        const primary = this.viewports.getPrimaryIndex()
        return {
          layout: this.viewports.getLayout(),
          primary,
          linked: this.viewports.camerasAreLinked(),
          slots: this.panelStates.map((p, i) => ({
            datasetId: p.dataset?.id ?? null,
            ...(formatTimeRule(this.panelTimeRules[i] ?? FOLLOW) ? { time: formatTimeRule(this.panelTimeRules[i] ?? FOLLOW) } : {}),
            ...(i !== primary ? { view: this.roomViewOf(i), layers: this.roomLayersOf(i) } : {}),
          })),
        }
      },
      setSlots: (state, driving) => this.applyRoomSlots(state, driving),
      getSlotUpdate: (n) => {
        const panel = this.panelStates[n]
        if (!panel) return null
        return { view: this.roomViewOf(n), datasetId: panel.dataset?.id ?? null, time: formatTimeRule(this.panelTimeRules[n] ?? FOLLOW) }
      },
      setSlotUpdate: (n, update) => this.applyRoomSlotUpdate(n, update),
      setCamerasLinked: (linked) => this.viewports.setCamerasLinked(linked),
      setExploring: (exploring, free) => this.setRoomExploring(exploring, free),
      // AYNI: the presenter's Khipu step, its captions and its pins, shown to the room.
      getNote: () => (activeKhipuPlayer()?.isOpen() ? this.khipuRoomNote : null),
      // AYNI (owner, 2026-10-22): the colours, the data pins and the pin graph follow the lead too.
      getColours: () => this.roomColoursNow(),
      setFollowedColours: (colours) => this.applyRoomColours(colours),
      getDataPins: () => this.roomDataPinsNow(),
      setFollowedPins: (pins) => this.applyRoomPins(pins),
      // AYNI (satellites plan): the lead's satellites, their clock and its selected object.
      getSatellites: () => roomSatellitesOf(this.satellites),
      setFollowedSatellites: (sats, at) => applyRoomSatellites(this.satellites, sats, at),
      restoreOwnSatellites: () => this.satellites.restoreOwn(),
      keepFollowed: () => {
        // The lead's satellites stay on as they are, now this device's own choice.
        this.satellites.followed = false
        // The old lead's pins and colours are this device's own now: nothing to give back.
        this.roomOwnPins = null
        this.dataPinStore?.keepShared()
        this.roomGraph = null
        this.roomGraphOut = false
        this.roomDisplays = null
        this.roomColoursKey = ''
        this.roomBaseColoured = false
        // Shown as they are, still not saved: like a Khipu's, they last until the dataset changes.
        this.roomRtColoured.clear()
      },
      setFollowedNote: (note) => {
        // Every state carries it, ten a second while the presenter moves: redraw on a change only.
        const key = JSON.stringify(note)
        if (key === followedNoteKey) return
        followedNoteKey = key
        showRoomNote(note)
        this.showKhipuPins(note?.pins ?? [])
      },
    }
  }

  private openBrowsePanel(): void {
    const overlay = document.getElementById('browse-overlay')
    // Only call showBrowseUI once — it wires event listeners on
    // category chips, search input, etc. Re-calling it after a
    // hideBrowseUI() would duplicate those listeners.
    if (!overlay || overlay.dataset.browseInitialized !== 'true') {
      showBrowseUI(this.appState.datasets, this.browseCallbacks())
      // Mark as initialized so subsequent opens skip showBrowseUI.
      const el = overlay ?? document.getElementById('browse-overlay')
      if (el) el.dataset.browseInitialized = 'true'
      return
    }
    // Already rendered — either collapsed or fully visible. Remove
    // the collapsed class either way and ensure `browse-open` is
    // set so other UI can react.
    const wasCollapsed = overlay.classList.contains('collapsed')
    const wasHidden = overlay.classList.contains('hidden')
    overlay.classList.remove('collapsed')
    overlay.classList.remove('hidden')
    document.body.classList.add('browse-open')
    // Re-opening from collapsed (or hidden, in pathological cases
    // where showBrowseUI ran but the overlay got hidden externally)
    // is conceptually a fresh open — emit browse_opened and
    // restart the dwell handle. The collapseBrowseUI path stops
    // dwell, so this pairs with that.
    if (wasCollapsed || wasHidden) {
      notifyBrowseOpened('tools')
    }
  }

  /**
   * Catalog tab clicked. Re-add `?catalog=true` to the URL (if it
   * was dropped by a previous Sphere click), expand the browse
   * panel, and flip the tab to Catalog-active. Body class
   * `catalog-mode` is sticky for the session — this handler just
   * re-asserts it in case something else cleared it.
   */
  private enterCatalogTab(): void {
    if (!getCatalogMode()) setCatalogMode(true)
    document.body.classList.add('catalog-mode')
    if (!this.appState.currentDataset) {
      document.body.classList.add('catalog-empty')
    }
    this.openBrowsePanel()
    setActiveCatalogTab('catalog')
  }

  /**
   * Sphere tab clicked. Drop `?catalog=true` from the URL,
   * collapse (or hide, when no dataset is loaded) the browse
   * panel, and flip the tab to Sphere-active. The
   * `catalog-mode` body class stays set so the tab control
   * remains visible — Plan §3.2's "shared MapRenderer between
   * tabs" pattern means we never tear catalog mode down within
   * a session, only on a fresh page load.
   */
  private enterSphereTab(): void {
    if (getCatalogMode()) setCatalogMode(false)
    document.body.classList.remove('catalog-empty')
    if (this.appState.currentDataset) {
      collapseBrowseUI()
    } else {
      hideBrowseUI()
    }
    setActiveCatalogTab('sphere')
  }

  /**
   * Wire a popstate handler so browser back/forward syncs the
   * catalog-mode UI with whatever the new URL says. Dataset
   * loads on popstate are out of scope (a pre-existing gap in
   * the app, not specific to catalog mode); the visitor sees
   * the URL flip and the browse-panel/tab visibility update but
   * the loaded dataset doesn't change without a refresh.
   */
  private wireCatalogModePopstate(): void {
    window.addEventListener('popstate', () => {
      const onCatalog = getCatalogMode()
      const datasetLoaded = this.appState.currentDataset !== null
      if (onCatalog) {
        document.body.classList.add('catalog-mode')
        showCatalogTabs()
        if (datasetLoaded) {
          // `?catalog=true&dataset=<id>` — case 2 from §3.1:
          // globe visible, browse panel collapsed as the "back to
          // catalog" affordance, Sphere tab active. Mirrors the
          // boot-path branch that handles the same URL shape.
          document.body.classList.remove('catalog-empty')
          collapseBrowseUI()
          setActiveCatalogTab('sphere')
        } else {
          // `?catalog=true` (no dataset) — case 1: browse panel
          // full-surface, globe hidden, Catalog tab active.
          document.body.classList.add('catalog-empty')
          this.openBrowsePanel()
          setActiveCatalogTab('catalog')
        }
      } else {
        document.body.classList.remove('catalog-empty')
        if (document.body.classList.contains('catalog-mode')) {
          // Sticky body class means tabs stay visible — just flip
          // active to Sphere.
          setActiveCatalogTab('sphere')
          if (datasetLoaded) {
            collapseBrowseUI()
          } else {
            hideBrowseUI()
          }
        } else {
          hideCatalogTabs()
        }
      }
    })
  }

  /** Detect WebGL support. If unavailable, display troubleshooting instructions and return false. */
  private checkWebGLSupport(): boolean {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    if (gl) return true

    const screen = document.getElementById('loading-screen')
    if (screen) {
      screen.innerHTML = `
        <div style="max-width:480px;padding:2rem;text-align:center;color:#e0e0e0;font-family:system-ui,sans-serif;">
          <div style="font-size:2.5rem;margin-bottom:0.75rem;" aria-hidden="true">&#x1F30D;</div>
          <h1 style="font-size:1.1rem;margin:0 0 0.75rem;color:#fff;">WebGL is not available</h1>
          <p style="font-size:0.8rem;line-height:1.5;color:#aaa;margin:0 0 1.25rem;">
            This application requires WebGL to render the interactive globe.
            Your browser's GPU acceleration appears to be disabled.
          </p>
          <details style="text-align:start;font-size:0.75rem;color:#999;line-height:1.6;">
            <summary style="cursor:pointer;color:#22c55e;margin-bottom:0.5rem;">How to fix this</summary>
            <ol style="padding-inline-start:1.25rem;margin:0;">
              <li>Open <strong style="color:#fff;">chrome://flags</strong> in your address bar</li>
              <li>Search for <strong style="color:#fff;">Override software rendering list</strong></li>
              <li>Set it to <strong style="color:#fff;">Enabled</strong> and relaunch Chrome</li>
            </ol>
            <p style="margin:0.75rem 0 0.25rem;color:#888;">Alternatively, launch Chrome from the terminal with:</p>
            <code style="display:block;background:#1a1a2e;padding:0.5rem 0.75rem;border-radius:4px;color:#22c55e;font-size:0.7rem;overflow-x:auto;">
              google-chrome --enable-webgl --ignore-gpu-blocklist
            </code>
            <p style="margin:0.75rem 0 0;color:#888;">
              If the problem persists, check <strong style="color:#fff;">chrome://gpu</strong>
              for driver issues. Installing or updating your GPU drivers
              (e.g. <code style="color:#ccc;">sudo apt install nvidia-driver-xxx</code>
              or <code style="color:#ccc;">mesa-utils</code>) usually resolves this.
            </p>
          </details>
        </div>`
      screen.style.display = 'flex'
      screen.style.alignItems = 'center'
      screen.style.justifyContent = 'center'
      screen.classList.remove('fade-out')
    }
    return false
  }

  /** Set the loading state. When false, fades out the loading screen with a short delay. */
  private setLoading(isLoading: boolean): void {
    this.appState.isLoading = isLoading
    if (!isLoading) {
      const screen = document.getElementById('loading-screen')
      if (screen) {
        screen.setAttribute('aria-busy', 'false')
        this.setLoadingStatus('Ready', 100)
        this.loadingHideTimer = setTimeout(() => {
          this.loadingHideTimer = null
          screen.style.opacity = ''
          screen.classList.add('fade-out')
          screen.addEventListener('transitionend', () => {
            if (screen.classList.contains('fade-out')) {
              screen.style.display = 'none'
            }
          }, { once: true })
        }, LOADING_HIDE_DELAY_MS)
      }
    }
  }

  /** Update the loading screen status message and progress bar. */
  private setLoadingStatus(message: string, progress?: number): void {
    const statusEl = document.getElementById('loading-status')
    if (statusEl) statusEl.textContent = message
    if (progress !== undefined) {
      const track = document.querySelector('.loading-progress-track')
      if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)))
      const fill = document.getElementById('loading-progress-fill')
      if (fill) (fill as HTMLElement).style.width = `${progress}%`
    }
  }

  /** Display an error message in the error banner and log it. */
  /**
   * AYNI (security fix plan 2026-10, S-02): the stream host, or the site's
   * token route, refused a stream. Signed out: a line saying to sign in.
   * Signed in: the plans, with a line saying the plan does not include it
   * (the same path a locked card takes, `streamLockedBy`). Once a minute
   * at most (streamToken.ts throttles the telling).
   */
  private showStreamDenied(why: StreamDenial): void {
    const signedOut = why === 'signed_out' || accountState().status !== 'signedIn'
    const notice = t(signedOut ? 'stream.denied.signIn' : 'stream.denied.plan')
    this.announce(notice)
    if (isVrActive()) {
      // AYNI (Quest review B2): the headset's own words (there are no plans "below" in a headset).
      notifyXr(t(signedOut ? 'vr.notice.streamSignIn' : 'vr.notice.streamPlan'))
      return
    }
    if (signedOut) {
      this.setError(notice)
      return
    }
    // A meeting's participant (a class exploring) is told, not offered plans.
    if (!currentMeetingRoom() || presentingMeeting()) openPlans(null, undefined, { notice, onStartMeeting: (trigger: HTMLElement) => openMeetingPanel(trigger) })
    else this.setError(notice)
  }

  private setError(error: string): void {
    this.appState.error = error
    const errorEl = document.getElementById('error-message')
    if (errorEl) {
      const textEl = document.getElementById('error-text')
      if (textEl) textEl.textContent = error
      errorEl.classList.toggle('hidden', !error)
      const dismissBtn = document.getElementById('error-dismiss')
      if (dismissBtn) {
        dismissBtn.onclick = () => {
          errorEl.classList.add('hidden')
          this.appState.error = null
        }
      }
    }
    logger.error('[App] Error:', error)
  }

  /** Push a message to the ARIA live region for screen reader announcements. */
  private announce(message: string): void {
    const el = document.getElementById('a11y-announcer')
    if (el) {
      el.textContent = ''
      requestAnimationFrame(() => { el.textContent = message })
    }
  }

  /** Emit a `layer_loaded` event and remember when the slot filled so
   * the matching `layer_unloaded` can report dwell_ms. */
  private emitLayerLoaded(
    dataset: Dataset,
    slot: number,
    trigger: import('./types').LoadTrigger,
    source: import('./types').LayerSource,
    loadMs: number,
  ): void {
    const now = Date.now()
    if (this.panelStates[slot]) {
      this.panelStates[slot].loadedAt = now
    }
    emit({
      event_type: 'layer_loaded',
      layer_id: dataset.id,
      layer_source: source,
      slot_index: String(slot),
      trigger,
      load_ms: Math.max(0, Math.round(loadMs)),
    })
  }

  /** Emit `layer_unloaded` for whatever dataset currently occupies
   * `slot`, if any. No-op when the slot is empty. Clears the
   * `loadedAt` timestamp so repeat unloads don't double-count. */
  private emitLayerUnloadedForSlot(
    slot: number,
    reason: import('./types').UnloadReason,
  ): void {
    const panel = this.panelStates[slot]
    if (!panel || !panel.dataset) return
    const loadedAt = panel.loadedAt ?? Date.now()
    emit({
      event_type: 'layer_unloaded',
      layer_id: panel.dataset.id,
      slot_index: String(slot),
      reason,
      dwell_ms: Math.max(0, Date.now() - loadedAt),
    })
    panel.loadedAt = null
  }

  /** Emit `playback_action` for a transport-control event. Reads the
   * current video's time and rate so downstream queries can see how
   * far into a time-series users actually scrub. */
  private emitPlaybackAction(action: 'play' | 'pause' | 'seek' | 'rate'): void {
    const layer_id = this.appState.currentDataset?.id ?? 'unknown'
    const video = this.hlsService?.getVideo?.()
    emit({
      event_type: 'playback_action',
      layer_id,
      action,
      playback_time_s: video?.currentTime ?? 0,
      playback_rate: video?.playbackRate ?? 1,
    })
  }

  /**
   * Wire the Enter VR button. The button hides itself on browsers
   * without WebXR, so calling this unconditionally is safe — the
   * Three.js chunk only downloads on devices that advertise
   * `immersive-vr` support (see src/ui/vrButton.ts). VR mode always
   * uses the primary panel's dataset; in multi-globe layouts the
   * other panels keep rendering in 2D behind the scenes.
   *
   * Both image and video datasets are supported: image datasets
   * reuse the primary panel's already-decoded `HTMLImageElement`
   * (stored in `panelStates[slot].image` after `loadImageDataset`
   * returns), so VR doesn't re-fetch or re-decode the bytes;
   * video datasets reuse the existing HLS `<video>` element.
   */
  private async wireVrButton(): Promise<void> {
    // Shared helper — returns the texture spec for a specific
    // panel slot by looking at that slot's own panel state (its
    // dataset + HLS service + decoded image). This is the
    // multi-panel foundation; the single-panel getters below are
    // just convenience wrappers that call it for the primary slot.
    // Per-slot memoization. `vrSession` polls `getPanelTexture` at
    // XR frame rate (~90 Hz on Quest); recomputing the spec —
    // including `overlayOptionsFromDataset` which allocates a fresh
    // options object for datasets with any Phase 3d field set —
    // would burn ~5400 allocations/min for no behavioural reason.
    // Cache on input-reference identity: as long as the panel's
    // dataset, image, and video references are stable, the spec is
    // the same object. Dataset metadata mutated in place would
    // bypass this cache; the codebase replaces dataset objects on
    // load rather than mutating, and `photorealEarth.setTexture`
    // re-applies overlay options on every call regardless of
    // memoization so a stale spec is at most one frame visible.
    const specCache: Array<{
      dataset: Dataset | null
      image: HTMLImageElement | null
      video: HTMLVideoElement | null
      spec: VrDatasetTexture | null
    } | undefined> = []
    const getPanelTexture = (slot: number): VrDatasetTexture | null => {
      const panel = this.panelStates[slot]
      const dataset = panel?.dataset ?? null
      const image = dataset && dataService.isImageDataset(dataset)
        ? (panel?.image ?? null)
        : null
      const video = dataset && !dataService.isImageDataset(dataset)
        ? (panel?.hlsService?.getVideo() ?? null)
        : null
      const cached = specCache[slot]
      if (
        cached
        && cached.dataset === dataset
        && cached.image === image
        && cached.video === video
      ) {
        return cached.spec
      }
      // Phase 3h: forward the same overlay options the 2D renderer
      // consumes (bbox / lonOrigin / flipY / celestialBody) so the
      // VR Phong material can clip regional datasets to their bbox
      // and reveal an Earth base diffuse outside. `undefined` for
      // datasets at all defaults — keeps legacy globals on the fast
      // path through the shader.
      let spec: VrDatasetTexture | null = null
      if (dataset) {
        const options = vrOverlayOptionsFor(dataset)
        if (image) spec = { kind: 'image', element: image, options }
        else if (video) spec = { kind: 'video', element: video, options }
      }
      specCache[slot] = { dataset, image, video, spec }
      return spec
    }

    const vrContext: VrSessionContext = {
      // Primary-focused convenience getters — equivalent to calling
      // the panel-slot version with the current primary index.
      getDatasetTexture: () => getPanelTexture(this.viewports.getPrimaryIndex()),
      getDatasetTitle: () => this.appState.currentDataset?.title ?? null,
      getDatasetId: () => this.appState.currentDataset?.id ?? null,
      getDatasetTimeLabel: () => {
        // Compute the label fresh from video.currentTime every call.
        //
        // Why not read appState.timeLabel? WebXR pauses
        // window.requestAnimationFrame while an immersive session
        // is live, which freezes `startPlaybackLoop` — the
        // per-frame loop that normally keeps appState.timeLabel
        // updated. A VR session reading appState.timeLabel would
        // see the value from the instant the user tapped Enter VR
        // and stay there forever. Recomputing here makes the
        // label advance on the XR animation loop instead, and as
        // a bonus gives us correct pause behaviour for free: a
        // paused video's currentTime doesn't advance, so the
        // formatted string stays fixed until playback resumes.
        const ds = this.appState.currentDataset
        if (!ds?.startTime) return null

        if (dataService.isVideoDataset(ds)) {
          const video = this.hlsService?.video
          if (!video || !ds.endTime) return null
          // `HTMLVideoElement.duration` is `NaN` until the video's
          // metadata has loaded — `?? 1` doesn't catch NaN because
          // it isn't nullish, so the previous check would pass NaN
          // straight into `videoTimeToDate` and `formatDate` would
          // render "Invalid Date". Suppress the label entirely
          // until duration is available; the per-frame poll will
          // pick it up the first frame after `loadedmetadata`.
          const duration = this.hlsService?.duration
          if (!Number.isFinite(duration) || (duration as number) <= 0) return null
          const start = new Date(ds.startTime)
          const end = new Date(ds.endTime)
          const snapMs = this.playback.displayInterval?.intervalMs
          const currentDate = videoTimeToDate(video.currentTime, duration as number, start, end, snapMs)
          const showTime = ds.period
            ? isSubDailyPeriod(ds.period)
            : (this.playback.displayInterval?.showTime ?? false)
          return formatDate(currentDate, showTime)
        }

        // Image dataset — the startTime is the only thing to show.
        const showTime = ds.period ? isSubDailyPeriod(ds.period) : false
        return formatDate(new Date(ds.startTime), showTime)
      },
      getDatasetTimeline: () => this.timelineSnapshot(),
      seekToTimelineDate: (epochMs) => {
        // AYNI: a refusal says so in the session rather than doing nothing.
        if (this.refusedByMeeting()) notifyXr(t('vr.notice.presenter'))
        else this.seekTimelineDate(epochMs)
      },
      hasVideoDataset: () => {
        const ds = this.appState.currentDataset
        return !!ds && dataService.isVideoDataset(ds)
      },
      // AYNI (Quest review B2): playing, but without the next frames yet.
      isBuffering: () => {
        const video = this.hlsService?.video
        return !!video && this.appState.isPlaying && !video.paused && !video.ended && video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA
      },
      isPlaying: () => this.appState.isPlaying,
      togglePlayPause: () => {
        if (this.refusedByMeeting()) {
          notifyXr(t('vr.notice.presenter'))
          return
        }
        togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m))
      },
      isMuted: () => {
        // AYNI (Quest review A5, 2026-11-03): in a meeting with someone to
        // hear (the presenter, a globe driver), the speaker button is the
        // meeting's sound: the page's Listen buttons are out of reach.
        const meeting = meetingSound()
        if (meeting) return meeting === 'off'
        // Read directly off the primary's <video> element —
        // hlsService sets it muted by default for autoplay
        // compliance; the 2D mute button (and the new VR HUD mute
        // button) flip it when the user wants sound.
        return this.hlsService?.video?.muted ?? true
      },
      toggleMute: () => {
        const meeting = meetingSound()
        if (meeting) {
          setMeetingSound(meeting === 'off')
          this.announce(meeting === 'off' ? 'Unmuted' : 'Muted')
          return
        }
        const video = this.hlsService?.video
        if (!video) return
        video.muted = !video.muted
        this.announce(video.muted ? 'Muted' : 'Unmuted')
      },

      // --- Phase 2.5 multi-panel getters ---
      getPanelCount: () => this.viewports.getPanelCount(),
      getPrimaryIndex: () => this.viewports.getPrimaryIndex(),
      getPanelTexture,
      getPanelTitle: (slot: number) =>
        this.panelStates[slot]?.dataset?.title ?? null,

      // --- Phase 3 in-VR browse ---
      getDatasets: () => {
        // AYNI: the licence's packages once the account is known (as Browse decides its locks).
        const packages = accountState().status === 'unknown' ? null : effectivePackages()
        // AYNI (owner, 2026-10-30, C9): a NOAA row whose stream twin the plan plays is left out, as in Browse.
        const twinHidden = twinHiddenIds(this.appState.datasets, packages, educationStreamsAllowed())
        return this.appState.datasets
          .filter(d => dataService.isSupportedDataset(d) && !d.isHidden && !twinHidden.has(d.id))
          .map(d => {
            // Mirror the 2D browse UI (browseUI.ts line 79-90):
            // chips are the UNION of enriched.categories keys and
            // `Dataset.tags`, minus 'Movies' and 'Layers' (those
            // are internal shape-of-data markers, not user-facing
            // filters). Without the tags merge, chips like
            // "Real-Time" — which live exclusively on tags — never
            // appear.
            const cats = new Set<string>()
            if (d.enriched?.categories) {
              for (const k of Object.keys(d.enriched.categories)) cats.add(k)
            }
            if (d.tags) {
              for (const t of d.tags) cats.add(t)
            }
            cats.delete('Movies')
            cats.delete('Layers')
            // AYNI (the AR refresh): the browser's kind and themes, and the
            // lock on a stream outside the plan, for the headset's Browse.
            const kind = dataKindOf(d)
            const locked = packages ? outsidePlan(d, packages, educationStreamsAllowed()) : null
            return {
              id: d.id,
              title: displayTitle(d),
              categories: Array.from(cats),
              thumbnailUrl: d.thumbnailLink ?? null,
              kind,
              kindLabel: t(DATA_KIND_LABEL[kind]),
              kindOrder: DATA_KINDS.indexOf(kind),
              themes: themesOf(d).map((th) => ({ id: th, label: t(THEME_LABEL[th]), order: THEMES.indexOf(th) })),
              locked: locked ? t(PACKAGE_NAME[locked]) : null,
            }
          })
      },
      loadDataset: (id: string) => {
        void this.loadDataset(id, 'orbit')
      },

      // AYNI: the same layer stack on the VR globe (polled per frame).
      getMapLayerImages: () => this.mapLayerImages,
      getDatasetLook: () => ({ opacity: this.baseOpacity, hidden: this.baseHidden }),
      // AYNI (2026-10-27, AYNI 3D docs/AI_CHATBOT_VOICE.md): the AYNI chatbot's voice on the dock's mic and caption strip.
      getVoiceState: () => this.ayniChat?.voice.vrState() ?? null,
      toggleVoice: () => this.ayniChat?.voice.tap(),
      // AYNI: the geo-media dataset's dots and panel, and taps on its globe.
      getGeoMedia: () => this.geoMedia?.vrState ?? null,
      // AYNI (DSA 1.8): every globe's vector layers, and the feature a tap inspected.
      getVectorFrames: () => this.vectorFramesForXr(),
      getVectorInspection: () => this.vectorXrInspection,
      // AYNI (satellites plan): the orbit shell around the headset/phone globe.
      getSatellites: () => this.satellites,
      // AYNI (owner, 2026-11-04): Solar System mode, drawn in the room while the session runs.
      getSolar: () => (isOutputWindow() || isWallDisplay() ? null : this.solarMode()),
      // AYNI: the playing Khipu or lesson, and its buttons, in the headset.
      getKhipu: () => {
        // AYNI (meeting games plan): a player in a meeting game sees the game there.
        const game = gameVrState()
        if (game) return game
        const player = activeKhipuPlayer() ?? activeLessonPlayer()
        if (player?.isOpen()) return player.vrState()
        // A meeting's room: the presenter's step, read-only.
        return roomNoteVrState(shownRoomNote())
      },
      khipuAction: (action) => {
        if (gameVrAnswer(action)) return
        const khipu = activeKhipuPlayer()
        const player = khipu ?? activeLessonPlayer()
        if (!player?.isOpen()) return
        if (action === 'close') player.close()
        else if (action === 'prev') player.prev()
        else if (action === 'play') khipu?.togglePlay()
        else if (action.startsWith('quiz-')) khipu?.answerQuiz(Number(action.slice(5)))
        else if (action.startsWith('rt-')) { if (!khipu) activeLessonPlayer()?.toggleRealtime(Number(action.slice(3))) }
        else if (player.vrState().isLast) player.close()
        else player.next()
      },
      onGlobeTap: (lat, lon, kind) => this.onVrGlobeTap(lat, lon, kind),

      // AYNI (the AR refresh): the dock, side panel, legend, pins and a
      // phone's overlay controls, over the browser's own state.
      tools: this.buildXrTools(),

      // AYNI (Quest review A5): a presenter already live is said on entering, with how to hear them.
      onSessionStart: () => tellXrMeetingSound(),
      onSessionEnd: (lastView) => {
        this.announce('Exited VR')
        // AYNI: the browser globe turns to where the immersive one faced the viewer.
        if (lastView && !this.refusedByMeeting()) void this.renderer?.flyTo(lastView.lat, lastView.lon)
        // Resume the 2D perf sampler now that VR has handed the
        // GPU back. The sampler stayed paused for the duration of
        // the immersive session — VR has its own end-of-session
        // FPS metric on `vr_session_ended.mean_fps`.
        if (TELEMETRY_BUILD_ENABLED) resumeForVrExit()
      },
    }
    // AYNI: kept for the browser checks of the headset panels and the phone's controls.
    this.vrContext = vrContext
    await initVrButton(vrContext)
  }

  /**
   * AYNI (the AR refresh, 2026-10-04): the tools an immersive session
   * uses — the headset's dock and side panel, the legend and pins on the
   * immersive globe, and a phone's overlay controls — over the same state
   * the browser's panels hold (services/xrToolsHost.ts). The pins are read
   * through a getter because the Data tab mounts after the VR button.
   */
  private buildXrTools(): VrToolsHost {
    const videoOf = (): HTMLVideoElement | null => {
      const ds = this.appState.currentDataset
      return ds && dataService.isVideoDataset(ds) ? this.hlsService?.video ?? null : null
    }
    const noPins: XrToolsDeps['pins'] = {
      store: new PinStore(),
      readability: () => 'none',
      picking: () => false,
      setPicking: () => {},
      markExtremes: () => 0,
      frame: () => null,
      format: () => '',
      formatAxis: (value) => String(value),
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const app = this
    /** The dataset the streams' dates were last fetched for (undefined: not yet). */
    let rtRangesAskedFor: string | null | undefined
    return createXrToolsHost({
      // AYNI (satellites plan): the Layers page's Satellites section.
      satellites: () => this.satellites,
      khipuPins: () => app.khipuPinMarks,
      dataset: () => this.appState.currentDataset ?? null,
      datasets: () => this.appState.datasets,
      video: videoOf,
      setRate: (rate) => {
        this.setPlaybackSpeed(rate)
      },
      speed: () => this.playbackSpeed(),
      stepFrame: (direction) => stepFrame(direction, this.hlsService, this.appState, this.playback, (m) => this.announce(m)),
      seekVideo: (seconds) => {
        const video = videoOf()
        if (!video) return
        video.currentTime = seconds
        if (cancelLoopHold(this.playback) && this.appState.isPlaying) void video.play().catch(() => {})
      },
      timeline: () => this.timelineSnapshot(),
      locked: () => this.refusedByMeeting(),
      pinsLocked: () => this.pinsRefusedByMeeting(),
      scale: () => (this.appState.currentDataset ? this.legendScaleAt(this.viewports.getPrimaryIndex()) : null),
      display: () => this.colorScaleDisplay,
      setDisplay: (next) => {
        if (this.refusedByMeeting()) notifyXr(t('vr.notice.presenter'))
        else this.applyColorScaleDisplay(next)
      },
      defaultDisplay: () => defaultDisplayOf(this.appState.currentDataset),
      legendElement: () => this.xrLegendElement(),
      // AYNI (DSA 1.8): a superseding vector layer brings its own legend.
      vectorLegend: () => {
        const v = this.vectorLegendAt(this.viewports.getPrimaryIndex())
        return v.replaces && v.models.length ? v.models : null
      },
      // AYNI (owner, 2026-10-11): the headset's and a phone's ✕ park it in Tools → View; their View brings it back.
      legendParked: () => !this.viewPrefs.legendVisible,
      setLegendParked: (parked) => this.setLegendVisible(!parked),
      get pins() {
        return app.xrPins ?? noPins
      },
      layerCatalog: () => {
        if (!this.layerCatalog) void fetchLayerCatalog().then((layers) => { this.layerCatalog = layers })
        return this.layerCatalog
      },
      layerSelection: () => this.mapLayerSelection,
      applyLayers: (selection) => this.chooseMapLayers(selection),
      rtChoices: () => {
        const current = this.appState.currentDataset?.id ?? null
        // The streams' dates are fetched as when the browser's list opens:
        // once per dataset (AYNI 2026-11-04: it was once per session, so a
        // dataset loaded later kept the first one's stale dates).
        if (rtRangesAskedFor !== current && this.rtOverlayCandidates().some((d) => !this.rtRanges.has(d.id) || this.rtRangesStale.has(d.id))) {
          rtRangesAskedFor = current
          this.fetchRtRanges()
        }
        // AYNI (owner, 2026-11-04): every candidate with its dates and topic;
        // the host lists them by the browser card's rules (rtOverlayList).
        return this.rtOverlayCandidates()
          .filter((d) => d.id !== current)
          .map((d) => ({
            id: d.id,
            title: displayTitle(d),
            ...(d.realtimeKind ? { kind: t(DATA_KIND_LABEL[d.realtimeKind]), kindId: d.realtimeKind } : {}),
            ...this.rtTopicOf(d),
            ...this.rtRangeEntry(d.id),
          }))
      },
      rtBase: () => {
        const ds = this.appState.currentDataset
        return ds ? { range: this.baseRange(), title: displayTitle(ds) } : null
      },
      rtSelected: () => this.rtWanted,
      rtCap: () => this.rtPlanCap,
      pickRt: (id) => this.pickRtOverlay(id),
      rtPlaying: () => this.rtOverlays.map((rt) => {
        const ds = dataService.getDatasetById(rt.id)
        return { id: rt.id, title: ds ? displayTitle(ds) : rt.id, hidden: !!rt.userHidden, opacity: rt.opacity ?? 1 }
      }),
      setRtHidden: (id, hidden) => this.setRtOverlayHidden(id, hidden),
      // AYNI (owner, 2026-10-12): the opacities, the dataset's eye and its ✕, in the headset and on a phone.
      setRtOpacity: (id, opacity) => this.setRtOverlayOpacity(id, opacity),
      baseLook: () => {
        const ds = this.appState.currentDataset
        return ds ? { title: displayTitle(ds), opacity: this.baseOpacity, hidden: this.baseHidden } : null
      },
      setBaseOpacity: (opacity) => this.setBaseOpacity(opacity),
      setBaseHidden: (hidden) => this.setBaseHidden(hidden),
      closeBase: () => void this.closeBaseLayer(),
      moveRt: (id, to) => this.moveRtOverlay(id, to),
      rtColours: (id) => {
        const rt = this.rtOverlays.find((r) => r.id === id)
        return rt ? { scale: overlayScale(rt.encoding), classified: rt.encoding?.kind === 'luma8-classified', display: this.rtDisplayOf(id) } : null
      },
      setRtDisplay: (id, display) => this.setRtOverlayDisplay(id, display),
      recentIds: () => getRecent(30),
      openBrowse: () => this.openBrowsePanel(),
      closeBrowse: () => collapseBrowseUI(),
      notify: (message) => notifyXr(message),
    })
  }

  /** AYNI: a phone's legend chip — the floating legend's bar or classes, or a picture's legend image. */
  private xrLegendElement(): HTMLElement | null {
    const ds = this.appState.currentDataset
    // AYNI: parked, a phone shows it in its View sheet (Tools → View, lent), not over the camera.
    if (!ds || !this.viewPrefs.legendVisible) return null
    // AYNI (DSA 1.8): a superseding vector layer brings its own legend.
    const vector = this.vectorLegendAt(this.viewports.getPrimaryIndex())
    if (vector.replaces && vector.models.length) return renderVectorLegend(vector.models, t('vector.legend.aria', { title: ds.title }))
    const scale = this.legendScaleAt(this.viewports.getPrimaryIndex())
    if (scale) {
      return renderClassLegend({ scale, title: ds.title })
        ?? withPhaseBars(renderColorbar({ scale, display: this.colorScaleDisplay, title: ds.title, references: shownReferenceLines(ds.id) }), scale, this.colorScaleDisplay)
    }
    if (ds.legendLink && !ds.releaseEncoding) {
      const img = document.createElement('img')
      img.src = ds.legendLink
      img.alt = t('vr.phone.legend')
      img.className = 'xr-phone-legend-img'
      return img
    }
    return null
  }

  /**
   * AYNI: a layer choice from Tools → Layers or the headset's Layers page:
   * the viewer's word on borders is noted, then the stack applied.
   */
  private chooseMapLayers(selection: LayerSelection): void {
    const before = this.mapLayerSelection
    void fetchLayerCatalog().then((layers) => {
      this.viewerBorders = bordersChoice(before, selection, layers) ?? this.viewerBorders
    })
    void this.applyMapLayers(selection, this.mapLayerSlot)
  }

  /** Mount the AYNI chatbot and wire playback positioning observers. */
  private initChat(): void {
    setChatCloser(() => this.chatClose())
    initPlaybackPositioning()
    this.mountTimelineTrack()
    this.initAyniChat()
  }

  // --- AYNI: lessons (Education tier plan F6/F7) ---

  /** Start a lesson from the Lessons tab: fetch it, close Browse, open the player on its first step. */
  private async startLesson(slug: string): Promise<void> {
    if (this.refusedByMeeting()) return
    const result = await fetchLesson(slug)
    if (!result.ok) {
      this.announce(t('lessons.error'))
      return
    }
    this.dismissBrowseAfterLoad()
    activeKhipuPlayer()?.close()
    openLessonPlayer(result.value, this.lessonPlayerHost())
  }

  /**
   * AYNI: open a Khipu (Browse → Khipus: My Khipus, a file from this
   * computer). A lesson is a canvas and opens in the lesson player; every
   * other kind plays in the Khipu player. One of the two is open at a time.
   */
  private openKhipu(k: Khipu, files: Array<{ name: string; body: string }> = []): void {
    if (this.refusedByMeeting()) return
    if (!currentEntitlements().khipus) {
      openPlans(null, 'public')
      return
    }
    this.dismissBrowseAfterLoad()
    activeLessonPlayer()?.close()
    activeKhipuPlayer()?.close()
    if (isCanvas(k)) openLessonPlayer(k, this.lessonPlayerHost())
    else openKhipuPlayer(k, this.khipuPlayerHost(), { files, ...this.editFromPlayer(k, files) })
  }

  /** AYNI: the player's Edit, for the kinds the editor edits (stories and briefings). */
  private editFromPlayer(k: Khipu, files: Array<{ name: string; body: string }>): { onEdit?: (position: number) => void } {
    if (k.kind !== 'story' && k.kind !== 'briefing') return {}
    return { onEdit: (position) => this.editKhipu(k, files, position) }
  }

  /** AYNI: open a Khipu in the Khipu editor (`khipuEditorUI`), at step `startAt`. */
  private editKhipu(k: Khipu, files: Array<{ name: string; body: string }> = [], startAt = 0, returnFocus: HTMLElement | null = null, unsaved = false): void {
    if (this.refusedByMeeting()) return
    if (!currentEntitlements().khipus) {
      openPlans(null, 'public')
      return
    }
    hideBrowseUI()
    activeLessonPlayer()?.close()
    activeKhipuPlayer()?.close()
    openKhipuEditor(k, this.khipuEditorHost(files), { files, startAt, returnFocus, unsaved })
    void fetchLayerCatalog().then((layers) => {
      for (const l of layers) this.layerTitles.set(l.id, l.title)
    })
  }

  /**
   * AYNI (owner, 2026-11-03: playlists retire for Khipus, "convert once"):
   * the playlists this browser and the account kept become Khipus
   * (`legacyPlaylists`): kept on the account where the plan keeps Khipus,
   * otherwise waiting in Browse → Khipus. Asked again when the account
   * changes, so a later sign-in on a plan that keeps Khipus keeps the
   * waiting ones; once nothing is left, a run costs nothing.
   */
  private convertOldPlaylists(): void {
    let running = false
    const run = (): void => {
      const state = accountState()
      if (state.status === 'unknown' || running) return
      running = true
      void convertLegacyPlaylists({
        lang: getLocale(),
        nowMs: Date.now(),
        titleOf: (id) => {
          const d = dataService.getDatasetById(id)
          return d ? displayTitle(d) : null
        },
        untitled: t('khipu.record.untitled'),
        email: state.status === 'signedIn' ? state.email : null,
        canKeep: keepsKhipus(),
      })
        .then((result) => {
          if (result.saved) this.announce(plural(result.saved, { one: 'khipus.converted.saved.one', other: 'khipus.converted.saved.other' }))
        })
        .catch((err: unknown) => logger.warn('[App] Old playlists could not be turned into Khipus:', err))
        .finally(() => { running = false })
    }
    run()
    onAccountChange(run)
  }

  /** AYNI: New Khipu (Browse → Khipus): a story whose first step is what the globe shows. */
  private newKhipu(trigger: HTMLElement): void {
    const lang = getLocale()
    const blank = emptyStory(newKhipuId(), { [lang]: t('khipu.record.untitled') }, lang, Date.now())
    const host = this.khipuEditorHost([])
    const first = stepFromGlobe({ ...blank, path: { steps: [] } }, host.scene(), host)
    this.editKhipu(insertStep(first.khipu, first.step, -1), [], 0, trigger)
  }

  /** What the Khipu editor may do with this page's globe. */
  private khipuEditorHost(files: Array<{ name: string; body: string }>): KhipuEditorHost {
    return {
      scene: () => this.globeScene(),
      titleOf: (id) => dataService.getDatasetById(id)?.title ?? null,
      layerTitleOf: (id) => this.layerTitles.get(id) ?? null,
      showStep: (k, index) => void this.showKhipuStep(k, index),
      preview: (k, index, unsaved) => {
        activeKhipuEditor()?.close()
        openKhipuPlayer(k, this.khipuPlayerHost(), { files, startAt: index, onEdit: (position) => this.editKhipu(k, files, position, null, unsaved) })
      },
      canKeep: () => accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null,
      exchangeOnly: () => currentEntitlements().khipuRetention === null,
      announce: (message) => this.announce(message),
    }
  }

  /** AYNI: put a Khipu step on the globe, as the player would (the editor's Show on the globe). */
  private async showKhipuStep(k: Khipu, index: number, opts: { camera?: boolean; host?: KhipuPlayerHost } = {}): Promise<void> {
    const step = k.path.steps[index]
    if (!step) return
    const lang = getLocale()
    const full = planStep(k, step, lang)
    // Without the camera: the Studio's preview moves it itself, frame by frame.
    const plan = opts.camera === false ? { ...full, view: null, region: null, motion: null, autorotate: { on: false } } : full
    const host = opts.host ?? this.khipuPlayerHost()
    if (plan.datasetKey === null) await this.clearToEarth()
    else if (plan.datasetKey !== undefined) {
      const resolved = await resolveDatasetKey(k, plan.datasetKey, lang)
      if (resolved.kind === 'unavailable') return this.announce(t('lessons.player.dataset.unavailable', { title: resolved.title }))
      if (resolved.kind !== 'none' && this.appState.currentDataset?.id !== resolved.datasetId) {
        if (resolved.datasetId.startsWith('LIB_') && !(await ensureLibraryDataset(resolved.datasetId))) return this.announce(t('lessons.player.dataset.unavailable', { title: resolved.title }))
        const pkg = await host.streamLock(resolved.datasetId)
        if (pkg) return this.announce(t('lessons.player.dataset.locked', { title: resolved.title, package: pkg }))
        if (!(await host.loadDataset(resolved.datasetId))) return this.announce(t('lessons.player.dataset.failed', { title: resolved.title }))
      }
    }
    // With the Khipu, so the step's other globes show too (multi-globe plan).
    await applyStepToGlobe(host, plan, () => true, { lang }, k)
  }

  /** AYNI: what the globe shows, as Record a Khipu and the Khipu editor take it. */
  private globeScene(): SoloScene {
    const m = this.viewports.getPrimary()?.getMap() ?? null
    const c = m?.getCenter()
    const axis = this.timelineSnapshot()
    return {
      datasetId: this.appState.currentDataset?.id ?? null,
      // AYNI (owner, 2026-10-09): with the camera's rotation and tilt, and the Data tab's pins.
      view: m && c ? { lat: c.lat, lon: c.lng, zoom: m.getZoom(), bearing: m.getBearing(), pitch: m.getPitch(), ...(this.flatMap ? { flat: true as const } : {}) } : null,
      pins: (this.dataPinStore?.list() ?? []).map((pin, i) => {
        const color = (MEASUREMENT_COLORS as readonly string[]).indexOf(pin.color)
        return { id: pin.id, lat: pin.lat, lon: pin.lon, color: color >= 0 ? color : i, ...(pin.name ? { name: pin.name } : {}) }
      }),
      date: axis && typeof axis.currentMs === 'number' ? new Date(axis.currentMs).toISOString().replace(/\.\d{3}Z$/, 'Z') : null,
      overlays: this.mapLayerSelection.overlays.map((o) => o.id),
      // AYNI (owner, 2026-10-12): the real-time overlays with their opacity and eye, and the dataset's.
      realtime: this.rtOverlays.map((rt) => ({
        id: rt.id,
        ...((rt.opacity ?? 1) < 1 ? { opacity: Math.round((rt.opacity ?? 1) * 100) / 100 } : {}),
        ...(rt.userHidden ? { hidden: true } : {}),
      })),
      ...(this.baseOpacity < 1 || this.baseHidden
        ? { look: { ...(this.baseOpacity < 1 ? { opacity: Math.round(this.baseOpacity * 100) / 100 } : {}), ...(this.baseHidden ? { hidden: true } : {}) } }
        : {}),
      playing: this.appState.isPlaying,
      spin: this.viewports.getPrimary()?.getRotationRate() ?? 0,
      // Khipu 1.2: the Satellites layer, when it is on.
      ...(this.satellites.on ? { satellites: captureKhipuSatellites(this.satellites) } : {}),
      // AYNI (2026-10-20): the speed bar's choice.
      speed: this.playbackSpeed(),
      // AYNI (multi-globe plan): the other globes, by position, with their time rules.
      ...(this.viewports.getPanelCount() > 1
        ? {
            layout: this.viewports.getLayout(),
            globes: this.panelStates
              .map((p, i) => ({ slot: i, datasetId: p.dataset?.id ?? null, time: formatTimeRule(this.panelTimeRules[i] ?? FOLLOW) }))
              .filter((g) => g.slot !== this.viewports.getPrimaryIndex())
              .map(({ datasetId, time }) => ({ datasetId, ...(time ? { time } : {}) })),
          }
        : {}),
    }
  }

  /** What the Khipu player may do with this page's globe: the lesson player's, plus moving it. */
  // ------------------------------------------------- AYNI: the Situation room

  /** AYNI (Situation room): open or close the operator's panel (Situation Room plans; Tools offers it). */
  private openSituation(trigger: HTMLElement | null): void {
    if (isSituationPanelOpen()) return closeSituationPanel()
    if (accountState().status !== 'signedIn' || !situationAllowed(currentEntitlements())) {
      openPlans(trigger, 'situation')
      return
    }
    openSituationPanel(this.situationHost(), trigger)
  }

  /** AYNI (Situation room, CR3): the briefing given from this page. */
  private situationBriefing(): BriefingSession {
    if (!this.briefingSessionNow) {
      this.briefingSessionNow = createBriefingSession({
        openPlayer: (k, hooks) => {
          if (this.refusedByMeeting()) return
          this.dismissBrowseAfterLoad()
          activeLessonPlayer()?.close()
          activeKhipuPlayer()?.close()
          openKhipuPlayer(k, this.situationKhipuHost(), { briefing: hooks })
        },
        closePlayer: () => activeKhipuPlayer()?.close(),
        globesShown: () => this.globesShown(),
        capturePictures: () => Promise.all(this.viewports.getAll().map((r) => r.captureScreenshot({ maxSize: 1280 }).catch(() => null))),
        userName: () => accountState().name.trim() || null,
        lang: () => getLocale(),
      })
    }
    return this.briefingSessionNow
  }

  /** AYNI (Situation room): the date globe `i` shows now, epoch ms; null without a dated video. */
  private slotDateMs(i: number): number | null {
    if (i === this.viewports.getPrimaryIndex()) {
      const ms = this.timelineSnapshot()?.currentMs
      if (typeof ms === 'number' && Number.isFinite(ms)) return ms
    }
    const panel = this.panelStates[i]
    const video = panel?.hlsService?.getVideo?.() ?? null
    const dates = video ? this.panelDates(i) : null
    if (!dates || !video || !panel?.dataset) return null
    if (dates.kind === 'frames') return frameTimeMs(dates.axis, panelFrameAt(dates, video.currentTime))
    return videoTimeToDate(video.currentTime, dates.duration, new Date(dates.startMs), new Date(dates.endMs), this.playback.displayInterval?.intervalMs).getTime()
  }

  /** AYNI (Situation room, CR2): each globe's stream and how old its data is (asked again in the background). */
  private freshnessRows(): FreshnessRow[] {
    const rows: FreshnessRow[] = []
    const now = Date.now()
    for (let i = 0; i < this.viewports.getPanelCount(); i++) {
      const ds = this.panelStates[i]?.dataset
      if (!ds) continue
      const title = displayTitle(ds)
      const kind = ds.realtimeKind === 'real-time' || ds.realtimeKind === 'forecast' ? ds.realtimeKind : null
      if (!kind) {
        rows.push({ globe: i + 1, title, level: null, ageMs: null, asOfMs: null })
        continue
      }
      void streamDates(ds).then((d) => {
        const before = this.streamDatesSeen.get(ds.id)
        this.streamDatesSeen.set(ds.id, d)
        if (JSON.stringify(before) !== JSON.stringify(d)) refreshSituationPanel()
      })
      if (!this.streamDatesSeen.has(ds.id)) {
        rows.push({ globe: i + 1, title, level: 'unknown', ageMs: null, asOfMs: null })
        continue
      }
      const d = this.streamDatesSeen.get(ds.id) ?? null
      const f = streamFreshness({ kind, lastFrameMs: d?.lastFrameMs ?? null, firstFrameMs: d?.firstFrameMs ?? null, cadenceMs: d?.cadenceMs ?? null }, now)
      rows.push({ globe: i + 1, title, level: f.level, ageMs: f.ageMs, asOfMs: f.asOfMs })
    }
    return rows
  }

  /** AYNI (Situation room, CR3): what each globe shows, for the briefing record. */
  private async globesShown(): Promise<BriefingGlobeShown[]> {
    const out: BriefingGlobeShown[] = []
    for (let i = 0; i < this.viewports.getPanelCount(); i++) {
      const ds = this.panelStates[i]?.dataset
      if (!ds) continue
      const kind = ds.realtimeKind === 'real-time' || ds.realtimeKind === 'forecast' ? ds.realtimeKind : null
      let freshness: BriefingGlobeShown['freshness'] = null
      if (kind) {
        const d = await streamDates(ds)
        this.streamDatesSeen.set(ds.id, d)
        freshness = streamFreshness({ kind, lastFrameMs: d?.lastFrameMs ?? null, firstFrameMs: d?.firstFrameMs ?? null, cadenceMs: d?.cadenceMs ?? null }, Date.now()).level
      }
      out.push({ datasetId: ds.id, title: displayTitle(ds), frameMs: this.slotDateMs(i), freshness })
    }
    return out
  }

  /** AYNI (Situation room): the meeting this page presents, to put on the walls. */
  private presentedMeeting(): { code: string; audience: string; expiresAt: number } | null {
    const code = this.roomCodeNow
    const status = this.lastRoomStatus
    if (!code || !status?.connected || !status.meeting || status.seat !== 'presenter') return null
    const m = rememberedMeeting(code)
    if (!m) return null
    const url = new URL(m.links.audience, window.location.origin)
    return { code: m.code, audience: `${url.pathname}${url.search}`, expiresAt: m.expiresAt }
  }

  /** AYNI (Situation room, CR3): the credit lines of the streams a briefing showed. */
  private briefingCredits(datasetIds: readonly string[]): string[] {
    const lines = new Set<string>()
    for (const id of datasetIds) {
      const ds = dataService.getDatasetById(id)
      if (!ds) continue
      const line = ds.rights?.credit?.line ?? (ds.organization ? `${displayTitle(ds)}: ${ds.organization}` : '')
      if (line) lines.add(line)
    }
    return [...lines]
  }

  /** AYNI (Situation room, CR1): change the room's board in the Khipu editor, saved for the walls. */
  private editBoard(board: Khipu, save: (k: Khipu) => Promise<{ ok: boolean; text: string }>): void {
    if (this.refusedByMeeting()) return
    hideBrowseUI()
    activeLessonPlayer()?.close()
    activeKhipuPlayer()?.close()
    openKhipuEditor(board, this.khipuEditorHost([]), { saveAs: { label: t('situation.board.saveAs'), save } })
  }

  private situationHost(): SituationPanelHost {
    return {
      showSituation: async (board) => {
        if (this.refusedByMeeting()) return
        activeKhipuPlayer()?.close()
        await this.showKhipuStep(board, 0, { host: this.situationKhipuHost() })
        refreshSituationPanel()
      },
      playBoard: (board) => {
        if (this.refusedByMeeting()) return
        this.dismissBrowseAfterLoad()
        activeLessonPlayer()?.close()
        activeKhipuPlayer()?.close()
        openKhipuPlayer(board, this.situationKhipuHost())
      },
      editBoard: (board, save) => this.editBoard(board, save),
      briefing: this.situationBriefing(),
      openSecondScreen: () => this.openOutputWindow(),
      openMeeting: (trigger) => openMeetingPanel(trigger),
      presentedMeeting: () => this.presentedMeeting(),
      freshness: () => this.freshnessRows(),
      ownLayers: this.ownLayers,
      saveRecord: async (k) => {
        const result = await saveKhipu(k, [])
        return result.ok
          ? { ok: true, text: t('lessons.player.saved', { date: formatLocalDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) }) }
          : { ok: false, text: t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed') }
      },
      downloadRecord: async (k) => {
        const { blob, filename } = await zipKhipu(k, [], { exchange: false })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 10_000)
        this.announce(t('lessons.player.downloaded', { filename }))
      },
      printPack: (r) => printBriefingPack({
        title: r.title,
        startedAt: r.startedAt,
        endedAt: r.endedAt,
        who: r.who,
        steps: r.steps,
        decisions: r.decisions,
        credits: this.briefingCredits(r.steps.flatMap((s) => s.globes.map((g) => g.datasetId))),
        contentSha256: r.contentSha256,
      }),
      canName: () => accountState().status === 'signedIn' && accountState().name.trim().length > 0,
      announce: (message) => this.announce(message),
    }
  }

  /**
   * AYNI (Situation room): the Khipu player's host for the board, briefings
   * and walls. A situation is "now" on every globe: a globe that follows
   * globe 1 and shows a live observation is held on its own stream's newest
   * frame instead of globe 1's date (where a daily stream would have no
   * frame yet and be dimmed). Forecasts and archives still follow globe 1.
   */
  private situationKhipuHost(): KhipuPlayerHost {
    const base = this.khipuPlayerHost()
    return {
      ...base,
      setGlobes: async (layout, others) => {
        const held = await Promise.all(others.map(async (o) => {
          const ds = o.datasetId ? dataService.getDatasetById(o.datasetId) : null
          if (!ds || o.time.kind !== 'follow' || ds.realtimeKind !== 'real-time') return o
          const dates = await streamDates(ds)
          return dates ? { datasetId: o.datasetId, time: { kind: 'fixed' as const, ms: dates.lastFrameMs } } : o
        }))
        await this.setGlobesFromStep(layout, held)
      },
    }
  }

  /**
   * AYNI (Situation room, decision CR-D14): in a briefing meeting, the
   * presenter's page sends which own layers it shows (on every change), and
   * everyone else's page asks for them every half minute and draws them.
   * A wall following the meeting keeps reading the room's layers by its own token.
   */
  private syncMeetingLayers(status: RoomStatus): void {
    // A dropped connection keeps the layers shown and the half-minute ask going until the room answers again.
    if (!status.connected) return
    const code = this.roomCodeNow
    const inMeeting = !!code && status.connected && status.meeting
    if (inMeeting && status.seat === 'presenter') {
      this.stopFollowingMeetingLayers()
      this.shareShownLayers()
      return
    }
    if (!inMeeting || isWallDisplay()) {
      this.stopFollowingMeetingLayers()
      return
    }
    if (this.meetingLayersCode === code) return
    this.meetingLayersCode = code
    const ask = (): void => {
      void loadMeetingLayers(code).then((res) => {
        if (this.meetingLayersCode !== code) return
        this.ownLayers.setList(res.ok ? res.body.layers : [], { kind: 'meeting', code })
      })
    }
    ask()
    this.meetingLayersTimer = window.setInterval(ask, 30_000)
  }

  private stopFollowingMeetingLayers(): void {
    if (this.meetingLayersTimer !== null) window.clearInterval(this.meetingLayersTimer)
    this.meetingLayersTimer = null
    if (this.meetingLayersCode !== null) {
      this.meetingLayersCode = null
      this.ownLayers.setList([], { kind: 'member' })
    }
  }

  /** AYNI (CR-D14): the presenter's page tells the room's server which own layers the meeting is shown, when that changed. */
  private shareShownLayers(): void {
    const code = this.roomCodeNow
    const status = this.lastRoomStatus
    const seat = this.roomSeatToken
    if (!code || !seat || !status?.connected || !status.meeting || status.seat !== 'presenter') return
    if (accountState().status !== 'signedIn' || !situationAllowed(currentEntitlements())) return
    const shown = this.ownLayers.list().filter((l) => this.ownLayers.shown(l.id)).map((l) => l.id)
    const key = `${code}:${shown.join(',')}`
    if (key === this.meetingLayersSent) return
    this.meetingLayersSent = key
    void shareMeetingLayers(code, seat, shown).then((res) => {
      if (!res.ok) this.meetingLayersSent = null
    })
  }

  /** AYNI (Situation room, CR1): run this page as a wall display. */
  private async startWall(): Promise<void> {
    document.body.classList.add('output-window')
    this.outputLock = true
    this.applyRoomLock()
    this.setLoading(false)
    await startWallDisplay({
      khipuHost: () => this.situationKhipuHost(),
      freshness: () => this.freshnessRows(),
      globeRects: () => this.viewports.getAll().map((r) => r.getMap()?.getContainer().getBoundingClientRect() ?? null),
      ownLayers: this.ownLayers,
      setGrant: (grant) => setWallGrant(grant),
      acting: async (change) => {
        this.roomActing = true
        try {
          await change()
        } finally {
          this.roomActing = false
        }
      },
    })
  }

  private khipuPlayerHost(): KhipuPlayerHost {
    const lesson = this.lessonPlayerHost()
    return {
      loadDataset: lesson.loadDataset,
      currentDatasetId: lesson.currentDatasetId,
      streamLock: lesson.streamLock,
      setView: lesson.setView,
      playMotion: (stop) => this.playStepMotion(stop),
      stopMotion: () => this.stopStepMotion(),
      // Khipu 1.2: the Satellites layer as the step sets it; `follow` keeps it under the camera while the step holds.
      setSatellites: (s) => {
        applyKhipuSatellites(this.satellites, s)
        if (s.on && s.follow && typeof s.selected === 'number') startSatelliteFollow(this.satellites, () => this.viewports.getPrimary()?.getMap() ?? null)
        else stopSatelliteFollow()
      },
      moveData: (id, move, load, current) => this.liveDataMove(id, move, load, current),
      // A plan with render credits (Media Desk, Newsroom) can make a video of any Khipu it plays.
      ...(currentEntitlements().credits > 0 ? { film: (k: Khipu) => filmKhipu(this.studioHost(), k, null) } : {}),
      seekToDate: lesson.seekToDate,
      setOverlays: lesson.setOverlays,
      setRtOverlays: lesson.setRtOverlays,
      // AYNI (owner, 2026-10-12): a step's streams' opacity and eye (absent: opaque and shown).
      setRtLook: (look) => {
        for (const l of look) {
          this.setRtOverlayOpacity(l.id, l.opacity)
          this.setRtOverlayHidden(l.id, l.hidden)
        }
      },
      // AYNI (multi-globe plan): a step's other globes and their time rules.
      setGlobes: (layout, others) => this.setGlobesFromStep(layout, others),
      openPlans: (trigger) => openPlans(trigger, 'public'),
      announce: lesson.announce,
      canKeep: lesson.canKeep,
      exchangeOnly: () => currentEntitlements().khipuRetention === null,
      clearDataset: () => void this.clearToEarth(),
      setPlaying: (playing) => {
        if (this.appState.isPlaying !== playing && this.hlsService) {
          togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
        }
      },
      setRate: (rate) => {
        this.setPlaybackSpeed(rate)
      },
      fitRegion: (r) => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
        this.viewports.getPrimary()?.fitBounds?.([r.west, r.south, r.east, r.north], { duration: reduced ? 0 : 1500 })
        if (isVrActive()) {
          // The box's middle, the short way round when it crosses the antimeridian.
          const east = r.east < r.west ? r.east + 360 : r.east
          const lon = (((r.west + east) / 2 + 540) % 360) - 180
          void flyToOnGlobe((r.south + r.north) / 2, lon)
        }
      },
      seekToEdge: (edge) => this.seekToEdge(edge),
      setAutorotate: (on, speed) => this.autoRotate?.set(on, speed),
      // AYNI (2026-10-19): a step on the flat map or the globe.
      setFlat: (on) => this.setFlatMap(on),
      seekToOffset: (seconds) => {
        const video = this.hlsService?.video
        if (video && video.duration > 0) video.currentTime = Math.max(0, Math.min(video.duration - 0.05, seconds))
      },
      setDisplay: (d) => {
        // AYNI (owner, 2026-10-12): the dataset's opacity and eye, for any dataset (a picture too).
        if (typeof d.opacity === 'number') this.setBaseOpacity(d.opacity)
        if (typeof d.hidden === 'boolean') this.setBaseHidden(d.hidden)
        const scale = this.appState.currentDataset ? this.viewports.getPrimary()?.displayScale() ?? null : null
        if (!scale) return false
        if (d.palette === undefined && d.invert === undefined && d.min === undefined) return true
        const now = this.colorScaleDisplay
        // AYNI: limits as values, on the stream's own axis (or the standard's).
        const axis: 'linear' | 'log' = now.limits?.scale ?? (releaseOf(scale)?.kind === 'luma8-log' ? 'log' : 'linear')
        const limits = typeof d.min === 'number' && typeof d.max === 'number' && d.min < d.max && (axis !== 'log' || d.min > 0)
          ? { min: d.min, max: d.max, scale: axis }
          : null
        this.applyColorScaleDisplay({
          ...now,
          ...(d.palette ? { palette: d.palette } : {}),
          ...(typeof d.invert === 'boolean' ? { inverted: d.invert } : {}),
          ...(limits ? { limits, stretch: { lo: 0, hi: 1 } } : {}),
        }, { save: false })
        return true
      },
      showPins: (pins) => this.showKhipuPins(pins),
      clearPins: lesson.clearPins,
      setRoomNote: (note) => {
        this.khipuRoomNote = note
      },
      askRoom: () => {
        const room = activeRoomSync()
        const status = room?.status()
        if (!room || !status?.connected || !status.meeting || status.role !== 'lead') return null
        return (quiz) => room.send({ t: 'activity', a: { kind: 'choice', prompt: quiz.question, durationMs: null, options: quiz.options } })
      },
    }
  }

  /** AYNI: Record a Khipu (Browse → Khipus): Browse steps aside, and the globe is the person's to explore. */
  private recordKhipu(trigger: HTMLElement): void {
    if (this.khipuRecordState() !== 'ready') return
    hideBrowseUI()
    startSoloRecording({
      scene: () => this.globeScene(),
      titleOf: (id) => dataService.getDatasetById(id)?.title ?? null,
      layerTitleOf: (id) => this.layerTitles.get(id) ?? null,
      canKeep: () => accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null,
      exchangeOnly: () => currentEntitlements().khipuRetention === null,
      play: (k) => this.openKhipu(k),
      edit: (k) => this.editKhipu(k, [], 0, null, true),
      announce: (message) => this.announce(message),
    }, { returnFocus: trigger })
    void fetchLayerCatalog().then((layers) => {
      for (const l of layers) this.layerTitles.set(l.id, l.title)
    })
  }

  /** Whether Record a Khipu can start: one at a time, and not in a meeting (which records itself). */
  private khipuRecordState(): 'ready' | 'busy' | 'room' {
    if (activeRoomSync()?.status().meeting) return 'room'
    return activeSoloRecording() ? 'busy' : 'ready'
  }

  /** AYNI: a Khipu's pins (a step's, or a record's), in the four pin colours; an empty list takes them away. */
  private showKhipuPins(pins: ReadonlyArray<{ lat: number; lon: number; note?: string; color?: number }>): void {
    for (const m of this.khipuMarkers) m.remove()
    this.khipuMarkers = []
    // The headset's globe shows them too, labelled with their notes.
    this.khipuPinMarks = pins.map((p, i) => ({ id: `khipu-${i}`, color: MEASUREMENT_COLORS[(p.color ?? i) % MEASUREMENT_COLORS.length], lat: p.lat, lon: p.lon, label: p.note ?? '' }))
    const renderer = this.viewports.getPrimary()
    for (const [i, p] of pins.entries()) {
      const marker = renderer?.addMarker(p.lat, p.lon, p.note || undefined, MEASUREMENT_COLORS[(p.color ?? i) % MEASUREMENT_COLORS.length])
      if (marker) this.khipuMarkers.push(marker)
    }
  }

  /** AYNI: the dataset's first or latest frame (a Khipu step's `time.relative`). */
  private seekToEdge(edge: 'first' | 'latest'): void {
    const url = this.appState.currentDataset?.timelineLink
    const timeline = url ? this.dsaTimelines.get(url) : null
    if (timeline) {
      const ms = edge === 'first' ? timeline.startMs : lastFrameDateMs(timeline)
      this.seekToIsoDate(new Date(ms).toISOString())
      return
    }
    const video = this.hlsService?.video
    if (!video || !(video.duration > 0)) return
    video.currentTime = edge === 'first' ? 0 : Math.max(0, video.duration - 0.05)
  }

  /** What the lesson player may do with this page's globe. */
  private lessonPlayerHost(): LessonPlayerHost {
    const map = () => this.viewports.getPrimary()?.getMap() ?? null
    return {
      loadDataset: (id) => this.loadDatasetAndConfirm(id, 'browse'),
      currentDatasetId: () => this.appState.currentDataset?.id ?? null,
      streamLock: async (id) => {
        const dataset = dataService.getDatasetById(id)
        const pkg = dataset ? await streamLockedBy(dataset) : null
        return pkg ? t(PACKAGE_NAME[pkg]) : null
      },
      getView: () => {
        const m = map()
        if (!m) return null
        const c = m.getCenter()
        return { lat: c.lat, lon: c.lng, zoom: m.getZoom(), bearing: m.getBearing(), pitch: m.getPitch() }
      },
      setView: (view) => {
        const m = map()
        if (!m) return
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
        m.easeTo({ center: [view.lon, view.lat], zoom: view.zoom, bearing: view.bearing ?? 0, pitch: view.pitch ?? 0, duration: reduced ? 0 : 1200 })
        if (isVrActive()) void flyToOnGlobe(view.lat, view.lon)
      },
      currentDate: () => {
        const axis = this.timelineSnapshot()
        return axis && typeof axis.currentMs === 'number' ? new Date(axis.currentMs).toISOString().replace(/\.\d{3}Z$/, 'Z') : null
      },
      seekToDate: (iso) => this.seekToIsoDate(iso),
      openPlans: (trigger) => openPlans(trigger, 'education'),
      openDataTools: () => openToolsTab('data'),
      announce: (message) => this.announce(message),
      canKeep: () => accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null,
      // AYNI: a step's suggested overlay, put on (or taken off) as from Tools → Layers; a class follows it.
      // AYNI (2026-10-09): two at most, as in Tools → Layers (`MAX_OVERLAYS`).
      overlayIds: () => this.mapLayerSelection.overlays.map((o) => o.id),
      toggleOverlay: async (layerId, tint) => {
        const layer = (await fetchLayerCatalog()).find((l) => l.id === layerId)
        if (!layer) return false
        this.chooseMapLayers({ ...this.mapLayerSelection, overlays: toggleOverlay(this.mapLayerSelection.overlays, layer.id, tint ?? tintFor(layer)) })
        return true
      },
      // A Khipu step's overlays: these and no others.
      setOverlays: async (wanted) => {
        const layers = await fetchLayerCatalog()
        const found = capOverlays(wanted).flatMap((w) => {
          const layer = layers.find((l) => l.id === w.layerId)
          return layer ? [{ id: layer.id, tint: w.tint ?? tintFor(layer) }] : []
        })
        this.chooseMapLayers({ ...this.mapLayerSelection, overlays: found })
        return found.length === capOverlays(wanted).length
      },
      // AYNI (owner, 2026-10-09): opening or closing a Khipu or a lesson removes every pin: the Data tab's and a Khipu's.
      clearPins: () => {
        this.dataPinStore?.clear()
        this.showKhipuPins([])
      },
      // AYNI (2026-10-09): a step's real-time variables, as from Tools → Layers → Real time; a class follows them.
      rtOverlayIds: () => this.rtWanted,
      rtCap: () => this.rtCap,
      setRtOverlays: async (ids) => {
        if (ids.join(' ') !== this.rtWanted.join(' ')) await this.setRtOverlays(ids)
        return this.rtWanted
      },
      showRtDates: (ids) => this.showRtDates(ids),
    }
  }

  // --- AYNI: the AYNI chatbot's pill and panel ---

  private chatShowTrigger(): void {
    this.ayniChat?.showTrigger()
  }

  private chatHideTrigger(): void {
    this.ayniChat?.hideTrigger()
  }

  private chatClose(): void {
    this.ayniChat?.close()
  }

  /** Mount the AYNI chatbot: its executor works this page's globe through these seams. */
  private initAyniChat(): void {
    const host: XrChatHost = {
      datasets: () => this.appState.datasets,
      currentDataset: () => this.appState.currentDataset,
      loadDataset: (id) => this.loadDatasetForAyniChat(id),
      flyTo: async (lat, lon, altitudeKm) => {
        if (isVrActive()) void flyToOnGlobe(lat, lon)
        await this.renderer?.flyTo(lat, lon, altitudeKm)
      },
      view: () => {
        const view = this.renderer?.getViewContext()
        return view ? { lat: view.center.lat, lon: view.center.lng, zoom: view.zoom } : null
      },
      playback: () => {
        const video = this.hlsService?.getVideo()
        if (!this.hlsService || !video) return null
        return {
          isPlaying: this.appState.isPlaying,
          currentTime: video.currentTime,
          duration: Number.isFinite(video.duration) ? video.duration : 0,
          rate: this.playbackSpeed(),
        }
      },
      togglePlay: () => togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback),
      seekSeconds: (seconds) => {
        const video = this.hlsService?.getVideo()
        if (!video || !video.duration) return
        video.currentTime = seconds
        this.playback.scrubbing = true
      },
      setRate: (rate) => {
        this.setPlaybackSpeed(rate)
      },
      seekToDate: (isoDate) => this.seekToIsoDate(isoDate),
      timeline: () => {
        const axis = this.timelineSnapshot()
        return axis ? { startMs: axis.startMs, endMs: axis.endMs, currentMs: axis.currentMs ?? null } : null
      },
      probe: (lat, lon) => this.renderer?.probeValueAt(lat, lon) ?? null,
      locked: () => this.refusedByMeeting(),
      streamLock: async (dataset) => {
        const pkg = await streamLockedBy(dataset)
        return pkg ? t(PACKAGE_NAME[pkg]) : null
      },
      dataTools: () => dataToolsAllowed(),
      // AYNI (owner, 2026-10-06): the chatbot's statistics are the Analyze panel's, shown there.
      analysis: (request, show) => runChatAnalysis(request, show),
      // AYNI (2026-10-07, the research and education contract): a meeting's audience explores, the
      // research tools through Analyze, the citation, and what a reading means.
      exploring: () => {
        const status = activeRoomSync()?.status()
        return !!status && isExploring(status)
      },
      research: createChatResearch({
        allowed: () => researchToolsAllowed(),
        stats: () => this.dataPinsUi?.statsView() ?? null,
        methodsInput: () => this.methodsInput(null),
        openFigure: (kind) => {
          if (!this.appState.currentDataset) return false
          openFigureDialog(this.figureSource(), { tab: 'figure', kind, announce: (m) => this.announce(m) })
          return true
        },
      }),
      cite: (format) => {
        const input = this.citationForView()
        return input ? { citation: citationIn(format, input).text.trim(), viewLink: input.viewUrl } : null
      },
      explain: (point) => explainReading({
        probe: (lat, lon) => this.viewports.getPrimary()?.probeValueAt(lat, lon) ?? null,
        display: () => this.colorScaleDisplay,
      }, point),
      // AYNI (DSA 1.8, 2026-10-12): the records the main globe's vector layers draw (`features.query`).
      features: () => this.vectorFeaturesForChat(),
      // AYNI (satellites ↔ catalog plan, 2026-10-24): the Satellites layer, for the `satellites.*` commands.
      satellites: {
        layer: this.satellites,
        datasetRows: (norad) => satelliteDatasets(this.satellites, norad),
        note: (norad) => platformNote(this.satellites, norad),
        platforms: () => {
          const url = this.appState.currentDataset?.timelineLink
          return (url ? this.dsaTimelines.getMetadata(url)?.platforms : null) ?? []
        },
        bands: () => this.platformBands(this.appState.currentDataset?.timelineLink),
      },
      pins: {
        readability: () => this.dataPinsForChat?.readability() ?? 'none',
        list: () => this.dataPinStore?.list() ?? [],
        add: (lat, lon) => (this.dataPinStore?.add(lat, lon) ?? null) !== null,
        limit: () => this.dataPinStore?.limit ?? MAX_PINS,
        clear: () => this.dataPinStore?.clear(),
        markExtremes: () => this.dataPinsForChat?.markExtremes() ?? 0,
        show: () => openToolsTab('data'),
      },
    }
    this.ayniChat = mountAyniChatPanel({
      // AYNI: the environment the page says it is in, for the chatbot's mode (the server narrows it).
      session: createAyniChat(host, undefined, { claim: () => (researchToolsAllowed() ? 'research' : 'general') }),
      language: () => chatLanguageFor(getLocale()),
      access: () => {
        if (accountState().status !== 'signedIn') return 'signed_out'
        return currentEntitlements().chatbot ? 'ok' : 'plan_required'
      },
      onSignIn: (trigger) => openSignIn(trigger),
      onPlans: (trigger) => openPlans(trigger),
    })
    onAccountChange(() => this.ayniChat?.refreshAccess())
  }

  /**
   * Load a dataset for the AYNI chatbot, the way the browse panel does, and
   * say whether it is on screen. Tagged 'orbit' (the analytics enum's name
   * for "the assistant") until the old assistant is removed.
   */
  private async loadDatasetForAyniChat(id: string): Promise<boolean> {
    return this.loadDatasetAndConfirm(id, 'orbit')
  }

  /**
   * Load a dataset the way the browse panel does and say whether it is on
   * screen: the AYNI chatbot's loads and a lesson's steps.
   */
  private async loadDatasetAndConfirm(id: string, trigger: import('./types').LoadTrigger): Promise<boolean> {
    if (this.refusedByMeeting()) return false
    // AYNI: a dataset chosen while the dataset's ✕ offers Undo: Undo would now undo something else.
    dismissUndoToast()
    const gen = ++this.loadGeneration
    this.dismissBrowseAfterLoad()
    this.announce('Loading dataset…')
    this.showLoadingScreen('Loading dataset…', 20)
    this.writeDatasetUrl(id, 'push')
    try {
      await this.loadDataset(id, trigger)
    } catch (error) {
      logger.warn('[App] load failed:', id, error)
      this.setLoading(false)
      return false
    }
    if (gen !== this.loadGeneration) return false
    this.setLoading(false)
    const dataset = this.appState.currentDataset
    // A refused load (a meeting, a stream outside the plan) leaves the previous dataset on screen.
    if (!dataset || (dataset.id !== id && dataset.legacyId !== id)) return false
    this.announce(`Loaded dataset: ${dataset.title}`)
    this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
    setHelpActiveDataset(dataset.id)
    return true
  }

  /** Open the chat panel and optionally pre-fill the input with a query string. */
  /**
   * AYNI: turn the globe to a regional dataset's box and fit it, in the
   * space the side panels leave free. The map canvas reaches as far past
   * the window's start edge as the panels take at its end
   * (`globePanelOffset.ts`), so the same padding on both sides centres
   * the box in what the viewer can see. A band all the way round only
   * tilts to its latitude. In a headset the globe turns the region to
   * face the viewer as well.
   */
  private focusOnDatasetRegion(dataset: Dataset): void {
    const release = dataset.releaseEncoding
    const focus = focusFor((release ? releaseBoundingBox(release) : null) ?? dataset.boundingBox)
    const primary = this.viewports.getPrimary()
    if (!focus || !primary) return
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    if (focus.kind === 'band') {
      const lon = primary.getMap()?.getCenter().lng ?? 0
      void primary.flyTo(focus.lat, lon)
      if (isVrActive()) void flyToOnGlobe(focus.lat, lon)
      return
    }
    const grid = document.getElementById('map-grid')
    const extent = grid ? Math.max(0, Math.round(grid.getBoundingClientRect().width - window.innerWidth)) : 0
    const pad = 60
    primary.fitBounds(focus.bounds, {
      padding: { top: pad, bottom: pad + 40, left: pad + extent, right: pad + extent },
      duration: reduced ? 0 : 2500,
    })
    if (isVrActive()) void flyToOnGlobe(focus.lat, focus.lon)
  }

  private openChatWithQuery(query?: string): void {
    this.ayniChat?.open(query)
  }


  /**
   * Called by ViewportManager when a setLayout adds/removes panels.
   * Resizes our parallel panelStates array to match, disposing any
   * videos in slots that are going away, and seeds newly-created
   * panels with the current map-controls toolbar state (labels,
   * borders, terrain) so they start in sync with the primary.
   */
  /**
   * The cursor readout at the top: lat/lng and, for a data-encoded
   * dataset, the value, read from whichever globe the cursor is over.
   * AYNI (multi-globe): bound to every globe, again on each layout change
   * (`setLatLngCallbacks` replaces a globe's previous pair). A held
   * baseline's change belongs to the main globe only (changeView.ts), so
   * the other globes read their plain values.
   */
  private bindLatLngReadout(): void {
    const latlngEl = document.getElementById('latlng-display')
    if (!latlngEl) return
    for (const renderer of this.viewports.getAll()) {
      renderer.setLatLngCallbacks(
        (lat: number, lng: number) => {
          const ns = lat >= 0 ? 'N' : 'S'
          const ew = lng >= 0 ? 'E' : 'W'
          const coords = `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lng).toFixed(1)}° ${ew}`
          // A data-encoded dataset can also say what the value
          // *is* here. Everything else reports coordinates only.
          // AYNI: the value only on a plan with the data tools (planAccess.ts).
          // AYNI: the change from a held baseline when the main globe shows it (changeView.ts).
          const isMain = renderer === this.viewports.getPrimary()
          // AYNI (owner, 2026-10-12): a dataset hidden with its eye is not read (a see-through one still is).
          const probed = dataToolsAllowed() && !(isMain && this.baseHidden) ? renderer.probeValueAt(lat, lng) : null
          const reading = isMain ? viewReading(probed, lat, lng) : probed
          latlngEl.textContent = reading
            ? `${coords} · ${isMain ? formatShownReading(reading) : formatProbeReading(reading)}`
            : coords
          latlngEl.classList.remove('hidden')
          this.placeLatLngReadout(latlngEl, renderer)
        },
        () => {
          latlngEl.classList.add('hidden')
        }
      )
    }
  }

  /**
   * AYNI (multi-globe): with several globes the readout sits over the globe
   * it reads, under that globe's head row, instead of the screen's top
   * centre (where it covered globe 2's chips). One globe keeps base.css's place.
   */
  private placeLatLngReadout(el: HTMLElement, renderer: MapRenderer): void {
    const viewport = renderer.getMap()?.getContainer().closest<HTMLElement>('.map-viewport')
    const parent = el.offsetParent
    if (this.viewports.getPanelCount() < 2 || !viewport || !parent) {
      el.classList.remove('per-globe')
      el.style.removeProperty('left')
      el.style.removeProperty('top')
      return
    }
    const box = viewport.getBoundingClientRect()
    const head = viewport.querySelector<HTMLElement>('.viewport-head')
    const headBox = head && head.getClientRects().length ? head.getBoundingClientRect() : null
    const origin = parent.getBoundingClientRect()
    const gap = 6
    el.classList.add('per-globe')
    el.style.left = `${box.left + box.width / 2 - origin.left}px`
    el.style.top = `${(headBox ? headBox.bottom : box.top) + gap - origin.top}px`
  }

  /**
   * AYNI (owner, 2026-10-19): every globe as the flat 2:1 map, or every one
   * a globe again (the 2D/3D button). The primary decides the view; the
   * others are put where it looks once all have switched, through the camera
   * sync when it is on.
   */
  private setFlatMap(on: boolean): Promise<void> {
    // Asked again while switching: the same switch, so whoever waits moves the camera after it.
    if (on === this.flatMap) return this.flatSwitch
    this.flatMap = on
    this.globeView?.sync()
    this.flatSwitch = (async () => {
      const primary = this.viewports.getPrimary()
      await primary?.setFlat(on)
      await Promise.all(this.viewports.getAll().filter((r) => r !== primary).map((r) => r.setFlat(on)))
      const map = primary?.getMap()
      if (map) map.jumpTo({ center: map.getCenter(), zoom: map.getZoom() })
      this.autoRotate?.sync()
    })()
    return this.flatSwitch
  }

  private onViewportLayoutChange(newCount: number, oldCount: number): void {
    // AYNI (Situation room, CR5): a new globe draws the room's own layers too, once its map exists.
    queueMicrotask(() => this.ownLayers.redraw())
    queueMicrotask(() => this.satelliteMaps?.refresh())
    if (newCount < oldCount) {
      // Tear down slots that are being removed
      for (let i = newCount; i < oldCount; i++) {
        // AYNI (phase 2): a globe that goes takes its own stack with it.
        if (i !== this.viewports.getPrimaryIndex()) this.disposeOtherStack(i)
        const panel = this.panelStates[i]
        if (!panel) continue
        this.cancelSiblingSeekUpload(i)
        if (panel.videoTexture) { panel.videoTexture.dispose() }
        if (panel.hlsService) { panel.hlsService.destroy() }
      }
      this.panelStates.length = newCount
    } else {
      // Add fresh empty slots for new panels
      while (this.panelStates.length < newCount) {
        this.panelStates.push(createPanelState())
      }
      // Seed the new viewports with the current toolbar state so
      // they visually match the primary's labels/borders/terrain.
      // We read state from the DOM button classes rather than
      // querying a renderer, since the new renderers may still be
      // mid-style-load when this fires. MapLibre queues layer
      // operations internally until the style is ready, so
      // dispatching the toggle now is safe.
      const labelsActive = document.getElementById('tools-menu-labels')?.classList.contains('active') ?? false
      const bordersActive = document.getElementById('tools-menu-borders')?.classList.contains('active') ?? false
      const terrainActive = document.getElementById('tools-menu-terrain')?.classList.contains('active') ?? false
      for (let i = oldCount; i < newCount; i++) {
        const newRenderer = this.viewports.getRendererAt(i)
        if (!newRenderer) continue
        const map = newRenderer.getMap()
        const applyOverlayState = () => {
          newRenderer.toggleLabels?.(labelsActive)
          newRenderer.toggleBoundaries?.(bordersActive)
          if (terrainActive) newRenderer.toggleTerrain?.(true)
        }
        // AYNI (2026-10-19): flat like the others, where the primary looks, as soon as its style is in
        // (setFlat waits for it; the full `load` can take a while on a new globe still fetching tiles).
        if (this.flatMap) {
          void newRenderer.setFlat(true).then(() => {
            const p = this.viewports.getPrimary()?.getMap()
            if (p && p !== map) map?.jumpTo({ center: p.getCenter(), zoom: p.getZoom() })
          })
        }
        if (map && map.isStyleLoaded()) {
          applyOverlayState()
        } else {
          map?.once('load', applyOverlayState)
        }
      }
    }
    // Layout change can affect whether a panel's legend is a
    // floating element vs inside the info panel, and the set of
    // available datasets in the info-panel picker — refresh both.
    // Also clear any picker override if the pinned slot just
    // vanished on a shrink.
    if (
      this.infoDisplayOverride !== null &&
      this.infoDisplayOverride >= newCount
    ) {
      this.infoDisplayOverride = null
    }
    this.refreshPanelLegends()
    this.renderInfoPanel()
    syncToolsMenuLayout(this.viewports.getLayout())
    // AYNI (multi-globe plan): the time rules go with the panels; a gone
    // panel's readings go with it; the address says what the globes show.
    if (newCount < oldCount) {
      this.panelTimeRules.length = newCount
      for (let i = newCount; i < oldCount; i++) {
        this.globeSeries.delete(i)
        this.globeReadings.delete(i)
      }
      if (this.browseTarget !== null && this.browseTarget >= newCount) this.setBrowseTarget(null)
    } else {
      while (this.panelTimeRules.length < newCount) this.panelTimeRules.push(FOLLOW)
    }
    // AYNI (multi-globe): the new globes report the cursor too.
    if (newCount > oldCount) this.bindLatLngReadout()
    this.refreshPanelHeads()
    this.writeSlotsUrl()
    // AYNI (phase 3): in a meeting, the new globes are locked (or the driver's kept) and named.
    this.relockGlobes()
    if (this.lastRoomStatus) this.showRoomDrivers(this.lastRoomStatus)
  }

  /**
   * Called by ViewportManager when the primary index changes (user
   * clicked a non-primary panel's indicator badge). Rewires the
   * singular UI — info panel, playback controls, video sync, URL —
   * to the new primary's dataset.
   */
  private onViewportPrimaryChange(newIndex: number, _oldIndex: number): void {
    logger.debug('[App] Primary panel changed:', _oldIndex, '→', newIndex)
    // AYNI (multi-globe plan, phase 2): each globe keeps its own layers; Tools → Layers follows the main one.
    this.swapLayerStacks(_oldIndex, newIndex)
    const newPrimaryPanel = this.panelStates[newIndex]
    const newDataset = newPrimaryPanel?.dataset ?? null

    // Analyze reads the primary and nothing else, and its transect is
    // drawn on that panel's map. Promoting a different one would leave
    // the line on the globe the panel is no longer describing, so it
    // closes here rather than being rewired — the same reasoning as the
    // dataset-swap teardown, which `notifyAnalyzeDatasetChanged` below
    // would miss whenever both panels hold the same row.
    closeAnalyzeUI()

    // Rewire video sync to the new primary's video (if any)
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)

    // Update the shared appState + info panel. Promoting a different
    // panel clears any picker override so the info panel follows the
    // new primary instead of silently pointing at a stale slot.
    this.appState.currentDataset = newDataset
    this.syncSatelliteBody()
    this.infoDisplayOverride = null
    if (newDataset) {
      this.renderInfoPanel()
      this.writeDatasetUrl(newDataset.id, 'replace')
      setHelpActiveDataset(newDataset.id)
      this.renderer?.setCanvasDescription(`3D globe showing ${newDataset.title}`)
    } else {
      const infoPanel = document.getElementById('info-panel')
      if (infoPanel) infoPanel.classList.add('hidden')
      this.clearDatasetUrl('replace')
      setHelpActiveDataset(null)
      this.renderer?.setCanvasDescription('Interactive 3D globe showing Earth')
    }
    // Legend placement depends on primary (single-view uses the
    // info panel's thumbnail for the primary; multi-view always
    // uses per-panel floating legends). Refresh so the primary's
    // legend moves to the correct surface.
    this.refreshPanelLegends()

    // Rewire playback controls based on the new primary's video state
    const isVideo = newDataset && dataService.isVideoDataset(newDataset) && newPrimaryPanel?.hlsService
    const hasTemporalRange = Boolean(newDataset?.startTime && newDataset?.endTime)
    if (isVideo) {
      this.showPlaybackControls(true)
      this.showTimeLabel(hasTemporalRange)
      updatePlayButton(newPrimaryPanel!.hlsService!.paused)
      // Recompute the display interval for the new primary's temporal
      // range so scrubber snapping and time label formatting are correct.
      // Pass `period` and `frames.count` for an imagery-accurate cadence
      // (Plan §5.1) — without these, multi-year climate datasets fall
      // back to the snap-list estimate and tick the label too fast.
      if (hasTemporalRange && newPrimaryPanel!.hlsService) {
        const start = new Date(newDataset!.startTime!)
        const end = new Date(newDataset!.endTime!)
        const videoDuration = newPrimaryPanel!.hlsService!.duration ?? 1
        this.playback.displayInterval = inferDisplayInterval(start, end, videoDuration, {
          period: newDataset!.period,
          frameCount: newDataset!.frames?.count,
        })
      }
      this.attachPrimaryVideoSync()
      this.doStartPlaybackLoop()
    } else {
      this.showPlaybackControls(false)
      if (!hasTemporalRange) {
        this.showTimeLabel(false)
      }
    }
    this.announce(newDataset ? `Active panel: ${newDataset.title}` : `Panel ${newIndex + 1} active`)
    // AYNI (multi-globe plan): the other globes' readings were on the old
    // primary's dates; the chips move (the new primary has no time chip).
    this.globeSeries.clear()
    this.globeReadings.clear()
    this.refreshPanelHeads()
    this.updateSiblingDateLabels()
    this.writeSlotsUrl()
  }

  /**
   * Store a video-load result into a specific panel's state.
   * Assumes the slot exists (callers must validate the index).
   */
  private storePanelVideoResult(
    slot: number,
    result: { hlsService: HLSService; videoTexture: VideoTextureHandle },
  ): void {
    const panel = this.panelStates[slot]
    if (!panel) return
    panel.hlsService = result.hlsService
    panel.videoTexture = result.videoTexture

    // A stream that dies after load used to say nothing at all, which
    // left this panel holding a frame for good while the shared time
    // label went on asserting a moment for it. Clear any stale mark
    // first: this slot may be being reused by a new dataset.
    this.viewports.setPanelStreamNotice(slot, false)
    result.hlsService.onFatalError((error) => {
      // Guard against a late callback for a dataset this slot no
      // longer holds — a Khipu swaps sibling datasets repeatedly.
      if (this.panelStates[slot]?.hlsService !== result.hlsService) return
      logger.error(`[App] Panel ${slot} stream failed terminally: ${error.type}/${error.details}`)
      this.viewports.setPanelStreamNotice(slot, true)
    })
  }

  /**
   * Sibling video sync — primary-as-master-clock, per-frame correction.
   *
   * The primary panel's video is the single source of truth for the
   * current real-world date; siblings are kept locked to it. Rather
   * than set a once-off `playbackRate` and let siblings free-run (which
   * integrates `video.duration` imprecision into visible drift — see
   * terraviz#132), we re-derive each sibling's target position from the
   * primary's date every frame and steer it back into alignment.
   *
   *   - **On play / seeked** (`seekSiblingsToDate`): exact-align every
   *     sibling to the primary's date and mirror its play/pause state.
   *     Runs once on attach and on the primary's `play` / `seeked`.
   *
   *   - **On pause** (`seekSiblingsToDate`): exact-align overlapping
   *     siblings to the primary's date *then* freeze them, so the held
   *     frame is frame-accurate for side-by-side study. Out-of-range
   *     siblings stay frozen at their nearest boundary frame.
   *
   *   - **Per-frame drift correction** (`correctSiblingDrift`, driven
   *     from the playback rAF loop while the primary is playing): every
   *     frame, re-derive each sibling's target/rate. Small drift is
   *     eased out by gently trimming `playbackRate` (no seek — a
   *     `currentTime` write on a playing video interrupts decode and
   *     flickers the panel; terraviz#229). A hard seek fires only for a
   *     large desync past {@link SIBLING_HARD_SEEK_THRESHOLD_S}, or to
   *     pin an out-of-range sibling to its boundary frame.
   *
   * Net: drift is bounded by the threshold (one frame to correct) rather
   * than by an interval, and seeking stays near-zero in steady state
   * because the smoothing rate keeps siblings inside the threshold.
   */
  private attachPrimaryVideoSync(): void {
    this.detachPrimaryVideoSync()
    const primaryIdx = this.viewports.getPrimaryIndex()
    const primaryPanel = this.panelStates[primaryIdx]
    const primaryHls = primaryPanel?.hlsService
    const primaryVideo = primaryHls?.getVideo?.() ?? null
    if (!primaryVideo) return

    /**
     * Seek every sibling to the primary's current date-mapped
     * position, update out-of-range indicators, and optionally
     * mirror the primary's play/pause state.
     *
     * @param mirrorPlayState If true, also play/pause siblings
     * to match the primary. On periodic drift checks we skip this
     * (they're already playing/paused) to avoid re-triggering
     * play() on videos that are fine.
     */
    const seekSiblingsToDate = (mirrorPlayState: boolean) => {
      const pIdx = this.viewports.getPrimaryIndex()
      // AYNI: the primary's date through its own kind of dates — the
      // catalogue's start and end, or a stream's .dsa frames
      // (panelDateSync.ts). Streams have no catalogue range, and the sync
      // used to find no date on them and leave every other globe behind.
      const primaryDates = this.panelDates(pIdx)
      const primaryDateMs = primaryDates ? panelDateAt(primaryDates, primaryVideo.currentTime) : null

      for (let i = 0; i < this.panelStates.length; i++) {
        if (i === pIdx) continue
        const sibPanel = this.panelStates[i]
        const sibHls = sibPanel?.hlsService
        const sibVideo = sibHls?.getVideo?.() ?? null
        // Metadata is enough to steer a sibling — see
        // {@link SIBLING_MIN_READY_STATE}. This is the call that has to
        // reach a sibling parked at its own end when the primary wraps,
        // so it must not wait for frame data the seek itself will fetch.
        if (!sibVideo || sibVideo.readyState < SIBLING_MIN_READY_STATE) continue

        const sibDates = this.panelDates(i)
        // AYNI (multi-globe plan): this globe's date under its time rule —
        // the primary's, moved by its offset, or held (globeSlots.ts).
        const rule = this.panelTimeRules[i] ?? FOLLOW
        const held = rule.kind === 'fixed'
        const dateMs = held ? rule.ms : primaryDateMs === null ? null : applyTimeRule(rule, primaryDateMs)

        if ((primaryDates || held) && dateMs !== null && sibDates) {
          // On a stream, the middle of the frame whose period holds the
          // date; on a catalogue dataset, `dateToVideoTime` as before.
          const { videoTime: targetTime, position } = panelLocate(sibDates, dateMs, 'frame')

          // Match the sibling's playback speed so it advances through
          // real-world time at the same pace as the primary, even if
          // the two videos have different durations (e.g. daily vs
          // weekly frames). Without this, a shorter sibling finishes
          // its entire date range while the primary is still early in
          // its timeline, causing constant drift corrections.
          // rate = (sib video seconds per real-world ms) / (primary video seconds per real-world ms),
          // scaled by the primary's actual playbackRate so the sibling
          // tracks playback-rate changes (e.g. 5fps → 0.167×),
          // not just the 1× case (terraviz#229); clamped to the
          // browser's limits (typically 0.0625–16×).
          if (primaryDates && panelMsPerSecond(primaryDates) > 0 && panelMsPerSecond(sibDates) > 0) {
            sibVideo.playbackRate = pacingRate(primaryDates, sibDates, primaryVideo.playbackRate)
          }

          if (position === 'inside') {
            this.viewports.setOutOfRange(i, false)
            this.alignSiblingTo(i, sibVideo, targetTime, sibDates)
            // A held globe stays on its frame whatever the primary does.
            if (held) {
              if (!sibVideo.paused) sibVideo.pause()
            } else if (mirrorPlayState) {
              if (primaryVideo.paused && !sibVideo.paused) {
                sibVideo.pause()
              } else if (!primaryVideo.paused && sibVideo.paused) {
                sibVideo.play().catch(() => { /* autoplay blocked */ })
              }
            }
          } else {
            this.viewports.setOutOfRange(i, true)
            this.alignSiblingTo(i, sibVideo, targetTime, sibDates)
            if (!sibVideo.paused) sibVideo.pause()
          }
        } else {
          this.viewports.setOutOfRange(i, false)
          if (mirrorPlayState) {
            if (primaryVideo.paused && !sibVideo.paused) {
              sibVideo.pause()
            } else if (!primaryVideo.paused && sibVideo.paused) {
              sibVideo.play().catch(() => { /* autoplay blocked */ })
            }
          }
        }

        this.uploadSiblingFrameOnSeek(i, sibVideo)
      }
      // AYNI (multi-globe plan): the date each globe landed on, in its head.
      this.updateSiblingDateLabels()
    }

    // --- Wire event listeners ---

    const onPlay = () => seekSiblingsToDate(true)
    // On pause, exact-align every overlapping sibling to the primary's
    // date *before* freezing, so the held frame is frame-accurate for
    // side-by-side study — paused is exactly when a viewer scrutinises
    // the comparison, and a one-shot seek can't thrash. seekSiblingsToDate
    // both snaps in-range siblings to the exact date and (because the
    // primary is already paused here) mirrors the pause to them; siblings
    // whose window doesn't contain the date stay frozen at their boundary.
    const onPause = () => seekSiblingsToDate(true)
    const onSeeked = () => seekSiblingsToDate(true)

    primaryVideo.addEventListener('play', onPlay)
    primaryVideo.addEventListener('pause', onPause)
    primaryVideo.addEventListener('seeked', onSeeked)
    this.primaryVideoSyncListeners.push(
      { event: 'play', handler: onPlay as EventListener },
      { event: 'pause', handler: onPause as EventListener },
      { event: 'seeked', handler: onSeeked as EventListener },
    )
    this.primaryVideoSyncTarget = primaryVideo

    // Arm per-frame drift correction (driven from the playback rAF
    // loop via `correctSiblingDrift`) and run a full resync once now so
    // siblings reflect the primary's current state immediately.
    this.primaryVideoSyncActive = true
    seekSiblingsToDate(true)

    // AYNI: a stream's dates are its .dsa, fetched on first use. Until it
    // arrives that panel has no date to sync on, so once every pending
    // one has, resync — unless this sync has been replaced meanwhile.
    const pending = this.panelStates
      .map((panel) => panel?.dataset?.timelineLink)
      .filter((url): url is string => !!url && !this.dsaTimelines.get(url))
    if (pending.length) {
      void Promise.all(pending.map((url) => this.dsaTimelines.load(url).catch(() => null))).then(() => {
        if (this.primaryVideoSyncActive && this.primaryVideoSyncTarget === primaryVideo) seekSiblingsToDate(true)
      })
    }
  }

  /**
   * AYNI: how a panel's video time maps to dates, for the multi-globe
   * sync (panelDateSync.ts). A catalogue start and end win when the
   * dataset has them, as the time label does; otherwise a stream's .dsa
   * frames (null until it has loaded). Null when the panel has neither.
   */
  private panelDates(slot: number): PanelDates | null {
    const panel = this.panelStates[slot]
    const ds = panel?.dataset
    const video = panel?.hlsService?.getVideo?.() ?? null
    if (!ds || !video) return null
    if (ds.startTime && ds.endTime) return rangePanelDates(ds.startTime, ds.endTime, video.duration)
    if (!ds.timelineLink) return null
    this.dsaTimelines.prefetch(ds.timelineLink)
    const timeline = this.dsaTimelines.get(ds.timelineLink)
    return timeline ? framePanelDates(timeline) : null
  }

  // --- AYNI: the multi-globe plan (docs/AYNI_XR_MULTI_GLOBE_PLAN.md) ---

  /** The Browse panel's callbacks, the same wherever it is shown. */
  private browseCallbacks(): Parameters<typeof showBrowseUI>[1] {
    return {
      onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
      onShowSatellite: (norad, name) => this.showSatellite(norad, name),
      announce: (msg) => this.announce(msg),
      isMobile: this.isMobile,
      onOpenChat: (query) => this.openChatWithQuery(query),
      onStartLesson: (slug) => void this.startLesson(slug),
      onOpenKhipu: (k, files) => this.openKhipu(k, files),
      onRecordKhipu: (trigger) => this.recordKhipu(trigger),
      recordState: () => this.khipuRecordState(),
      onEditKhipu: (k, files, opts) => this.editKhipu(k, files, 0, null, opts?.unsaved === true),
      onNewKhipu: (trigger) => this.newKhipu(trigger),
      // Phase 0: a dataset beside the one on the globe, on a second globe.
      onOpenBeside: (id) => void this.openBeside(id),
    }
  }

  /** A globe's chip: Browse opens to load into that globe. */
  private openBrowseFor(slot: number): void {
    // AYNI (phase 3): a meeting's audience does not choose; a driver chooses for its own globe.
    if (this.refusedByMeeting(slot)) return
    this.openBrowsePanel()
    this.setBrowseTarget(slot)
  }

  /**
   * Which globe Browse loads into next. The primary (or null) is the
   * ordinary case and shows nothing; another globe puts a banner on
   * Browse and marks each card by whether it shares dates with globe 1.
   */
  private setBrowseTarget(slot: number | null): void {
    const target = slot !== null && slot !== this.viewports.getPrimaryIndex() && slot < this.panelStates.length ? slot : null
    this.browseTarget = target
    if (target === null) {
      setBrowseTarget(null)
      setBrowseCompatibility(null)
      return
    }
    setBrowseTarget({
      label: t('browse.target.banner', { n: target + 1 }),
      cancel: t('browse.target.cancel'),
      cancelAria: t('browse.target.cancel.aria', { n: target + 1 }),
      onCancel: () => this.setBrowseTarget(null),
    })
    setBrowseCompatibility((id) => this.compatibilityWithPrimary(id))
  }

  /**
   * Whether a dataset shares dates with the one on the primary globe, as
   * the Real time list judges it (`rangesOverlap`); null while either side's
   * dates are unknown (a stream's arrive from its release or .dsa, and the
   * cards are redrawn as they do).
   */
  private compatibilityWithPrimary(id: string): 'yes' | 'no' | null {
    const base = this.baseRange()
    const d = dataService.getDatasetById(id)
    if (!base || !d) return null
    const range = this.datasetRange(d)
    if (!range) return null
    return rangesOverlap(range, base) ? 'yes' : 'no'
  }

  /** A dataset's dates: the catalogue's start and end, else its stream's (fetched once, `rtRanges`); null until known or when it has none. */
  private datasetRange(d: Dataset): TimeRange | null {
    const start = d.startTime ? Date.parse(d.startTime) : NaN
    const end = d.endTime ? Date.parse(d.endTime) : NaN
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) return { startMs: start, endMs: end }
    if (!d.releaseDescriptorLink && !d.timelineLink) return null
    if (this.rtRanges.has(d.id)) return this.rtRanges.get(d.id) ?? null
    this.rangeWanted.add(d.id)
    this.fetchRtRanges()
    return null
  }

  /**
   * "Open beside": the dataset on a second globe, next to the one shown.
   * One globe becomes two (side by side, or stacked on a portrait screen)
   * when the plan and the device allow; otherwise the plans say why. It
   * goes to an empty globe first, else to the first globe that is not
   * the primary.
   */
  private async openBeside(id: string): Promise<void> {
    if (this.viewports.getPanelCount() === 1) {
      const portrait = window.innerHeight >= window.innerWidth
      this.viewports.setLayout(portrait ? '2v' : '2h')
      if (this.viewports.getPanelCount() === 1) {
        this.announce(t('viewport.openBeside.locked'))
        openPlans(null, 'research', { notice: t('plans.locked.globes') })
        return
      }
    }
    const primary = this.viewports.getPrimaryIndex()
    let slot = this.panelStates.findIndex((p, i) => i !== primary && !p.dataset)
    if (slot < 0) slot = this.panelStates.findIndex((_, i) => i !== primary)
    if (slot < 0) return
    this.dismissBrowseAfterLoad()
    if (await this.loadDatasetInto(slot, id)) {
      const ds = this.panelStates[slot]?.dataset
      if (ds) this.announce(t('viewport.openBeside.done', { title: displayTitle(ds), n: slot + 1 }))
    }
  }

  /**
   * Load a dataset into one globe. The primary takes the ordinary path
   * (the address, the transport); another globe gets only the dataset,
   * and its chip and the address say so. True once it is up.
   */
  private async loadDatasetInto(slot: number, id: string, trigger: import('./types').LoadTrigger = 'browse'): Promise<boolean> {
    if (slot === this.viewports.getPrimaryIndex()) {
      await this.loadDatasetAndConfirm(id, trigger)
      return this.appState.currentDataset?.id === id
    }
    if (slot < 0 || slot >= this.panelStates.length) return false
    if (this.refusedByMeeting(slot)) return false
    this.bumpLoadGen(slot)
    try {
      await this.loadDataset(id, trigger, slot)
    } catch (error) {
      logger.warn('[App] load into globe failed:', slot + 1, id, error)
    }
    const panel = this.panelStates[slot]
    return panel?.dataset?.id === id && !!(panel.hlsService || panel.image)
  }

  /** A globe's time rule changed (its menu, a link, a Khipu): resync it now, and say so. */
  private setPanelTimeRule(slot: number, rule: GlobeTimeRule, announce = true): void {
    if (slot < 0 || slot >= this.panelStates.length) return
    // AYNI (phase 3): in a meeting the presenter sets the rules; a driver its own globe's.
    if (announce && this.refusedByMeeting(slot)) return
    const before = this.panelTimeRules[slot] ?? FOLLOW
    this.panelTimeRules[slot] = rule
    // Its series were read on the old dates.
    this.globeSeries.delete(slot)
    this.refreshPanelHeads()
    if (!sameRule(before, rule)) this.attachPrimaryVideoSync()
    this.updateSiblingDateLabels()
    this.writeSlotsUrl()
    if (announce) this.announce(t('viewport.time.changed', { n: slot + 1, label: timeRuleLabel(rule) }))
  }

  /** Each globe's chips: its dataset (or "+ Add a dataset") and, off the primary, its time rule. */
  private refreshPanelHeads(): void {
    const primary = this.viewports.getPrimaryIndex()
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const ds = this.panelStates[slot]?.dataset ?? null
      const n = slot + 1
      const title = ds ? displayTitle(ds) : null
      this.viewports.setPanelDataset(slot, title, {
        add: t('viewport.panel.addDataset'),
        aria: title ? t('viewport.panel.datasetChip.aria', { n, title }) : t('viewport.panel.emptyChip.aria', { n }),
      })
      const dated = !!ds && dataService.isVideoDataset(ds)
      if (slot === primary || !dated) {
        this.viewports.setPanelTimeRule(slot, null)
      } else {
        const label = timeRuleLabel(this.panelTimeRules[slot] ?? FOLLOW)
        this.viewports.setPanelTimeRule(slot, label, t('viewport.time.chip.aria', { n, label }))
      }
      if (slot === primary || !ds) this.viewports.setPanelDate(slot, null)
    }
  }

  /**
   * The date each other globe shows, in its head: the label of the frame
   * it is on, as the date track would print it. Written only when the
   * globe's playhead moved, so a still globe costs a comparison.
   */
  private updateSiblingDateLabels(): void {
    const primary = this.viewports.getPrimaryIndex()
    for (let i = 0; i < this.panelStates.length; i++) {
      if (i === primary) continue
      const panel = this.panelStates[i]
      const video = panel?.hlsService?.getVideo?.() ?? null
      const dates = video ? this.panelDates(i) : null
      if (!dates || !video || !panel?.dataset) {
        this.viewports.setPanelDate(i, null)
        this.siblingDateAt[i] = NaN
        continue
      }
      if (this.siblingDateAt[i] === video.currentTime) continue
      this.siblingDateAt[i] = video.currentTime
      this.viewports.setPanelDate(i, this.slotDateText(i))
    }
  }

  /** AYNI (multi-globe plan): the date a globe shows, as its head reads it; null without a dated video. */
  private slotDateText(i: number): string | null {
    const panel = this.panelStates[i]
    const video = panel?.hlsService?.getVideo?.() ?? null
    const dates = video ? this.panelDates(i) : null
    if (!dates || !video || !panel?.dataset) return null
    if (dates.kind === 'frames') {
      return formatFrameLabel(frameTimeMs(dates.axis, panelFrameAt(dates, video.currentTime)), dates.axis.precision, getLocale())
    }
    const ds = panel.dataset
    const showTime = ds.period ? isSubDailyPeriod(ds.period) : (this.playback.displayInterval?.showTime ?? false)
    const date = videoTimeToDate(video.currentTime, dates.duration, new Date(dates.startMs), new Date(dates.endMs), this.playback.displayInterval?.intervalMs)
    return formatDate(date, showTime)
  }

  /**
   * AYNI (multi-globe plan, a Studio split render): whether every other
   * globe shows the frame of its own date. They follow the main globe's
   * seek; one still off its date is sent there (the live sync may hold a
   * seek back), and one whose frame has not reached its texture is asked
   * to upload it again.
   */
  private otherGlobesOnDate(): boolean {
    const p = this.viewports.getPrimaryIndex()
    const primaryVideo = this.panelStates[p]?.hlsService?.getVideo?.() ?? null
    const primaryDates = primaryVideo ? this.panelDates(p) : null
    const primaryDateMs = primaryDates && primaryVideo ? panelDateAt(primaryDates, primaryVideo.currentTime) : null
    let ready = true
    for (let i = 0; i < this.viewports.getPanelCount(); i++) {
      if (i === p) continue
      const panel = this.panelStates[i]
      const video = panel?.dataset ? panel.hlsService?.getVideo?.() ?? null : null
      // A bare Earth or a still image: its tiles are the map's to wait for.
      if (!video) continue
      if (video.readyState < 2 || video.seeking) { ready = false; continue }
      const rule = this.panelTimeRules[i] ?? FOLLOW
      const dates = this.panelDates(i)
      const dateMs = rule.kind === 'fixed' ? rule.ms : primaryDateMs === null ? null : applyTimeRule(rule, primaryDateMs)
      if (dates && dateMs !== null) {
        const { videoTime } = panelLocate(dates, dateMs, 'frame')
        const on = dates.kind === 'frames' ? isOnFrame(dates, video.currentTime, videoTime) : Math.abs(video.currentTime - videoTime) <= 0.02
        if (!on) { video.currentTime = videoTime; ready = false; continue }
      }
      const renderer = this.viewports.getRendererAt(i)
      const uploaded = renderer?.getUploadedFrameTime() ?? null
      if (uploaded === null || Math.abs(uploaded - video.currentTime) > 0.002) {
        renderer?.requestVideoUpdate()
        ready = false
      }
    }
    return ready
  }

  /** The globes besides the primary as a link names them (globeSlots.ts), by slot. */
  private linkedSlots(): Array<LinkedSlot | null> {
    const primary = this.viewports.getPrimaryIndex()
    return this.panelStates.map((p, i) => (i === primary ? null : { dataset: p.dataset?.id ?? null, time: this.panelTimeRules[i] ?? FOLLOW }))
  }

  /**
   * The other globes in the address (`g2`, `g2t`…, `p` for a primary that
   * is not globe 1) with the layout, so the view can be shared and comes
   * back on reload. Nothing is written for a single globe.
   */
  private writeSlotsUrl(): void {
    if (typeof window === 'undefined') return
    const many = this.viewports.getPanelCount() > 1
    const params = new URLSearchParams(writeLinkedSlots(window.location.search, many ? this.linkedSlots() : [], many ? this.viewports.getPrimaryIndex() : 0))
    if (many) params.set('layout', this.viewports.getLayout())
    else {
      params.delete('layout')
      params.delete('setview')
    }
    const q = params.toString()
    const next = `${window.location.pathname}${q ? `?${q}` : ''}`
    if (next !== `${window.location.pathname}${window.location.search}`) window.history.replaceState(window.history.state, '', next)
  }

  /** AYNI (multi-globe plan): what the link said of the other globes, read at boot; null once applied. */
  private linkedAtBoot: ReturnType<typeof parseLinkedSlots> | null = null

  /** The link's other globes, loaded once the layout is there; a slot that already shows the dataset is left alone. Once. */
  private async applyLinkedSlots(): Promise<void> {
    const linked = this.linkedAtBoot
    if (!linked) return
    const { slots, primary } = linked
    if (!slots.some(Boolean)) return
    this.linkedAtBoot = null
    if (primary !== null && primary < this.viewports.getPanelCount() && primary !== this.viewports.getPrimaryIndex()) {
      this.viewports.promoteToPrimary(primary)
    }
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const l = slots[slot]
      if (!l || slot === this.viewports.getPrimaryIndex()) continue
      this.panelTimeRules[slot] = l.time
      if (l.dataset && this.panelStates[slot]?.dataset?.id !== l.dataset) await this.loadDatasetInto(slot, l.dataset, 'url')
    }
    this.refreshPanelHeads()
    this.attachPrimaryVideoSync()
  }

  /** The reading a globe (not the primary) has at a pin, as its label prints it; '' with none. */
  private globeValueText(slot: number, pin: Pin): string {
    const r = this.globeReadings.get(slot)?.get(pin.id)
    if (!r) return ''
    return r.noData ? t('tools.data.pins.nothing') : formatProbeReading(r)
  }

  /**
   * Phase 1: the same pins read on every other globe, each on its own
   * dataset and date — the latest reading beside the pin there, and a
   * series on the primary's dates (the graph's x-axis, owner's decision
   * 1, 2026-10-10), so "then" draws over "now".
   */
  private sampleGlobePins(pins: readonly Pin[], at: PinFrame | null): void {
    const primary = this.viewports.getPrimaryIndex()
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      if (slot === primary) continue
      const renderer = this.viewports.getRendererAt(slot)
      const ds = this.panelStates[slot]?.dataset
      if (!renderer || !ds || !renderer.probeKind()) {
        this.globeReadings.delete(slot)
        this.globeSeries.delete(slot)
        continue
      }
      const video = this.panelStates[slot]?.hlsService?.getVideo?.() ?? null
      // A seek in flight still shows the frame it left.
      if (video?.seeking) continue
      let readings = this.globeReadings.get(slot)
      if (!readings) this.globeReadings.set(slot, (readings = new Map()))
      let bySeries = this.globeSeries.get(slot)
      if (!bySeries) this.globeSeries.set(slot, (bySeries = new Map()))
      for (const pin of pins) {
        const r = renderer.probeValueAt(pin.lat, pin.lon)
        readings.set(pin.id, r)
        if (!pin.series) {
          // Moved, or a still frame: what was read there is no longer its.
          bySeries.delete(pin.id)
          continue
        }
        if (!at || at.timeMs === null || !r) continue
        let series = bySeries.get(pin.id)
        if (!series || series.startMs !== pin.series.startMs || series.endMs !== pin.series.endMs || series.slots.length !== pin.series.slots.length) {
          series = createSeries(pin.series.startMs, pin.series.endMs, pin.series.slots.length) ?? undefined
          if (!series) continue
          bySeries.set(pin.id, series)
        }
        recordSample(series, at.timeMs, r.noData ? null : r.value)
      }
      for (const id of [...bySeries.keys()]) {
        if (!pins.some((p) => p.id === id)) {
          bySeries.delete(id)
          readings.delete(id)
        }
      }
    }
  }

  /**
   * A Khipu step's globes: the layout (as far as the plan and the device
   * allow), then each other globe's dataset (loaded only when it is not
   * there already; null empties it) and its time rule. Globe 1 is the
   * step's own dataset, already shown.
   */
  private async setGlobesFromStep(layout: ViewLayout, others: ReadonlyArray<{ datasetId: string | null; time: GlobeTimeRule }>): Promise<void> {
    if (this.refusedByMeeting()) return
    if (this.viewports.getLayout() !== layout) this.viewports.setLayout(layout, 'tools')
    const primary = this.viewports.getPrimaryIndex()
    let k = 0
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      if (slot === primary) continue
      const wanted = others[k++]
      if (!wanted) break
      this.panelTimeRules[slot] = wanted.time
      const shown = this.panelStates[slot]?.dataset?.id ?? null
      if (wanted.datasetId && shown !== wanted.datasetId) {
        if (!(await this.loadDatasetInto(slot, wanted.datasetId, 'url'))) {
          const title = dataService.getDatasetById(wanted.datasetId)?.title ?? wanted.datasetId
          this.announce(t('khipu.step.globes.failed', { n: slot + 1, title }))
        }
      } else if (!wanted.datasetId && shown) {
        await this.unloadPanelDataset(slot)
      }
    }
    this.refreshPanelHeads()
    this.attachPrimaryVideoSync()
    this.updateSiblingDateLabels()
    this.writeSlotsUrl()
  }

  // --- AYNI: the multi-globe plan, phase 3 (a driver per globe in meetings) ---

  /** A globe's camera as the room carries it. */
  private roomViewOf(slot: number): RoomView | null {
    const map = this.viewports.getRendererAt(slot)?.getMap()
    if (!map) return null
    const c = map.getCenter()
    return { lat: c.lat, lon: c.lng, zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch(), ...(this.flatMap ? { flat: true as const } : {}) }
  }

  /** Move one globe's camera where the room says, the short way round, with the ease `setView` uses. */
  private easeRoomView(slot: number, view: RoomView): void {
    const map = this.viewports.getRendererAt(slot)?.getMap()
    if (!map) return
    // AYNI (2026-10-19): every globe is flat or none is; switch first, then ease.
    if (!!view.flat !== this.flatMap) {
      void this.setFlatMap(!!view.flat).then(() => this.easeRoomView(slot, view))
      return
    }
    const here = map.getCenter().lng
    const lng = here + (((((view.lon - here + 180) % 360) + 360) % 360) - 180)
    map.easeTo({ center: [lng, view.lat], zoom: view.zoom, bearing: view.bearing, pitch: view.pitch, duration: 260, easing: (t) => t })
  }

  /**
   * A follower takes the lead's globes: the layout (as far as this device
   * and the plan allow), the main one, and each globe's dataset and time
   * rule — not the globe this device drives, nor one with another driver
   * (their `slot` messages say). Idempotent: asked on every state.
   */
  private applyRoomSlots(state: RoomState, driving: number | null): void {
    if (!state.layout || !state.slots) {
      // Back to one globe.
      if (this.viewports.getPanelCount() > 1 && this.roomFollowing) this.asRoom(() => this.viewports.setLayout('1', 'plan'))
      return
    }
    this.asRoom(() => {
      if (this.viewports.getLayout() !== state.layout) this.viewports.setLayout(state.layout!, 'plan')
      const primary = state.primary ?? 0
      if (primary < this.viewports.getPanelCount() && this.viewports.getPrimaryIndex() !== primary) this.viewports.promoteToPrimary(primary)
    })
    const now = performance.now()
    state.slots.forEach((s, i) => {
      if (i === (state.primary ?? 0) || i >= this.panelStates.length) return
      // The globe this device drives is its own: it takes the presenter's
      // dataset and rule once, while it still shows nothing, and keeps its own after.
      if (i === driving && (this.panelStates[i]?.dataset || this.roomSlotAsked.has(i))) return
      const rule = parseTimeRule(s.time ?? '') ?? FOLLOW
      if (!sameRule(this.panelTimeRules[i] ?? FOLLOW, rule)) this.setPanelTimeRule(i, rule, false)
      this.roomLoadInto(i, s.datasetId, now)
      // AYNI (phase 2): that globe's own layers and streams, once its dataset is there.
      if (s.layers !== undefined && this.panelStates[i]?.dataset?.id === s.datasetId) this.applyOtherRoomLayers(i, s.layers)
    })
    // The lock and the drivers' names cover the globes that just appeared.
    this.relockGlobes()
    if (this.lastRoomStatus) this.showRoomDrivers(this.lastRoomStatus)
  }

  /** AYNI (multi-globe plan): the room's latest status, for the globes that appear after it arrived. */
  private lastRoomStatus: RoomStatus | null = null
  /** AYNI (Situation room): the room code this page joined, if any (to put its meeting on the walls). */
  private roomCodeNow: string | null = null
  /** AYNI (Situation room, CR-D14): this page's seat token in that room (the presenter's proves it presents). */
  private roomSeatToken: string | null = null
  /** AYNI (CR-D14): the meeting whose shown layers this page follows (someone in its audience), and how often it asks. */
  private meetingLayersCode: string | null = null
  private meetingLayersTimer: number | null = null
  /** AYNI (CR-D14): the layer ids last sent for the meeting this page presents. */
  private meetingLayersSent: string | null = null
  /** AYNI (Situation room, CR5): the room's own layers on every globe of this page. */
  private ownLayers = createOwnLayers()
  /** AYNI (satellites plan): the Satellites layer (one per page) and its drawing on the browser globes. */
  private satellites = getSatelliteLayer()
  private satelliteMaps: SatelliteMapUiHandle | null = null

  /**
   * AYNI (owner, 2026-10-23): satellites orbit the Earth. The layer is told
   * which body the main globe shows (offEarthBody.ts), so a dataset of
   * Saturn, the Moon or the Sun draws none on it, in the headset or on the
   * phone, and the switch is held; the other globes of a layout answer for
   * their own body as they draw (attachSatellitesToMaps). Called wherever a
   * globe's dataset is set, cleared, or becomes the main one.
   */
  private syncSatelliteBody(): void {
    this.satellites.setBody(bodyOfDataset(this.panelStates[this.viewports.getPrimaryIndex()]?.dataset))
    this.satelliteMaps?.refresh()
    // AYNI: the same moment tells Solar System mode which body the globe shows.
    this.solarModeHandle?.datasetChanged()
  }

  /** AYNI (owner, 2026-11-04): Solar System mode, made on first use (docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md). */
  private solarModeHandle: SolarModeHandle | null = null
  private solarLibrary: LessonLibraryIndex | null = null
  private solarMode(): SolarModeHandle {
    if (this.solarModeHandle) return this.solarModeHandle
    setSolarEarthPicture(`${EARTH_ASSET_BASE}/earth_diffuse_2048.jpg`)
    setBrowseBodyScopeLabel((values) => {
      const id = solarBodyOfDatasetBody(values[0] ?? null)
      return id ? solarBodyName(id) : values[0] ?? ''
    })
    const container = document.getElementById('container') as HTMLElement
    const primaryDataset = (): Dataset | null => this.panelStates[this.viewports.getPrimaryIndex()]?.dataset ?? null
    this.solarModeHandle = createSolarSystemMode({
      container,
      uiRoot: document.getElementById('ui') as HTMLElement,
      loadThree,
      currentDatasetId: () => primaryDataset()?.id ?? null,
      currentDatasetBody: () => bodyOfDataset(primaryDataset()),
      datasetExists: (id) => !!dataService.getDatasetById(id) || id.startsWith(LIBRARY_PREFIX),
      datasetLocked: async (id) => {
        const d = dataService.getDatasetById(id)
        return d ? (await streamLockedBy(d)) !== null : false
      },
      loadDataset: (id) => this.loadDataset(id, 'browse'),
      clearDataset: () => this.goHome({ browse: false }),
      setBrowseBodies: (bodies) => setBrowseBodyScope(bodies),
      globeOnScreen: () => this.globeOnScreen(container),
      pictureUrl: (sosId) => {
        const entry = this.solarLibrary?.datasets[sosId]
        return entry && this.solarLibrary ? this.solarLibrary.base + entry.media : null
      },
      libraryReady: async () => {
        this.solarLibrary ??= await loadLessonLibrary()
      },
      announce: (msg) => this.announce(msg),
      notice: (msg) => {
        this.announce(msg)
        if (isVrActive()) notifyXr(msg)
      },
      mayChangeDataset: () => !this.refusedByMeeting(),
      onChange: (state) => syncSolarButton(state === 'overview'),
    })
    return this.solarModeHandle
  }

  /**
   * AYNI (Solar System mode): the primary globe on screen, CSS px in
   * `container` — its centre and radius, where a flight's arriving body
   * lands. Measured, not worked out: the farthest points of the globe's
   * edge to the north, south, east and west of the centre, found by
   * projecting points along great circles out to 90° (MapLibre's globe is
   * not quite centred on the centre's point when the view looks at high
   * latitudes, and its size has no simple formula).
   */
  private globeOnScreen(container: HTMLElement): { cx: number; cy: number; radiusPx: number } {
    const box = container.getBoundingClientRect()
    const fallback = { cx: box.width / 2, cy: box.height / 2, radiusPx: Math.min(box.width, box.height) * 0.36 }
    const map = this.viewports.getPrimary()?.getMap()
    if (!map || this.viewports.getPrimary()?.getProjection() !== 'globe') return fallback
    const canvas = map.getCanvas().getBoundingClientRect()
    const c = map.getCenter()
    const p0 = map.project(c)
    const D = Math.PI / 180
    const lat1 = c.lat * D
    const lon1 = c.lng * D
    /** The farthest a point along the great circle at `bearing` projects from the centre's point, as a screen vector. */
    const edge = (bearing: number): { x: number; y: number } => {
      let best = { x: 0, y: 0 }
      let bestD = -1
      const b = bearing * D
      for (let a = 30; a <= 90; a += 0.5) {
        const d = a * D
        const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b))
        const lon2 = lon1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2))
        const q = map.project([lon2 / D, lat2 / D])
        const dist = Math.hypot(q.x - p0.x, q.y - p0.y)
        if (dist > bestD) {
          bestD = dist
          best = { x: q.x, y: q.y }
        }
      }
      return best
    }
    const n = edge(0)
    const s = edge(180)
    const e = edge(90)
    const w = edge(270)
    const cx = (e.x + w.x) / 2
    const cy = (n.y + s.y) / 2
    const radiusPx = (Math.hypot(n.x - s.x, n.y - s.y) + Math.hypot(e.x - w.x, e.y - w.y)) / 4
    if (!Number.isFinite(radiusPx) || radiusPx <= 0) return fallback
    return { cx: canvas.left - box.left + cx, cy: canvas.top - box.top + cy, radiusPx }
  }
  /** AYNI (Situation room, CR3): the briefing being given from this page, made when first asked for. */
  private briefingSessionNow: BriefingSession | null = null
  /** AYNI (Situation room, CR2): each live stream's dates as last read (null: could not be read). */
  private streamDatesSeen = new Map<string, StreamDates | null>()

  /** Lock the globes again after the layout changed, keeping the one this device drives. */
  private relockGlobes(): void {
    if (this.lockedHandlers === null) return
    this.setGlobeLocked(false)
    this.setGlobeLocked(true, this.roomDriving)
  }

  /** Load (or unload) what the room says into one globe, asked again only after a while. */
  private roomLoadInto(slot: number, datasetId: string | null, now: number): void {
    const shown = this.panelStates[slot]?.dataset?.id ?? null
    if (shown === datasetId) return
    const asked = this.roomSlotAsked.get(slot)
    const key = datasetId ?? ''
    if (asked && asked.id === key && now - asked.at < 5000) return
    if (datasetId && !dataService.getDatasetById(datasetId) && this.appState.datasets.length === 0) return
    this.roomSlotAsked.set(slot, { id: key, at: now })
    this.asRoom(() => {
      if (datasetId) void this.loadDatasetInto(slot, datasetId, 'url')
      else void this.unloadPanelDataset(slot).then(() => { this.refreshPanelHeads(); this.writeSlotsUrl() })
    })
  }

  /** A driver's globe: its camera, and its dataset or time rule when they changed. */
  private applyRoomSlotUpdate(n: number, update: RoomSlotUpdate): void {
    if (n >= this.panelStates.length) return
    if (update.datasetId !== undefined) this.roomLoadInto(n, update.datasetId, performance.now())
    if (update.time !== undefined) {
      const rule = parseTimeRule(update.time) ?? FOLLOW
      if (!sameRule(this.panelTimeRules[n] ?? FOLLOW, rule)) this.setPanelTimeRule(n, rule, false)
    }
    if (update.view) {
      const here = this.roomViewOf(n)
      if (!here || viewsDiffer(here, update.view)) this.easeRoomView(n, update.view)
    }
  }

  /** Who drives which globe, on the globes' head rows. */
  private showRoomDrivers(status: RoomStatus): void {
    for (let slot = 0; slot < this.viewports.getPanelCount(); slot++) {
      const d = status.connected && status.meeting ? status.drivers.find((x) => x.slot === slot) : undefined
      if (!d) this.viewports.setPanelDriver(slot, null)
      else if (status.driving === slot) this.viewports.setPanelDriver(slot, t('viewport.driver.you'))
      else this.viewports.setPanelDriver(slot, t('viewport.driver.label', { name: d.name ?? t('activity.drivers.someone') }))
    }
  }

  /** The other globes' series per pin, for the pin graph and the CSV (dataPinsUI `globeTraces`). */
  private globeTraces(): GlobeTrace[] {
    const out: GlobeTrace[] = []
    const pins = this.dataPinStore?.list() ?? []
    for (const [slot, bySeries] of this.globeSeries) {
      const ds = this.panelStates[slot]?.dataset
      if (!ds) continue
      const ruleLabel = timeRuleLabel(this.panelTimeRules[slot] ?? FOLLOW)
      for (const pin of pins) {
        const series = bySeries.get(pin.id)
        if (!series) continue
        out.push({ pinId: pin.id, globe: slot + 1, ruleLabel, datasetTitle: displayTitle(ds), series, value: this.globeValueText(slot, pin) })
      }
    }
    return out
  }

  /**
   * Per-frame sibling drift correction, invoked from the playback rAF
   * loop (`startPlaybackLoop`'s `onTick`). The primary video is the
   * master clock: every frame we re-derive each sibling's target
   * position from the primary's real-world date and snap it back if it
   * has drifted past {@link SIBLING_DRIFT_THRESHOLD_S}.
   *
   * This replaces the old once-set-`playbackRate` + 5 s/1 s interval
   * scheme (terraviz#132). The rate is kept only as a smoothing aid so
   * we don't seek every frame — it's re-derived continuously here so it
   * tracks `video.duration` as HLS segments buffer and the reported
   * duration firms up (the original failure mode: an imprecise duration
   * froze a slightly-wrong rate whose error integrated into visible
   * drift — ~2.83 years per second of video on a four-globe climate
   * comparison). The threshold-gated seek is the closed loop that bounds it.
   */
  private correctSiblingDrift(): void {
    if (!this.primaryVideoSyncActive) return

    const pIdx = this.viewports.getPrimaryIndex()
    const pPanel = this.panelStates[pIdx]
    const primaryVideo = pPanel?.hlsService?.getVideo?.() ?? null
    // Only correct while the primary is actively playing; paused state
    // is handled by the `pause` event handler in attachPrimaryVideoSync.
    if (!primaryVideo || primaryVideo.paused || primaryVideo.readyState < 2) return
    if (!(primaryVideo.duration > 0)) return
    // AYNI: a catalogue range or a stream's .dsa frames (panelDateSync.ts).
    const primaryDates = this.panelDates(pIdx)
    if (!primaryDates) return

    const primaryDateMs = panelDateAt(primaryDates, primaryVideo.currentTime)
    const now = performance.now()

    for (let i = 0; i < this.panelStates.length; i++) {
      if (i === pIdx) continue
      const sibPanel = this.panelStates[i]
      const sibVideo = sibPanel?.hlsService?.getVideo?.() ?? null
      // See {@link SIBLING_MIN_READY_STATE}. A sibling below
      // HAVE_CURRENT_DATA is exactly the one that needs correcting: the
      // desync at a loop wrap is the whole video duration, far past the
      // hard-seek threshold, and the corrective write is what restores it.
      if (!sibVideo || sibVideo.readyState < SIBLING_MIN_READY_STATE) continue

      const sibDates = this.panelDates(i)
      if (!sibDates || !(sibVideo.duration > 0)) continue

      // AYNI (multi-globe plan): the globe's date under its time rule. A
      // held globe is not paced at all: it stays paused on its frame.
      const rule = this.panelTimeRules[i] ?? FOLLOW
      if (rule.kind === 'fixed') {
        if (!sibVideo.paused) sibVideo.pause()
        const { videoTime, position } = panelLocate(sibDates, rule.ms, 'frame')
        if (!sibVideo.seeking) this.alignSiblingTo(i, sibVideo, videoTime, sibDates)
        this.viewports.setOutOfRange(i, position !== 'inside')
        this.siblingOutOfRange[i] = position !== 'inside'
        continue
      }
      const dateMs = applyTimeRule(rule, primaryDateMs)

      // Re-derive target/rate/drift every frame so the rate tracks
      // `video.duration` as HLS firms it up. Small in-range drift is
      // eased out via the returned (gently trimmed) rate; `shouldSeek`
      // is true only for a large desync or to pin an out-of-range
      // sibling to its boundary frame (terraviz#229 — per-frame seeking
      // interrupted decode and flickered the sibling). Two catalogue
      // panels get exactly `computeSiblingSyncCorrection`.
      const { position, targetTime, rate, shouldSeek } = computePanelSyncCorrection({
        dateMs,
        primary: primaryDates,
        sibling: sibDates,
        sibCurrentTime: sibVideo.currentTime,
        primaryPlaybackRate: primaryVideo.playbackRate,
      })

      sibVideo.playbackRate = rate

      // A seek already in flight is left alone. This runs every frame,
      // and a lower hard-seek threshold means it fires during the
      // ~2s a sibling spends re-buffering after a scrub — precisely
      // when it is least able to absorb another one. Writing
      // `currentTime` again there restarts the fetch hls.js is already
      // running, so a stall could be extended indefinitely by the very
      // correction meant to end it. The seek in flight is heading close
      // to the right place; whatever it misses by, the next frame after
      // it lands will still see and correct.
      // AYNI: a stream is also given FRAMES_SEEK_COOLDOWN_MS between
      // corrective seeks — each one flushes a DASH decoder.
      const cooling = sibDates.kind === 'frames' && now - (this.siblingLastSeekAt[i] ?? -Infinity) < FRAMES_SEEK_COOLDOWN_MS
      if (shouldSeek && !sibVideo.seeking && !cooling) {
        sibVideo.currentTime = targetTime
        this.siblingLastSeekAt[i] = now
        this.uploadSiblingFrameOnSeek(i, sibVideo)
      }

      if (position === 'inside') {
        this.viewports.setOutOfRange(i, false)
        const wasOutOfRange = this.siblingOutOfRange[i] === true
        this.siblingOutOfRange[i] = false
        // Resume a sibling that was frozen out-of-range and has now
        // re-entered its window as the primary advanced. Only on the
        // transition — re-issuing play() every frame would churn
        // Promises (and log warnings) if autoplay is blocked.
        if (wasOutOfRange && sibVideo.paused) {
          sibVideo.play().catch(() => { /* autoplay blocked */ })
        }
      } else {
        // Primary date is outside this sibling's range — it's been
        // pinned to its boundary frame above; freeze + mark it.
        this.viewports.setOutOfRange(i, true)
        this.siblingOutOfRange[i] = true
        if (!sibVideo.paused) sibVideo.pause()
      }
    }
  }

  /**
   * Move a sibling to a target position — but only if it is not already
   * there.
   *
   * A seek is expensive in a way the old unconditional write assumed it
   * was not. Browser capture of a 4-globe session: four panels already
   * aligned at 0.5820 of their duration, pressing play seeked the three
   * siblings to 0.5822 — a fifth of one frame — and left all three at
   * `HAVE_METADATA` for five seconds, frozen on their previous frame
   * while the primary played on. Every play, pause and scrub was paying
   * that, because `seekSiblingsToDate` wrote `currentTime` whether or
   * not it changed anything.
   *
   * Within {@link SIBLING_SEEK_EPS_S} the panel is on the same frame it
   * would seek to, so the seek is skipped — but the texture is still
   * nudged, since being on the right frame and *showing* it are
   * different claims, and the second is the one that keeps failing.
   */
  private alignSiblingTo(slot: number, video: HTMLVideoElement, targetTime: number, dates?: PanelDates | null): void {
    // AYNI: on a stream, already on the target frame (clear of its edges,
    // where the decoder could resolve either way) counts as there: a
    // DASH seek within one frame buys nothing and stalls the panel.
    const onFrame = dates?.kind === 'frames' && isOnFrame(dates, video.currentTime, targetTime)
    if (onFrame || Math.abs(video.currentTime - targetTime) <= SIBLING_SEEK_EPS_S) {
      const tex = this.panelStates[slot]?.videoTexture
      if (tex) tex.needsUpdate = true
      return
    }
    video.currentTime = targetTime
    this.uploadSiblingFrameOnSeek(slot, video)
  }

  /**
   * Upload a sibling's frame once its seek has actually landed.
   *
   * Setting `needsUpdate` straight after writing `currentTime` schedules
   * a repaint that arrives before the seek completes, so it uploads the
   * frame the panel was *already* showing and clears the one-shot flag.
   * The video is then paused, `!paused` is false, and the repaint
   * heartbeat in `earthTileLayer` does not run — nothing re-uploads,
   * ever. The panel keeps its pre-seek frame for good while its clock
   * reads the right time, which is how a globe ends up two hours behind
   * the label with no way back.
   *
   * Measured seeks complete in ~2 ms against locally buffered media,
   * which is why this never showed up in instrumented tests; against
   * real HLS a sibling can hold `HAVE_METADATA` for ~2 s, and the
   * repaint wins every time.
   *
   * `seeked` only fires once the data is there, so the upload it arms
   * cannot land early. The texture is looked up when the event fires
   * rather than captured, so a dataset swap in between retargets it
   * instead of writing through a stale handle.
   */
  private uploadSiblingFrameOnSeek(slot: number, video: HTMLVideoElement): void {
    const tex = this.panelStates[slot]?.videoTexture
    // Still worth the immediate hint: a seek that needs no data never
    // fires `seeked`, and this is the only upload it will get.
    if (tex) tex.needsUpdate = true
    if (!video.seeking || this.siblingSeekUploads[slot]) return

    const armed = new AbortController()
    this.siblingSeekUploads[slot] = armed
    video.addEventListener('seeked', () => {
      this.siblingSeekUploads[slot] = null
      // Looked up now rather than captured, so a dataset swap between
      // arming and firing retargets this instead of writing through a
      // handle that no longer belongs to the panel.
      const current = this.panelStates[slot]?.videoTexture
      if (current) current.needsUpdate = true
    }, { once: true, signal: armed.signal })
  }

  /**
   * Drop any armed upload for a slot, or for every slot.
   *
   * Called wherever a panel's video goes away. `seeked` never fires on a
   * destroyed element, so without this the slot stays marked armed
   * forever and refuses to arm again — a Khipu can swap sibling datasets a
   * dozen times, so the fix would quietly stop working panel by panel.
   */
  private cancelSiblingSeekUpload(slot?: number): void {
    if (slot === undefined) {
      for (const armed of this.siblingSeekUploads) armed?.abort()
      this.siblingSeekUploads = []
      if (this.siblingRepairTimer !== null) {
        clearTimeout(this.siblingRepairTimer)
        this.siblingRepairTimer = null
      }
      return
    }
    this.siblingSeekUploads[slot]?.abort()
    this.siblingSeekUploads[slot] = null
  }

  /**
   * Read back whether every sibling actually shows the date the shared
   * time label claims, and mark the ones that don't.
   *
   * The multi-globe layout asserts one label over every panel, derived
   * from the primary alone. Siblings are commanded to that date by
   * `seekSiblingsToDate` and never read back, so a write that fails to
   * land leaves the label quietly speaking for a panel that is somewhere
   * else. `correctSiblingDrift` would notice, but it returns early while
   * the primary is paused — and paused is exactly when a viewer reads
   * the label and compares panels.
   *
   * Runs on settle rather than per frame: the playback settle watcher
   * fires once the transport is paused, not seeking, and still, which is
   * both the only moment the answer is stable and the only moment it
   * matters. Verification only — correcting here would fight the sync
   * controller for the playhead, and a panel that cannot reach the
   * labelled moment should say so rather than silently retry.
   */
  private verifySiblingTimes(afterRepair = false): void {
    if (this.siblingRepairTimer !== null) {
      clearTimeout(this.siblingRepairTimer)
      this.siblingRepairTimer = null
    }
    // A re-check that arrives after the transport moved again describes
    // nothing; the moving-clear path owns the notices from here.
    const primaryVideo = this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService?.getVideo?.() ?? null
    if (afterRepair && (!primaryVideo || !primaryVideo.paused || primaryVideo.seeking)) return

    let repairing = false
    const labelDate = this.assertedLabelDate
    // Nothing is being asserted (no dataset, or no temporal range), so
    // there is no claim for a panel to contradict.
    if (!labelDate) {
      this.clearSiblingTimeNotices()
      return
    }

    const pIdx = this.viewports.getPrimaryIndex()
    const snapMs = this.playback.displayInterval?.intervalMs
    // AYNI: a stream primary sends the others to its running date, not to
    // the stamp its label shows (panelDateSync.ts), so that date is what a
    // catalogue sibling is checked against, to a video frame's tolerance.
    const primaryDates = this.panelDates(pIdx)
    const primaryFrames = primaryDates?.kind === 'frames' && primaryVideo
    const rangeLabelDate = primaryFrames ? new Date(panelDateAt(primaryDates, primaryVideo.currentTime)) : labelDate
    const rangeSnapMs = primaryFrames ? undefined : snapMs

    for (let i = 0; i < this.panelStates.length; i++) {
      if (i === pIdx) continue
      const panel = this.panelStates[i]
      const video = panel?.hlsService?.getVideo?.() ?? null
      const renderer = this.viewports.getRendererAt(i)
      const ds = panel?.dataset

      // AYNI: a stream sibling is checked on its frames: the period of the
      // frame it shows must overlap the primary's labelled period.
      const sibDates = video && ds && !(ds.startTime && ds.endTime) ? this.panelDates(i) : null
      // AYNI (multi-globe plan): a globe on its own time rule is checked
      // against the date its rule gives, not the label's.
      const rule = this.panelTimeRules[i] ?? FOLLOW
      if (sibDates?.kind === 'frames') {
        if (!primaryDates || !primaryVideo) {
          this.setSiblingTimeNotice(i, null)
          continue
        }
        const verdict = verifyFramesSibling({
          primary: primaryDates,
          primaryTime: primaryVideo.currentTime,
          dateMs: rule.kind === 'follow' ? undefined : applyTimeRule(rule, panelDateAt(primaryDates, primaryVideo.currentTime)),
          sibling: sibDates,
          sibFrameTime: shownFrameTime(
            renderer instanceof MapRenderer ? renderer.getUploadedFrameTime() : null,
            video!.currentTime,
          ),
        })
        if (verdict.alignment !== 'off') {
          this.setSiblingTimeNotice(i, null)
          continue
        }
        const tex = this.panelStates[i]?.videoTexture
        if (!afterRepair && tex) {
          tex.needsUpdate = true
          repairing = true
          continue
        }
        this.setSiblingTimeNotice(i, formatFrameLabel(verdict.shownMs, sibDates.axis.precision, getLocale()))
        continue
      }

      // No video, no range, or a duration we cannot map through: there
      // is nothing to compare, which is not the same as a mismatch.
      if (!video || !ds?.startTime || !ds.endTime || !(video.duration > 0)) {
        this.setSiblingTimeNotice(i, null)
        continue
      }

      const verdict = verifySiblingTime({
        labelDate: rule.kind === 'follow' ? rangeLabelDate : new Date(applyTimeRule(rule, rangeLabelDate.getTime())),
        // What the globe is showing, not what its video element says —
        // see `shownFrameTime`. The renderer knows which frame actually
        // reached the texture; the element only knows its own clock.
        sibFrameTime: shownFrameTime(
          renderer instanceof MapRenderer ? renderer.getUploadedFrameTime() : null,
          video.currentTime,
        ),
        sibDuration: video.duration,
        sibStart: new Date(ds.startTime),
        sibEnd: new Date(ds.endTime),
        snapIntervalMs: rangeSnapMs,
      })

      // `uncovered` is left alone deliberately — the out-of-range
      // treatment already explains that panel, and saying it twice in
      // two different vocabularies is worse than saying it once.
      if (verdict.alignment !== 'off') {
        this.setSiblingTimeNotice(i, null)
        continue
      }

      // The panel is showing the wrong frame. Before saying so, ask for
      // a redraw — the overwhelmingly common cause is a texture left
      // behind a clock that is already correct, and one upload fixes
      // that whatever produced it. This is not the correction
      // `verifySiblingTime`'s contract rules out: that is about seeking,
      // which would fight the sync controller for the playhead. Nothing
      // here moves a playhead; it repaints what the video already holds.
      const tex = this.panelStates[i]?.videoTexture
      if (!afterRepair && tex) {
        tex.needsUpdate = true
        repairing = true
        continue
      }

      const showTime = ds.period
        ? isSubDailyPeriod(ds.period)
        : (this.playback.displayInterval?.showTime ?? false)
      this.setSiblingTimeNotice(i, formatDate(verdict.shownDate, showTime))
      logger.debug(
        `[App] Panel ${i} is ${Math.round(verdict.driftMs / 1000)}s of real time off the label`
        + (afterRepair ? ' and did not repaint' : ''),
      )
    }

    // Confirm the repair rather than assuming it. A notice is only
    // earned by a panel that stayed wrong after being asked to redraw,
    // which keeps the ribbon meaning "I could not fix this" instead of
    // flashing up for every transient staleness.
    if (repairing) {
      this.siblingRepairTimer = setTimeout(() => {
        this.siblingRepairTimer = null
        this.verifySiblingTimes(true)
      }, SIBLING_REPAIR_CONFIRM_MS)
    }
  }

  /** Show or hide one panel's time notice, tracking it for cheap clearing. */
  private setSiblingTimeNotice(slot: number, shownDate: string | null): void {
    this.viewports.setPanelTimeNotice(slot, shownDate)
    this.siblingTimeNoticed[slot] = shownDate !== null
  }

  /**
   * Drop every time notice.
   *
   * A notice describes one settled position, so it goes stale the
   * instant the transport moves again. The array scan short-circuits
   * when nothing is marked, which is the overwhelmingly common case.
   */
  /**
   * Drop stale time notices once the transport is moving again.
   *
   * "Moving" is playing *or* seeking. A scrub while paused leaves
   * `paused` true and only raises `seeking`, so testing `paused` alone
   * held the previous notice on screen for the whole drag — naming one
   * date while the label and the panels moved through others, which is
   * the opposite of what a notice about a settled frame should mean.
   *
   * Called from the playback loop, which ticks whether or not the
   * primary is playing, so it makes its own check. Both guards are
   * cheap and short-circuit before touching the DOM.
   */
  private clearSiblingTimeNoticesWhileMoving(): void {
    if (!this.siblingTimeNoticed.some(Boolean)) return
    const primary = this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService?.getVideo?.() ?? null
    if (!primary) return
    if (primary.paused && !primary.seeking) return
    this.clearSiblingTimeNotices()
  }

  private clearSiblingTimeNotices(): void {
    if (!this.siblingTimeNoticed.some(Boolean)) return
    for (let i = 0; i < this.siblingTimeNoticed.length; i++) {
      if (this.siblingTimeNoticed[i]) this.setSiblingTimeNotice(i, null)
    }
  }

  /**
   * Detach all sibling-sync listeners from the previous primary video,
   * disarm per-frame drift correction, and clear any lingering
   * out-of-range or time-notice state from siblings.
   */
  private detachPrimaryVideoSync(): void {
    this.primaryVideoSyncActive = false
    this.siblingOutOfRange = []
    this.siblingLastSeekAt = []
    // A time notice names a date for a specific panel showing a specific
    // dataset. Both can change out from under it here — a layout change,
    // a new primary — so it is cleared rather than left to be corrected
    // on the next settle.
    this.clearSiblingTimeNotices()
    this.siblingTimeNoticed = []
    this.cancelSiblingSeekUpload()
    // And the settle detector has to forget where it was, for the reason
    // its own docs give: it reports a position once, so a new primary
    // paused at the same `currentTime` — the normal case for
    // equal-duration videos held in lockstep — would be suppressed as
    // already-reported and never verified. The playhead is the same
    // number but a different panel's frame.
    this.playbackSettle.reset()
    const target = this.primaryVideoSyncTarget
    if (target) {
      for (const { event, handler } of this.primaryVideoSyncListeners) {
        target.removeEventListener(event, handler)
      }
    }
    this.primaryVideoSyncListeners = []
    this.primaryVideoSyncTarget = null
    // Reset sibling playback rates to 1.0 so videos don't stay at
    // the adjusted speed after sync is torn down.
    for (let i = 0; i < this.panelStates.length; i++) {
      this.viewports.setOutOfRange(i, false)
      const sibVideo = this.panelStates[i]?.hlsService?.getVideo?.() ?? null
      if (sibVideo) sibVideo.playbackRate = 1.0
    }
  }

  /**
   * Dispose the video texture and HLS service for a specific panel.
   * If `slot` is omitted, operates on the primary panel (common case
   * for single-viewport flows like goHome / loadDataset teardown).
   *
   * When tearing down the primary, also resets the shared playback
   * state; non-primary slots don't touch that state.
   */
  private cleanupPanelVideo(slot?: number): void {
    const targetSlot = slot ?? this.viewports.getPrimaryIndex()
    const panel = this.panelStates[targetSlot]
    if (!panel) return

    // Before anything else: this slot's video is about to be destroyed,
    // and an armed listener on it would never fire.
    this.cancelSiblingSeekUpload(targetSlot)

    const isPrimary = targetSlot === this.viewports.getPrimaryIndex()
    if (isPrimary) {
      this.detachPrimaryVideoSync()
      stopPlaybackLoop(this.playback)
    }

    if (panel.videoTexture) {
      panel.videoTexture.dispose()
      panel.videoTexture = null
    }
    if (panel.hlsService) {
      panel.hlsService.destroy()
      panel.hlsService = null
    }
    // The notice described a stream that no longer exists. `destroy()`
    // above already dropped the handler, so nothing can re-raise it.
    this.viewports.setPanelStreamNotice(targetSlot, false)

    if (isPrimary) {
      this.appState.isPlaying = false
      resetPlaybackState(this.playback)
    }
  }

  /**
   * Legacy alias for `cleanupPanelVideo(primarySlot)`. Kept so
   * existing call sites that mean "clean up the video that's
   * currently playing" stay readable.
   */
  private cleanupVideo(): void {
    this.cleanupPanelVideo()
  }

  /**
   * Tear down datasets in every panel — used by goHome and clearToEarth.
   * Clears dataset + hls + video texture per slot and resets shared
   * Delegates to the per-panel unload path so every slot gets the
   * same dataset/video teardown AND renderer reset (back to default
   * Earth materials) behavior.
   */
  private async unloadAllPanels(): Promise<void> {
    this.stopGeoMedia()
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      await this.unloadPanelDataset(slot)
    }
    this.infoDisplayOverride = null
    this.refreshPanelLegends()
    this.renderInfoPanel()
  }

  /**
   * Unload a single panel's dataset — video, texture, metadata.
   * Called per slot by `unloadAllPanels`; keeps the rest of the
   * panels intact.
   *
   * When the target slot is the primary, also tears down the shared
   * playback state. Non-primary slots leave the playback UI alone.
   */
  private async unloadPanelDataset(slot: number): Promise<void> {
    const panel = this.panelStates[slot]
    if (!panel) return
    const isPrimarySlot = slot === this.viewports.getPrimaryIndex()

    // Clean the video stream, texture, and (if primary) the shared
    // playback state. cleanupPanelVideo handles both.
    this.cleanupPanelVideo(slot)

    // Clear the dataset reference and reset the base earth on that
    // specific panel's renderer so it doesn't keep showing a stale
    // texture.
    panel.dataset = null
    panel.image = null
    this.syncSatelliteBody()
    const renderer = this.viewports.getRendererAt(slot)
    if (renderer instanceof MapRenderer) {
      // Drop the panel's dataset-credits phantom source so Tools
      // → Credits no longer surfaces a stale attribution.
      renderer.setDatasetCredits(null)
    }
    if (renderer) {
      renderer.removeCloudOverlay?.()
      renderer.removeNightLights?.()
      renderer.disableSunLighting?.()
      try {
        await renderer.loadDefaultEarthMaterials?.()
        const sun = getSunPosition(new Date())
        renderer.enableSunLighting?.(sun.lat, sun.lng)
      } catch (err) {
        logger.warn('[App] Earth material reload after unload failed:', err)
      }
    }

    // If we just cleared the currently-displayed slot in the info
    // panel, drop the override so renderInfoPanel picks a new target.
    if (this.infoDisplayOverride === slot) {
      this.infoDisplayOverride = null
    }

    // Reflect the clear in the primary UI if applicable.
    if (isPrimarySlot) {
      this.appState.currentDataset = null
    } else {
      // AYNI (phase 2): another globe left bare loses its own layers and streams.
      this.disposeOtherStack(slot)
    }

    this.refreshPanelLegends()
    this.renderInfoPanel()
  }

  // --- Event listeners ---

  /** Wire up all DOM event listeners: transport controls, keyboard shortcuts, scrubber, mute. */
  setupEventListeners(): void {
    // Back/forward cache restore (iOS Safari in particular). MapLibre
    // caches its canvas dimensions; after a bfcache restore the canvas
    // can be stuck at a stale size and the globe renders in a thin
    // strip. `persisted === true` only fires for bfcache restores, so
    // this is cheap on every other navigation.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.viewports.resizeAll()
    })

    // AYNI (owner, 2026-10-20): the playback speed bar, where the Browse
    // button was (Browse stays at the bottom right).
    this.speedControl = initPlaybackSpeedControl({
      state: () => {
        const ds = this.appState.currentDataset
        if (!ds) return null
        const video = dataService.isVideoDataset(ds) ? this.hlsService?.video ?? null : null
        return { speed: video ? this.playbackSpeed() : 1, moves: !!video, ceiling: this.rtSpeedCeiling, locked: this.refusedByMeeting() }
      },
      set: (rate) => this.setPlaybackSpeed(rate),
      announce: (msg) => this.announce(msg),
    })
    // AYNI: the anchor button under it, and the date at the bottom centre.
    initAnchorButton()
    initShareButton({
      currentDataset: () => this.appState.currentDataset ?? null,
      announce: (msg) => this.announce(msg),
    })
    // AYNI: auto-rotate under Help, with its speed slider (was Tools → More).
    this.autoRotate = initAutoRotateControl({
      primary: () => this.viewports.getPrimary(),
      // A playing Khipu and a room's lead drive the camera; the viewer's choice waits for them.
      suspended: () => this.roomFollowing || (activeKhipuPlayer()?.playing() ?? false),
      // AYNI (2026-10-19): the flat map has no far side to spin round; the choice waits for the globe.
      unavailable: () => this.flatMap,
      announce: (msg) => this.announce(msg),
      onChange: (on) => emit({ event_type: 'settings_changed', key: 'auto_rotate', value_class: on ? 'on' : 'off' }),
    })
    // AYNI (owner, 2026-10-19): Reset and 2D/3D under auto-rotate.
    this.globeView = initGlobeViewControl({
      primary: () => this.viewports.getPrimary(),
      flat: { isFlat: () => this.flatMap, setFlat: (on) => { void this.setFlatMap(on) } },
      suspended: () => this.roomFollowing || (activeKhipuPlayer()?.playing() ?? false),
      announce: (msg) => this.announce(msg),
      onChange: (what) => emit({ event_type: 'settings_changed', key: 'globe_view', value_class: what }),
    })
    initTimeLabelPosition()
    // AYNI (owner, 2026-10-11): a lesson panel open with Tools shares Tools' column.
    // Before the offset below, so its observer settles the column first.
    initLessonToolsColumn()
    // AYNI: the globe moves aside for the browse panel and the Tools
    // popover, to the middle of what they leave free.
    const mapGrid = document.getElementById('map-grid')
    if (mapGrid) {
      initGlobePanelOffset({
        grid: mapGrid,
        resizeMaps: () => {
          for (const renderer of this.viewports.getAll()) {
            // `redraw` paints now rather than on the next frame.
            renderer.getMap()?.resize().redraw()
          }
        },
        // Through the panel's own close button: hide and announce.
        closeBrowse: () => document.getElementById('browse-close')?.click(),
        closeTools: closeToolsMenu,
        // AYNI: Analyze is the third side panel (owner, 2026-10-06), the Studio another (2026-10-10).
        closeAnalyze: () => closeAnalyzeUI(),
        closeStudio: () => closeStudio(),
        closeSituation: () => closeSituationPanel(),
      })
    }
    // AYNI (multi-globe plan, phase 4): an output window shows the globes
    // and nothing that is pressed, and follows the window that opened it —
    // over a BroadcastChannel, or through the meeting it was opened from.
    const outputChannel = outputChannelFromSearch()
    if (outputChannel) {
      document.body.classList.add('output-window')
      this.outputLock = true
      this.applyRoomLock()
      this.announce(t('output.following'))
      const sphere = sphereFeedFromSearch()
      if (sphere) this.startSphereOutput(sphere)
      if (outputChannel !== 'room') {
        startRoomSync(outputChannel, this.roomSyncHost(), {
          connect: (code, opts) => connectRoom(code, { ...opts, createSocket: () => createOutputSocket('output', code) as unknown as WebSocket }),
        })
      }
    }
    // AYNI: a page opened with ?room=CODE is in a shared session — the
    // first device in the room leads, the rest follow its dataset,
    // playhead and (in AR) its sphere.
    const roomCode = roomCodeFromSearch()
    if (roomCode) {
      this.roomCodeNow = roomCode
      const seat = takeRoomToken(roomCode)
      this.roomSeatToken = seat.token
      if (seat.cleanHref) window.history.replaceState(window.history.state, '', seat.cleanHref)
      const room = startRoomSync(roomCode, this.roomSyncHost(), { token: seat.token })
      // A guest in an open room only watches; recomputed as the room or the account changes.
      let lastRoom = room.status()
      const guestLock = (): void => {
        const guest = lastRoom.connected && !lastRoom.meeting && accountState().status === 'signedOut'
        if (guest === this.roomGuestLock) return
        this.roomGuestLock = guest
        this.applyRoomLock()
      }
      // AYNI (owner, 2026-10-05): a class whose teacher's plan lends the
      // data tools — values, pins, the graph, Line and Box — for as long as
      // this browser is connected to it. The audience keeps Tools, Data tab only.
      let lent = false
      const applyLend = (status: RoomStatus): void => {
        // A dropped connection keeps what is lent until the room says otherwise on reconnecting
        // (owner, 2026-10-12: a drop took a student's pin tools away mid-pin).
        if (!status.connected) return
        // AYNI (multi-globe plan, owner 2026-10-10): a meeting may lend globes (an Education host lends four).
        setLentGlobes(status.connected && status.meeting ? status.grants?.globes ?? 0 : 0)
        const now = status.connected && status.meeting && status.grants?.data === true
        if (now === lent) return
        lent = now
        setLentDataTools(now)
        // AYNI: a class borrowing the tools does not see My data (own data).
        syncMyDataTab()
        document.body.classList.toggle('room-data-lent', now)
        if (now) showToolsTab('data')
        // The bar comes back while the transport stays hidden: place it again.
        updateMapControlsPosition()
      }
      // AYNI (owner, 2026-10-12): the way out — the presenter ended the meeting, this person left, or was
      // removed. The meeting's buttons come down, the room is let go, and one card says which.
      const meetingParts: (() => void)[] = []
      let over = false
      const leaveMeeting = (why: MeetingOver): void => {
        if (over) return
        over = true
        for (const teardown of meetingParts) teardown()
        room.stop()
        setLentDataTools(false)
        setLentGlobes(0)
        setLentPackages(null)
        this.roomFollowing = false
        setXrRoomFollower(false)
        setFollowerPictureCap(false)
        setMeetingRoom(null, null)
        setStreamMeeting(null, null)
        document.body.classList.remove('room-data-lent')
        this.roomCodeNow = null
        window.history.replaceState(window.history.state, '', addressWithoutRoom(window.location.href))
        showMeetingOver(document.getElementById('xr-dom-overlay') ?? document.body, why)
        // AYNI (Quest review A7, 2026-11-03): a headset can't see the card; the dock says it (the card waits for the way out).
        if (isVrActive()) notifyXr(t(why === 'ended' ? 'meeting.over.ended' : why === 'left' ? 'meeting.over.left' : 'activity.kicked'))
      }
      room.onStatus((status) => {
        if (status.ended || status.kicked) {
          // Not while the room is still telling its listeners.
          queueMicrotask(() => leaveMeeting(status.ended ? 'ended' : 'kicked'))
          return
        }
        lastRoom = status
        // AYNI — own data (decision O6, behind DATA_MEETING_SHARES): in a meeting, token requests name
        // the room, so a private dataset the presenter shows opens here too; the presenter's seat token
        // lets My data offer "Show to this meeting".
        setMeetingRoom(status.connected && status.meeting ? roomCode : null, status.connected && status.meeting && status.seat === 'presenter' ? seat.token : null)
        // AYNI (S-02): in a meeting the stream token is asked for this seat (its ticket), so it carries what the host lends.
        setStreamMeeting(status.connected && status.meeting ? roomCode : null, status.connected && status.meeting ? status.ticket : null)
        this.roomFollowing = status.connected && status.role === 'follower'
        setXrRoomFollower(this.roomFollowing)
        // AYNI (Quest review A11): a class shares one access point; its followers get pictures at most 2K wide.
        setFollowerPictureCap(this.roomFollowing)
        this.lastRoomStatus = status
        this.syncLentPackages()
        guestLock()
        applyLend(status)
        this.showRoomDrivers(status)
        // AYNI (Situation room, CR-D14): own layers between the presenter and everyone in the meeting.
        this.syncMeetingLayers(status)
      })
      onAccountChange(guestLock)
      const overlay = document.getElementById('xr-dom-overlay') ?? document.body
      // A tap on the meeting line sends the invitations again.
      meetingParts.push(mountRoomChip(overlay, room.onStatus, (chip, status) =>
        openMeetingSharePanel(roomCode, status.seat, chip),
      ))
      // The presenter's camera and microphone: Go live for the presenter,
      // the floating window for everyone else.
      // AYNI (stage plan): with the stage, the person invited answers through the room.
      meetingParts.push(mountMeetingMedia(overlay, room.onStatus, { code: roomCode, token: seat.token, send: (m) => room.send(m) }))
      // Doing things together: explore, questions on the globe, choices.
      const activityGlobe = activityGlobeFor(() => this.viewports.getPrimary()?.getMap() ?? null)
      meetingParts.push(mountMeetingActivities(overlay, room, activityGlobe, {
        code: roomCode,
        // AYNI (owner, 2026-10-12): Leave, for everyone but the presenter (who ends the meeting instead).
        onLeave: () => leaveMeeting('left'),
        // AYNI (multi-globe plan, phase 3): which globes the presenter can hand out.
        globes: () => ({ count: this.viewports.getPanelCount(), primary: this.viewports.getPrimaryIndex() }),
        // A recap: the presenter's page plays the meeting as a Khipu, and the room follows it.
        playKhipu: (k) => this.openKhipu(k),
        canKeep: () => accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null,
        exchangeOnly: () => currentEntitlements().khipuRetention === null,
        titleOf: (id) => dataService.getDatasetById(id)?.title ?? null,
        // AYNI (meeting games plan): while a game is on, a raised hand by the player's game name.
        gameNameOf: gameNameOfConn,
        // AYNI (stage plan, ST3): the host whose plan has no stage sees it locked; the lock opens the plans at their
        // segment. A host who has it, in a meeting made without it (before the upgrade), is not sent to buy it again.
        onStageUpgrade: currentEntitlements().stage ? undefined : (trigger) => openPlans(trigger, currentEntitlements().plan?.segment),
      }))
      // AYNI (meeting games plan): games, in a meeting whose host's plan lends them (Education).
      meetingParts.push(mountMeetingGames(overlay, room, activityGlobe, {
        openKhipu: () => {
          const player = activeKhipuPlayer()
          return player?.isOpen() ? player.khipu() : null
        },
        showKhipuStep: (i) => activeKhipuPlayer()?.goTo(i),
        setKhipuGame: (on) => activeKhipuPlayer()?.setGameMode(on),
        dataProbe: () => {
          const primary = this.viewports.getPrimary()
          const dataset = this.panelStates[this.viewports.getPrimaryIndex()]?.dataset
          // A dataset with values answers the probe (no data is still an answer); a picture does not.
          if (!primary || !dataset || primary.probeValueAt(0, 0) === null) return null
          return {
            title: displayTitle(dataset),
            probe: (lat, lon) => {
              const r = primary.probeValueAt(lat, lon)
              return r && !r.noData ? r.value : null
            },
          }
        },
        flyTo: (p) => activityGlobe.flyTo(p),
        datasetId: () => this.panelStates[this.viewports.getPrimaryIndex()]?.dataset?.id ?? null,
        titleOf: (id) => dataService.getDatasetById(id)?.title ?? null,
        canKeep: () => accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null,
        exchangeOnly: () => currentEntitlements().khipuRetention === null,
      }, { code: roomCode }))
    }

    // Browse panel opens via the Tools menu's Browse button (see
    // openBrowsePanel). No standalone peek-out toggle tab.

    // Transport controls — delegate to playback module
    document.getElementById('rewind-btn')?.addEventListener('click', () => {
      rewind(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('step-back-btn')?.addEventListener('click', () => {
      stepFrame(-1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('play-btn')?.addEventListener('click', () => {
      const wasPaused = this.hlsService?.paused ?? true
      togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
      this.emitPlaybackAction(wasPaused ? 'play' : 'pause')
    })
    document.getElementById('step-fwd-btn')?.addEventListener('click', () => {
      stepFrame(1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('ff-btn')?.addEventListener('click', () => {
      fastForward(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('cc-btn')?.addEventListener('click', () =>
      toggleCaptions(this.playback))

    // Mute/unmute toggle
    const muteBtn = document.getElementById('mute-btn')
    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        const video = this.hlsService?.video
        if (!video) return
        video.muted = !video.muted
        muteBtn.textContent = video.muted ? '\u{1F507}\uFE0E' : '\u{1F50A}\uFE0E'
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute audio' : 'Mute audio')
        muteBtn.style.color = video.muted ? '#aaa' : 'var(--color-accent)'
        muteBtn.style.borderColor = video.muted ? '#555' : 'var(--color-accent)'
      })
    }

    // Auto-rotate lives inside the Tools menu now — see toolsMenuUI.ts.

    // Scrubber
    const scrubber = document.getElementById('scrubber') as HTMLInputElement
    if (scrubber) {
      scrubber.addEventListener('input', () => {
        onScrub(parseInt(scrubber.value, 10), this.hlsService, this.playback, this.appState)
      })
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      // AYNI: playback is the presenter's in a meeting.
      if (this.refusedByMeeting()) return
      const browseOverlay = document.getElementById('browse-overlay')
      if (browseOverlay && !browseOverlay.classList.contains('hidden') && browseOverlay.contains(e.target as Node)) return
      // AYNI (2026-10-20): Space on a focused speed step (or the speed chip) presses it, not Play.
      if (e.code === 'Space' && e.target instanceof Element && e.target.closest('#playback-speed, #speed-badge')) return

      if (e.code === 'Space') {
        e.preventDefault()
        togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m), this.playback)
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault()
        stepFrame(-1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'ArrowRight') {
        e.preventDefault()
        stepFrame(1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'Home') {
        e.preventDefault()
        rewind(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'End') {
        e.preventDefault()
        fastForward(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      }
    })
  }

  // --- Navigation ---

  /** Load a dataset selected via the browse panel, updating URL and shifting focus to playback controls. */
  /**
   * Owner, 2026-10-24: a satellite named on a Browse card is shown on the
   * globe: the layer on, the satellite selected (its card beside it). One
   * the mirror does not carry is only named, with why.
   */
  private showSatellite(norad: number, name: string): void {
    const sats = this.satellites
    const note = platformNote(sats, norad)
    if (note) {
      this.announce(`${name}: ${note}`)
      return
    }
    if (!sats.on) sats.setOn(true)
    if (sats.selectNorad(norad)) return
    // The objects are still loading: select it once they are in.
    const off = sats.onChange(() => {
      if (sats.status === 'ready' && sats.selectNorad(norad)) off()
      else if (sats.status === 'error' || !sats.on) off()
    })
  }

  private async selectDatasetFromBrowse(id: string): Promise<void> {
    // AYNI (multi-globe plan): a globe's chip opened Browse — the dataset goes there.
    if (this.browseTarget !== null && this.browseTarget !== this.viewports.getPrimaryIndex()) {
      const target = this.browseTarget
      this.setBrowseTarget(null)
      this.dismissBrowseAfterLoad()
      if (await this.loadDatasetInto(target, id, 'browse')) {
        const ds = this.panelStates[target]?.dataset
        if (ds) this.announce(t('viewport.loadInto.done', { n: target + 1, title: displayTitle(ds) }))
      }
      return
    }
    const gen = ++this.loadGeneration
    this.dismissBrowseAfterLoad()
    this.chatClose()
    this.announce('Loading dataset\u2026')
    this.showLoadingScreen('Loading dataset\u2026', 20)
    // `writeDatasetUrl` carries the existing query across, which
    // preserves `?catalog=true` so the catalog↔sphere tab control
    // (Phase 1 §3.2) can offer a "back to catalog" affordance. The
    // body class stays set so CSS continues to recognise the
    // catalog-mode surface; the regular load flow swaps the visible
    // browse panel for the globe via `dismissBrowseAfterLoad()`.
    // Read `getCatalogMode()` before the write so the URL state we
    // observe and the tab state we update agree by construction.
    const inCatalogMode = getCatalogMode()
    this.writeDatasetUrl(id, 'push')
    // The globe is now the active surface — drop the
    // `catalog-empty` flag so CSS reveals `#map-grid`. The
    // `catalog-mode` body class stays (sticky session marker).
    // Flip the tab control to Sphere since the globe is what the
    // visitor is now looking at.
    document.body.classList.remove('catalog-empty')
    if (inCatalogMode) setActiveCatalogTab('sphere')
    await this.loadDataset(id, 'orbit')
    if (gen !== this.loadGeneration) return // a newer load superseded this one
    this.setLoading(false)
    this.chatShowTrigger()
    const dataset = this.appState.currentDataset
    if (dataset) {
      this.announce(`Loaded dataset: ${dataset.title}`)
      this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
      setHelpActiveDataset(dataset.id)
    }
    const playBtn = document.getElementById('play-btn')
    const infoHeader = document.getElementById('info-header')
    if (playBtn && !playBtn.closest('.hidden')) {
      playBtn.focus()
    } else if (infoHeader) {
      infoHeader.focus()
    }
  }

  /** Re-show the loading screen with a fade-in if it was hidden, cancelling any pending hide timer. */
  private showLoadingScreen(message = 'Loading dataset\u2026', progress = 0): void {
    if (this.loadingHideTimer !== null) {
      clearTimeout(this.loadingHideTimer)
      this.loadingHideTimer = null
    }
    const screen = document.getElementById('loading-screen')
    if (!screen) return
    screen.classList.remove('fade-out')

    const wasHidden = screen.style.display === 'none'
    screen.style.display = 'flex'
    if (wasHidden) {
      screen.style.opacity = '0'
      void screen.offsetHeight
      screen.style.transition = 'opacity 0.3s ease'
      screen.style.opacity = '1'
      screen.addEventListener('transitionend', () => {
        screen.style.transition = ''
        screen.style.opacity = ''
      }, { once: true })
    } else {
      screen.style.opacity = ''
    }
    this.setLoadingStatus(message, progress)
  }

  /** AYNI: show the playback speed bar (a dataset is on the globe). */
  private showSpeedBar(): void {
    document.getElementById('playback-speed')?.classList.remove('hidden')
    this.speedControl?.sync()
  }

  /** AYNI: hide the playback speed bar (back to the bare Earth). */
  private hideSpeedBar(): void {
    document.getElementById('playback-speed')?.classList.add('hidden')
    this.speedControl?.sync()
  }

  /**
   * AYNI (2026-10-20): the speed the viewer chose for the main globe's
   * dataset (1: normal). While live layers hold the dataset slower, its
   * video plays at their pace, but the choice is still this.
   */
  private playbackSpeed(): number {
    const video = this.hlsService?.video
    if (!video) return 1
    if (this.rtOverlays.length && this.rtBaseRateSet !== null && Math.abs(video.playbackRate - this.rtBaseRateSet) < 1e-3) return this.rtBaseUserRate
    return video.playbackRate
  }

  /**
   * AYNI (2026-10-20): play the main globe's dataset at this speed — the
   * speed bar, the headset dock, a Khipu step, the chatbot. The live
   * layers' pacer takes it as the base's own speed and holds it to what
   * they can keep up with; the other globes follow the main one's pace.
   */
  private setPlaybackSpeed(rate: number): void {
    if (!this.hlsService || !(rate > 0)) return
    const r = Math.max(MIN_PLAYBACK_RATE, Math.min(MAX_PLAYBACK_RATE, rate))
    const changed = Math.abs(this.playbackSpeed() - r) > 1e-3
    this.hlsService.playbackRate = r
    if (this.rtOverlays.length) {
      this.rtBaseUserRate = r
      this.rtBaseRateSet = null
      this.tickRtOverlay()
    }
    // A Khipu says its speed on every step: only a change is an action.
    if (changed) this.emitPlaybackAction('rate')
    this.speedControl?.sync()
  }

  /**
   * Navigate back to the default Earth view: tear down every panel's dataset, reload Earth materials, and re-show the browse panel.
   * AYNI: `browse: false` stays on the Earth without opening Browse (the dataset's ✕, owner 2026-10-12).
   */
  private async goHome(opts: { browse?: boolean } = {}): Promise<void> {
    // AYNI: nor may the audience clear it.
    if (this.refusedByMeeting()) return
    // AYNI: home ends a playing Khipu (a lesson stays: the teacher may go home between steps).
    activeKhipuPlayer()?.close()
    // Emit layer_unloaded for every panel that currently has a
    // dataset so dwell_ms can be accounted for. Runs before
    // unloadAllPanels() so panelStates[*].dataset is still populated.
    for (let i = 0; i < this.panelStates.length; i++) {
      this.emitLayerUnloadedForSlot(i, 'home')
    }
    await this.unloadAllPanels()
    this.clearMapLayers()
    this.appState.currentDataset = null
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    document.getElementById('info-panel')?.classList.add('hidden')
    this.hideSpeedBar()
    // Reset overlays a Khipu or the viewer may have turned on. In multi-view
    // mode we fan out across every panel so siblings don't keep
    // stale labels/borders after home.
    for (const r of this.viewports.getAll()) {
      r.toggleLabels?.(false)
      r.toggleBoundaries?.(false)
    }
    // AYNI: and VR's, which reads the shared flag.
    setBordersVisible(false)
    syncToolsMenuState({ labels: false, borders: false, terrain: false, autoRotate: false })
    this.clearDatasetUrl('push')

    this.showLoadingScreen('Loading Earth\u2026', 20)
    if (this.renderer) {
      const cloudUrl = CLOUD_TEXTURE_URL
      let earthFraction = 0
      let cloudFraction = 0
      const updateProgress = () => {
        const combined = earthFraction * EARTH_TEXTURE_WEIGHT + cloudFraction * CLOUD_TEXTURE_WEIGHT
        this.setLoadingStatus('Loading Earth\u2026', LOADING_BASE_PROGRESS + Math.round(combined * LOADING_TEXTURE_RANGE))
      }
      await Promise.all([
        this.renderer.loadDefaultEarthMaterials((f: number) => { earthFraction = f; updateProgress() }),
        this.renderer.loadCloudOverlay(cloudUrl, (f: number) => { cloudFraction = f; updateProgress() })
      ])
      const sun = getSunPosition(new Date())
      this.renderer.enableSunLighting(sun.lat, sun.lng)
    }
    this.setLoading(false)
    this.chatHideTrigger()
    this.chatClose()
    if (opts.browse !== false) showBrowseUI(this.appState.datasets, this.browseCallbacks())
    this.renderer?.setCanvasDescription('Interactive 3D globe showing Earth')
    setHelpActiveDataset(null)
  }

  /**
   * Synchronously release video/HLS resources across all panels
   * without the async Earth-material reload that `unloadAllPanels`
   * performs. Used by `dispose()` where the renderers are about to
   * be torn down and awaiting async work is neither necessary nor
   * safe.
   */
  private teardownAllPanelResources(): void {
    this.stopGeoMedia()
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)
    for (let i = 0; i < this.panelStates.length; i++) {
      const panel = this.panelStates[i]
      if (panel.videoTexture) { panel.videoTexture.dispose(); panel.videoTexture = null }
      if (panel.hlsService) { panel.hlsService.destroy(); panel.hlsService = null }
      panel.dataset = null
      panel.image = null
      this.syncSatelliteBody()
      const renderer = this.viewports.getRendererAt(i)
      if (renderer instanceof MapRenderer) {
        renderer.setDatasetCredits(null)
      }
    }
  }

  /**
   * AYNI: the Data tab's pins and graph (docs/AYNI_TOOLS_DATA_PLAN.md,
   * Phase 3). Pins read through the primary panel's own probe, the call
   * the hover readout makes, so a pin and the cursor never disagree about
   * the same place. A 200 ms tick re-checks what the globe can be read for
   * (a few property reads) and, only while pins exist, reads them when the
   * frame has changed; a layout change that replaces the primary panel
   * moves the markers and pin mode to the new one.
   */
  private initDataPins(): void {
    const host = document.getElementById('tools-data-pins')
    if (!host) return
    const store = new PinStore(() => maxPins())
    this.dataPinStore = store
    const primary = () => this.viewports.getPrimary()
    const readability = (): PinReadability => {
      if (!this.appState.currentDataset) return 'none'
      const kind = primary()?.probeKind() ?? 'picture'
      // AYNI: values are part of the plans with the data tools (planAccess.ts).
      return kind !== 'picture' && !dataToolsAllowed() ? 'plan' : kind
    }
    // A stream's dates are in its .dsa timeline (the one the track draws);
    // a catalogue row's are its start and end.
    const catalogueDated = (): boolean => {
      const ds = this.appState.currentDataset
      return !!(ds?.startTime && ds.endTime && Date.parse(ds.endTime) > Date.parse(ds.startTime))
    }
    const dated = (): boolean => !!this.timelineSnapshot() || catalogueDated()
    /** AYNI: the dataset's video, when it is one (the pins' frame clock). */
    const pinVideo = (): HTMLVideoElement | null => {
      const ds = this.appState.currentDataset
      return ds && dataService.isVideoDataset(ds) ? this.hlsService?.video ?? null : null
    }
    /**
     * The frame on the globe. AYNI: `mediaTime` is the frame a video
     * callback says is shown (`measure/frameSync.ts`), which the pixels
     * read in that callback belong to; without it, `currentTime`.
     */
    const frame = (mediaTime?: number): PinFrame | null => {
      const ds = this.appState.currentDataset
      const shownId = primary()?.currentFrameId() ?? null
      if (!ds || shownId === null) return null
      const id = mediaTime === undefined || shownId === 'static' ? shownId : `shown:${mediaTime}`
      const still: PinFrame = { id, datasetKey: ds.id, timeMs: null, startMs: null, endMs: null }
      const video = pinVideo()
      const duration = this.hlsService?.duration ?? video?.duration ?? 0
      if (!video || id === 'static' || !(duration > 0)) return still
      const videoTime = mediaTime ?? video.currentTime
      const timeline = this.timelineSnapshot()
      if (timeline) {
        // To the last frame's stamp (the track's end is exclusive), one slot per frame.
        const last = timeline.cadenceMs > 0 && timeline.frameCount > 1 ? lastFrameMsOf(timeline) : timeline.endMs
        // AYNI: every frame its own slot, so a lap never overwrites one frame with its neighbour.
        const slots = timeline.frameCount > 1 ? Math.min(MAX_SERIES_SLOTS, timeline.frameCount) : undefined
        // A shown frame's media time is its start: half a frame on lands inside it, never on the cut.
        const dsa = mediaTime === undefined ? null : this.dsaTimelines.get(ds.timelineLink!)
        const atMs = dsa ? dateAtVideoTimeMs(dsa, videoTime + 0.5 / dsa.videoFrameRate) : timeline.currentMs
        return { ...still, timeMs: Math.min(atMs, last), startMs: timeline.startMs, endMs: last, slots }
      }
      if (catalogueDated()) {
        const start = Date.parse(ds.startTime!)
        const end = Date.parse(ds.endTime!)
        const timeMs = videoTimeToDate(videoTime, duration, new Date(start), new Date(end)).getTime()
        return { ...still, timeMs, startMs: start, endMs: end }
      }
      return { ...still, timeMs: videoTime * 1000, startMs: 0, endMs: duration * 1000 }
    }
    // AYNI: a time on the pin graph's axis (spread evenly from the first
    // stamp to the last) as the stamp of the frame nearest it. On a monthly
    // stream an even spread sits a day or two off the calendar, which can
    // be the month before; the nearest stamp is the frame that slot holds.
    const graphFrameMs = (ms: number): number => {
      const axis = this.timelineSnapshot()?.axis
      return axis ? frameTimeMs(axis, nearestFrameAtTimeMs(axis, ms)) : ms
    }
    const seek = (timeMs: number): void => {
      const ds = this.appState.currentDataset
      const video = ds && dataService.isVideoDataset(ds) ? this.hlsService?.video : null
      if (!video || !Number.isFinite(timeMs)) return
      if (this.timelineSnapshot()) {
        this.seekTimelineDate(graphFrameMs(timeMs))
        return
      }
      if (catalogueDated()) {
        seekToDate(new Date(timeMs).toISOString(), this.hlsService, this.appState, this.playback)
        return
      }
      video.currentTime = Math.max(0, timeMs / 1000)
      if (cancelLoopHold(this.playback) && this.appState.isPlaying) void video.play().catch(() => {})
    }
    this.pinClock = { frame, dated, seek }
    // AYNI: the Accuracy section names the frame on screen (its run and source files) when the release records them.
    setAccuracyFrameClock(() => (dated() ? frame()?.timeMs ?? null : null))
    const rawUnitsOf = (): string | undefined =>
      (store.list().find((p) => (p.reading as ProbeReading | null)?.units)?.reading as ProbeReading | undefined)?.units

    let picking = false
    // AYNI (multi-globe plan): the pins are drawn on every globe, each with that globe's reading.
    let markedOn = new Set<MapRenderer>()
    let markedSig = ''
    const onPick = (lat: number, lon: number) => {
      const pin = store.add(lat, lon)
      if (pin) this.announce(t('tools.data.pins.added', { where: `${lat.toFixed(1)}, ${lon.toFixed(1)}` }))
    }
    // AYNI (owner, 2026-10-09): while the globe is paused, the pointer on
    // the pin graph sets the values the pins show: each pin's at the time
    // under it, as the graph holds it, until it leaves the graph or
    // playback starts again.
    let scrub: PinScrub | null = null
    const scrubValue = (pin: Pin): string | null => {
      if (!scrub || this.appState.isPlaying) return null
      const at = scrub.pins.find((p) => p.id === pin.id)
      if (!at) return null
      if (!at.sampled) return t('tools.data.graph.notRead')
      if (at.value === null) return t('tools.data.pins.nothing')
      // The graph holds the values shown (a change, while one is), in the units the pin's reading is shown in.
      const shown = pin.reading ? (viewPin(pin).reading as ProbeReading) : null
      return formatShownReading({ ...(shown ?? { units: unitsOf() }), value: at.value, noData: false } as ProbeReading)
    }
    // AYNI (owner, 2026-10-09): the value beside each pin on the globe, as
    // the Data tab's row and the headset print it; none while the globe
    // cannot be read for values (a picture, or a plan without data tools).
    const markerValue = (pin: Pin): string => {
      const r = readability()
      if (r !== 'release' && r !== 'encoded') return ''
      const scrubbed = scrubValue(pin)
      if (scrubbed !== null) return scrubbed
      if (!pin.reading) return ''
      return formatShownReading(viewPin(pin).reading as ProbeReading)
    }
    const syncMarkers = () => {
      const p = primary()
      const pins = store.list()
      const values = pins.map(markerValue)
      const scrubbing = !!scrub && !this.appState.isPlaying
      // AYNI (multi-globe plan): every globe shows the same pins; the
      // primary's carry its readings (or the scrubbed ones), the others'
      // carry what that globe reads at the same place.
      const all = this.viewports.getAll()
      const globeValues = all.map((r, slot) => (r === p ? values : pins.map((pin) => this.globeValueText(slot, pin))))
      // AYNI (owner, 2026-10-18): the track each following pin left, up to the frame on the globe.
      const nowMs = store.trackShown ? frame()?.timeMs ?? null : null
      const tracks = store.trackShown
        ? pins.filter((pin) => pin.follow).map((pin) => ({ id: pin.id, color: pin.color, segments: trackSegments(pin.series, nowMs) }))
        : []
      const trackSig = tracks.map((tr) => `${tr.id}:${tr.segments.map((s) => `${s.length}:${s[s.length - 1].lat}:${s[s.length - 1].lon}`).join(';')}`).join(',')
      const sig = `${scrubbing}|${all.length}|${pins.map((pin, i) => `${pin.id}:${pin.lat}:${pin.lon}:${pin.name ?? ''}:${values[i]}`).join(',')}|${globeValues.map((v) => v.join(';')).join('/')}|${trackSig}`
      const sameSet = all.length === markedOn.size && all.every((r) => markedOn.has(r))
      if (sameSet && sig === markedSig) return
      for (const r of markedOn) {
        if (all.includes(r)) continue
        r.setPins([], () => {})
        r.setPinTracks([])
        r.disarmPinPick()
      }
      if (picking) for (const r of all) if (!markedOn.has(r)) r.armPinPick(onPick)
      markedOn = new Set(all)
      markedSig = sig
      all.forEach((r, slot) => r.setPins(
        pins.map((pin, i) => ({ id: pin.id, lat: pin.lat, lon: pin.lon, color: pin.color, label: pinName(pin, i), named: !!pin.name, value: globeValues[slot][i], scrubbed: r === p && scrubbing && values[i] !== '' })),
        (id, lat, lon) => store.move(id, lat, lon),
      ))
      for (const r of all) r.setPinTracks(tracks)
    }
    store.subscribe(syncMarkers)

    // AYNI (owner, 2026-10-18): the extremes looked for everywhere, on land
    // only or at sea only (`measure/surfaceMask.ts`): a mask per dataset grid,
    // made once from our land and sea tiles. Until it is ready the following
    // pins wait, and a press of Mark highest & lowest places them when it is.
    let surfaceKey: string | null = null
    let surfaceFellBack = false
    let pendingMark = false
    let surfaceTiles: boolean | null = null
    // The choice is offered once the tiles are there (the VPS builds them).
    void fetch('/api/map/city/world/water/0/0/0.png').then((r) => { surfaceTiles = r.ok }, () => { surfaceTiles = false }).finally(() => this.dataPinsUi?.refresh())
    const surfaceMasks = new SurfaceMasks(httpWaterTiles, () => {
      store.refresh()
      const state = surfaceKey ? surfaceMasks.peek(surfaceKey) : null
      if (store.surface !== 'all' && state === 'none') {
        // No map here: look everywhere, and say so.
        surfaceFellBack = true
        store.setSurface('all')
        this.announce(t('tools.data.pins.surface.none'))
      }
      if (pendingMark && state !== 'loading') {
        pendingMark = false
        const placed = markExtremes()
        this.announce(t(placed ? 'tools.data.pins.extremesPlaced' : 'tools.data.pins.extremesNone'))
      }
      this.dataPinsUi?.refresh()
    })
    /** The texels the chosen surface looks at on a frame, or why there are none yet. */
    const surfaceAllowed = (width: number, height: number, grid: () => MaskGrid | null): Uint8Array | null | 'loading' | 'none' => {
      const ds = this.appState.currentDataset
      if (!ds || store.surface === 'all') return null
      surfaceKey = `${ds.id}|${width}x${height}`
      return surfaceMasks.allowed(surfaceKey, grid, store.surface)
    }
    // Highest and lowest on screen: the Data tab's button, and the AYNI chatbot's pins.extremes.
    // AYNI (owner, 2026-10-18): `near` is where the following pins stand, so a tie goes to the one nearest.
    const locateExtremes: ExtremesLocator = (near) => {
      const p = primary()
      if (!p) return null
      let hi: { lat: number; lon: number } | null = null
      let lo: { lat: number; lon: number } | null = null
      const analysisAllowed = (snapshot: { width: number; height: number }, options: DatasetOverlayOptions) =>
        surfaceAllowed(snapshot.width, snapshot.height, () => gridOf(snapshot.width, snapshot.height, { x0: 0, y0: 0, x1: snapshot.width, y1: snapshot.height },
          (x, y) => texelUvToLatLon({ u: (x + 0.5) / snapshot.width, v: (y + 0.5) / snapshot.height }, options)))
      const release = p.releaseFrame()
      if (changeShown()) {
        // AYNI (owner, 2026-10-20): while the globe shows a change from a
        // baseline, the highest and lowest change, not the values under it
        // (`measure/changeExtremes.ts`). Compare finds none.
        const frameData = p.analysisFrame()
        if (!frameData) return null
        const allowed = analysisAllowed(frameData.snapshot, frameData.options)
        if (allowed === 'loading' || allowed === 'none') return null
        const found = findChangeExtremes(frameData, changeViewState(), near, allowed)
        hi = found?.max ?? null
        lo = found?.min ?? null
      } else if (release?.options.releaseEncoding) {
        const { snapshot, options } = release
        const enc = options.releaseEncoding!
        const allowed = surfaceAllowed(snapshot.width, snapshot.height, () => releaseGrid(snapshot.width, snapshot.height, enc, options))
        if (allowed === 'loading' || allowed === 'none') return null
        const found = findReleaseExtremes(snapshot, enc, options, near, allowed)
        hi = found?.max ?? null
        lo = found?.min ?? null
      } else {
        const frameData = p.analysisFrame()
        if (frameData) {
          const { snapshot, options } = frameData
          const allowed = analysisAllowed(snapshot, options)
          if (allowed === 'loading' || allowed === 'none') return null
          hi = findExtremum(snapshot, frameData.scale, 'max', options, undefined, allowed)
          lo = findExtremum(snapshot, frameData.scale, 'min', options, undefined, allowed)
        }
      }
      return hi && lo ? { max: { lat: hi.lat, lon: hi.lon }, min: { lat: lo.lat, lon: lo.lon } } : null
    }
    /** Where the chosen surface stands: everywhere, its map being made, made, or none to be had. */
    const surfaceStatus = (): 'all' | 'loading' | 'ready' | 'none' | 'fallback' => {
      if (store.surface === 'all') return surfaceFellBack ? 'fallback' : 'all'
      // The first ask reads the frame once, which starts the map.
      if (!surfaceKey) locateExtremes({})
      return surfaceKey ? surfaceMasks.peek(surfaceKey) ?? 'loading' : 'none'
    }
    const setSurface = (mode: SurfaceMode): void => {
      surfaceFellBack = false
      if (!store.setSurface(mode)) return
      locateBudget.reset()
      surfaceKey = null
      if (mode !== 'all') surfaceStatus()
      this.dataPinsUi?.refresh()
    }
    // AYNI (owner, 2026-10-18): the pins follow the data as it plays; the
    // whole-frame read runs only while it costs under 10 % of the clock
    // (`followExtremes.ts`), so playback waits on it a tenth of the time at most.
    // Paused (or stepped, or scrubbed) there is no playback to hold up: the
    // pins land on the frame shown at once.
    const locateBudget = createLocateBudget()
    const locateFollowing: ExtremesLocator = (near) => (this.appState.isPlaying
      ? locateBudget.run(() => locateExtremes(near))
      : locateExtremes(near))
    // Classes have no highest: on a classified dataset the pins stay where they were marked.
    const canFollow = (): boolean => this.appState.currentDataset?.releaseEncoding?.kind !== 'luma8-classified'
    const markExtremes = (): number => {
      const found = locateExtremes({})
      if (!found?.max || !found.min) {
        // The land and sea map is on its way: the pins land when it comes.
        if (store.surface !== 'all' && surfaceKey && surfaceMasks.peek(surfaceKey) === 'loading') pendingMark = true
        return 0
      }
      locateBudget.reset()
      return store.markExtremes([{ ...found.max, tag: 'max' }, { ...found.min, tag: 'min' }], canFollow())
    }
    this.dataPinsForChat = { readability, markExtremes }

    // AYNI: in the change's units (or %) while the globe shows a change (changeView.ts).
    const unitsOf = (): string | undefined => viewUnits(rawUnitsOf())
    const formatAxis = (value: number): string => {
      if (changeShown()) return signed(value, formatProbeReading({ value, units: unitsOf(), noData: false }))
      const classes = this.appState.currentDataset?.releaseEncoding?.kind === 'luma8-classified'
        ? this.appState.currentDataset.releaseEncoding.classes
        : []
      if (classes.length) {
        let nearest = classes[0]
        for (const c of classes) if (Math.abs(c.value - value) < Math.abs(nearest.value - value)) nearest = c
        return nearest.label
      }
      return formatProbeReading({ value, units: unitsOf(), noData: false })
    }
    // AYNI: the axis labels without the units, which the graph draws once
    // (a long unit such as "km depth" pushed the number out of the margin).
    const formatTick = (value: number): string => {
      const full = formatAxis(value)
      const units = unitsOf()
      return units && full.endsWith(` ${units}`) ? full.slice(0, -(units.length + 1)) : full
    }
    const ui = mountDataPins(host, {
      store,
      readability,
      format: (reading) => formatShownReading(reading as ProbeReading),
      formatAxis,
      formatTick,
      axisUnits: () => {
        const isClassified = this.appState.currentDataset?.releaseEncoding?.kind === 'luma8-classified'
        return isClassified ? null : unitsOf() ?? null
      },
      dated,
      // AYNI: a time on the graph or in the CSV as the frame it seeks to, at
      // the stream's precision (frameCalendar.ts); a catalogue row keeps the
      // graph's own form.
      frameLabel: (ms) => {
        const axis = this.timelineSnapshot()?.axis
        return axis ? formatFrameLabel(graphFrameMs(ms), axis.precision) : null
      },
      frameIso: (ms) => {
        const axis = this.timelineSnapshot()?.axis
        return axis ? formatFrameIso(graphFrameMs(ms), axis.precision) : null
      },
      // AYNI (owner, 2026-10-14): the dataset's reference lines; none on a change, whose values are differences.
      referenceLines: () => (changeShown() ? [] : referenceLinesOf(this.appState.currentDataset?.id)),
      // AYNI: the CSV's header (pinsCsv.ts).
      datasetTitle: () => (this.appState.currentDataset ? displayTitle(this.appState.currentDataset) : null),
      provenance: () => this.viewProvenance({ frame: false, exportedAt: false }),
      units: unitsOf,
      // AYNI: the list, graph and CSV read what the globe shows.
      viewPins: (pins) => pins.map((p) => viewPin(p)),
      // AYNI: for the statistics (pinTrendUI): one step of the data, and whether it is an ensemble statistic.
      quantum: () => {
        const scale = primary()?.analysisFrame()?.scale
        if (!scale || (changeViewState().mode === 'percent' || changeViewState().mode === 'sigma') && changeShown()) return null
        // AYNI: the publisher's step, not a decoded code's (value error audit, 2026-10-19); null for log and classes.
        return scaleCodeStep(scale)
      },
      ensemble: () => {
        const ds = this.appState.currentDataset
        return looksLikeEnsemble([ds?.abstractTxt, ds?.enriched?.description, ds?.title].filter(Boolean).join(' '))
      },
      valuesNote: () => {
        const s = changeViewState()
        if (!changeShown(s)) return null
        if (s.other) return `A minus B, B being ${s.other.label}` // i18n-exempt: CSV header, English like every CSV row
        // AYNI: a period's climatology: each value against its own calendar month.
        const period = s.field?.period
        if (period) {
          const what = s.mode === 'percent' ? '% change' : s.mode === 'sigma' ? 'standardised change (sigma units)' : 'change' // i18n-exempt: CSV header
          return `${what} from the ${period.label} climatology, each value against its own group (${period.clim.calendar.grouping}); the climatology's mean and sigma per slot follow each pin` // i18n-exempt: CSV header, English like every CSV row
        }
        return s.mode === 'percent'
          ? `% change from the baseline held at ${s.field?.frameLabel ?? 'a frame without a date'}` // i18n-exempt: CSV header, English like every CSV row
          : `change from the baseline held at ${s.field?.frameLabel ?? 'a frame without a date'}` // i18n-exempt: CSV header, English like every CSV row
      },
      // AYNI: a period baseline's mean and σ per slot, beside each pin in the CSV.
      pinReference: () => {
        const field = changeViewState().field
        if (!changeShown() || !field?.period || changeViewState().other) return null
        return (lat, lon, timeMs) => baselineReferenceAt(lat, lon, field, timeMs)
      },
      // AYNI: the graph's playhead and click-to-seek, in its series' own time.
      now: () => frame()?.timeMs ?? null,
      seek,
      scrub: (at) => {
        scrub = at
        syncMarkers()
      },
      setPicking: (on) => {
        picking = on
        // AYNI (multi-globe plan): a pin can be dropped on any globe; all share it.
        for (const r of this.viewports.getAll()) {
          if (on) r.armPinPick(onPick)
          else r.disarmPinPick()
        }
      },
      // AYNI (multi-globe plan): the other globes' readings of the pins.
      globeTraces: () => this.globeTraces(),
      openPlans: (trigger, reason = 'data') => openPlans(trigger, reason === 'data' ? 'education' : 'research', {
        notice: t(reason === 'pins' ? 'plans.locked.pins' : reason === 'csv' ? 'plans.locked.csv' : 'plans.locked.data'),
      }),
      csvLocked: () => pinsExportLocked(),
      // AYNI: Trend, Emergence and Correlation are research tools (R2); a class borrowing the data tools does not see them.
      statistics: () => (researchToolsAllowed() ? 'open' : teachingPlan() ? 'hidden' : 'locked'),
      openResearchPlans: (trigger) => openPlans(trigger, 'research', { notice: t('plans.locked.statistics') }),
      // AYNI: the graph dragged out onto the globe goes back to the Tools panel or its button.
      dock: {
        panel: () => {
          const popover = document.getElementById('tools-menu-popover')
          return popover && !popover.classList.contains('hidden') ? popover : null
        },
        button: () => document.getElementById('tools-menu-toggle'),
        showData: () => openToolsTab('data'),
        // The same width at which the panel closes on an outside click (toolsMenuUI.ts).
        stepAside: () => {
          if (window.innerWidth <= 768) closeToolsMenu()
        },
      },
      markExtremes,
      canFollow: () => canFollow(),
      // AYNI (owner, 2026-10-18): everywhere, land only or sea only.
      surfaceAvailable: () => surfaceTiles === true,
      setSurface,
      surfaceStatus,
      markPending: () => pendingMark,
      announce: (msg) => this.announce(msg),
    })

    this.dataPinsUi = ui

    // AYNI (the AR refresh): the same pins in the headset and over a phone's
    // camera — its Data page, the pins drawn on the immersive globe, and a
    // tap on that globe in pin mode (onVrGlobeTap).
    this.xrPins = {
      store,
      readability,
      picking: () => ui.isPicking(),
      setPicking: (on) => ui.setPicking(on),
      markExtremes,
      canFollow,
      surfaceAvailable: () => surfaceTiles === true,
      setSurface,
      frame,
      // The headset reads the store's own values (the change view is the browser's, for now).
      format: (reading) => formatProbeReading(reading as ProbeReading),
      formatAxis: (v) => (changeShown() ? formatProbeReading({ value: v, units: rawUnitsOf(), noData: false }) : formatAxis(v)),
      // AYNI (owner, 2026-10-14): the store's own values, so the dataset's reference lines hold.
      referenceLines: () => shownReferenceLines(this.appState.currentDataset?.id),
    }
    this.xrPinPick = (lat, lon) => {
      onPick(lat, lon)
      if (store.full) ui.setPicking(false)
    }

    // AYNI: the research record (research tier, slice 4): what the pins read goes into it while it records.
    const pinSnapshots = (): PinSnapshot[] => store.list().map((p) => {
      const reading = p.reading as ProbeReading | null
      return {
        id: p.id,
        lat: p.lat,
        lon: p.lon,
        color: p.color,
        value: reading && !reading.noData ? reading.value : null,
        ...(reading?.noData ? { noData: true } : {}),
        ...(reading?.units ? { units: reading.units } : {}),
      }
    })
    // Loaded after start-up: nothing records until the researcher switches it on.
    const recordReady = this.initResearchRecordUI(pinSnapshots).then((handle) => { this.researchRecord = handle; return handle })
    // AYNI: Lab space (research tier, decision R5): what a lab's seats share, opened through the same paths.
    void this.initLabSpaceUI(store, recordReady)
    store.subscribe(() => {
      if (this.researchRecord?.isRecording()) this.researchRecord.pins(pinSnapshots())
    })

    // AYNI: switching between values and change redraws the list and the graph at once.
    onChangeView(() => ui.refresh())
    // AYNI (owner, 2026-10-20): the following pins rank what the globe
    // shows, so a change switched on or off, another mode or baseline,
    // moves them on the frame shown, paused too.
    let extremesKey: unknown[] = []
    onChangeView((s) => {
      const key = [changeShown(s), s.mode, s.field, s.other, s.floor]
      if (key.every((k, i) => k === extremesKey[i])) return
      extremesKey = key
      if (!store.following) return
      locateBudget.reset()
      store.refresh()
    })
    // AYNI: and the floating legend says what the colours on the globe mean now.
    let legendShown = changeShown()
    let legendKey = ''
    onChangeView((s) => {
      const key = changeShown(s) ? `${s.mode}|${s.limit}|${s.range?.min}|${s.range?.max}|${s.colours.palette}|${s.colours.inverted}|${s.field?.frameLabel}` : ''
      if (changeShown(s) === legendShown && key === legendKey) return
      legendShown = changeShown(s)
      legendKey = key
      this.refreshPanelLegends()
    })
    const readPin = (lat: number, lon: number) => primary()?.probeValueAt(lat, lon) ?? null
    // AYNI: the pins are read on the frame the video shows (`measure/frameSync.ts`).
    const frameSync = createFrameSync((mediaTime) => {
      if (store.list().length) store.sample(frame(mediaTime ?? undefined), readPin, locateFollowing)
    })
    let lastReadability: PinReadability | null = null
    setInterval(() => {
      const r = readability()
      if (r !== lastReadability) {
        lastReadability = r
        ui.refresh()
      }
      syncMarkers()
      // AYNI: the View tab's colours, and the legends, follow the dataset
      // once its frame is up.
      this.viewPalette?.refresh()
      this.viewOpacity?.refresh()
      this.refreshLegendsIfStale()
      // AYNI: the bar's Analyze button follows the plan, the dataset and the panel.
      syncAnalyzeButton()
      this.syncStudioButton()
      const video = pinVideo()
      frameSync.watch(video)
      if (!store.list().length) return
      // AYNI (owner, 2026-10-18): a classified dataset loaded under following pins: they stay where they are.
      if (store.following && !canFollow()) store.setFollow(false)
      // AYNI: nothing is read mid-seek: the time already names the new
      // frame while the picture is still the old one (`frameSync.ts`).
      if (video?.seeking) return
      this.sampleComparePins(store.list(), frame())
      // On the frame shown, or by the clock where frames are not reported (`frameSync`).
      frameSync.tick(video)
      // AYNI (multi-globe plan): the same pins on the other globes.
      if (this.panelStates.length > 1) this.sampleGlobePins(store.list(), frame())
    }, 200)
  }

  /**
   * AYNI: the research record (docs/AYNI_XR_RESEARCH_TIER_PLAN.md, slice 4):
   * what it reads of the primary globe (the dataset, the view's link and
   * citation, the pins), and how a saved record's step is put back: its
   * dataset loaded, then its view link applied, as a pasted link would be.
   */
  private async initResearchRecordUI(pinSnapshots: () => PinSnapshot[]): Promise<ResearchRecordHandle> {
    const [{ initResearchRecord }, { viewQueryOf }] = await Promise.all([import('./ui/researchRecordUI'), import('./services/research/researchReplay')])
    const primary = () => this.viewports.getPrimary()
    return initResearchRecord({
      view: () => {
        const ds = this.appState.currentDataset
        const input = ds ? this.citationForView() : null
        if (!ds || !input) return null
        const meta = ds.timelineLink ? this.dsaTimelines.getMetadata(ds.timelineLink) : null
        const pins = pinSnapshots()
        const units = pins.find((p) => p.units)?.units ?? primary()?.analysisFrame()?.scale.units
        const kind = primary()?.probeKind() ?? 'picture'
        const state = this.currentViewState()
        return {
          dataset: {
            id: ds.id,
            title: displayTitle(ds),
            ...(units ? { units } : {}),
            dataEnabled: kind !== 'picture',
            ...(ds.releaseDescriptorLink ? { releaseDescriptor: ds.releaseDescriptorLink } : {}),
            ...(meta?.modified ? { dataVersion: meta.modified } : {}),
          },
          frameMs: input.frameMs,
          camera: state.camera,
          viewUrl: input.viewUrl,
          citation: citationText(input),
          playing: this.appState.isPlaying,
          pins,
        }
      },
      openView: async (step) => {
        if (!step.appId) return false
        const ds = this.appState.currentDataset
        if (ds?.id !== step.appId && ds?.legacyId !== step.appId) {
          if (!(await this.loadDatasetAndConfirm(step.appId, 'url'))) return false
        }
        const view = parseViewParams(viewQueryOf(step.viewUrl))
        if (view) await this.applyViewLink(view)
        return true
      },
      readPoint: (lat, lon) => {
        const r = primary()?.probeValueAt(lat, lon) ?? null
        if (!r) return undefined
        return r.noData ? null : r.value
      },
      readRegion: (bounds) => {
        const f = primary()?.analysisFrame()
        if (!f) return undefined
        const window = bounds ? windowForBounds(f.snapshot, bounds, f.options) : undefined
        if (bounds && !window) return null
        return summarize(f.snapshot, f.scale, f.options, window ?? undefined)?.mean ?? null
      },
      tolerance: () => {
        const scale = primary()?.analysisFrame()?.scale
        const release = (scale as { release?: { kind?: string } } | undefined)?.release
        if (!scale || (release?.kind && release.kind !== 'luma8-linear')) return 0
        return (scaleCodeStep(scale) ?? 0) / 2
      },
      announce: (m) => this.announce(m),
    })
  }

  /**
   * AYNI: Lab space (docs/AYNI_XR_RESEARCH_TIER_PLAN.md, decision R5): what
   * it reads of the primary globe (the dataset, the view link's query, the
   * pins), and how a shared item is opened: its dataset loaded and its view
   * link applied as a pasted link would be, pins put down in place of the
   * ones there, a record handed to the Research record panel's replay.
   */
  private async initLabSpaceUI(store: PinStore, recordReady: Promise<ResearchRecordHandle>): Promise<void> {
    const { initLabSpace } = await import('./ui/labSpaceUI')
    initLabSpace({
      view: () => {
        const ds = this.appState.currentDataset
        return ds ? { dataset: ds.id, datasetTitle: displayTitle(ds), query: viewQuery(this.currentViewState()) } : null
      },
      pins: () => store.list().map((p, i) => ({ lat: p.lat, lon: p.lon, label: pinName(p, i), ...(p.name ? { named: true as const } : {}) })),
      openView: async (view) => {
        const ds = this.appState.currentDataset
        if (ds?.id !== view.dataset && ds?.legacyId !== view.dataset) {
          if (!(await this.loadDatasetAndConfirm(view.dataset, 'url'))) return false
        }
        const state = parseViewParams(view.query)
        if (state) await this.applyViewLink(state)
        return true
      },
      placePins: (pins) => {
        store.clear()
        return pins.filter((p) => {
          const pin = store.add(p.lat, p.lon)
          if (pin && p.named) store.rename(pin.id, p.label)
          return pin !== null
        }).length
      },
      playing: () => this.appState.isPlaying,
      replayRecord: async (k, files) => (await recordReady).replay(k, files),
      announce: (m) => this.announce(m),
    })
  }

  /** AYNI: while comparing, the overlay's value at each pin, on the pin's own slots (Compare's pins and graph). */
  private sampleComparePins(pins: readonly Pin[], at: PinFrame | null): void {
    if (!this.compareId || !at || at.timeMs === null) return
    const candidate = this.compareCandidates().find((c) => c.id === this.compareId)
    if (!candidate) return
    for (const pin of pins) {
      if (!pin.series) continue
      let series = this.compareSeries.get(pin.id)
      if (!series || series.startMs !== pin.series.startMs || series.endMs !== pin.series.endMs || series.slots.length !== pin.series.slots.length) {
        series = createSeries(pin.series.startMs, pin.series.endMs, pin.series.slots.length) ?? undefined
        if (!series) continue
        this.compareSeries.set(pin.id, series)
      }
      recordSample(series, at.timeMs, candidate.valueAt(pin.lat, pin.lon))
    }
  }

  /**
   * Fullscreen, F11 and the idle cursor. AYNI: the browser's Fullscreen
   * API only; TerraViz's desktop window chrome (decorations, Ctrl+Q,
   * fullscreen restored at launch) went with the desktop app. Nothing is
   * restored at boot: `requestFullscreen` needs a user gesture.
   */
  private initWindowChrome(): void {
    const fullscreen = createFullscreenController({ host: createDomChromeHost() })
    const idleCursor = createIdleCursor()
    // Only while fullscreen: hiding the pointer of a windowed app the
    // user is still driving would be a bug, not a feature.
    fullscreen.onChange(on => idleCursor.setActive(on))
    this.fullscreen = fullscreen
    this.idleCursor = idleCursor
  }

  /** Clean up all resources: video streams, textures, and every viewport renderer. */
  dispose(): void {
    this.fullscreen?.dispose()
    this.fullscreen = null
    this.idleCursor?.dispose()
    this.idleCursor = null
    this.teardownAllPanelResources()
    this.viewports.dispose()
    this.panelStates = []
  }
}

// Register service worker for tile caching (cache-first strategy for GIBS tiles).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(err => {
    logger.warn('[SW] Registration failed:', err)
  })
}

// Initialize app on DOM ready
document.addEventListener('DOMContentLoaded', async () => {
  // Resolve user locale and apply data-i18n attributes before any
  // UI module renders. English is bundled inline; non-English
  // locales lazy-load here. See docs/archive/terraviz/I18N_PLAN.md.
  await bootstrapI18n()

  // AYNI: the feedback reader (`src/ui/adminFeedback.ts`). `/admin/feedback`
  // boots its own page and skips the globe / dataset / chat boot; its
  // chunk is fetched only on this route. TerraViz's publisher portal
  // (`/publish`) and blog (`/blog`) gates were removed on 2026-10-06:
  // those addresses now open the normal app.
  if (location.pathname.replace(/\/+$/, '') === '/admin/feedback') {
    const { bootFeedbackReader } = await import('./ui/adminFeedback')
    await bootFeedbackReader()
    return
  }

  const app = new InteractiveSphere()
  app.setupEventListeners()
  await app.initialize()

  ;(window as any).app = app

  // Emit session_start after the app is fully initialized so every
  // subsequent event carries a non-zero client_offset_ms relative to
  // it. The hook itself is best-effort — if telemetry is off (tier
  // or compile flag) the emit() calls inside no-op.
  if (TELEMETRY_BUILD_ENABLED) {
    void initSession()
    startPerfSampler()
  }

  // §9.2 — stamp the session-end timestamp on pagehide so the next
  // visit can compute "new since your last visit" and (Phase 7 §9.3)
  // the returning-user Orbit greeting trigger. This is a synchronous
  // localStorage write, which is permitted from `pagehide` (unlike
  // sendBeacon); it rides alongside the analytics session_end flush
  // but is independent of the telemetry build flag — visit memory is
  // a local convenience cache, never sent server-side.
  window.addEventListener('pagehide', () => writeLastSession())
})
